import { maiPubGetPrebidSplit } from './lib/prebid-split';

let maiPubVersion    = '1012';
let _gptEnabled = false;
let _coreGptConfigQueued = false;
let _stickyFooterHandled = false; // Guards one-time sticky-footer setup (offset sync + ResizeObserver) so the late-DOM reconciliation can run it if the footer parsed in after maiPubRun's snapshot.
let _stickyFooterLoweredPx = 0; // How far the sticky footer bar is moved below the screen so it shows only its ad (maiPubFitStickyFooter).
let _stickyFooterFitLogged = ''; // The sticky footer fit's last logged outcome this render, so the observers' re-checks don't repeat it (maiPubLogStickyFooterFit).
let _stickyFooterUnfitRank = 0; // How telling the reason the sticky footer isn't fitted, logged this request, is: 1 the creative still 1x1 or no ad, 2 what the creative drew (maiPubUnfitStickyFooter).
let _stickyFooterRequestPending = false; // Between the sticky footer's slotRequested and slotRenderEnded: the slot still holds the last ad (maiPubWatchStickyFooterFit).

// --- Prebid ownership/compat guard  ---
if (window.__MAI_PB_INIT__) {
	console.warn('[maiPub] Detected another Prebid wrapper. Entering compat mode (no Prebid init).');
	window.__MAI_PB_COMPAT__ = true; // we won't configure Prebid/Magnite
} else {
	window.__MAI_PB_INIT__ = true;
	window.__MAI_PB_OWNED__ = true;

	// Safe to set up our Magnite DM adpater
	window.pbjs = window.pbjs || { que: [] };
}

// Minimal GPT bootstrap
// window.googletag = window.googletag || {cmd: []};

// ensures that googletag is an object and that googletag.cmd is an array, no matter what
// issue on fast reloads where a race condition occurred
window.googletag = window.googletag || {};
window.googletag.cmd = window.googletag.cmd || [];

// Configure SRA Immediately
// This does not track users. It simply tells GPT "When you eventually run, use SRA mode."
// Doing this here prevents the "service already enabled" warning if another script triggers GPT early.
googletag.cmd.push(function() {
    if (typeof googletag.setConfig === 'function') {
        googletag.setConfig({ singleRequest: true });
    } else {
        googletag.pubads().enableSingleRequest();
    }
});


// Define global variables.
const adGamIds          = []; 		// Stores GAM Ad Unit Paths (e.g., /12345/leaderboard) for slots defined by this script.
const adSlots           = []; 		// Stores the actual GPT Slot objects returned by defineSlot.
const refreshTime       = 32000;  	// Seen-time (NOT wall-clock) a slot must bank before it may refresh. Google's floor is 30s; the +2s buffer absorbs timer jitter so we never fire under 30s. Tunable (e.g. 35000 for a longer dwell).
const refreshViewabilityThreshold = 50; // % of pixels in view required to (a) accumulate seen-time and (b) fire a refresh. Matches MRC display viewability (>=50% for 1s).
const largeFormatViewabilityThreshold = 30; // MRC large-format viewability: ads at/above largeFormatPixels use 30% instead of 50%.
const largeFormatPixels = 242500; // MRC "large ad" area threshold in px (e.g. 970x250 = 242,500). At/above this, use largeFormatViewabilityThreshold.
const refreshOutOfPage  = false; 	// Refresh out-of-page slots (Kargo "spotlight": overlay/in-article/masthead)? FALSE per publisher decision — spotlight is a Kargo-controlled single-render high-impact format (measured by Kargo/IAS, not GAM Active View), so it renders once per page and is not refreshed. Set true to restore a time-based refresh.
const slotManager       = {};
const timeoutManager    = {}; 		// Manages setTimeout IDs for per-slot refreshes.
const cmpTimeout        = 2000; 	// Fallback in case CMP never responds.
const bidderTimeout     = 2800; 	// Timeout for PBJS 
const prebidAuctionDelay = 50; 	// Prebid userSync.auctionDelay — max wait for ID modules (ID5) before the auction starts. A saved ID is in the auction anyway while ID5 refreshes it, and at 400ms ID5 never answered in time in testing, so the wait only delayed the ads. Keep it above 0: at 0 Prebid doesn't ask ID5 until the first auction has ended. Sits in front of the bidder timeout on cold loads.
const prebidPreAuctionAllowance = 400; // Room in the maiPubRequestSlots failsafe for Prebid's waits before the auction starts: ID5 (prebidAuctionDelay) and, since the North American auction no longer waits for the CMP, Prebid's own wait for SourcePoint. Was prebidAuctionDelay itself (400ms) until 1.37.5-beta8; kept at 400 so the shorter ID5 wait doesn't make the failsafe fire sooner.
const maxEmptyRetries 	= 1; 		// Number of times to retry an empty slot
const fastPathTimeout   = 1500; 	// Timeout for high-priority ads (sticky + ATF, and lazy ads already on screen when asked for) - faster delivery.
const emptySlotTimeout  = 2000; 	// Timeout for retrying empty slots on first render (2 seconds).
const maxBlankRetries   = 1; 		// Times a slot whose ad drew nothing is asked again per auction (maiPubWatchBlankRender).
const blankRenderWaitMs = 4000; 	// An ad that has drawn nothing this long after its slot came half on screen is blank (maiPubWatchBlankRender).
const outstreamNoFillLimit = 2; 	// A looping outstream player stops for the pageview after this many failed tries in a row with no ad (a no-fill error, or a pod in which no ad came back); 3 when ads came back but failed to play. See _countFailedAttempt().
const videoGateMaxWaitMs = 2000; 	// The most a page's first display ads wait for an on-screen outstream video ad to start before going to GAM anyway. In a real-browser log the waits that ended with the video starting took 1.6-2.0s, so this rarely cuts one short; it stops an empty or looping video pod holding them for good.
const lazyLoadRootMarginPx = 200; 	// Lazy-load ads within this many px of the viewport. Lower = load closer to the fold = higher viewability (was 600). Tunable: raise if slots show blank before render, lower to tighten viewability.
const lazyLookaheadMs   = 1500; 	// Scroll look-ahead: a lazy ad is also requested once it is this many ms of scrolling away at the current speed: the time from request to render (0.9-1.2s in a real-browser log: ~0.1s batching, 0.35-0.7s Prebid, ~0.45s GAM), plus ~0.3s for a reader who speeds up. Below ~130px/s the lazyLoadRootMarginPx zone is the larger of the two, so a reader scrolling slowly loads exactly as before.
const lazyLookaheadMaxScreens = 2; 	// ...but never more than this many screen heights ahead, so a fling doesn't request ads the reader may never reach.
const floatingVideoFooterGapPx = 15; // Breathing room added above the sticky footer when a floating video auto-clears it (Gutter Y = 0). Tunable.
const debug =
  window.location.search.includes('dfpdeb') ||
  window.location.search.includes('maideb') ||
  window.location.search.includes('pbjs_debug=true') ||
  window.__MAI_PUB_DEBUG__ === true;

const refreshKey        = 'refresh';
const requestCountKey   = 'rc'; 		// GAM key: how many times the slot was asked for before on this pageview (maiPubSetRequestCount).
const requestCountMax   = 5; 		// ...its top value, standing for that many or more.
const botKey            = 'bot'; 	// GAM key: why the visitor is very likely a bot (crawler, automation, datacenter), on a page that loads the ads anyway (maiPubTagBot).
let timestamp           = Date.now(); // Before any maiPubLog() — used for log timing
let spotlightOopSlug    = null; // First spotlight out-of-page slot on the page (one per page)

/**
 * Batched request system for optimizing ad slot requests
 *
 * Structure:
 * - pendingSlugs: Set - Collection of ad slot slugs waiting to be processed
 * - batchTimeout: number|null - Timeout ID for the current batch delay
 * - batchDue: number - When (performance.now()) the pending batch is due to go out
 * - batchDelay: number - Delay in milliseconds before processing batch (default: 100ms)
 * - fastBatchDelay: number - The delay for ads on screen (fast path, or lazy ads already in view)
 * - processing: boolean - Flag to prevent concurrent batch processing
 *
 * Purpose: Collects multiple ad slot requests and processes them in batches to improve
 * performance by reducing the number of individual ad requests to GAM/Prebid.
 *
 * Usage: Called by maiPubBatchRequestSlots() which adds slugs to pendingSlugs and
 * sets a timeout. When timeout fires, maiPubProcessBatchedRequests() processes all
 * pending slugs at once.
 */
const requestBatcher = {
	pendingSlugs: new Set(),
	pendingSources: new Map(), // Track source for each slug to determine timeout
	batchTimeout: null,
	batchDue: 0,
	// Configurable batch delay - can be adjusted based on performance needs
	// 100ms balances batching effectiveness with perceived responsiveness
	batchDelay: maiPubAdsVars.batchDelay || 100, // Default 100ms, configurable via PHP
	// Ads on screen wait only long enough for the on-screen lazy ads the zone reports a frame
	// or so after the fast path (13ms on allhiphop.com), so they share one auction.
	fastBatchDelay: 25,
	processing: false
};

// set up global variables from maiPubAdsVars
const ads                   = maiPubAdsVars['ads'];
const gamBase               = maiPubAdsVars.gamBase;
const gamChildNetworkCode   = maiPubAdsVars.gamChildNetworkCode;
const refreshValue          = maiPubAdsVars.targets.refresh;
const log                   = maiPubAdsVars.debug;
// Debug only: state for the "CMP timing" log lines. ID5's saved ID is read here, before
// Prebid loads and rewrites it. See maiPubLogSourcepointTiming().
const maiPubTiming          = { spReady: null, wrapperLoaded: null, sharedByWrapper: false, id5Saved: ( debug || log ) ? maiPubReadId5Saved() : null };
maiPubWatchId5Keys(); // Debug only: names whoever writes ID5's shared "id5id" key, and our own key's consent fingerprint.
// Debug only: each slot's last fill (GAM line item and advertiser), for the "Prebid won" line. See maiPubLogPrebidWin().
const maiPubLastFill        = {};
// Debug only: each slot's latest render, so a [SIZE-DIAG] check still waiting on an older one is dropped. See maiPubLogAdSizeDiag().
const maiPubSizeDiagRenders = {};
let _prebidOutcomesWatched  = false; // Debug only: maiPubWatchPrebidOutcomes() hooks Prebid once.
const serverPpid            = maiPubAdsVars.ppid;                       // from the funciton maipub_get_ppid() in functions-utility.php, passed from Localization in class-output.php (it's the hashed eamil if user logged in)
const gamManageAccount = maiPubAdsVars.manager_domain === 'Manage Account' ? true : false;

// --- Prebid display wrapper split test ---
// When display prebid wrappers (bidder adpaters in the settings) are configured, pick exactly one per visitor
// (or none = GAM Only) and overwrite the wrapper flags so only the winner bids.
// All downstream injection/bidding logic reads these flags unchanged. Runs at
// module load, before initStatus/requestManager are built. 

console.info('[maiPub] Adapter Status: Magnite DM: ', maiPubAdsVars.magnite);
console.info('[maiPub] Adapter Status: RevIQ: ', maiPubAdsVars.reviq);
console.info('[maiPub] Adapter Status: Catalyst: ', maiPubAdsVars.catalyst);

if ( Array.isArray( maiPubAdsVars.prebidSplit ) && maiPubAdsVars.prebidSplit.length ) {
	// Normally decided in <head> by mai-publisher-split.js, before the CMP, so a
	// RevIQ visitor never loads SourcePoint. Decided here only when that script
	// did not run, e.g. a page cached before it existed.
	const early = window.maiPubDisplaySplit;
	let   splitWinner;

	if ( early && 'object' === typeof early ) {
		( early.logs || [] ).forEach( ( args ) => maiPubLog( ...args ) );
		splitWinner = early.winner;
	} else {
		splitWinner = maiPubGetPrebidSplit( maiPubAdsVars.prebidSplit, maiPubLog );
	}

	maiPubAdsVars.magnite  = !! splitWinner && 'magnite'  === splitWinner.key;
	maiPubAdsVars.catalyst = !! splitWinner && 'catalyst' === splitWinner.key;
	maiPubAdsVars.reviq    = !! splitWinner && 'reviq'    === splitWinner.key;

	console.info('[maiPub] Prebid Adapter split: adapter winner =', splitWinner ? splitWinner.label : 'none (GAM Only)' );
	maiPubLog( 'Prebid Adapter split: adapter winner =', splitWinner ? splitWinner.label : 'none (GAM Only)' );
}

// --- Display routing ---
// Derive the active display path from the (post-split-resolution) booleans
// above. Exactly one of these is true after maiPubGetPrebidSplit runs, or
// none (= 'gam-direct') if the visitor was bucketed to "GAM Only".
//
// Magnite and Catalyst remain header-bidding wrappers feeding our GAM.
// RevIQ is different: when displayMode === 'reviq', RevIQ takes over the
// entire display stack (loads gpt.js, defines slots, runs the auction,
// refreshes). B2.3 will use this flag to no-op maiPubRunDisplay under
// 'reviq' so we stay out of RevIQ's way on the shared window.googletag.
const displayMode =
	maiPubAdsVars.reviq    ? 'reviq'    :
	maiPubAdsVars.magnite  ? 'magnite'  :
	maiPubAdsVars.catalyst ? 'catalyst' :
	                         'gam-direct';

console.info('[maiPub] Display Mode:', displayMode);
maiPubLog('Display Mode:', displayMode);

const SESSION_KEY = 'maiPubSessionData';

// The last real consent answer from SourcePoint or RevIQ's CMP, kept across
// sessions. The passport is sessionStorage and dies with the tab, so without this
// every new tab or visit waited on the CMP again. See maiPubApplySavedConsent().
const CONSENT_COOKIE = 'maipub_consent';

// SourcePoint keeps its consent record for a year. So does the cookie, or less
// when SourcePoint's own record expires sooner.
const CONSENT_COOKIE_MAX_MS = 365 * 24 * 60 * 60 * 1000;

// Consent fields that live at the top level of the session passport. Used both
// to persist them (maiPubSetConsent) and to prune the legacy nested copies
// (maiPubPruneNestedConsent).
const CONSENT_PASSPORT_KEYS = [
    'source',
    'hasConsent',
    'gdprConsentUUID',
    'gdprApplies',
    'gdprConsentedToAll',
    'usnatConsentUUID',
    'usnatApplies'
];

// The only keys that legitimately belong on passport.consent — raw consent
// strings other systems read. Everything else found there is fallout from the
// nesting bug and gets pruned on load.
const CONSENT_STRING_KEYS = ['tcString', 'gppString'];

// Minimum geo.js worker version we accept. Workers are deployed per Cloudflare
// account and roll out independently, so treat this as a floor, never an equality.
const GEO_MIN_VERSION = '1.0.5';

// How long the Prebid configuration will wait for geo.js to settle before
// configuring without it. This exists because maiPubGetGeo() injects /geo.js
// fire-and-forget while the Prebid config path runs on its own schedule, so the
// two race — and on a live tvnewscheck.com load the auction won:
//
//     127ms  ID5: pd built with 3 key(s): puid, domain, ua
//     202ms  geo.js enrichment complete
//
// pd therefore shipped without keys 10/11 (ipv4/ipv6), which are the whole
// reason the worker returns an IP. Worse, ID5 derives its local cache key from
// a hash of the pd string, so a pd that gains a key on the next pageview forces
// a fresh ID5 fetch instead of a cache hit.
//
// The cap is a worst case, not a cost: geo.js resolved in ~75ms above, and the
// common path (a cached passport geo on any pageview after the first) resolves
// the gate synchronously with no delay at all. 300ms only comes into play when
// geo.js is hanging, and a hard failure fires onerror well before that.
const GEO_PD_WAIT_MS = 300;

// Has geo settled — resolved, failed, or read from the passport? "Settled" is
// deliberately not "succeeded": a site with no worker route must not stall the
// auction for 300ms on every pageview.
let geoSettled = false;
let geoWaiters = [];

/**
 * Marks geo as settled and releases anything waiting on it.
 *
 * Idempotent, so it is safe to call from every geo outcome path.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubMarkGeoSettled() {
	if ( geoSettled ) {
		return;
	}

	geoSettled = true;

	const waiters = geoWaiters;
	geoWaiters    = [];

	waiters.forEach( function ( fn ) {
		try {
			fn();
		} catch ( err ) {
			maiPubLog( 'GEO gate: waiter threw', err );
		}
	} );
}

/**
 * Whether geo is known right now: settled this pageview, or read from the session passport.
 *
 * @since TBD
 *
 * @return {boolean}
 */
function maiPubGeoKnown() {
	return !! ( geoSettled || ( maiPubSessionData.geo && maiPubSessionData.geo.country ) );
}

/**
 * Runs a callback once geo has settled, or after GEO_PD_WAIT_MS, whichever is first.
 *
 * Synchronous when geo is already known, so the overwhelmingly common case
 * (pageview 2+ reading geo from the session passport) pays nothing.
 *
 * @since TBD
 *
 * @param {Function} callback Invoked exactly once.
 * @return {void}
 */
function maiPubWhenGeoReady( callback ) {
	if ( typeof callback !== 'function' ) {
		return;
	}

	if ( maiPubGeoKnown() ) {
		callback();
		return;
	}

	let done = false;

	const once = function ( reason ) {
		if ( done ) {
			return;
		}

		done = true;

		maiPubLog( `GEO gate: releasing Prebid configuration (${reason}).` );
		callback();
	};

	geoWaiters.push( function () {
		once( 'geo settled' );
	} );

	setTimeout( function () {
		once( `timed out after ${GEO_PD_WAIT_MS}ms` );
	}, GEO_PD_WAIT_MS );

	maiPubLog( `GEO gate: geo not resolved yet — holding Prebid configuration for up to ${GEO_PD_WAIT_MS}ms so ID5 pd can carry the IP.` );
}

// --- BOT CHECK ---
// A visitor who is very likely a bot gets the page without the ads: no bidders, no GPT
// slots or ad requests, no video or audio ads. Every bot pageview with ads sends bid
// requests that bidders count against the domain's invalid-traffic rate, and skews our
// impressions, viewability and RPM. See maiPubStartAdsUnlessBot().

// The site's Bots setting (Ad Settings). mode 'block' leaves the ads out for crawlers and
// automated browsers, and for cloud networks too with blockCloud; a bot it doesn't block
// gets the ads, tagged. 'log' loads the ads for every bot, tagged. 'off' doesn't check.
// Missing (a page cached before the setting existed) is off.
const maiPubBotSettings = maiPubAdsVars.bots || { mode: 'off', blockCloud: false };

// ?maiads forces the ads on for a bot: for our own checks in automated browsers
// (Playwright and Puppeteer set navigator.webdriver). The tag still goes on.
const maiPubForceAds = window.location.search.includes( 'maiads' );

// Crawlers that name themselves in the user agent, matched without case. Never a bare
// "bot": Cubot phones have "CUBOT" in theirs.
const maiPubBotCrawlers = [
	'Googlebot', 'Google-InspectionTool', 'GoogleOther', 'Storebot-Google', 'AdsBot-Google', 'Mediapartners-Google',
	'bingbot', 'Applebot', 'DuckDuckBot', 'YandexBot', 'Baiduspider', 'PetalBot',
	'GPTBot', 'ChatGPT-User', 'OAI-SearchBot', 'ClaudeBot', 'Claude-User', 'Claude-SearchBot', 'PerplexityBot', 'Perplexity-User',
	'Bytespider', 'Amazonbot', 'CCBot', 'facebookexternalhit', 'meta-externalagent', 'Twitterbot',
	'AhrefsBot', 'SemrushBot', 'MJ12bot', 'DotBot',
];

// Cloud and hosting networks, where bots run normal-looking browsers: AWS (16509, 14618),
// Google Cloud (396982), DigitalOcean, OVH, Hetzner, Akamai Connected Cloud (Linode),
// Vultr, Oracle, Alibaba, Tencent, Contabo, Scaleway, Leaseweb. Matched on geo.js's ASN,
// not its network name (`isp`), which can be the cloud's customer rather than the cloud.
// Never the networks iCloud Private Relay and Cloudflare WARP exit through (Cloudflare
// 13335, Akamai 36183 and 20940, Fastly 54113): those visitors are people. Microsoft (8075)
// and Google (15169) carry their own staff and services too, and VPN providers carry
// people, so they're left off.
const maiPubCloudAsns = [ 16509, 14618, 396982, 14061, 16276, 24940, 63949, 20473, 31898, 45102, 132203, 51167, 12876, 60781 ];

// This visitor's verdict (maiPubBotVerdict): read at startup, again once geo.js answers.
let maiPubBot = { bot: false, reason: '', detail: '' };

/**
 * Whether a visitor is very likely a bot, and why.
 *
 * In order:
 *  - Lighthouse (PageSpeed Insights) is never a bot, so its scores are measured with the
 *    ads. It runs in headless Chrome, which the automation check could otherwise catch.
 *  - crawler: the user agent names one (maiPubBotCrawlers).
 *  - automation: navigator.webdriver (Playwright, Puppeteer, Selenium, Chrome started with
 *    --enable-automation), or headless Chrome in the user agent or its brands.
 *  - datacenter: geo.js's network is a cloud or hosting one (maiPubCloudAsns).
 *
 * @since TBD
 *
 * @param {Navigator}   nav The browser's navigator.
 * @param {Object|null} geo geo.js data (the passport's `geo`), or null while it isn't known.
 *
 * @return {{bot: boolean, reason: string, detail: string}} reason: 'crawler', 'automation', 'datacenter' or ''.
 */
function maiPubBotVerdict( nav, geo ) {
	const ua = String( ( nav && nav.userAgent ) || '' );

	if ( ua.includes( 'Chrome-Lighthouse' ) ) {
		return { bot: false, reason: '', detail: 'Lighthouse' };
	}

	const uaLower = ua.toLowerCase();
	const crawler = maiPubBotCrawlers.find( ( token ) => uaLower.includes( token.toLowerCase() ) );

	if ( crawler ) {
		return { bot: true, reason: 'crawler', detail: crawler };
	}

	if ( nav && true === nav.webdriver ) {
		return { bot: true, reason: 'automation', detail: 'navigator.webdriver' };
	}

	const brands = ( nav && nav.userAgentData && nav.userAgentData.brands ) || [];

	if ( ua.includes( 'HeadlessChrome' ) || brands.some( ( b ) => b && 'HeadlessChrome' === b.brand ) ) {
		return { bot: true, reason: 'automation', detail: 'HeadlessChrome' };
	}

	const asn = geo ? Number( geo.asn ) : 0;

	if ( asn && maiPubCloudAsns.includes( asn ) ) {
		return { bot: true, reason: 'datacenter', detail: `AS${ asn }${ geo.isp ? ` (${ geo.isp })` : '' }` };
	}

	return { bot: false, reason: '', detail: '' };
}

/**
 * Whether the site's Bots setting leaves the ads out for a verdict.
 *
 * @since TBD
 *
 * @param {Object}  verdict  maiPubBotVerdict().
 * @param {Object}  settings The Bots setting (maiPubBotSettings).
 * @param {boolean} force    ?maiads is on.
 *
 * @return {boolean}
 */
function maiPubBotSkipsAds( verdict, settings, force ) {
	if ( ! verdict.bot || force || 'block' !== settings.mode ) {
		return false;
	}

	return 'datacenter' !== verdict.reason || !! settings.blockCloud;
}

/**
 * Starts the ads, unless the visitor is very likely a bot the site's Bots setting leaves
 * them out for.
 *
 * Adds no wait for people: the crawler and automation checks are read at startup, and so
 * is the network on any pageview after the first (geo.js from the session passport). On a
 * first pageview the network is known once geo.js answers:
 *  - with cloud networks blocked, the ads wait for it, at most GEO_PD_WAIT_MS (the most
 *    Prebid's configuration already waits for it), then go ahead without it;
 *  - otherwise they go ahead, and are tagged if it turns out to be a cloud network.
 * Fails open: no geo, no verdict, the ads as usual.
 *
 * @since TBD
 *
 * @param {Function} startAds Starts the ads (maiPubInitParallel).
 *
 * @return {void}
 */
function maiPubStartAdsUnlessBot( startAds ) {
	const settings = maiPubBotSettings;

	if ( 'block' !== settings.mode && 'log' !== settings.mode ) {
		startAds();
		return;
	}

	if ( maiPubBot.bot || maiPubGeoKnown() ) {
		maiPubApplyBotVerdict( maiPubBot, startAds );
		return;
	}

	let started = false;
	const start = () => {
		if ( started ) {
			return;
		}

		started = true;
		maiPubApplyBotVerdict( maiPubBot, startAds );
	};

	geoWaiters.push( () => {
		maiPubBot = maiPubBotVerdict( navigator, maiPubSessionData.geo );

		if ( ! started ) {
			start();
		} else if ( maiPubBot.bot ) {
			maiPubApplyBotVerdict( maiPubBot, null );
		}
	} );

	if ( 'block' === settings.mode && settings.blockCloud && ! maiPubForceAds ) {
		maiPubLog( `Bot check: waiting up to ${ GEO_PD_WAIT_MS }ms for geo.js to check the network.` );
		setTimeout( start, GEO_PD_WAIT_MS );
		return;
	}

	start();
}

/**
 * Acts on a verdict: leaves the ads out, or starts them tagged bot=<reason>. One debug
 * line says which and why.
 *
 * @since TBD
 *
 * @param {Object}        verdict  maiPubBotVerdict().
 * @param {Function|null} startAds Starts the ads; null when they already have (geo.js answered late): tag only.
 *
 * @return {void}
 */
function maiPubApplyBotVerdict( verdict, startAds ) {
	if ( ! verdict.bot ) {
		if ( verdict.detail ) {
			maiPubLog( `Bot check: ${ verdict.detail }, not a bot: ads load.` );
		}

		if ( startAds ) {
			startAds();
		}

		return;
	}

	const what    = 'datacenter' === verdict.reason ? `cloud network ${ verdict.detail }` : `${ verdict.reason } (${ verdict.detail })`;
	const blocked = maiPubBotSkipsAds( verdict, maiPubBotSettings, maiPubForceAds );

	if ( blocked && startAds ) {
		console.info( `[maiPub] Bot check: ${ what }: ads skipped.` );
		maiPubLog( `Bot check: ${ what }: ads skipped.` );
		return;
	}

	let why = 'log only';

	if ( maiPubForceAds ) {
		why = '?maiads forces them on';
	} else if ( blocked ) {
		why = 'geo.js answered after they started';
	} else if ( 'block' === maiPubBotSettings.mode ) {
		why = 'cloud networks not blocked';
	}

	maiPubLog( `Bot check: ${ what }: ${ why }, ads load.` );
	maiPubTagBot( verdict.reason );

	if ( startAds ) {
		startAds();
	}
}

/**
 * Tags every ad request on the page bot=<reason> (page-level targeting), so GAM reports
 * can compare bot traffic that still gets ads with people. GPT merges page targeting key
 * by key, so this keeps the page's other keys whenever it runs. Display only: video and
 * audio ads build their own requests.
 *
 * @since TBD
 *
 * @param {string} reason 'crawler', 'automation' or 'datacenter'.
 *
 * @return {void}
 */
function maiPubTagBot( reason ) {
	googletag.cmd.push( () => {
		if ( 'function' === typeof googletag.setConfig ) {
			googletag.setConfig( { targeting: { [ botKey ]: reason } } );
		} else {
			googletag.pubads().setTargeting( botKey, reason );
		}
	} );
}

/**
 * ID5 partner data (pd) semantic key -> numeric key.
 *
 * ID5's pd is a base64-encoded query string with numeric keys, NOT JSON. The
 * numbering is fixed by ID5 and mirrored from their own client.
 *
 * @see https://wiki.id5.io/docs/passing-partner-data-to-id5
 */
const ID5_PD_KEYS = {
    other:         0,
    hem:           1,   // SHA256 hashed, normalized email
    phone:         2,   // SHA256 hashed, E.164 phone
    xpuid:         3,   // Cross-partner user ID
    xpuidSource:   4,
    puid:          5,   // Partner-specific user ID
    idfa:          6,
    gaid:          7,
    url:           8,
    domain:        9,
    ipv4:          10,
    ipv6:          11,
    ua:            12,
    isBurnerEmail: 13,
    idfv:          14,
    iabToken:      17
};
/**
 * Session "passport" - stores data across page loads within the same browser session
 *
 * Dynamic properties added during session:
 * - ppid: string - Publisher Provided ID (hashed user identifier)
 * - ppidSource: string - Where ppid came from: 'server' (email hash), 'matomo', 'random'
 * - hem: string - SHA256 of the ID5-normalized email. Server-rendered on logged-in
 *                 (uncached) responses, then persisted here so ID5 pd key 1 survives
 *                 the Varnish-cached pageviews that follow.
 * - phone: string - SHA256 of the E.164 phone number, when derivable (ID5 pd key 2)
 * - isBurnerEmail: string - 'true'/'false' for the address behind `hem` (ID5 pd key 13)
 * - arrival: string - How this SESSION started: newsletter|login|search|social|direct|other.
 *                     Resolved once on the first pageview and reused, because arrival
 *                     describes the session, not the pageview. Matomo dim 1 / GAM `ref`.
 * - matomoIdentitySent: boolean - Whether the identity dimensions have been sent to
 *                     Matomo this visit. Once per visit is enough; the values cannot
 *                     change without the passport changing.
 *
 * Consent state lives at the TOP level of the passport, not under `consent` —
 * maiPubSetConsent writes these fields directly and every reader uses them from
 * there. (They were previously documented as nested because the save folded the
 * whole passport into .consent; that bug is fixed, see maiPubPruneNestedConsent.)
 * - source: string - Where the consent came from: 'cmp', 'cookie', 'geo'
 * - hasConsent: boolean - Whether the user allows personalized ads
 * - gdprConsentUUID: string - GDPR consent UUID
 * - gdprApplies: boolean - Whether GDPR applies to this user
 * - gdprConsentedToAll: boolean - Whether the user consented to all GDPR purposes
 * - usnatConsentUUID: string - USNat consent UUID
 * - usnatApplies: boolean - Whether USNat applies to this user
 * - consent: object - Raw consent strings only
 *   - tcString: string - IAB TCF consent string (passed to Catalyst for server-side GVL validation)
 *   - gppString: string - Global Privacy Platform string
 * - geo: object - Geographic data
 *   - country: string - Country code (e.g., 'US', 'GB')
 *   - region: string - State/region code
 *   - city: string - City name
 *   - postalCode: string - ZIP/postal code
 *   - metroCode: string - Metro area code
 *   - ipv4: string - Visitor IPv4 from the geo.js worker (ID5 pd key 10), when available
 *   - ipv6: string - Visitor IPv6 from the geo.js worker (ID5 pd key 11), when available
 *   - lat: number - Latitude
 *   - lon: number - Longitude
 *   - isNorthAmerica: boolean - Whether user is in North America
*
*/
let maiPubSessionData = {}; // This global variable will hold our session "passport"

/**
 * Global geographic data object - populated by geo.js worker and used for ad targeting
 *
 * Structure (populated by geo.js processing):
 * - version: string - Version of geo data (e.g., "1.0.5")
 * - cfRay: string - Cloudflare Ray ID for request tracking
 * - isNorthAmerica: boolean - Whether user is in North America (affects GDPR compliance)
 * - city: string - City name
 * - region: string - State/region code
 * - country: string - Country code (e.g., 'US', 'GB')
 * - postalCode: string - ZIP/postal code
 * - metroCode: string - Metro area/DMA code
 * - continent: string - Continent code
 * - timezone: string - User's timezone
 * - lat: number - Latitude coordinate
 * - lon: number - Longitude coordinate
 * - asn: string - Autonomous System Number
 * - isp: string - Internet Service Provider name
 * - deviceType: string - Device type ('desktop', 'mobile', 'tablet')
 * - connectionType: string - Connection type (e.g., 'wifi', 'cellular')
 * - ppid: string - Publisher Provided ID (if available)
 * - screenWidth: number - Screen width in pixels
 * - screenHeight: number - Screen height in pixels
 */
let maiPubGeo   		= [];

// // Location-based optimization variables
// let isNorthAmerica = false;


// Initialize flags for IMA SDK loading and callbacks.
let isImaSdkLoading = false;
let onImaSdkLoadedCallbacks = [];
let videoPlayers     = []; // Array to track all video player instances

let hasConsent       = true; // we are a go unless cmp says otherwise
let ppid             = '';

/**
 * CMP Consent Processing Status Flag
 *
 * Tracks whether the CMP (Consent Management Platform) has fired at least one consent event.
 * This is separate from `hasConsent` which tracks the actual consent value.
 *
 * - `hasConsent`: The consent value (true/false) - whether user has granted consent
 * - `cmpConsentProcessed`: Whether CMP has fired an event (true/false) - whether CMP is "ready"
 *
 * CMP is considered "ready" when:
 * 1. An onConsentReady event has been processed (GDPR or USNat)
 * 2. OR timeout fallback has been triggered (with North America geo check)
 *
 * This flag is used to gate bidding systems initialization - bidding adapters (currently Magnite DM+, Catalyst, or RevIQ)
 * should wait for CMP to be ready before initializing, per Magnite recommendations.
 * None of the bidding adapters ever run at the same time.
 *
 * @since 1.21.0
 */
let cmpConsentProcessed = false;
let cmpReadinessPromise = null;
// let gptInitialized   = false;
let isPageVisible    = !document.hidden; // Initialize based on current state

// If debugging, log, but also log to console.info for visibility
console.info('[maiPub] Script Version: ' + maiPubVersion);
maiPubLog('[maiPub] Script Version: ' + maiPubVersion);

// Process any queued consent data from early CMP events
// Initialize queue if it doesn't exist
if (typeof window._maiPubConsentQueue === 'undefined') {
    window._maiPubConsentQueue = [];
}

if (window._maiPubConsentQueue && window._maiPubConsentQueue.length > 0) {
    console.info('[maiPub] Processing ' + window._maiPubConsentQueue.length + ' queued consent events');
    window._maiPubConsentQueue.forEach(function(item) {
        console.info('[maiPub] Processing queued consent:', item.consentData);
        maiPubSetConsent(item.consentData);

        // Process privacy manager link if applicable
        if (item.privacyManager && item.privacyManager.applies === true) {
            // The footer link may not be parsed yet, so wait for it (helper is defined inline with the _sp_ config).
            const setupPmLink = function(pmLink) {
                console.info('[maiPub] Setting up privacy manager link:', item.privacyManager.linkText);
                pmLink.textContent = item.privacyManager.linkText;
                pmLink.onclick = function(e) {
                    e.preventDefault();
                    item.privacyManager.modalLoader();
                };
            };

            if (typeof window.maiPubWhenPmLink === 'function') {
                window.maiPubWhenPmLink(setupPmLink);
            } else {
                const pmLink = document.getElementById("pmLink");
                if (pmLink) {
                    setupPmLink(pmLink);
                }
            }
        }
    });
    window._maiPubConsentQueue = []; // Clear the queue
} else {
    maiPubLog('[maiPub] No queued consent events to process');
}

// --- AFTER (Optimized v816 Dual-Path Checker) ---
window._sp_queue = window._sp_queue || [];
window._sp_queue.push(function() {
    // RevIQ's CMP decides consent in the RevIQ bucket (maiPubSetReviqConsent).
    // SourcePoint only loads there from a page cached before the split moved to <head>.
    if ('reviq' === displayMode) {
        maiPubLog('CMP: Sourcepoint loaded in the RevIQ bucket. Ignoring it; RevIQ decides consent.');
        return;
    }

    maiPubLog('CMP: Sourcepoint loaded. Registering dynamic event listener.');

    const processConsentState = function(message_type, consentUUID, euconsent, info) {
        maiPubLogSourcepointTiming();
        maiPubLog('CMP: Processing consent state for ' + message_type);
        maiPubSetConsent({
            message_type: message_type,
            consentUUID: consentUUID,
            consentSource: 'cmp',
            applies: info ? info.applies : false,
            consentedToAll: info ? info.consentedToAll : false,
            typeApplies: info ? (info.typeApplies || info.applies) : false,
            uuid: consentUUID,
            euconsent: euconsent
        });
    };

    // Path A: Listen for any future consent updates
    window._sp_.addEventListener('onConsentReady', processConsentState);

    // Path B: Immediate Check Fallback
    // If Sourcepoint has already run and populated its state cache, resolve it instantly
    if (window._sp_.gdpr?.consentData || window._sp_.usnat?.consentData) {
        maiPubLog('CMP: Cached consent data detected on boot. Resolving gateway immediately.');
        if (window._sp_.gdpr?.consentData) {
            const gdprData = window._sp_.gdpr;
            processConsentState('gdpr', gdprData.uuid, gdprData.euconsent, { applies: gdprData.applies, consentedToAll: gdprData.consentedToAll });
        }
        if (window._sp_.usnat?.consentData) {
            const usnatData = window._sp_.usnat;
            processConsentState('usnat', usnatData.uuid, null, { applies: usnatData.applies });
        }
    }
});

// --- GET SESSION DATA, DETECT SECOND PAGE LOAD, APPLY SESSION OPTIMIZATIONS ---

maiPubLoadSessionData(); // Load our session passport immediately

// Is this visitor very likely a bot? Read now from the user agent and the browser, and,
// after the first pageview, the network geo.js gave (in the passport). The ads start, or
// don't, in maiPubStartAdsUnlessBot().
maiPubBot = maiPubBotVerdict( navigator, maiPubSessionData.geo );

// Set global hasConsent from Session Data
if (typeof maiPubSessionData.hasConsent === 'boolean') {
    hasConsent = maiPubSessionData.hasConsent;
    maiPubLog('Consent: Set from session data:', {hasConsent});
} else {
    // Fallback if session data is somehow missing the flag
    // (Shouldn't happen with new init logic, but good safeguard)
    hasConsent = true; // Default to true if undefined, CMP will correct if needed
    maiPubLog('Consent: Session data missing hasConsent flag, defaulting global state to true.');
}

// --- CMP FAST PATH: A SAVED DECISION, OR THE VISITOR'S REGION ---
// Reuse a consent decision saved earlier (this session's passport, or the consent
// cookie from an earlier visit) instead of waiting for the CMP. With none, a North
// American visitor with no opt-out on record goes ahead once geo.js answers (US
// privacy is opt-out). waitForCMPReady() short-circuits on cmpConsentProcessed, so
// the bidders inject immediately.
//
// The CMP still loads and still reports. Every real answer, including a choice
// changed later in either CMP's dialog, updates the passport and the cookie
// (maiPubMarkConsentResolved), and the next pageview reads it.
if (!maiPubApplySavedConsent()) {
    maiPubSettleConsentFromRegion();
}

// RevIQ's loader goes in at DOMContentLoaded, once the slot divs exist. Start its
// download now, unless the ads are left out for a bot.
if ( ! maiPubBotSkipsAds( maiPubBot, maiPubBotSettings, maiPubForceAds ) ) {
	maiPubPreloadScripts();
}


//Apply other session optimizations if this is a second page load.
if (maiPubSessionData.pageCount > 1) {
    maiPubLog('Session: applying optimizations, subsequent page visit ', maiPubSessionData.pageCount);

    // Apply Cached Geo Data from session passport
    if (maiPubSessionData.geo && maiPubSessionData.geo.country) {
		maiPubLog('Session: Using cached Geo Data from session passport:', maiPubSessionData.geo);
		window.maiPubGeo = maiPubSessionData.geo;

		enrichOrtb2FromGeo(maiPubSessionData.geo);

		// Nothing to wait for — the Prebid configuration gate opens immediately.
		maiPubMarkGeoSettled();
    }
	else {
		// No usable geo in the passport on a subsequent pageview. This branch used
		// to be absent entirely — geo.js was fetched ONLY on the first pageview of a
		// session, so if that one attempt failed (worker 403, blocked request,
		// network blip) the session went without geo for its entire life and never
		// retried. Nothing was logged either, because nothing was attempted, which
		// made it look like geo.js was broken when it had simply never been called.
		//
		// The cost of retrying is one small request per pageview while the endpoint
		// is genuinely broken; the cost of not retrying is silent, permanent loss of
		// ORTB2 device enrichment, the ISP signal, ID5 pd keys 10/11, and the CMP
		// region gate — which falls back to treating the visitor as North American.
		maiPubLog('Session: no cached geo in the passport on a subsequent pageview — fetching geo.js.');
		maiPubGetGeo(function (ok) {
			// Was logged unconditionally, so a failed fetch still reported success on
			// the very next line — actively misleading while debugging a missing geo.
			maiPubLog(ok
				? 'GEO: data available from worker and enrichment is done'
				: 'GEO: unavailable — continuing without geo enrichment or region gating.');

			// Release the Prebid configuration gate on BOTH outcomes. A failed fetch
			// must not hold the auction for the full GEO_PD_WAIT_MS window.
			maiPubMarkGeoSettled();

			if (ok) {
				maiPubSettleConsentFromRegion();
			}
		});
	}

    // Repair a PPID corrupted into an object with numeric keys by an older build.
    // Must run before maiPubGeneratePpid(), whose session short-circuit would
    // otherwise adopt the broken value.
    if (maiPubSessionData.ppid && 'object' === typeof maiPubSessionData.ppid && null !== maiPubSessionData.ppid) {
        maiPubLog('Session: detected corrupted PPID (object format), repairing to string');
        maiPubSessionData.ppid = Object.values(maiPubSessionData.ppid).join('');
        maiPubSaveSessionData({ ppid: maiPubSessionData.ppid });
    }
}
else {
	maiPubLog('First page load detected., calling geo.js');
	maiPubGetGeo(function (ok) {
		// Was logged unconditionally, so a failed fetch still reported success on
		// the very next line — actively misleading while debugging a missing geo.
		maiPubLog(ok
			? 'GEO: data available from worker and enrichment is done'
			: 'GEO: unavailable — continuing without geo enrichment or region gating.');

		// Release the Prebid configuration gate on BOTH outcomes. A failed fetch
		// must not hold the auction for the full GEO_PD_WAIT_MS window.
		maiPubMarkGeoSettled();

		// A first pageview with nothing saved: a North American visitor goes ahead
		// now rather than waiting on the CMP.
		if (ok) {
			maiPubSettleConsentFromRegion();
		}
	});

}

// Identity resolution, in dependency order. All three are synchronous, and the
// order between them is load-bearing:
//
//   1. maiPubStoreId5Identity()  — carries the server-rendered hashed email into
//      the passport. Only populated on uncached (logged-in) responses, so this
//      is a no-op on the Varnish-cached ones that follow.
//   2. maiPubResolveArrival()    — decides how this SESSION started. Must run
//      BEFORE the capture below, which strips ?sw_fpid from the URL: arrival
//      reads that parameter to detect a newsletter click, so resolving it after
//      the strip reported 'direct' for every anonymous newsletter arrival — the
//      exact cohort the dimension exists to measure. It only surfaced as
//      'newsletter' for LOGGED-IN visitors, because the capture returns early
//      for them and skips the strip; the bug and the feature were masking each
//      other. Memoized into the passport, so calling it here costs nothing and
//      the later call sites reuse the answer.
//   3. maiPubCaptureUrlIdentity() — reads a hashed email off an inbound
//      newsletter link (?sw_fpid) into the passport.
//   4. maiPubGeneratePpid()      — resolves the PPID, and now PREFERS the hashed
//      email the two calls above may have just written.
//
// These used to run AFTER the PPID was resolved, which was harmless only because
// nothing read `hem` at PPID time. It does now, so a newsletter click would have
// been resolved to a random id on the very pageview that identified the visitor.
maiPubStoreId5Identity();
maiPubResolveArrival();
maiPubCaptureUrlIdentity();

// Called unconditionally on every pageview, not just when the passport is empty.
// The old code only generated on a missing PPID, so a visitor who logged in
// mid-session never picked up the server upgrade — the passport kept whatever
// anonymous id it had for the rest of the session. maiPubGeneratePpid() now
// short-circuits internally when the session is already resolved, so calling it
// every time is both correct and cheap.
maiPubGeneratePpid();
maiPubLog('PPID resolved.', maiPubSessionData.ppid, `(source: ${maiPubSessionData.ppidSource || 'none'})`);

// Matomo visit-scoped custom dimension IDs. Visit scope is load-bearing, not a
// preference: class-output.php hoists the Matomo scripts ABOVE the ads script on
// any page with ads, and a pageview can still go out before this script has run
// (mai-publisher-analytics.js waits for it only so long, see maiPubWhenIdentity()).
// Action-scoped dimensions must accompany the action they describe; visit-scoped
// ones attach to the visit whenever they arrive ("the last value set will be used"). Declared HERE — ahead of the block below
// that reads them — because these are top-level const: a reference above the
// declaration line is a TDZ ReferenceError, which is what silently defeated the
// previous ping-based delivery (its setCustomDimension calls sat in a try/catch).
const MATOMO_DIM_ARRIVAL       = 1;
const MATOMO_DIM_IDENTITY_TIER = 3;

// Identity is now fully resolved. Publish the resolved values ONCE so both
// consumers reuse them instead of recomputing — that is what keeps the Matomo
// dimensions and the GAM key-values describing an identically-defined population:
//   - mai-publisher-analytics.js reads window.maiPubIdentity.matomoDimensions and
//     applies them with setCustomDimension() right before it replays trackPageView
//     (waiting for this script when Matomo is ready first: maiPubWhenIdentity()),
//     so arrival + identity tier ride the PAGEVIEW request. That is what records
//     them: a `ping` does not persist custom dimensions (which is why the previous
//     ping delivery never populated dimensions 1 and 3), and delivering them on the
//     pageview rather than a trackEvent keeps them out of Actions and Bounce Rate.
//   - the GAM identity probe (pageConfig.targeting.ref/id) calls the same memoized
//     resolvers directly.
// Resolved at parse time, immediately after identity resolution, because a visitor
// who bounces must still be counted; waiting for the ad auction or an engagement
// event would systematically drop the least-engaged visits from every cohort.
window.maiPubIdentity = ( function() {
	const arrival = maiPubResolveArrival();       // memoized on the passport at first touch
	const tier    = maiPubResolveIdentityTier();

	return {
		arrival: arrival,
		tier:    tier,
		// Matomo visit-scope custom-dimension index -> value. analytics.js iterates
		// this map, so the index numbers live here as the single source of truth.
		matomoDimensions: {
			[ MATOMO_DIM_ARRIVAL ]:       arrival,
			[ MATOMO_DIM_IDENTITY_TIER ]: tier,
		},
	};
} )();

// if we have a google tag from the publisher, log it.
if (maiPubAdsVars.pubGoogleTag) {
	maiPubLog(`CMP: Publisher Google Tag: ${maiPubAdsVars.pubGoogleTag}`);
} else {
	maiPubLog('CMP: No Publisher Google Tag found');
}

// if we have a springwire google tag, log it.
if (maiPubAdsVars.swGoogleTag) {
	maiPubLog(`CMP: Springwire Google Tag: ${maiPubAdsVars.swGoogleTag}`);
} else {
	maiPubLog('CMP: No Springwire Google Tag found');
}

maiPubStartAdsUnlessBot( maiPubInitParallel );


/**
 * Parallel initialization for all page loads
 * Runs bidding systems and LCP detection simultaneously
 *
 * @since 1.20.3
 */
function maiPubInitParallel() {
	maiPubLog('=== PARALLEL INITIALIZATION STARTED ===');

	// Start bidding immediately. This returns a promise.
	// maiPubInitBiddingSystems will wait on CMP
	const biddingPromise = maiPubInitBiddingSystems();

	// Add logging to track when bidding promise actually resolves
	biddingPromise.then(() => {
		maiPubLog('Bidding Promise: Promise resolved (biddingPromise.then() fired)');
	});

	// Create a new promise for DOM/LCP readiness.
	const lcpPromise = new Promise((resolveLCP) => {

		const startLCPCheck = () => {
			const lcpMaxWaitTime = 750;
			let adsTriggered = false;

			const lcpFailsafeTimeout = setTimeout(() => {
				if (!adsTriggered) {
					adsTriggered = true;
					maiPubLog('LCP Failsafe Timeout. Resolving LCP promise.');
					resolveLCP(); // Resolve the promise
					maiPubLog('LCP Promise: resolveLCP() called (failsafe timeout)');
				}
			}, lcpMaxWaitTime);

			try {
				// maiPubLog('LCP: Setting up PerformanceObserver with buffered: true');
				const observer = new PerformanceObserver((entryList) => {
					const entries = entryList.getEntries();
					// maiPubLog(`LCP: PerformanceObserver callback fired with ${entries.length} entries`);
					// if (entries.length > 0) {
					// 	maiPubLog(`LCP: Entry details:`, entries[0]);
					// }
					if (entries.length > 0 && !adsTriggered) {
						adsTriggered = true;
						clearTimeout(lcpFailsafeTimeout);
						maiPubLog('LCP Detected. Resolving LCP promise.');
						resolveLCP(); // Resolve the promise
						maiPubLog('LCP Promise: resolveLCP() called (PerformanceObserver)');
					}
				});
				observer.observe({ type: 'largest-contentful-paint', buffered: true });
				// maiPubLog('LCP: PerformanceObserver created and observing');
			} catch (e) {
				maiPubLog('PerformanceObserver not supported. Wait 2 seconds for this old browser to load before triggering ads.');
				setTimeout(() => {
					if (!adsTriggered) {
						adsTriggered = true;
						maiPubLog('Fallback timeout reached. Resolving LCP promise.');
						resolveLCP();
						maiPubLog('LCP Promise: resolveLCP() called (fallback timeout)');
					}
				}, 2000); // Fallback for very old browsers.
			}
		};

		// Set up LCP observer immediately - PerformanceObserver with buffered: true
		// will capture LCP events even if they occurred before the observer was created.
		// No need to wait for DOMContentLoaded.
		startLCPCheck();
	});

	// Add logging to track when LCP promise actually resolves
	lcpPromise.then(() => {
		maiPubLog('LCP Promise: Promise resolved (lcpPromise.then() fired)');
	});

	// Wait for CMP to be ready, v1.22.1

	// --- The New Gatekeeper ---
	// We remove waitForCMPReady() from here because 'biddingPromise'
	// internally waits for CMP before it resolves.
	Promise.all([biddingPromise, lcpPromise]).then(() => {
		maiPubLog('=== PARALLEL INIT COMPLETE === Bidders (inc. CMP) and LCP are ready.');

		// (Phase 5 & 6) Now, and only now, run the ad logic.
		maiPubRun();
	});
}

/**
 * Mark CMP as Ready and Dispatch Event
 *
 * Helper function that marks the CMP as processed and dispatches a custom event
 * to notify listeners that CMP is ready. This centralizes the logic for marking
 * CMP as ready and ensures all listeners are notified via event.
 *
 * @since 1.21.0
 */
function markCMPAsReady() {
	if (cmpConsentProcessed === true) {
		maiPubLog('CMP: Already marked as ready - skipping duplicate dispatch.');
		return; // Already marked as ready
	}

	cmpConsentProcessed = true;
	maiPubLog('CMP: Marked as ready - dispatching event.');

	// Consent is settled, so any identity captured earlier this pageview (before we
	// were allowed to write it) can now be persisted.
	maiPubPersistIdentityCookies();

	// Dispatch custom event for CMP readiness
	const event = new CustomEvent('maiPubCMPReady', {
		detail: { timestamp: Date.now() }
	});
	window.dispatchEvent(event);
}

/**
 * Wait for CMP Consent Processing to Complete (Singleton Pattern)
 * * This function ensures CMP (Consent Management Platform) has fired at least one
 * consent event before proceeding. It handles two scenarios:
 * * 1. CMP already processed (queued events or previous processing)
 * 2. CMP event arrives while waiting (via event listener) or timeout fallback
 * * @return {Promise<void>} Resolves when CMP is ready (event fired or timeout fallback)
 * * @since 1.21.0
 */
function waitForCMPReady() {
    // 1. IMMEDIATE CHECK: If already done, return immediately (Standard Promise.resolve)
    if (cmpConsentProcessed === true) {
        maiPubLog('CMP: Already processed - ready immediately.');
        return Promise.resolve();
    }

    // 2. SINGLETON CHECK: If a check is ALREADY running, return the existing promise.
    // This prevents multiple listeners/timeouts if called from multiple places.
    if (cmpReadinessPromise) {
        maiPubLog('CMP: Readiness check already in progress - returning existing promise.');
        return cmpReadinessPromise;
    }

    // 3. START NEW CHECK: Create the singleton promise
    cmpReadinessPromise = new Promise((resolve) => {
        maiPubLog('=== CMP READINESS CHECK STARTED ===');
        maiPubLog('CMP: Not yet processed - setting up event listener and timeout fallback.');

		// --- BEFORE (v813 Location Guesswork Optimization) ---		
        // // Use session data (if available) or current geo data immediately.
        // // Use session data first (cached from previous page) if available, then window.maiPubGeo (current page)
        // const geoForTimeout = maiPubSessionData.geo || window.maiPubGeo || {};
        // // Unknown geo → NA for timeout (USNat-first sites; geo.js often arrives after CMP).
        // const isNorthAmericaForTimeout = maiPubIsCmpNorthAmerica();
        // // Conditional timeout: 2 seconds for USNat (NA + unknown), 10 seconds for GDPR (non-NA)
        // const timeoutDuration = isNorthAmericaForTimeout ? cmpTimeout : 10000; // cmpTimeout is 2000ms

		// --- AFTER (Optimized v814 Dynamic Gating) ---
		// If we are on the first page view and geo data hasn't resolved yet,
		// default to a lenient 5-second window so European users don't get trapped by the USNat 2s limit.

		const geoResolved = !!(maiPubSessionData.geo?.country || window.maiPubGeo?.country);
		const isNorthAmericaForTimeout = maiPubIsCmpNorthAmerica(); // Fixed: Lifted out of the block scope
		let timeoutDuration = 5000; 
		
		if (geoResolved) {
			timeoutDuration = isNorthAmericaForTimeout ? cmpTimeout : 10000;
		} else {
			maiPubLog('CMP: Geo location unknown at initialization. Defaulting to 5000ms safety window.');
		}

		const geoRegionForTimeout = maiPubGetGeoRegion();
        maiPubLog(`CMP: Timeout set to ${timeoutDuration}ms (${isNorthAmericaForTimeout ? 'USNat' : 'GDPR'} region, geo=${geoRegionForTimeout}).`);

        // Track if we've already resolved to prevent multiple resolutions
        let resolved = false;

        // Define the resolution helper
        const markCMPReady = () => {
            if (!resolved) {
                resolved = true;
                maiPubLog('CMP: Resolving promise.');
                resolve();
                // We do NOT clear cmpReadinessPromise here. It stays resolved for any future calls.
            }
        };

        // Set up event listener for CMP ready event
        const handleCMPReady = () => {
            window.removeEventListener('maiPubCMPReady', handleCMPReady);
            clearTimeout(timeoutId);
            maiPubLog('CMP: Event detected - marking as ready.');
            markCMPReady();
        };

        window.addEventListener('maiPubCMPReady', handleCMPReady);
        maiPubLog('CMP: Event listener active, waiting for CMP to fire (bidding systems paused until then).');

		let fallbackTimerActive = true;
		let timeoutId = setTimeout(executeTimeoutFallback, timeoutDuration);

		function executeTimeoutFallback() {
			if (!fallbackTimerActive || resolved) { return; }
			window.removeEventListener('maiPubCMPReady', handleCMPReady);

			maiPubLog('CMP: Timeout reached - checking fallback conditions.');

			if (maiPubIsCmpNorthAmerica() && maiPubReviqOptedOut()) {
				maiPubLog('CMP: North America, opted out in RevIQ\'s consent dialog - applying fallback (hasConsent = false).');
				hasConsent = false;
				maiPubSessionData.hasConsent = false;
				maiPubSaveSessionData({ hasConsent: false });
			} else if (maiPubIsCmpNorthAmerica()) {
				maiPubLog('CMP: North America detected - applying fallback (hasConsent = true).');
				hasConsent = true;
				maiPubSessionData.hasConsent = true;
				maiPubSaveSessionData({ hasConsent: true });
			} else {
				maiPubLog('CMP: Timeout fallback - GDPR region, no consent (hasConsent = false). Skipping bidding adapters, using non-personalized GAM ads.');
				hasConsent = false;
				maiPubSessionData.hasConsent = false;
				maiPubSaveSessionData({ hasConsent: false });
			}

			markCMPAsReady();
			markCMPReady();
		}

		// Expose a global hook so geo.js can safely extend the window if a GDPR region is discovered mid-flight
		window.maiPubExtendCmpTimeout = function(newDuration) { //v819
			if (resolved) return;
			clearTimeout(timeoutId);
			
			// CORRECTION: Use the global initialization timestamp to compute elapsed layout time
			const timeElapsed = Date.now() - timestamp; 
			const adjustedRemainingTime = Math.max(newDuration - timeElapsed, 1000);
			
			timeoutId = setTimeout(executeTimeoutFallback, adjustedRemainingTime);
			maiPubLog(`CMP Circuit Guard: Timeout extended dynamically to ${newDuration}ms (${adjustedRemainingTime}ms remaining).`);
		};
    });

    return cmpReadinessPromise;
}

/**
 * Initialize All Bidding Systems (Strict Mode)
 * Dynamically injects scripts only AFTER CMP consent is ready.
 *
 * @since 1.20.3
 */
function maiPubInitBiddingSystems() {
    return new Promise((resolve) => {
        maiPubLog('=== BIDDING SYSTEMS INITIALIZATION (STRICT MODE) ===');

        const initStatus = {
            prebid: false,
            reviq: false,
            catalyst: false,
            cmp: false,
            magniteDisabled: !maiPubAdsVars.magnite,
            reviqDisabled: !maiPubAdsVars.reviq,
            catalystDisabled: !maiPubAdsVars.catalyst,
            cmpDisabled: false
        };

		// Helper to inject scripts dynamically with a safety timeout.
		// Optional `attrs` object sets arbitrary attributes on the script tag
		// (used by RevIQ for the `dev` attribute in Manage Account mode).
		const injectScript = (id, src, onLoadCallback, attrs = {}) => {
			if (document.getElementById(id)) {
				maiPubLog(`Script ${id} already exists. Calling callback.`);
				onLoadCallback();
				return;
			}

			maiPubLog(`Dynamically injecting script: ${src}`);
			const script = document.createElement('script');
			script.id = id;
			script.async = true;
			script.src = src;

			// Calls back once, on load, error or the safety timeout, whichever comes
			// first. A script that loaded after the timeout used to call back again.
			let calledBack  = false;
			let safeTimeout = null;
			const callBackOnce = () => {
				if (calledBack) {
					return;
				}
				calledBack = true;
				clearTimeout(safeTimeout);
				onLoadCallback();
			};

			// Safety Timeout: If script hangs for 3 seconds, proceed anyway (don't block the page)
			safeTimeout = setTimeout(() => {
				maiPubLog(`Warning: Script ${id} took too long to load. Proceeding without it to save LCP.`);
				callBackOnce();
			}, 3000); // 3 second limit for the script to download

			script.setAttribute('fetchpriority', 'high');

			Object.keys(attrs).forEach((name) => {
				script.setAttribute(name, attrs[name]);
			});

			script.onload = () => {
				if (calledBack) {
					maiPubLog(`Script ${id} loaded after the safety timeout.`);
				}
				callBackOnce();
			};

			script.onerror = () => {
				maiPubLog(`Error loading script ${src}`);
				callBackOnce();
			};

			document.head.appendChild(script);
		};

        // Helper function to initialize GPT/GAM configuration
        // (Runs AFTER bidders are injected and ready)
        const initializeGAMConfig = () => {
            if (!_coreGptConfigQueued) {
                maiPubLog('GPT Init: Initializing core GPT configuration');
                googletag.cmd.push(() => {
                    const pubads = googletag.pubads();

					_coreGptConfigQueued = true;

                    // Page-level settings (Config API — replaces deprecated pubads.setCentering, etc.)
                    if (typeof googletag.setConfig === 'function') {
                        const pageConfig = {
                            centering: true,
                            disableInitialLoad: true,
                        };

                        if (maiPubAdsVars.targets) {
                            pageConfig.targeting = {};
                            Object.keys(maiPubAdsVars.targets).forEach((k) => {
                                pageConfig.targeting[k] = String(maiPubAdsVars.targets[k]);
                            });
                        }

                        // Identity diagnostics. Two predefined GAM keys, reported on
                        // rather than targeted: `ps` (which source produced this
                        // visitor's PPID) and `ha` (how old the persistent hashed-email
                        // cookie is). Crossed with GAM's native Browser dimension they
                        // answer two questions we cannot answer from the client alone —
                        // what share of traffic never gets past a random id, and whether
                        // Safari is capping the hashed-email cookie's lifetime.
                        //
                        // These are values PHP cannot supply: both live in the session
                        // passport and in cookies, and anything per-visitor rendered
                        // server-side would be baked into Varnish-cached HTML.
                        //
                        // Temporary. Both keys are diagnostic only — do not target line
                        // items on them — and both this block and the maipub_hem_set
                        // companion cookie come out once the question is answered.
                        if (maiPubAdsVars.identityProbe) {
                            pageConfig.targeting     = pageConfig.targeting || {};

                            // Diagnostics: which source produced the PPID, and how old
                            // the persistent hashed-email cookie is.
                            pageConfig.targeting.ps  = maiPubGetPpidSourceBucket();
                            pageConfig.targeting.ha  = maiPubGetHemCookieAgeBucket();

                            // Measurement: the SAME values written to Matomo custom
                            // dimensions 1 and 3, from the same resolvers. GAM answers
                            // "was this population worth more" and Matomo answers "did
                            // it browse deeper"; the comparison is only valid because
                            // both describe an identically-defined population.
                            //
                            // There is deliberately no separate newsletter flag: `ref`
                            // already carries 'newsletter', and a redundant key costs a
                            // GAM key plus width in every report it appears in.
                            pageConfig.targeting.ref = maiPubResolveArrival();
                            pageConfig.targeting.id  = maiPubResolveIdentityTier();
                            pageConfig.targeting.cn  = maiPubResolveConsentState();

                            maiPubLog(`Identity probe: ps=${pageConfig.targeting.ps}, ha=${pageConfig.targeting.ha}, ref=${pageConfig.targeting.ref}, id=${pageConfig.targeting.id}, cn=${pageConfig.targeting.cn}`);
                        }

                        googletag.setConfig(pageConfig);
                    } else {
                        try { pubads.setCentering(true); } catch (e) {}
                        try { pubads.disableInitialLoad(); } catch (e) {}
                        if (maiPubAdsVars.targets) {
                            Object.keys(maiPubAdsVars.targets).forEach((k) => pubads.setTargeting(k, String(maiPubAdsVars.targets[k])));
                        }
                    }

                    // Non-Personalized Ads (GDPR Fallback)
                    if (hasConsent === true) {
						// GAM PPID must be 32-150 alphanumeric — strip hyphens from
						// the random-source UUID (matches the video/audio VAST path).
						const gamPpid = maiPubNormalizeGamPpid(maiPubSessionData.ppid);
						if (gamPpid) {
							try { pubads.setPublisherProvidedId(gamPpid); } catch (e) { console.error(e); }
							maiPubLog('GPT Init: Set Publisher Provided ID:', gamPpid);
						} else if (maiPubSessionData.ppid) {
							maiPubLog('GPT Init: PPID present but not GAM-valid (need 32-150 alphanumeric); skipping setPublisherProvidedId.');
						}

						// Publisher Provided Signals (PPS) — gated behind consent because
						// the payload includes IAB_AUDIENCE_1_1 user audience segments
						// (personal data). Only sent when we have consent, mirroring the
						// setPublisherProvidedId gate above. Content taxonomy rides along
						// in the same object; if we ever want to keep contextual signals
						// without consent, split the audience taxonomy out server-side.
						if (typeof googletag.setConfig === 'function' && maiPubAdsVars.pps && Object.keys(maiPubAdsVars.pps).length > 0) {
							googletag.setConfig(maiPubAdsVars.pps);
							maiPubLog('GPT Init: PPS signals sent (consent present).');
						}
					} else if (true !== maiPubSessionData.gdprApplies && maiPubReviqOptedOut()) {
						// An opt-out in RevIQ's consent dialog. Restricted data processing
						// is Google's setting for US privacy opt-outs. It is page-wide, so
						// it also covers RevIQ's display, whose slots are in our network.
						try {
							pubads.setPrivacySettings({ restrictDataProcessing: true });
						} catch (e) {}
						maiPubLog('GPT Init: Opted out in RevIQ\'s consent dialog - restricted data processing on; no PPID or PPS.');
					} else {
						maiPubLog('GPT Init: No consent, setting non-personalized ads.');
                        try {
                            pubads.setPrivacySettings({ nonPersonalizedAds: true });
                        } catch (e) {
                            try { pubads.setRequestNonPersonalizedAds(1); } catch (e2) {}
                        }
                    }

                    maiPubLog('GPT Init: Core GPT Configuration (services not enabled yet).');
                });
            }
        };

        // Runs once. Every adapter's callback and every branch that skips one calls
        // it, so once all were in, each later call queued the core GPT config again
        // (seen in the RevIQ bucket when the loader's callback came before consent).
        let allSystemsReady = false;
        const checkAllSystemsReady = () => {
            if (allSystemsReady) {
                return;
            }

            const magniteReady = initStatus.magniteDisabled || initStatus.prebid;
            const reviqReady = initStatus.reviqDisabled || initStatus.reviq;
            const catalystReady = initStatus.catalystDisabled || initStatus.catalyst;
            const cmpReady = initStatus.cmpDisabled || initStatus.cmp;

            if (magniteReady && reviqReady && catalystReady && cmpReady) {
                allSystemsReady = true;
                maiPubLog('=== ALL BIDDING SYSTEMS READY ===');
                initializeGAMConfig();
                resolve();
                maiPubLog('Bidding Promise: resolve() called (all systems ready)');
            }
        };

        // --- 1. RevIQ INJECTION (before the consent wait) ---
        // RevIQ is a display takeover, NOT a header bidder. When this branch
        // runs (displayMode === 'reviq'), RevIQ owns the entire display
        // pipeline on the shared window.googletag: loads gpt.js, defines
        // slots, runs the auction, refreshes. Our maiPubRunDisplay no-ops
        // under 'reviq' so we stay out of RevIQ's way.
        //
        // What we do here:
        //  1. Mark each .mai-ad-unit div with `data-ad="{placement}"` and
        //     per-slot `data-kv-*` from config so RevIQ's DOM scan finds them.
        //     Slots whose ad-unit type has no reviq_placement in config.php
        //     are skipped — RevIQ will not render those slots in this bucket.
        //  2. Inject the RevIQ loader. `dev` attribute when in Manage Account
        //     mode so admin previews don't pollute production placements.
        //  3. Push page-level KVs (setKvs) and ORTB2 (defensive setOrtb2
        //     guard until Lyric ships the API) via the reviq command queue.
        //
        // Injected before the consent wait, not inside it. RevIQ's CMP ships inside
        // its loader and replaces SourcePoint in this bucket (the SourcePoint scripts
        // stand down, see mai-publisher-split.js), so waiting for consent first would
        // wait on a CMP that has not loaded. RevIQ gates its own demand on its CMP.
        // reviq.consent.ready() then settles our consent state for what still runs on
        // our GAM (video, audio, PPID) and for setUid (maiPubSetReviqConsent).
        if (maiPubAdsVars.reviq === true) {
            maiPubLog('RevIQ Init: Wiring RevIQ display takeover.');

            // 1a and 1b need the slot divs, which sit in <body>. This can run while
            // the page is still being parsed (the script is async, so it may run
            // before or after), before any .mai-ad-unit exists. Stamping
            // then marks 0 slot divs and RevIQ finds nothing to fill (seen as no
            // display ads from the second pageview on, when the session fast path
            // used to bring this forward). Wait for the DOM if needed.
            const wireReviqSlots = () => {
                // 1a. Stamp data-ad + data-kv-* on slot divs.
                let mapped   = 0;
                let unmapped = 0;
                const timed  = []; // Debug only: the slots for the "RevIQ ad timing" line.
                document.querySelectorAll('.mai-ad-unit').forEach((adUnit) => {
                    const slotId = adUnit.getAttribute('id');
                    if (!slotId) return;
                    const slotKey = slotId.replace('mai-ad-', '');

                    // OOP spotlight de-dup — moved here from maiPubRunPrelude
                    // (whose copy is guarded off under RevIQ). Must run before we
                    // strip the id below, since it keys off the slug. One
                    // out-of-page spotlight per page; remove duplicates from the
                    // DOM so RevIQ never discovers them.
                    if (maiPubIsSpotlightOopUnit(slotKey)) {
                        if (spotlightOopSlug && spotlightOopSlug !== slotKey) {
                            maiPubLog(`RevIQ: removing duplicate spotlight (${slotId}); only one per page.`);
                            adUnit.remove();
                            return;
                        }
                        if (!spotlightOopSlug) { spotlightOopSlug = slotKey; }
                    }

                    const cfg     = ads ? ads[slotKey] : null;
                    if (!cfg) {
                        if (log) maiPubLog(`RevIQ: no ads config for slot ${slotId}; skipping.`);
                        unmapped++;
                        return;
                    }
                    const placement = cfg.reviq_placement || '';
                    if (!placement) {
                        if (log) maiPubLog(`RevIQ: no reviq_placement mapped for ad-unit '${cfg.id}' (slot ${slotId}); skipping. Set it in config.php's ad_units.`);
                        unmapped++;
                        return;
                    }

                    adUnit.setAttribute('data-ad', placement);

                    // Per-slot KVs as data-kv-{key}. Static keys from cfg.targets
                    // plus runtime keys mirrored from maiPubDefineSlot (refresh,
                    // ad_placement_type for sticky, st for random split-test).
                    // Skip array values — RevIQ's data-kv-* syntax is single-value.
                    if (cfg.targets) {
                        Object.keys(cfg.targets).forEach((key) => {
                            const val = cfg.targets[key];
                            if (Array.isArray(val)) {
                                if (log) maiPubLog(`RevIQ: data-kv-${key} value is array; skipping (single-value only).`, val);
                                return;
                            }
                            adUnit.setAttribute(`data-kv-${key}`, String(val));
                        });
                    }
                    adUnit.setAttribute('data-kv-refresh', refreshValue.toString());
                    const adPosition = adUnit.getAttribute('data-ap');
                    if (adPosition === 'ts' || adPosition === 'bs' || adPosition === 'vs') {
                        adUnit.setAttribute('data-kv-ad_placement_type', 'sticky');
                    }
                    if (cfg.splitTest && 'rand' === cfg.splitTest) {
                        adUnit.setAttribute('data-kv-st', Math.floor(Math.random() * 100).toString());
                    }

                    // Strip our id so RevIQ can assign its own — RevIQ enforces
                    // unique ids and warns "RevIQ id should not be set on ad
                    // units" otherwise (per Lyric). Safe: under RevIQ our display
                    // path (maiPubRunDisplay) no-ops and never looks these divs up
                    // by id; the slug was already captured above. Only mapped divs
                    // (the ones RevIQ discovers via data-ad) need this.
                    adUnit.removeAttribute('id');

                    if (!maiPubIsOutOfPageSlug(slotKey)) {
                        timed.push({ el: adUnit, slug: slotKey });
                    }

                    mapped++;
                });
                maiPubLog(`RevIQ: marked ${mapped} slot div(s); ${unmapped} unmapped/skipped.`);
                maiPubWatchReviqAdTiming(timed);

                // 1b. Inject the loader. Domain comes from maiPubAdsVars.domain
                // (the existing GAM Domain setting). `dev` attribute in admin
                // Manage Account mode mirrors the existing gamManageAccount flag.
                //
                // crossorigin: RevIQ's loader fetch()es its own URL to read the
                // CR-IPCountry header, and the response carries Vary: Origin. A
                // plain script tag sends no Origin header, so the browser could
                // not reuse the copy it had just downloaded: every pageview
                // downloaded the loader twice, neither copy was ever served from
                // cache, and consent.ready() (and RevIQ's Prebid setup) waited on
                // the second download. With crossorigin both requests send the
                // same Origin: the geo lookup comes from cache, and a repeat
                // pageview within RevIQ's max-age downloads nothing. Relies on
                // RevIQ sending Access-Control-Allow-Origin (it sends *, which its
                // own geo fetch needs too).
                const reviqLoaderUrl = `//js.rev.iq/${maiPubAdsVars.domain}`;
                const reviqAttrs     = { crossorigin: 'anonymous', ...(gamManageAccount ? { dev: '' } : {}) };
                maiPubLog(`RevIQ Init: Injecting loader ${reviqLoaderUrl}${gamManageAccount ? ' (dev mode)' : ''}`);
                injectScript('mai-publisher-reviq', reviqLoaderUrl, () => {
                    maiPubLog('RevIQ Init: Loader callback fired.');
                    maiPubLogReviqId5();
                    maiPubWatchPrebidOutcomes();
                    initStatus.reviq = true;
                    checkAllSystemsReady();
                }, reviqAttrs);
            };

            if (document.readyState === 'loading') {
                maiPubLog('RevIQ Init: Waiting for DOMContentLoaded so the slot divs exist.');
                document.addEventListener('DOMContentLoaded', wireReviqSlots, { once: true });
            } else {
                wireReviqSlots();
            }

            // 1c. Push page-level KVs, content metadata, and identity via
            // the command queue. Safe before or after the loader executes.
            globalThis.reviq = globalThis.reviq || [];
            globalThis.reviq.push((r) => {
                // Page-level targeting KVs. RevIQ relays these to
                // googletag.setConfig({targeting}), which REQUIRES string
                // values — raw numbers/booleans (e.g. cc:1378, refresh:true)
                // are rejected by GPT ("Invalid value encountered"). Stringify
                // scalars and map array elements to strings, mirroring what our
                // own GPT config already does at initializeGAMConfig.
                if (maiPubAdsVars.targets && Object.keys(maiPubAdsVars.targets).length > 0) {
                    try {
                        const reviqKvs = {};
                        Object.keys(maiPubAdsVars.targets).forEach((k) => {
                            const v = maiPubAdsVars.targets[k];
                            reviqKvs[k] = Array.isArray(v) ? v.map(String) : String(v);
                        });
                        r.setKvs(reviqKvs);
                        maiPubLog('RevIQ: setKvs page-level targets (stringified).', reviqKvs);
                    } catch (e) {
                        maiPubLog('RevIQ: setKvs failed.', e);
                    }
                }

                // Content metadata. RevIQ's setContentMetadata takes an
                // ortb2 site.content object (OpenRTB 3.2.16) — just our
                // content sub-object, NOT the full ORTB2 tree. It now lives
                // at ortb2.site.content (OpenRTB puts Content under Site; the
                // former top-level ortb2.content was unread by Prebid demand).
                // Guarded so it's inert until the method ships RevIQ-side.
                if (typeof r.setContentMetadata === 'function') {
                    const content = maiPubAdsVars.ortb2 && maiPubAdsVars.ortb2.site && maiPubAdsVars.ortb2.site.content;
                    if (content) {
                        try {
                            r.setContentMetadata(content);
                            maiPubLog('RevIQ: setContentMetadata content payload sent.', content);
                        } catch (e) {
                            maiPubLog('RevIQ: setContentMetadata failed.', e);
                        }
                    }
                } else if (log) {
                    maiPubLog('RevIQ: setContentMetadata not yet available — content metadata not passed this load.');
                }
            });

            // 1d. Consent. RevIQ answers through consent.ready() once no consent law
            // applies, or once the visitor accepts or rejects. Identity goes only
            // after that answer, and only with consent.
            globalThis.reviq.push((r) => {
                if (!r.consent || typeof r.consent.ready !== 'function') {
                    maiPubLog('RevIQ: consent.ready() not available — the CMP timeout fallback decides consent this load.');
                    return;
                }

                r.consent.ready().then((tcData) => {
                    if (true !== maiPubSetReviqConsent(tcData)) {
                        maiPubLog('RevIQ: no consent — setUid skipped this load.');
                        return;
                    }

                    // Our ID5 partner data, for RevIQ's ID5 settings to read (as Magnite's do).
                    maiPubWhenGeoReady(maiPubPublishId5Pd);

                    // Identity. We never send a raw email. The session passport
                    // carries ppid + ppidSource:
                    //   'server' -> ppid is sha256(normalized email) => pass as
                    //               a hashed email in e.sha256.
                    //   'matomo'/'random' -> ppid is a pseudonymous session id,
                    //               NOT an email => pass as u.
                    // RevIQ treats u as a username: it hashes `${u}@gmail.com` into
                    // its email slot (confirmed by Lyric), and from there into ID5's
                    // pd (key 1) and a pubProvidedId eid with atype 3. Lyric said it
                    // also becomes the GAM PPID; in a test on lamag.com (1.37.5-beta9)
                    // the PPID on RevIQ's GAM requests was RevIQ's SharedID instead.
                    // Kept until RevIQ adds a ppid parameter we can pass through.
                    // RevIQ self-suppresses setUid on opt-out; we also only get
                    // here when RevIQ's own consent answer granted consent.
                    // Prefer the passport's `hem`: it's normalized to the shared
                    // ecosystem spec, whereas `ppid` uses our own legacy
                    // normalization and is kept as-is for GAM PPID continuity.
                    // Same address, different hashes — only `hem` matches what
                    // other partners produce.
                    const hem       = maiPubSessionData.hem;
                    const uid       = maiPubSessionData.ppid;
                    const uidSource = maiPubSessionData.ppidSource;
                    if (hem || uid) {
                        try {
                            if (hem) {
                                r.setUid({ e: { sha256: hem } });
                                maiPubLog('RevIQ: setUid sent hashed email (normalized).');
                            } else if (maiPubIsPersonLevelPpid(uidSource)) {
                                // Reachable when the ppid is a hashed email but `hem` itself
                                // is gone — e.g. a session that adopted a 'fpid'-sourced
                                // maipub_ppid cookie while the maipub_hem cookie had been
                                // purged. Tested the source directly before 'fpid' existed,
                                // which would have shipped a hashed email as a pseudonymous
                                // `u` id: not a leak, but a mislabel that costs the match.
                                r.setUid({ e: { sha256: uid } });
                                maiPubLog(`RevIQ: setUid sent hashed email (${uidSource} PPID).`);
                            } else {
                                r.setUid({ u: uid });
                                maiPubLog(`RevIQ: setUid sent pseudonymous session id (source: ${uidSource || 'unknown'}).`);
                            }
                        } catch (e) {
                            maiPubLog('RevIQ: setUid failed.', e);
                        }
                    } else if (log) {
                        maiPubLog('RevIQ: no PPID in session passport — setUid skipped this load.');
                    }
                }).catch((e) => {
                    maiPubLog('RevIQ: consent.ready() failed — the CMP timeout fallback decides consent this load.', e);
                });
            });
        }

        // --- WAITING FOR CMP ---
        // Catalyst and Prebid injection only runs INSIDE this .then() block
        maiPubLog('CMP: Waiting for onConsentReady event before injecting scripts...');

        waitForCMPReady().then(() => {
            maiPubLog('CMP: Ready - proceeding with Strict Script Injection.');
            initStatus.cmp = true;

            // RevIQ (1.) was started before the wait, above. Its loader callback and
            // the Catalyst and Prebid branches below each call checkAllSystemsReady(),
            // so the promise resolves once both the loader and consent are in.

            // --- 2. CATALYST INJECTION ---
			if (maiPubAdsVars.catalyst === true && hasConsent === true) {
				maiPubLog('Catalyst Init: Injecting Catalyst script...');

                const catalystScriptUrl = 'https://ads.thenexusengine.com/assets/catalyst-sdk-v1.0.0.js';
                injectScript('mai-publisher-catalyst', catalystScriptUrl, () => {
                    try {
                        if (typeof window.catalyst !== 'undefined' && typeof window.catalyst.init === 'function') {
                            window.catalyst.init({
                                accountId: maiPubAdsVars.catalystAccountId,
                                serverUrl: 'https://ads.thenexusengine.com',
                                timeout: maiPubAdsVars.catalystTimeout || 2800,
                                debug: maiPubAdsVars.catalystDebug,
                                targeting: {}
                            });
                            maiPubLog('Catalyst Init: Catalyst SDK initialized successfully');
                        } else {
                            maiPubLog('Catalyst Init: Error - catalyst object not available');
                        }
                    } catch (e) {
                        maiPubLog('Catalyst Init: Error initializing catalyst', e);
                    }
                    initStatus.catalyst = true;
                    checkAllSystemsReady();
                });
            } else if (maiPubAdsVars.catalyst === true && hasConsent === false) {
                maiPubLog('Catalyst Init: No consent, skipping Catalyst script.');
                initStatus.catalyst = true;
                checkAllSystemsReady();
            } else {
                maiPubLog('Catalyst Init: Catalyst disabled, skipping script.');
                checkAllSystemsReady();
            }

            // --- 3. PREBID/MAGNITE INJECTION ---
            // Load the wrapper when EITHER display won the adapter split OR video
            // header bidding is enabled.
            //
            // maiPubAdsVars.magnite is rewritten client-side by the display adapter
            // split (see maiPubGetPrebidSplit), so on a pageview bucketed to RevIQ or
            // Catalyst it is false. Video must NOT follow that split - video is always
            // GAM + Magnite when enabled - so gating injection on the display flag
            // alone would silently make video header bidding impossible on the
            // non-Magnite share of traffic (pbjs.rp would never exist).
            //
            // Loading the wrapper does not make display bid: the display auction is
            // separately gated on maiPubAdsVars.magnite, so under RevIQ/Catalyst the
            // wrapper is present for video only.
            const maiPubNeedsPrebid = maiPubAdsVars.magnite === true || maiPubVideoHbEnabled();

            if (maiPubNeedsPrebid && hasConsent === true) {
                maiPubLog(`pbjs Init: Injecting Magnite/Prebid script... (display: ${maiPubAdsVars.magnite === true}, video: ${maiPubVideoHbEnabled()})`);

                // NOTE: site.mobile and site.privacypolicy are set server-side in
                // maipub_get_ortb2_site_data() (both integer 1). We previously ALSO
                // set them at the ORTB2 root here — but mobile/privacypolicy are
                // Site fields, not top-level BidRequest fields, so the root copies
                // were non-standard and unread. Removed; the site.* values stand.

                // Magnite's own ID5 settings read pd from window.mai_publisher_pd. Prebid uses
                // those settings, not ours, whenever it starts ID5 before our config arrives,
                // so publish pd first. Only once geo is known: pd waits for the IP.
                if (maiPubGeoKnown()) {
                    maiPubPublishId5Pd();
                }

                // HARDCODED URL
                injectScript('mai-publisher-prebid', '//micro.rubiconproject.com/prebid/dynamic/26298.js', () => {
                    maiPubLog('pbjs Init: Script loaded. Configuring...');
                    if (debug || log) { maiPubTiming.wrapperLoaded = performance.now(); }

                    window.pbjs = window.pbjs || { que: [] };
                    pbjs.que.push(function() {
                      // Bounded wait for geo.js. Everything below is unchanged and
                      // deliberately not re-indented, so the diff shows the gate and
                      // nothing else. maiPubWhenGeoReady() runs its callback
                      // synchronously whenever geo is already known — which is every
                      // pageview after the first — so this costs nothing in the common
                      // case. See GEO_PD_WAIT_MS for why the gate exists at all.
                      maiPubWhenGeoReady(function () {
                        try {
                            const pbjsConfig = {
                                bidderTimeout: bidderTimeout,
                                enableTIDs: true,
                                ortb2: maiPubAdsVars.ortb2,
                                bidderSettings: { standard: { storageAllowed: true } }
                            };

                            // Supply chain (schain) provided as ORTB2 first-party data.
                            // Prebid deprecated the schain *module* (pbjsConfig.schain /
                            // setConfig({schain})) in favour of ortb2.source.schain. Same
                            // values, no behaviour change — schain still ships on every bid
                            // request; this just removes the deprecation warning and future-
                            // proofs against the module being dropped. asi/sid/hp unchanged:
                            // sid is the sellers.json-matching encoded network code
                            // (maipub_encode(network_code), e.g. 8098843 -> ODA5ODg0Mw).
                            // Mutating maiPubAdsVars.ortb2 also updates pbjsConfig.ortb2 (same ref).
                            maiPubAdsVars.ortb2 = maiPubAdsVars.ortb2 || {};
                            maiPubAdsVars.ortb2.source = maiPubAdsVars.ortb2.source || {};
                            maiPubAdsVars.ortb2.source.schain = {
                                ver: "1.0",
                                complete: 1,
                                nodes: [{
                                    asi: maiPubAdsVars.manager_domain === 'true' ? "bizbudding.com" : maiPubAdsVars.domain,
                                    sid: maiPubAdsVars.sellersId,
                                    hp: 1
                                }]
                            };

                            // User Sync Config
                             let userIds = [{
                                name: "pubCommonId",
                                storage: { type: "cookie", name: "_pubcid", expires: 365, refreshInSeconds: 8 * 3600 }
                            }];

                            // Add ID5
                            if (maiPubAdsVars.id5UserSync && maiPubAdsVars.id5UserSync.userIds) {
                                if (Array.isArray(maiPubAdsVars.id5UserSync.userIds)) {
                                    userIds.push(...maiPubAdsVars.id5UserSync.userIds);
                                }
                            }

                            // ID5 partner data (pd). Built entirely client-side — PHP
                            // no longer ships a pd string, because the user agent, the
                            // worker's IP and the resolved passport identity are only
                            // knowable here, and anything per-visitor rendered server-side
                            // would be baked into Varnish-cached HTML. See
                            // maiPubBuildId5Pd() for what goes in and what deliberately
                            // doesn't.
                            const id5Entry = userIds.find(u => u && u.name === 'id5Id');
                            if (id5Entry) {
                                const id5Pd = maiPubBuildId5Pd();

                                id5Entry.params = id5Entry.params || {};

                                if (id5Pd) {
                                    id5Entry.params.pd = id5Pd;
                                } else {
                                    // Send no key at all rather than an empty one.
                                    delete id5Entry.params.pd;
                                }
                            }

                            // Add PPID — use the resolved session passport id so ALL
                            // visitors carry identity into the Prebid auction, matching
                            // GAM (setPublisherProvidedId), RevIQ (setUid) and VAST. The
                            // old gate (maiPubAdsVars.ppid) was the server email-hash,
                            // present only for logged-in users, so anonymous visitors
                            // (matomo/random passport) sent no id to SSPs. Prebid does not
                            // require GAM's 32-150 alphanumeric format, so the raw passport
                            // id (incl. a random UUID) is valid as a pubProvidedId eid.
                            //
                            // atype declares WHAT KIND of id this is, per ORTB:
                            //   1 = tied to a specific browser or device
                            //   3 = person-based, the same across a person's devices
                            // It was hardcoded to 1. That is right for the cookie/matomo/
                            // random passport id, but when the ppid is a hashed email it is
                            // a person-based id. Declaring that as device-tied understates
                            // it to every bidder reading the field, since a cross-device
                            // person id is worth more than a cookie id and is treated
                            // differently downstream.
                            //
                            // Both 'server' AND 'fpid' are hashed emails — a newsletter
                            // click identifies the person exactly as a login does. This
                            // read `'server' === ppidSource` until 'fpid' existed, which
                            // would have declared every newsletter-identified visitor as
                            // device-tied.
                            if (maiPubSessionData.ppid) {
                                const ppidAtype = maiPubIsPersonLevelPpid() ? 3 : 1;

                                userIds.push({
                                    name: "pubProvidedId",
                                    params: { eids: [{ source: maiPubAdsVars.domain, uids: [{ id: maiPubSessionData.ppid, atype: ppidAtype }] }] }
                                });

                                maiPubLog(`pubProvidedId: eid configured (source: ${maiPubAdsVars.domain}, atype: ${ppidAtype}, ppidSource: ${maiPubSessionData.ppidSource || 'unknown'}).`);

                                // Belt-and-suspenders: the pubProvidedId submodule above only
                                // emits an eid when `pubProvidedIdSystem` is compiled into the
                                // loaded Prebid build. The Magnite DM wrapper build ships only
                                // `id5IdSystem` + the userId core (confirmed live via
                                // pbjs.installedModules), so the userSync entry above is silently
                                // dropped — getUserIdsAsEids() returns ID5 only and our first-party
                                // id never reaches the SSP partners. Inject the same eid straight
                                // into ortb2.user.ext.eids, which Prebid forwards to every bidder
                                // regardless of which userId submodules exist in the build (same
                                // ref as pbjsConfig.ortb2, applied at setConfig below). If a future
                                // build does include pubProvidedIdSystem, Prebid dedupes eids by
                                // source+id, so keeping both is safe.
                                maiPubAdsVars.ortb2 = maiPubAdsVars.ortb2 || {};
                                maiPubAdsVars.ortb2.user = maiPubAdsVars.ortb2.user || {};
                                maiPubAdsVars.ortb2.user.ext = maiPubAdsVars.ortb2.user.ext || {};
                                maiPubAdsVars.ortb2.user.ext.eids = maiPubAdsVars.ortb2.user.ext.eids || [];
                                maiPubAdsVars.ortb2.user.ext.eids.push({
                                    source: maiPubAdsVars.domain,
                                    uids: [{ id: maiPubSessionData.ppid, atype: ppidAtype }]
                                });
                                maiPubLog(`pubProvidedId: also injected into ortb2.user.ext.eids so it reaches bidders without the submodule.`);
                            }

                            pbjsConfig.userSync = {
                                auctionDelay: prebidAuctionDelay,
                                syncsPerBidder: 5,
                                userIds: userIds
                            };

                            // PAAPI (Protected Audience) is now owned by Magnite: the paapi +
                            // paapiForGpt modules and their config (enabled, defaultForSlots,
                            // gpt.configWithTargeting) come from the Magnite Demand Manager
                            // parameter set / wrapper build. We no longer set pbjsConfig.paapi
                            // here to avoid double-configuration. We STILL bridge the results
                            // to GPT at request time via maiPubApplyPaapiForGpt()
                            // (pbjs.setPAAPIConfigForGPT), which is guarded and no-ops if the
                            // paapiForGpt module isn't present.

                            // Match Prebid ad units to GPT slots when multiple divs share one GAM path.
                            pbjsConfig.customGptSlotMatching = function( slot ) {
                                return maiPubPrebidAdUnitMatcher( slot );
                            };

                            pbjs.setConfig(pbjsConfig);

                            // Magnite's wrapper has Prebid assume GDPR applies when the CMP
                            // has not answered within a second (defaultGdprScope: true). We
                            // no longer wait on the CMP in North America, so the auction can
                            // start before SourcePoint has loaded, and that assumption blocks
                            // ID5 and the bidders for a visitor GDPR does not cover.
                            if ('na' === maiPubGetGeoRegion()) {
                                pbjs.mergeConfig({ consentManagement: { gdpr: { defaultGdprScope: false } } });
                                maiPubLog('pbjs Init: North America — GDPR is not assumed if SourcePoint has not answered yet (defaultGdprScope: false).');
                            }

                            maiPubConfigurePrebidPaapi();
                            maiPubWatchPrebidTiming();
                            maiPubLog('pbjs Init: Prebid.js configured successfully');
                            maiPubWatchPrebidOutcomes();
                        } catch (error) {
                            maiPubLog('pbjs Init: Prebid.js configuration failed:', error);
                        }

                        initStatus.prebid = true;
                        checkAllSystemsReady();
                      }); // maiPubWhenGeoReady
                    });
                });
            } else if (maiPubNeedsPrebid && hasConsent === false) {
                maiPubLog('pbjs Init: No consent, skipping Magnite/Prebid script.');
                initStatus.prebid = true;
                checkAllSystemsReady();
            } else {
                maiPubLog('pbjs Init: Magnite/Prebid disabled, skipping script (display and video both off).');
                checkAllSystemsReady();
            }
        });
    });
}


/**
 * Checks if an element is in the viewport.
 *
 * @since 1.16.0
 *
 * @param {Element} el The element to check.
 * @returns {boolean} True if the element is in the viewport.
 */
function isElementInViewport(el) {
	if (!el) {
		return false;
	}
	const rect = el.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
}

/**
 * DOMContentLoaded and IntersectionObserver handler.
 * This function is the entry point for ad rendering (Phases 5 & 6).
 * It is called by maiPubInitParallel() after both LCP and Bidders are ready.
 *
 * @since 1.13.0
 *
 * @return {void}
 */
function maiPubRun() {
	maiPubLog( 'maiPubRun starting...' );

	const adUnits      = document.querySelectorAll('.mai-ad-unit');
	const adVideoUnits = document.querySelectorAll('.mai-ad-unit-video');
	const adAudioUnits = document.querySelectorAll('.mai-ad-unit-audio');

	// Prelude runs first and is intentionally insensitive to whether display
	// runs (sticky padding, visibility listener, OOP spotlight dedup). Batch 2
	// (RevIQ display takeover) will no-op maiPubRunDisplay without affecting
	// any of these.
	maiPubRunPrelude(adUnits);

	// No ad units yet, but the page is still loading: they may simply not be parsed
	// yet. On a page whose only ad is the sticky footer this is the norm, not an edge
	// case, and returning here used to leave that page with no ads at all, because the
	// late-unit check only exists inside the display pipeline. Look again once the
	// page has finished loading.
	if ( ! adUnits.length && ! adVideoUnits.length && ! adAudioUnits.length && 'loading' === document.readyState ) {
		maiPubLog('No ad units yet and the page is still loading - checking again when it has finished.');
		document.addEventListener('DOMContentLoaded', maiPubRunAdUnits, { once: true });
		return;
	}

	maiPubRunAdUnits();
}

/**
 * Hand the page's ad units to the display, video and audio pipelines.
 *
 * Split out of maiPubRun so a page with no ad units parsed yet can run it again on
 * DOMContentLoaded. maiPubRunPrelude is not repeated: it only runs once per page.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubRunAdUnits() {
	const adUnits      = document.querySelectorAll('.mai-ad-unit');
	const adVideoUnits = document.querySelectorAll('.mai-ad-unit-video');
	const adAudioUnits = document.querySelectorAll('.mai-ad-unit-audio');

	// Check if ad units are available
	if ( ! adUnits.length && ! adVideoUnits.length && ! adAudioUnits.length ) {
		maiPubLog('No ad units, video ad units, or audio ad units found on page - this may be a page without ads or ad units not yet rendered');
		return;
	}

	maiPubLog(`Found ${adUnits.length} ad units. Processing in a single loop...`);
	maiPubLog(`Found ${adVideoUnits.length} video ad units.`);
	maiPubLog(`Found ${adAudioUnits.length} audio ad units.`);

	if ( adUnits.length > 0 )      maiPubRunDisplay();
	if ( adVideoUnits.length > 0 ) maiPubRunVideo( adVideoUnits );
	else                           maiPubLog('maiPubRun: skipping maiPubInitVideoAds, zero on page');
	if ( adAudioUnits.length > 0 ) maiPubRunAudio( adAudioUnits );
}

/**
 * Sync the sticky-footer height into the floating-video offset var.
 *
 * The floating player's bottom offset is `calc(--gutter-y-* + --footer-offset-*)`.
 * `--gutter-y-*` is the publisher's chosen gap (block Positions tab); `--footer-offset-*`
 * is meant to hold the sticky footer's height so the player clears it. That var is
 * emitted inline by PHP but hard-defaults to 0 via an unconfigured filter — i.e. it was
 * dead, so the float never accounted for the footer and rendered behind it (seen on iOS).
 * Here we measure the real footer height at runtime and write it onto each player element
 * (overriding the inline 0), so the player clears footers of any height — including
 * expandable creatives (Kargo etc.) via the ResizeObserver in maiPubRunPrelude.
 *
 * A bar fitted to a shorter ad (maiPubFitStickyFooter) is moved down by a transform, so
 * the player follows it the same way: `--footer-offset-*` stays at the bar's full box and
 * `--footer-lowered-*` moves the player down by as much as the bar (CSS `translate`).
 * Changing `bottom` would move a fixed player in the layout, which Chrome counts as a
 * layout shift (CLS); a translate isn't counted. The body padding is the visible part.
 *
 * Gutter Y acts as an override (per breakpoint):
 *   Gutter Y = 0    -> AUTO: clear the sticky footer (measured height + a small gap).
 *   Gutter Y = N>0  -> FORCED: honor the publisher's exact offset; ignore the footer.
 * This supersedes the PHP `mai_publisher_video_footer_offsets` filter default (dead).
 *
 * @return {void}
 */
function maiPubSyncFooterOffset() {
	const stickyFooter = document.querySelector('.mai-ad-unit-sticky-footer');
	// Footer height when a sticky footer is present; 0 when the page has none. Either
	// way we still apply the auto buffer below, so a floating player keeps a bottom gap
	// even on footer-less pages (otherwise it sits flush against the viewport bottom).
	const footerHeight = stickyFooter ? Math.max(stickyFooter.offsetHeight, 90) : 0;
	// The part of the bar moved below the screen (0 unless fitted to a shorter ad).
	const lowered = stickyFooter ? Math.min(_stickyFooterLoweredPx, footerHeight) : 0;

	// Keep the body padding in sync only when a footer is actually reserving space
	// (content shouldn't sit under the footer). No footer => nothing to pad for.
	if (footerHeight > 0) {
		document.body.style.paddingBottom = `${footerHeight - lowered}px`;
	}

	// The float bottom is calc(--gutter-y-* + --footer-offset-*). Apply the override
	// rule per breakpoint by choosing --footer-offset-*:
	//   Gutter Y = 0    -> --footer-offset = footer height + gap  => bottom = height + gap (auto),
	//                      and --footer-lowered = what the bar is lowered by.
	//                      With no footer this is just the gap, so the player never sits
	//                      flush against the viewport bottom.
	//   Gutter Y = N>0  -> --footer-offset = 0                    => bottom = N (forced gutter).
	// Set on the element itself so it overrides the inline PHP default; the __inner that
	// reads it inherits the value.
	const autoOffset = footerHeight + floatingVideoFooterGapPx;
	const breakpoints = ['mobile', 'tablet', 'desktop'];
	const players = document.querySelectorAll('.mai-ad-unit-video');
	players.forEach((v) => {
		const cs = window.getComputedStyle(v);
		breakpoints.forEach((bp) => {
			const gutter = parseInt(cs.getPropertyValue(`--gutter-y-${bp}`), 10) || 0;
			v.style.setProperty(`--footer-offset-${bp}`, gutter === 0 ? `${autoOffset}px` : '0px');
			v.style.setProperty(`--footer-lowered-${bp}`, gutter === 0 ? `${lowered}px` : '0px');
		});
	});

	maiPubLog(`Sticky footer: synced --footer-offset (auto=${footerHeight}+${floatingVideoFooterGapPx}gap px${lowered ? `, moved ${lowered}px down with the bar` : ''} where Gutter Y=0, else forced gutter; footer ${stickyFooter ? 'present' : 'absent'}) to ${players.length} floating video player(s).`);
}

/**
 * One-time sticky-footer setup: sync the floating-video offset and body padding
 * (maiPubSyncFooterOffset) and attach a ResizeObserver so expandable footer
 * creatives keep the player clear.
 *
 * Guarded by _stickyFooterHandled so it runs at most once, whether it is first
 * reached in maiPubRunPrelude or later via the late-DOM reconciliation — the footer
 * was the last node in <body> and could parse in after maiPubRun's snapshot. It now
 * prints at the top of <body> (wp_body_open), except on themes that never fire that
 * hook, where it is still the last node.
 *
 * @since 1.33.2
 *
 * @return {boolean} True if the footer was found and handled (now or previously), false otherwise.
 */
function maiPubSetupStickyFooter() {
	if (_stickyFooterHandled) {
		return true;
	}

	// Sync the floating-video offset on every setup attempt — even before/without a
	// sticky footer — so the player keeps its bottom buffer on footer-less pages too.
	// When a footer is present this also measures its height and reserves body padding.
	// Idempotent, so running it again below (once the footer parses in) is safe.
	maiPubSyncFooterOffset();

	const stickyFooter = document.querySelector('.mai-ad-unit-sticky-footer');
	if (!stickyFooter) {
		// No footer (yet). Leave _stickyFooterHandled false so the caller reconciles on
		// DOMContentLoaded in case the footer parses in late; the base buffer is applied.
		return false;
	}

	_stickyFooterHandled = true;
	maiPubLog('Sticky footer ad found.');

	// Expandable footer creatives (Kargo etc.) grow after load, so re-sync when the
	// footer's size changes — otherwise the player clears the initial height but not
	// the expanded one (the iOS bug: floating video rendered behind the footer).
	if (typeof ResizeObserver === 'function') {
		const footerResizeObserver = new ResizeObserver(() => maiPubSyncFooterOffset());
		footerResizeObserver.observe(stickyFooter);
	}

	maiPubWatchStickyFooterFit(stickyFooter);

	return true;
}

/**
 * The sticky footer's creative iframe (GPT's), or null.
 *
 * @since TBD
 *
 * @param {HTMLElement} bar The .mai-ad-unit-sticky-footer container.
 * @return {HTMLIFrameElement|null}
 */
function maiPubStickyFooterIframe(bar) {
	return bar.querySelector('.mai-ad-unit iframe[id^="google_ads_iframe"]') || bar.querySelector('.mai-ad-unit iframe');
}

/**
 * A short name for an element in a debug line: its tag, id and first two classes,
 * e.g. "div.kargo-ad-container".
 *
 * @since TBD
 *
 * @param {Element} el
 * @return {string}
 */
function maiPubDescribeElement(el) {
	const id      = el.id ? `#${el.id}` : '';
	const classes = [...el.classList].slice(0, 2).map((c) => `.${c}`).join('');

	return `${el.tagName.toLowerCase()}${id}${classes}`;
}

/**
 * What in the sticky footer is more than GPT's ad, or null when it holds only that: in
 * the slot, GPT's container div with its iframe, neither fixed nor absolute, and nothing
 * else in the bar.
 *
 * Sticky footer creatives from Kargo and other bidders get out of their frame (a
 * non-SafeFrame creative can reach the page) and draw a unit into the slot or next to
 * it, or set themselves fixed to expand past the slot. That is the creative's own
 * layout: the bar is left as it was. A transform would make the bar the frame of
 * reference for anything fixed inside it, so an ad expanding to the screen's edges
 * would be placed and clipped inside the bar instead.
 *
 * Scripts and styles a creative adds don't count: they take no space.
 *
 * The answer names the element, for the "not fitted" debug line: text without sizes,
 * so the line isn't repeated while the element animates.
 *
 * @since TBD
 *
 * @param {HTMLElement} bar   The .mai-ad-unit-sticky-footer container.
 * @param {HTMLElement} inner The slot (.mai-ad-unit, GPT's div).
 * @return {{text: string, el: Element}|null}
 */
function maiPubStickyFooterNotPlain(bar, inner) {
	const inert    = (el) => /^(SCRIPT|STYLE|LINK|NOSCRIPT|TEMPLATE)$/.test(el.tagName);
	const position = (el) => getComputedStyle(el).position;
	const placed   = (el) => -1 !== ['fixed', 'absolute'].indexOf(position(el));

	for (const el of bar.children) {
		if (el !== inner && !inert(el)) {
			return { text: `${maiPubDescribeElement(el)} was added to the bar, next to the slot`, el };
		}
	}

	for (const el of inner.children) {
		if (inert(el)) {
			continue;
		}

		if (!/^google_ads_iframe_.*__container__$/.test(el.id)) {
			return { text: `${maiPubDescribeElement(el)} was drawn into the slot`, el };
		}

		if (placed(el)) {
			return { text: `GPT's container is position: ${position(el)}`, el };
		}

		for (const d of el.querySelectorAll('*')) {
			if (inert(d)) {
				continue;
			}

			if ('IFRAME' !== d.tagName) {
				return { text: `${maiPubDescribeElement(d)} was drawn inside GPT's container, next to the frame`, el: d };
			}

			if (placed(d)) {
				return { text: `the creative's frame is position: ${position(d)}, to expand past the slot`, el: d };
			}
		}
	}

	return null;
}

/**
 * Logs the sticky footer fit's outcome, once per render and outcome: the observers
 * check again on every change in the slot, so the same outcome comes up many times.
 * maiPubWatchStickyFooterFit() starts each ad request afresh.
 *
 * A reason the bar isn't fitted is logged once while the bar stays that way, unless a
 * more telling one follows (a higher rank). Teads' player gave three lines per ad on
 * allhiphop.com: its element was logged again as it changed classes while opening, and
 * the 1x1 check a second after the render repeated it. What the creative drew says more
 * than "still 1x1", which is all there is to say about a player drawn late, at first.
 *
 * @since TBD
 *
 * @param {string} key     The outcome, without sizes that change as an ad animates.
 * @param {string} message The debug line.
 * @param {number} rank    0 when fitted, else how telling the reason is (_stickyFooterUnfitRank).
 * @return {void}
 */
function maiPubLogStickyFooterFit(key, message, rank = 0) {
	if (key === _stickyFooterFitLogged || (rank && rank <= _stickyFooterUnfitRank)) {
		return;
	}

	_stickyFooterFitLogged = key;
	_stickyFooterUnfitRank = rank;
	maiPubLog(message);
}

/**
 * Fits the sticky footer bar to the ad in it.
 *
 * The bar's box reserves the tallest size its slot can serve (100px for 320x100 on
 * mobile), so a 320x50 ad left a 50px empty strip at the bottom of the screen. Now the
 * ad sits at the top of the box and the bar moves down by the empty space below it,
 * out of the screen. It moves with a transform rather than a smaller box: a fixed bar
 * whose height changes moves its top edge, which Chrome counts as a layout shift
 * (CLS); a transform isn't counted. The box keeps its reserve, so a taller ad on a
 * refresh fits as before and the bar comes back up.
 *
 * The bar stays where it is while the creative is 1px tall: a header-bidding ad renders
 * through a 1x1 creative that resizes itself once the bid has rendered.
 * maiPubWatchStickyFooterFit() follows that resize, and any later expand.
 *
 * Only a plain ad is fitted (maiPubStickyFooterNotPlain): once a creative writes into
 * the page, to draw a unit outside its frame or expand past the slot, the bar goes back
 * to how it was, with no transform. The debug log names what it found.
 *
 * The floating video player moves down with the bar, so it still sits just above the
 * visible part: maiPubSyncFooterOffset() runs here, because a transform doesn't fire
 * the bar's ResizeObserver.
 *
 * @since TBD
 *
 * @param {HTMLElement} bar The .mai-ad-unit-sticky-footer container.
 * @return {void}
 */
function maiPubFitStickyFooter(bar) {
	const inner  = bar.querySelector('.mai-ad-unit');
	const iframe = maiPubStickyFooterIframe(bar);

	if (!inner || !iframe) {
		return;
	}

	// A creative that has written into the page gets the bar as it was, untouched.
	const notPlain = maiPubStickyFooterNotPlain(bar, inner);

	if (notPlain) {
		const r = notPlain.el.getBoundingClientRect(); // any element, SVG included

		maiPubUnfitStickyFooter(bar, `the creative reaches out of its frame: ${notPlain.text} (${Math.round(r.width)}x${Math.round(r.height)})`, notPlain.text, 2);
		return;
	}

	if (iframe.offsetHeight <= 1) {
		return;
	}

	// The ad sits at the top of the box (CSS), so the empty space is all below it.
	// Measured from the box, not the screen: the bar's current transform moves both alike.
	bar.classList.add('is-fitted');

	const box     = inner.getBoundingClientRect();
	const frame   = iframe.getBoundingClientRect();
	const lowered = Math.max(0, Math.round(box.bottom - frame.bottom));
	const down    = Math.round(frame.top - box.top);

	// Not moved: the ad fills the box, or something places it lower (a label, a theme rule).
	const how = lowered
		? `moved ${lowered}px down, showing ${bar.offsetHeight - lowered}px of its ${bar.offsetHeight}px box`
		: (down > 1 ? `not moved, the ad sits ${down}px down its ${bar.offsetHeight}px box with nothing empty below it` : `the ad fills its ${bar.offsetHeight}px box`);

	maiPubLogStickyFooterFit(`fitted ${iframe.offsetWidth}x${iframe.offsetHeight} ${lowered}`, `Sticky footer: fitted to its ${iframe.offsetWidth}x${iframe.offsetHeight} ad: ${how}.`);

	if (lowered === _stickyFooterLoweredPx) {
		return;
	}

	_stickyFooterLoweredPx = lowered;
	bar.style.transform    = lowered ? `translateY(${lowered}px)` : '';

	maiPubSyncFooterOffset();
}

/**
 * Puts the sticky footer bar back as it was: full height, no transform. For an empty
 * render, a creative still 1x1 after a second, or one that writes into the page.
 *
 * Logs why, once per ad request (maiPubLogStickyFooterFit), whether or not the bar had
 * been fitted: a bar left at its full height with a small ad in it is otherwise silent
 * in the log. Not while the next ad is on its way: the slot still holds the last one and
 * what it drew (Teads' player outlives its frame), whose line has been logged.
 *
 * @since TBD
 *
 * @param {HTMLElement} bar  The .mai-ad-unit-sticky-footer container.
 * @param {string}      why  Why the bar isn't fitted, for the debug line.
 * @param {string}      key  The same without sizes that change as an ad animates.
 * @param {number}      rank How telling the reason is: 1 the creative still 1x1 or no ad,
 *                           2 what the creative drew.
 * @return {void}
 */
function maiPubUnfitStickyFooter(bar, why, key = why, rank = 1) {
	const fitted = bar.classList.contains('is-fitted');

	if (fitted || !_stickyFooterRequestPending) {
		maiPubLogStickyFooterFit(`not fitted ${key}`, fitted
			? `Sticky footer: back to its full ${bar.offsetHeight}px height: ${why}.`
			: `Sticky footer: not fitted to its ad: ${why}. The bar keeps its full ${bar.offsetHeight}px box.`, rank);
	}

	if (!fitted) {
		return;
	}

	bar.classList.remove('is-fitted');
	bar.style.transform    = '';
	_stickyFooterLoweredPx = 0;

	maiPubSyncFooterOffset();
}

/**
 * Fits the sticky footer bar to each ad that renders in it (maiPubFitStickyFooter).
 *
 * Listens to GPT directly rather than through maiPubRunDisplay's render handler, so it
 * works in every display mode, RevIQ's included. Each render watches its creative with
 * a ResizeObserver, which reports the size at once and again on every change (a 1x1
 * header-bidding creative resizing, an expand). A creative still 1x1 after a second
 * gets the bar back at its full height.
 *
 * A creative can write into the page any time after it renders (an expand on tap, a
 * unit drawn a few seconds in), so every change in the slot, and anything added to
 * the bar, checks again (maiPubStickyFooterNotPlain) in the same frame. Our own
 * changes are to the bar's attributes, which aren't watched, so this can't loop.
 *
 * @since TBD
 *
 * @param {HTMLElement} bar The .mai-ad-unit-sticky-footer container.
 * @return {void}
 */
function maiPubWatchStickyFooterFit(bar) {
	const resizeWaitMs = 1000;
	const inner        = bar.querySelector('.mai-ad-unit');
	let observer       = null;
	let render         = 0;

	if (typeof MutationObserver === 'function') {
		const check = () => maiPubFitStickyFooter(bar);

		if (inner) {
			new MutationObserver(check).observe(inner, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'width', 'height'] });
		}

		new MutationObserver(check).observe(bar, { childList: true });
	}

	googletag.cmd.push(() => {
		// Each request's render logs its own outcome. Reset at the request: GPT puts the
		// creative's iframe in before slotRenderEnded, and the observers fit it at once.
		googletag.pubads().addEventListener('slotRequested', (event) => {
			const inner = document.getElementById(event.slot.getSlotElementId());

			if (inner && bar.contains(inner)) {
				_stickyFooterFitLogged      = '';
				_stickyFooterUnfitRank      = 0;
				_stickyFooterRequestPending = true;
			}
		});

		googletag.pubads().addEventListener('slotRenderEnded', (event) => {
			const inner = document.getElementById(event.slot.getSlotElementId());

			if (!inner || !bar.contains(inner)) {
				return;
			}

			_stickyFooterRequestPending = false;

			const thisRender = ++render;

			if (observer) {
				observer.disconnect();
				observer = null;
			}

			if (event.isEmpty) {
				maiPubUnfitStickyFooter(bar, 'GAM returned no ad');
				return;
			}

			// Let GPT insert the creative's iframe first.
			setTimeout(() => {
				const iframe = maiPubStickyFooterIframe(bar);

				if (thisRender !== render || !iframe) {
					return;
				}

				if (typeof ResizeObserver === 'function') {
					observer = new ResizeObserver(() => maiPubFitStickyFooter(bar));
					observer.observe(iframe);
				} else {
					maiPubFitStickyFooter(bar);
				}

				setTimeout(() => {
					if (thisRender === render && iframe.offsetHeight <= 1) {
						maiPubUnfitStickyFooter(bar, `its creative is still ${iframe.offsetWidth}x${iframe.offsetHeight} ${resizeWaitMs}ms after the render (a header-bidding ad that never resized, or one drawing outside its frame)`);
					}
				}, resizeWaitMs);
			}, 60);
		});
	});
}

/**
 * Always-on setup that runs regardless of which display path owns the page.
 *
 * Responsibilities:
 *  - Sticky-footer body padding so content doesn't sit under a sticky ad.
 *  - Page visibilitychange listener (manages both display refresh timers and
 *    video pause/resume). The display-refresh branch is safe-by-empty when
 *    display is no-op'd because slotManager stays empty.
 *  - OOP spotlight de-duplication: only one GPT out-of-page "spotlight" slot
 *    may render per page. We remove duplicate spotlight divs from the DOM
 *    here so that neither maiPubRunDisplay nor any external display owner
 *    (RevIQ, batch 2) double-renders. maiPubDefineSlot enforces the same
 *    rule defensively via spotlightOopSlug.
 *
 * Extracted from maiPubRun in batch 1 (RevIQ P0 plan, §3.2) so batch 2 can
 * skip maiPubRunDisplay without losing these page-level concerns.
 *
 * @since TBD
 *
 * @param {NodeList} adUnits Snapshot of `.mai-ad-unit` divs from maiPubRun().
 *                           Used only by the spotlight dedup pass.
 * @return {void}
 */
function maiPubRunPrelude(adUnits) {
	// --- Sticky Footer Padding + floating-video offset sync ---
	// The footer prints at the top of <body>, or as the last node on themes without
	// wp_body_open; if the parser hasn't reached it when
	// maiPubRun fires (LCP/CMP can win the race against body parsing), it isn't here
	// yet. Handle it now if present, otherwise reconcile on DOMContentLoaded so the
	// floating-video offset is synced in every display mode (this concern runs even
	// under RevIQ, where maiPubRunDisplay's reconciliation does not).
	if (!maiPubSetupStickyFooter() && document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', maiPubSetupStickyFooter, { once: true });
	}

	maiPubLogAdLayoutShifts();


	maiPubLog('maiPubRun: setting up event listeners for visibility and refreshes.');


	// --- 1. UNIFIED VISIBILITY LISTENER (Display + Video)
	// Register visibility listener immediately
	// This observer handles visibility state changes for both video and display ads
	document.addEventListener('visibilitychange', function() {
        maiPubLog('GLOBAL EVENT: Visibility changed. document.hidden:', document.hidden);

        if (document.hidden) {
            // --- PAGE HIDDEN ---
            isPageVisible = false;
            maiPubLog('Status: HIDDEN. Pausing all ad activity.');

            // A. Handle Display Ads (Pause seen-time countdowns; bank elapsed time)
            let pausedTimers = 0;
            for (const slotId in slotManager) {
                if (slotManager[slotId].seenSince) {
                    maiPubPauseSeenCountdown(slotId);
                    pausedTimers++;
                }
            }
            // Clear any remaining timers (OOP refresh, empty-slot retry) so nothing fires while hidden.
            for (const slotId in timeoutManager) {
                clearTimeout(timeoutManager[slotId]);
                delete timeoutManager[slotId];
            }
            if (pausedTimers > 0) maiPubLog(`Paused ${pausedTimers} display ad refresh countdowns (seen-time banked).`);

            // B. Handle Video Ads (Pause Players)
            let pausedVideoCount = 0;
            videoPlayers.forEach(player => {
                // Clear retry timers
                if (player.retryTimer) {
                    clearTimeout(player.retryTimer);
                    player.retryTimer = null;
                }
                // Pause playing ads
                if (player.adsManager && player.isAdPlaying) {
                    maiPubLog(`Pausing video ad for #${player.config.id}`);
                    player.adsManager.pause();
                    player.wasPausedByVisibility = true;
                    pausedVideoCount++;
                }
            });
            if (pausedVideoCount > 0) maiPubLog(`Paused ${pausedVideoCount} video ads.`);

        } else {
            // --- PAGE VISIBLE ---
            isPageVisible = true;
            maiPubLog('Status: VISIBLE. Resuming ad activity.');

            // A. Handle Display Ads (Resume seen-time countdowns for in-view slots)
            // Seen-time was banked on hide, so each slot picks up where it left off.
            // A slot that already banked its full seen-time resumes with remaining 0
            // and fires as soon as impressionViewable + threshold still hold.
            let resumedTimers = 0;
            for (const slotId in slotManager) {
                const s = slotManager[slotId];
                if (s.isOOP) {
                    if (refreshOutOfPage) maiPubArmOopRefresh(slotId); // OOP (Kargo spotlight) refresh — gated by refreshOutOfPage
                    continue;
                }
                const threshold = s.vizThreshold || refreshViewabilityThreshold;
                if (s.slotObject && !s.processing && (s.currentInView || 0) >= threshold) {
                    maiPubResumeSeenCountdown(slotId);
                    resumedTimers++;
                }
            }
            if (resumedTimers > 0) maiPubLog(`Resumed ${resumedTimers} display ad refresh countdowns.`);

            // B. Handle Video Ads (Resume Players)
            let resumedVideoCount = 0;
            videoPlayers.forEach(player => {
                if (player.adsManager && player.wasPausedByVisibility) {
                    maiPubLog(`Resuming video ad for #${player.config.id}`);
                    player.adsManager.resume();
                    player.wasPausedByVisibility = false;
                    player.justResumed = true;
                    setTimeout(() => { player.justResumed = false; }, 2000);
                    resumedVideoCount++;
                }
            });

            // Check for new video ad cycles after a short delay
            setTimeout(() => {
                videoPlayers.forEach(player => {
                    if (!player.wasPausedByVisibility && !player.justResumed && !player.isAdPlaying && isPageVisible && player.adsLoader &&
                        player.adState !== 'loading' && player.adState !== 'completed' && !player.maxRetriesReached) {
                        maiPubLog(`Starting new video ad cycle for #${player.config.id}`);
                        player.loadAdPod();
                    }
                });
            }, 100);
        }
	}); // <-- end document.addEventListener('visibilitychange')

	// --- OOP Spotlight de-duplication ---
	// Walk the snapshot, record the first spotlight slug into spotlightOopSlug,
	// and remove any subsequent duplicate spotlight divs from the DOM. Display
	// re-queries `.mai-ad-unit` after the prelude so it never sees the removed
	// elements. Functionally identical to the inline check that previously
	// lived in maiPubRun's ad-unit processing loop.
	//
	// Skipped under RevIQ: the RevIQ branch (maiPubInitBiddingSystems) already
	// dedups spotlights before stripping div ids, so these divs no longer carry
	// the `mai-ad-` id this loop keys off. Running it here would be a no-op at
	// best; the guard makes the ownership explicit.
	if ( 'reviq' !== displayMode ) {
		adUnits.forEach(adUnit => {
			const slotId = adUnit.getAttribute('id');
			if ( !slotId ) return;
			const slug = slotId.replace('mai-ad-', '');
			if ( !maiPubIsSpotlightOopUnit(slug) ) return;
			if ( spotlightOopSlug && spotlightOopSlug !== slug ) {
				maiPubLog( `maiPubRun: Skipping duplicate spotlight (${slotId}); only one per page.` );
				adUnit.remove();
				return;
			}
			if ( !spotlightOopSlug ) {
				spotlightOopSlug = slug;
			}
		});
	}
}

/**
 * Classify a single ad unit into fast-path (requested immediately) or lazy-load
 * (observed), populating slotManager.
 *
 * Shared by maiPubRunDisplay's initial loop and maiPubReconcileLateAdUnits so both
 * make identical decisions. Sticky ads (top/bottom/vertical adhesions) are
 * position:fixed and always visible by definition, so they unconditionally qualify
 * for the fast path — we do NOT gate them on a viewport-geometry check
 * (isElementInViewport), which is unreliable before fixed positioning is applied and
 * used to demote the sticky footer to lazy-load. Vertical sticky (vs) is detected by
 * its position target because it carries no sticky container class.
 *
 * @since 1.33.2
 *
 * @param {Element}              adUnit   The `.mai-ad-unit` element.
 * @param {IntersectionObserver} observer Lazy-load observer to attach non-fast-path units to.
 * @return {string|null} The slug if it qualifies for fast path (caller batches it), else null.
 */
/**
 * Whether an ad position is fixed adhesion — position:fixed, floating over the viewport.
 *
 * Only top ('ts') and bottom ('bs') adhesion qualify. Vertical sticky ('vs') is a
 * sidebar unit using position:sticky, which pins inside its own containing block and
 * never floats over the viewport, so it is visible only when the layout puts it in
 * view — on mobile the sidebar reflows to the bottom of the page and it is not.
 * Conflating the two is what let a mobile sidebar ad be fetched immediately and
 * reported as fully viewable while sitting thousands of pixels below the fold.
 *
 * @since TBD
 *
 * @param {string|null} adPosition The kv_ad_position / data-ap value.
 *
 * @return {boolean}
 */
function maiPubIsFixedAdhesion(adPosition) {
	return 'ts' === adPosition || 'bs' === adPosition;
}

// Recent scroll positions, { t: performance.now(), y: scrollY }, for maiPubScrollSpeed().
const maiPubScrollSamples = [];

/**
 * Records the scroll position, keeping roughly the last 250ms.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubNoteScroll() {
	const t = performance.now();

	maiPubScrollSamples.push({ t, y: window.scrollY || window.pageYOffset || 0 });

	while (maiPubScrollSamples.length > 1 && t - maiPubScrollSamples[0].t > 250) {
		maiPubScrollSamples.shift();
	}
}

/**
 * The current scroll speed in px per second, over roughly the last 250ms.
 *
 * @since TBD
 *
 * @return {number} Positive scrolling down, negative scrolling up, 0 when the page
 *                  hasn't scrolled for 150ms.
 */
function maiPubScrollSpeed() {
	const n = maiPubScrollSamples.length;

	if (n < 2) {
		return 0;
	}

	const first = maiPubScrollSamples[0];
	const last  = maiPubScrollSamples[n - 1];
	const dt    = last.t - first.t;

	if (dt <= 0 || performance.now() - last.t > 150) {
		return 0;
	}

	return (last.y - first.y) / dt * 1000;
}

/**
 * How far an element is from the screen in the direction of scrolling, in px.
 *
 * @since TBD
 *
 * @param {Element} el        The element.
 * @param {number}  direction Positive for down, negative for up (a scroll speed will do).
 * @param {number}  height    The viewport height.
 *
 * @return {number|null} 0 when it is on screen, null when it is behind the scroll or not
 *                       laid out (display: none).
 */
function maiPubDistanceAhead(el, direction, height) {
	const rect = el.getBoundingClientRect();

	if (0 === rect.width && 0 === rect.height) {
		return null;
	}

	if (rect.bottom > 0 && rect.top < height) {
		return 0;
	}

	if (direction >= 0) {
		return rect.top >= height ? rect.top - height : null;
	}

	return rect.bottom <= 0 ? -rect.bottom : null;
}

/**
 * The lazy-load trigger for display ads.
 *
 * Two ways an ad is requested, whichever comes first:
 *  - the zone: it is within lazyLoadRootMarginPx of the screen (IntersectionObserver).
 *  - the scroll look-ahead: while scrolling, it is within lazyLookaheadMs of scrolling at
 *    the current speed, never more than lazyLookaheadMaxScreens screens ahead.
 *
 * Requesting an ad takes about a second from request to render, so with only the zone
 * anyone scrolling faster than ~200px/s saw an empty slot first. A reader scrolling slowly
 * is still served by the zone alone, so viewability for them doesn't change.
 *
 * @since TBD
 *
 * @param {Function} onReady Called with (elements, trigger), trigger 'zone' or 'look-ahead'.
 *
 * @return {Object} { observe(el), unobserve(el) }.
 */
function maiPubLazyLoader(onReady) {
	const pending = new Set();

	const zone = new IntersectionObserver((entries) => {
		const ready = [];

		maiPubLog(`Observer: IntersectionObserver entries: ${entries.length}`);

		entries.forEach((entry) => {
			// If the ad placeholder is not intersecting (visible), do nothing.
			if (!(entry.isIntersecting || entry.intersectionRatio > 0)) {
				maiPubLog(`Observer: Entry is not intersecting, skipping: ${entry.target.getAttribute('id')}`);
				return;
			}

			maiPubLog(`Observer: Entry is intersecting, adding to newlyVisibleSlots: ${(entry.target.getAttribute('id') || '').replace('mai-ad-', '')}`);
			ready.push(entry.target);
		});

		onReady(ready, 'zone');
	}, {
		root: null, 							// Use the viewport as the root.
		rootMargin: `${lazyLoadRootMarginPx}px 0px ${lazyLoadRootMarginPx}px 0px`, // Trigger when an ad is within lazyLoadRootMarginPx of the viewport.
		threshold: 0
	});

	let frame = 0;

	const lookAhead = () => {
		frame = 0;

		const speed = maiPubScrollSpeed();

		if (!speed || !pending.size) {
			return;
		}

		const height = window.innerHeight || document.documentElement.clientHeight;
		const ahead  = Math.min(Math.max(Math.abs(speed) * lazyLookaheadMs / 1000, lazyLoadRootMarginPx), lazyLookaheadMaxScreens * height);
		const ready  = [];

		pending.forEach((el) => {
			const gap = maiPubDistanceAhead(el, speed, height);

			if (null !== gap && gap <= ahead) {
				ready.push(el);
			}
		});

		if (!ready.length) {
			return;
		}

		maiPubLog(() => `Scroll look-ahead: ${ready.map((el) => (el.getAttribute('id') || '').replace('mai-ad-', '')).join(', ')} within ${Math.round(ahead)}px (scrolling ${Math.round(Math.abs(speed))}px/s ${speed > 0 ? 'down' : 'up'}).`);

		// Handled here once, whether or not onReady could take them: the zone still
		// watches them either way.
		ready.forEach((el) => pending.delete(el));
		onReady(ready, 'look-ahead');
	};

	window.addEventListener('scroll', () => {
		maiPubNoteScroll();

		if (!frame) {
			frame = requestAnimationFrame(lookAhead);
		}
	}, { passive: true });

	return {
		observe(el) {
			pending.add(el);
			zone.observe(el);
		},
		unobserve(el) {
			pending.delete(el);
			zone.unobserve(el);
		},
	};
}

// Debug only: per lazy-loaded slot, when it was asked for and when each step after it
// happened, for the "Lazy ad timing" line. Keyed by slot div id.
const maiPubLazyTiming = {};
let maiPubLazySeen     = null; // IntersectionObserver noting when a requested slot first shows on screen.

/**
 * Debug only: notes that a lazy-loaded slot was asked for, and starts watching for it to
 * show on screen.
 *
 * Out-of-page slots (spotlight) are left out: they take no space on the page, so "on
 * screen with no ad" would only measure the page's first auction.
 *
 * @since TBD
 *
 * @param {Element} el      The slot div.
 * @param {string}  trigger 'zone' or 'look-ahead'.
 *
 * @return {void}
 */
function maiPubNoteLazyRequest(el, trigger) {
	if (!debug && !log) {
		return;
	}

	const id = el.getAttribute('id');

	if (!id || maiPubIsOutOfPageSlug(id.replace('mai-ad-', ''))) {
		return;
	}

	const height = window.innerHeight || document.documentElement.clientHeight;
	const rect   = el.getBoundingClientRect();
	const below  = rect.top >= height;
	const above  = rect.bottom <= 0;

	maiPubLazyTiming[id] = {
		asked: performance.now(),
		trigger,
		speed: maiPubScrollSpeed(),
		gap: below ? rect.top - height : (above ? -rect.bottom : 0),
		side: below ? 'below' : (above ? 'above' : ''),
		bids: null,
		gam: null,
		rendered: null,
		empty: false,
		seen: null,
	};

	if (!maiPubLazySeen) {
		maiPubLazySeen = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}

				const slotId = entry.target.getAttribute('id');
				const t      = maiPubLazyTiming[slotId];

				maiPubLazySeen.unobserve(entry.target);

				if (t && null === t.seen) {
					t.seen = performance.now();
					maiPubLogLazyTiming(slotId);
				}
			});
		}, { threshold: 0 });
	}

	maiPubLazySeen.observe(el);
}

/**
 * Debug only: notes a step of a lazy-loaded slot's first request.
 *
 * @since TBD
 *
 * @param {Array}  slots GPT slots in the request.
 * @param {string} step  'bids' (bids back, adserver request committed) or 'gam' (GAM asked).
 *
 * @return {void}
 */
function maiPubNoteLazyStep(slots, step) {
	if (!debug && !log) {
		return;
	}

	const now = performance.now();

	slots.forEach((slot) => {
		const t = maiPubLazyTiming[slot.getSlotElementId()];

		if (t && null === t[step]) {
			t[step] = now;
		}
	});
}

/**
 * Debug only: notes that a lazy-loaded slot's first ad rendered (or came back empty).
 *
 * @since TBD
 *
 * @param {string}  slotId  The slot div id.
 * @param {boolean} isEmpty GAM returned no ad.
 *
 * @return {void}
 */
function maiPubNoteLazyRendered(slotId, isEmpty) {
	if (!debug && !log) {
		return;
	}

	const t = maiPubLazyTiming[slotId];

	if (!t || null !== t.rendered) {
		return;
	}

	t.rendered = performance.now();
	t.empty    = !!isEmpty;

	maiPubLogLazyTiming(slotId);
}

/**
 * Debug only: logs a lazy-loaded slot's timing once it has both rendered and shown on
 * screen: what asked for it and how fast the reader was scrolling, each step after, and
 * how long the slot sat empty on screen (or how early the ad was ready).
 *
 * @since TBD
 *
 * @param {string} slotId The slot div id.
 *
 * @return {void}
 */
function maiPubLogLazyTiming(slotId) {
	const t = maiPubLazyTiming[slotId];

	if (!t || null === t.rendered || null === t.seen) {
		return;
	}

	delete maiPubLazyTiming[slotId];

	const ms    = (x) => Math.round(x);
	const after = (x) => (null === x ? '?' : `+${ms(x - t.asked)}ms`);
	const how   = 'look-ahead' === t.trigger ? 'the scroll look-ahead' : `the ${lazyLoadRootMarginPx}px zone`;
	const speed = Math.abs(t.speed) < 1 ? 'not scrolling' : `scrolling ${ms(Math.abs(t.speed))}px/s ${t.speed > 0 ? 'down' : 'up'}`;
	const where = t.side ? `${ms(t.gap)}px ${t.side} the screen` : 'already on screen';
	let wait;

	if (t.empty) {
		wait = 'GAM returned no ad';
	} else if (t.seen <= t.rendered) {
		wait = `it was on screen with no ad for ${ms(t.rendered - t.seen)}ms`;
	} else {
		wait = `the ad was ready ${ms(t.seen - t.rendered)}ms before it came into view`;
	}

	maiPubLog(`Lazy ad timing (${slotId.replace('mai-ad-', '')}): asked for at ${ms(t.asked)}ms after navigation by ${how} (${speed}, ${where}). Bids back ${after(t.bids)}, GAM asked ${after(t.gam)}, ${t.empty ? 'came back empty' : 'rendered'} ${after(t.rendered)}, on screen ${after(t.seen)}: ${wait}.`);
}

// Debug only: per slot, GAM's side of its latest request, for the "GAM timing" line: when
// we called refresh(), when GPT sent the request, when GAM answered and when the ad
// rendered (performance.now()). Keyed by slot div id.
const maiPubGamTiming     = {};
const maiPubGamTimingWait = 5000; // Say what's missing if a slot hasn't rendered this long after refresh().
let maiPubGamTimingHooked = false;

/**
 * Debug only: notes that refresh() is about to run for these slots, for the "GAM timing"
 * line. A slot asked again before it rendered starts over.
 *
 * @since TBD
 *
 * @param {Array} slots The GPT slots.
 *
 * @return {void}
 */
function maiPubNoteGamRefresh(slots) {
	if (!debug && !log) {
		return;
	}

	if (!maiPubGamTimingHooked) {
		maiPubGamTimingHooked = true;
		googletag.pubads().addEventListener('slotRequested', (event) => maiPubNoteGamStep(event.slot.getSlotElementId(), 'requested'));
		googletag.pubads().addEventListener('slotResponseReceived', (event) => maiPubNoteGamStep(event.slot.getSlotElementId(), 'answered'));
		googletag.pubads().addEventListener('slotRenderEnded', (event) => maiPubNoteGamStep(event.slot.getSlotElementId(), 'rendered', event.isEmpty));
	}

	const now = performance.now();

	slots.forEach((slot) => {
		const slotId = slot.getSlotElementId();

		clearTimeout(maiPubGamTiming[slotId] && maiPubGamTiming[slotId].timer);

		const t = maiPubGamTiming[slotId] = { refreshed: now, requested: null, answered: null, rendered: null, empty: false, timer: null };

		t.timer = setTimeout(() => maiPubLogGamTiming(slotId, t), maiPubGamTimingWait);
	});
}

/**
 * Debug only: notes a GPT event for a slot we asked for, first time per request only.
 * Logs the "GAM timing" line once it has rendered.
 *
 * @since TBD
 *
 * @param {string}  slotId  The slot div id.
 * @param {string}  step    'requested', 'answered' or 'rendered'.
 * @param {boolean} isEmpty For 'rendered': GAM returned no ad.
 *
 * @return {void}
 */
function maiPubNoteGamStep(slotId, step, isEmpty) {
	const t = maiPubGamTiming[slotId];

	if (!t || null !== t[step]) {
		return;
	}

	t[step] = performance.now();

	if ('rendered' === step) {
		t.empty = !!isEmpty;
		clearTimeout(t.timer);
		delete maiPubGamTiming[slotId];
		maiPubLogGamTiming(slotId, t);
	}
}

/**
 * Debug only: logs the "GAM timing" line for a slot: when we called refresh(), then when
 * GPT sent the request, GAM answered and the ad rendered. Before it has rendered (the
 * maiPubGamTimingWait check), it says which of those hasn't happened.
 *
 * @since TBD
 *
 * @param {string} slotId The slot div id.
 * @param {object} t      Its maiPubGamTiming entry.
 *
 * @return {void}
 */
function maiPubLogGamTiming(slotId, t) {
	const ms    = (x) => Math.round(x);
	const after = (x) => `+${ms(x - t.refreshed)}ms`;
	const done  = null !== t.rendered;

	// Once rendered, a missing step didn't happen at all: GPT gives up on a request that
	// fails (blocked, offline) and renders the slot empty without an answer from GAM.
	const sent  = null === t.requested ? (done ? 'GPT sent no request' : 'GPT has not sent the request') : `GPT sent the request ${after(t.requested)}`;
	const reply = null === t.answered ? (done ? 'no answer from GAM' : 'GAM has not answered') : `GAM answered ${after(t.answered)}`;
	const head  = `GAM timing (${slotId.replace('mai-ad-', '')}): refresh() at ${ms(t.refreshed)}ms after navigation. ${sent}, ${reply}`;

	if (!done) {
		maiPubLog(`${head}, not rendered ${maiPubGamTimingWait}ms later.`);
		return;
	}

	maiPubLog(`${head}, ${t.empty ? 'came back empty' : 'rendered'} ${after(t.rendered)}.`);
}

function maiPubClassifyAdUnit(adUnit, observer) {
	const slotId = adUnit.getAttribute('id');

	// in demo mode, the ad unit may not have an ID
	if (!slotId) {
		maiPubLog('maiPubClassifyAdUnit: Skipping ad unit with no ID', adUnit);
		return null;
	}

	const slug = slotId.replace('mai-ad-', '');
	const cfg  = (typeof ads === 'object' && ads) ? ads[slug] : null;

	if (!cfg) {
		maiPubLog(`Skipping ${slotId}, no config found.`);
		return null; // Skip if no config
	}

	// Pre-populate the slotManager for each ad unit found on the page.
	if (!slotManager[slotId]) {
		slotManager[slotId] = {
			processing: false,
			visible: null,
			lastRefreshTime: 0,
			firstRender: true,
			retryCount: 0,
			slotObject: null
		};
	}

	// --- Fast Path vs. Lazy Load Logic ---
	const parentContainer = adUnit.closest('.mai-ad-unit-container');
	const ap = (cfg.targets && cfg.targets.ap) ? cfg.targets.ap : null;

	// Fixed adhesion (top/bottom) gets `.mai-ad-unit-sticky { position: fixed }`, so it
	// floats over the viewport and genuinely is always visible.
	const isFixedAdhesion = maiPubIsFixedAdhesion(ap) || !!(parentContainer && (
		parentContainer.classList.contains('mai-ad-unit-sticky-header') ||
		parentContainer.classList.contains('mai-ad-unit-sticky-footer')
	));

	// Vertical sticky is NOT the same thing. It gets its stickiness from
	// `.mai-ad-unit > * { position: sticky }`, which only pins inside its own
	// containing block — it never floats over the viewport. So its visibility depends
	// entirely on where the sidebar lands in the layout.
	const isVerticalSticky = ap === 'vs';
	const isATF = ap === 'atf';

	const rect = adUnit.getBoundingClientRect();
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
	const isAboveTheFold = rect.top < viewportHeight;

	let qualifiesForFastPath = false;

	if (isATF) {
		// ATF ad qualifies if its top edge is above the viewport bottom (above the fold)
		// Also qualify if element has zero dimensions (not yet laid out) - assume ATF
		qualifiesForFastPath = (rect.width === 0 && rect.height === 0) || isAboveTheFold;
	} else if (isFixedAdhesion) {
		// position:fixed — always visible, so unconditionally fast-path.
		qualifiesForFastPath = true;
	} else if (isVerticalSticky) {
		// Vertical sticky (sidebar medium rectangle) must EARN the fast path on
		// geometry. On desktop the sidebar sits beside the content and the unit is
		// above the fold, so it qualifies. On mobile the sidebar reflows to the bottom
		// of the page: the unit still computes position:sticky (the CSS rule carries no
		// media query) but its containing block is thousands of pixels down, so it was
		// being fetched immediately, filled, and never seen — an impression served to
		// nobody and a direct hit to measured viewability.
		//
		// Deliberately geometry, not device detection. geo.deviceType exists in the
		// session passport, but it is UA-derived (a narrow desktop window reflows the
		// sidebar and still reports 'desktop') and it resolves asynchronously, so it can
		// be undefined at exactly the moment this runs on a first pageview. Where the
		// element actually is answers the question directly, on every device and
		// breakpoint.
		//
		// Not-yet-laid-out (zero rect) deliberately does NOT qualify here: assuming
		// visible is what caused the bug. Falling through to lazy-load is safe — the
		// IntersectionObserver below requests it the moment it scrolls into view.
		qualifiesForFastPath = !(rect.width === 0 && rect.height === 0) && isAboveTheFold;

		if (!qualifiesForFastPath) {
			maiPubLog(`maiPubRun: Vertical sticky ${slug} is below the fold (top: ${Math.round(rect.top)}px, viewport: ${viewportHeight}px) — lazy-loading it instead of fast-pathing.`);
		}
	}

	// Check if this ad qualifies for fast path processing (Phase 5)
	if (qualifiesForFastPath) {
		maiPubLog(`maiPubRun: Fastpath candidate: ${slug} (fixed adhesion: ${isFixedAdhesion}, vertical sticky: ${isVerticalSticky}, atf: ${isATF})`);
		return slug;
	}

	// It's a Lazy Load ad (Phase 6)
	maiPubLog(`maiPubRun: Lazyload candidate: ${slug}. Observing.`);
	observer.observe(adUnit);
	return null;
}

/**
 * Define, prepare, and batch-request a set of fast-path slots.
 *
 * Extracted from maiPubRunDisplay so the late-DOM reconciliation can request
 * newly-parsed fast-path units (e.g. the sticky footer) through the identical path.
 *
 * @since 1.33.2
 *
 * @param {string[]} fastPathSlots Slugs to define and request immediately.
 * @param {string}   source        Request source label; include 'fast-path' to keep the fast timeout.
 * @return {void}
 */
function maiPubProcessFastPathSlots(fastPathSlots, source) {
	if (!fastPathSlots.length) {
		maiPubLog('maiPubRun: No Fastpath slots found, proceeding with lazy-load processing only');
		return;
	}

	maiPubLog(`maiPubRun: Fastpath slot processing: ${fastPathSlots.length} ${fastPathSlots.join(', ')}`);

	googletag.cmd.push(() => {
		// Define fast path slots
		fastPathSlots.forEach(slug => {
			const slotId = `mai-ad-${slug}`;
			const slotObject = maiPubDefineSlot(slug);
			if (slotObject) {
				if (!slotManager[slotId]) {
					slotManager[slotId] = {
						processing: false,
						visible: null,
						lastRefreshTime: 0,
						firstRender: true,
						retryCount: 0,
						slotObject: null
					};
				}
				slotManager[slotId].slotObject = slotObject;
				slotManager[slotId].processing = true;
				slotManager[slotId].visible = true;

				// Register the slot with GPT before it's requested, as the lazy path does: only
				// a displayed slot is meant to be refreshed. With disableInitialLoad (the core
				// GPT config, queued before maiPubRun can run) display() fetches nothing; the
				// request is the refresh() in maiPubRequestSlots. Until 1.37.7-beta6 these
				// slots were never displayed (the display after refresh() checked a firstRender
				// flag already cleared) and GPT served them anyway, but its docs don't promise to.
				googletag.cmd.push(() => {
					googletag.display(slotId);
				});
				maiPubLog(`maiPubRun: Fastpath: Prepared and displayed slot ${slotId} for ad request`);
			}
		});

		// Enable GPT services and request ads for fast path slots
		if (!_gptEnabled) {
			googletag.enableServices();
			_gptEnabled = true;
		}

		maiPubLog(`maiPubRun: Fastpath: Requesting ads for slots: ${fastPathSlots.join(', ')}`);
		maiPubBatchRequestSlots(fastPathSlots, source);
	});
}

/**
 * Late-DOM reconciliation for tail-of-body ad units.
 *
 * maiPubRun snapshots `.mai-ad-unit` gated on CMP+LCP, which can win the race against
 * <body> parsing. The sticky footer is the very last node in the document, so it (and
 * any other late-parsed unit) can be missed by that one-time snapshot. When the document
 * was still loading at snapshot time, this runs on DOMContentLoaded, re-scans, and
 * processes only the newcomers (diffed against slotManager) — ATF/top slots already fired
 * and are untouched, so there is zero above-the-fold penalty.
 *
 * @since 1.33.2
 *
 * @param {Object} adObserver The display lazy loader (maiPubLazyLoader) to hand late lazy-load units to.
 * @return {void}
 */
function maiPubReconcileLateAdUnits(adObserver) {
	const adUnits       = document.querySelectorAll('.mai-ad-unit');
	const fastPathSlots = [];
	let   newcomerCount = 0;

	adUnits.forEach(adUnit => {
		const slotId = adUnit.getAttribute('id');
		// Skip units without an id or already tracked/processed in the initial pass.
		if (!slotId || slotManager[slotId]) {
			return;
		}
		newcomerCount++;
		const slug = maiPubClassifyAdUnit(adUnit, adObserver);
		if (slug) {
			fastPathSlots.push(slug);
		}
	});

	// A late-parsed sticky footer also needs its prelude concerns (offset sync +
	// ResizeObserver). Guarded, so this is a no-op if the prelude already handled it.
	maiPubSetupStickyFooter();

	if (newcomerCount === 0) {
		maiPubLog('maiPubReconcileLateAdUnits: no late-parsed ad units found.');
		return;
	}

	maiPubLog(`maiPubReconcileLateAdUnits: processing ${newcomerCount} late-parsed ad unit(s).`);
	maiPubProcessFastPathSlots(fastPathSlots, 'fast-path-late-reconcile');
}

/**
 * Display ad pipeline: IntersectionObserver setup, ad-unit processing loop
 * (fast-path vs lazy-load assignment + slotManager population), fast-path
 * slot definition and request, and the GPT slotRenderEnded /
 * slotVisibilityChanged event listeners.
 *
 * Extracted from maiPubRun in batch 1 (RevIQ P0 plan, §3.2) so batch 2 can
 * replace the body with `if (displayMode === 'reviq') return;` and hand
 * display control to RevIQ without touching video, audio, or prelude work.
 *
 * Today this is byte-for-byte equivalent to the inline implementation that
 * lived in maiPubRun, with one structural change: the OOP spotlight dedup
 * has moved to maiPubRunPrelude. We re-query `.mai-ad-unit` here so we never
 * see the removed duplicate spotlight divs (which the original loop would
 * have skipped via inline `return`).
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubRunDisplay() {
	// Under RevIQ display mode, RevIQ owns the entire display pipeline on
	// the shared window.googletag (loads gpt.js, defines slots, refreshes).
	// We must stay out of the way — no defineSlot, no enableServices, no
	// IntersectionObserver, no slotRenderEnded listener. Video and audio
	// still run from maiPubRun's sibling calls and are unaffected.
	if ('reviq' === displayMode) {
		maiPubLog('maiPubRunDisplay: RevIQ owns display this bucket; skipping our display pipeline.');
		return;
	}

	// Re-query after prelude has removed any duplicate spotlight divs.
	const adUnits = document.querySelectorAll('.mai-ad-unit');

	// --- 2. Lazy-load trigger (For Ads) ---
	// Requests each non-fast-path ad (Phase 6) once it is within lazyLoadRootMarginPx of
	// the screen, or sooner when the reader scrolls fast (see maiPubLazyLoader). It must
	// be defined *before* the loop that uses it.
	const lazyLoader = maiPubLazyLoader((elements, trigger) => {
		const newlyVisibleSlots = [];
		const onScreenSlots     = [];
		const height            = window.innerHeight || document.documentElement.clientHeight;

		elements.forEach(el => {
			const slotId = el.getAttribute('id');
			const slug = slotId.replace('mai-ad-', '');

			// Add the slug to the list of newly visible slots to be processed.
			// Ensure we only handle each slot once
			if (slotManager[slotId] && !slotManager[slotId].processing) {
				slotManager[slotId].processing = true;
				slotManager[slotId].visible = true;
				newlyVisibleSlots.push(slug);
				lazyLoader.unobserve(el);
				maiPubNoteLazyRequest(el, trigger);

				// An ad already on screen (0px away) is as visible as an above-the-fold one,
				// so it gets the fast path's bidder timeout and batching wait. Until
				// 1.37.7-beta9 it got the regular 2800ms unless it happened to share a batch
				// with the fast path.
				if (0 === maiPubDistanceAhead(el, 1, height)) {
					onScreenSlots.push(slug);
				}
			}
		});

		// If there are no newly visible slots, we're done.
		if (newlyVisibleSlots.length === 0) {
			if ('zone' === trigger) {
				maiPubLog('Observer: No newly visible slots to process.');
			}
			return;
		}

		// Define, display, and request bids for the newly visible slots.
		// With singleRequest: true, defer each display() into its own cmd task so GPT layout
		// maps settle before the batched Prebid auction (maiPubBatchRequestSlots).
		googletag.cmd.push(() => {
			newlyVisibleSlots.forEach(slug => {
				const slotId = `mai-ad-${slug}`;
				const slotObject = maiPubDefineSlot(slug);
				if (slotObject) {
					if (slotManager[slotId]) {
						slotManager[slotId].slotObject = slotObject;
					}
					googletag.cmd.push(() => {
						googletag.display(slotId);
					});
					maiPubLog(`Observer: Layout container cleanly displayed: ${slotId}`);
				} else {
					maiPubLog(`Observer: Skipping display for slot ${slotId} - slot definition failed`);
				}
			});

			if (!_gptEnabled) { googletag.enableServices(); _gptEnabled = true; }
			maiPubLog(() => `Observer: Requesting ads for visible slots: ${newlyVisibleSlots.join(', ')}`);

			const source    = 'look-ahead' === trigger ? 'scroll-lookahead' : 'observer-visible';
			const offScreen = newlyVisibleSlots.filter(slug => !onScreenSlots.includes(slug));

			maiPubBatchRequestSlots(onScreenSlots, `${source}-onscreen`);
			maiPubBatchRequestSlots(offScreen, source);
		});
	});


	// --- 3. [INCOMPLETE - TODO] Facade Pattern Observer (For Social Embeds) ---
	// This is where the logic for the facade pattern would go.
	// It requires `class-output.php` to first create the static placeholders
	// with a class like `.mai-social-facade`.
	/*
	maiPubLog('Setting up IntersectionObserver for social embeds.');
	const socialObserver = new IntersectionObserver((entries, observer) => {
		entries.forEach(entry => {
			if (!entry.isIntersecting) {
				return;
			}

			const facade = entry.target;
			const embedType = facade.dataset.embedType; // e.g., 'twitter', 'instagram'

			maiPubLog(`Social embed facade for ${embedType} is now visible. Loading script.`);

			if (embedType === 'twitter') {
				// Example: You would create a function to load the Twitter widget script
				// and then call `twttr.widgets.load(facade);`
			} else if (embedType === 'instagram') {
				// Example: You would create a function to load the Instagram embed script
				// and then call `window.instgrm.Embeds.process();`
			}

			// Stop observing this facade
			observer.unobserve(facade);
		});
	}, {
		root: null,
		rootMargin: '400px 0px', // Can be a shorter rootMargin than ads
		threshold: 0
	});

	// Observe all facades on the page
	document.querySelectorAll('.mai-social-facade').forEach(facade => {
		socialObserver.observe(facade);
	});
	*/
	// --- End of Facade Pattern Placeholder ---


	// --- 4. Ad Unit Processing (SINGLE LOOP) ---
	// This single loop populates the slotManager and assigns all ad units
	// to either the "Fast Path" (Phase 5) or the "Lazy Load" observer (Phase 6).
	maiPubLog('=== AD PROCESSING: Processing High-Priority and Lazy-Load Ads ===');

	// Classify every ad unit into fast-path (requested immediately) or lazy-load
	// (observed). Shared with the late-DOM reconciliation via maiPubClassifyAdUnit so
	// both make identical decisions. OOP spotlight de-duplication already ran in
	// maiPubRunPrelude and adUnits was re-queried above, so duplicates are gone.
	const fastPathSlots = [];

	adUnits.forEach(adUnit => {
		const slug = maiPubClassifyAdUnit(adUnit, lazyLoader);
		if (slug) {
			fastPathSlots.push(slug);
		}
	});

	// --- 5. Process Fast Path Slots (Phase 5) ---
	// Define + batch-request fast-path slots immediately (no-op if none).
	maiPubProcessFastPathSlots(fastPathSlots, 'fast-path-immediate');

	// --- 5b. Late-DOM reconciliation for tail-of-body units (e.g. sticky footer) ---
	// maiPubRun's snapshot is gated on CMP+LCP, which can win the race against <body>
	// parsing. The sticky footer is the last node in the document, so it can be missed.
	// If the document is still parsing, re-scan on DOMContentLoaded and process only the
	// newcomers. ATF/top slots already fired above, so there is no above-the-fold penalty.
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => maiPubReconcileLateAdUnits(lazyLoader), { once: true });
	} else {
		maiPubLog('maiPubRunDisplay: document already fully parsed; no late-DOM reconciliation needed.');
	}

	// --- 6. Event Listeners Configuration ---
	// Set up event listeners for slotRenderEnded and slotVisibilityChanged.
	googletag.cmd.push(() => {

	// Slot render event
	googletag.pubads().addEventListener('slotRenderEnded', (event) => {
		const slot = event.slot;
		const slotId = slot.getSlotElementId();

		if (!maiPubIsMaiSlot(slot) || !slotManager[slotId]) {
			return;
		}

		slotManager[slotId].lastRefreshTime = Date.now();
		slotManager[slotId].processing = false; // Mark as no longer processing
		maiPubNoteLazyRendered( slotId, event.isEmpty );

		// --- FIX: Initialize retry count if missing ---
		if (typeof slotManager[slotId].retryCount === 'undefined') {
			slotManager[slotId].retryCount = 0;
		}

		if (event.isEmpty) {
			// The last ad's blank check (if one is running) is over.
			maiPubWatchBlankRender( slotId, slot, true );

			// If an ad is empty, assume it's still visible for one refresh attempt.
			slotManager[slotId].visible = true;

			maiPubLog(`maiPubRun event slotRenderEnded: Slot is empty ${slotId}`, () => ({
				slug: slotId.replace('mai-ad-', ''),
				adUnitPath: slot.getAdUnitPath(),
				sizes: event.size,
				targeting: slot.getTargetingMap(),
				event
			}));
			maiPubLogSlotHeaderBiddingTargeting( [slot], 'slotRenderEnded empty' );
			maiPubLogHbPresentButGAMEmpty( slot, event );

			// --- FIX: Retry Logic with Limit (Max 1 Retry) ---
			const slotInView = slotManager[slotId].visible === true;
			const canRetryEmpty = maiPubIsRefreshable( slot ) &&
				( isPageVisible || slotInView ) &&
				slotManager[slotId].retryCount < maxEmptyRetries;

			// Only retry if refreshable, slot/page visible, AND we haven't hit the limit
			if ( canRetryEmpty ) {

				slotManager[slotId].retryCount++; // Increment the counter

				maiPubLog(`Slot empty: ${slotId}. Retrying (Attempt ${slotManager[slotId].retryCount})...`);

				clearTimeout(timeoutManager[slotId]); // Clear any existing timer for this slot.
				timeoutManager[slotId] = setTimeout(() => {
					// Check visibility again right before refreshing.
					if ( slotManager[slotId].visible && ( isPageVisible || slotInView ) ) {
						maiPubBatchRequestSlots([slotId.replace('mai-ad-', '')], 'empty-slot-timeout');
					}
				}, emptySlotTimeout); // Retry empty slots after emptySlotTimeout (2s).
			} else {
				maiPubLog(`Slot ${slotId} empty. Giving up.`, {
					retryCount: slotManager[slotId].retryCount,
					maxEmptyRetries,
					refreshable: maiPubIsRefreshable(slot),
					isPageVisible,
					slotInView
				});
			}

		} else {

			// --- FIX: Reset Retry Count on Success ---
			// If the slot filled successfully, reset the counter so future refreshes start fresh
			slotManager[slotId].retryCount = 0;

			// Ad-ops triage: which demand source actually filled the slot (AdX backfill /
			// GAM line item / Open Bidding) alongside the hb_* bid that was present.
			// Pairs with maiPubLogHbPresentButGAMEmpty() so an empty-then-fill sequence
			// shows both the miss (HB bid, GAM empty) and the eventual winner.
			maiPubLogSlotFillSource( slot, event );

			// This block handles successfully filled ads.
			maiPubLog(`Slot filled: ${slotId}`, () => ({
				slug: slotId.replace('mai-ad-', ''),
				adUnitPath: slot.getAdUnitPath(),
				sizes: event.size,
				targeting: slot.getTargetingMap(),
				event
			}));

			// Find the ad unit's parent container.
			const adUnitElement = document.getElementById(slotId);
			if (adUnitElement) {
				const adContainer = adUnitElement.closest('.mai-ad-unit-container');
				if (adContainer) {
					// Add the 'is-filled' class to make the container and its contents (ad + label) visible.
					adContainer.classList.add('is-filled');
					maiPubLog(`Added 'is-filled' class to container for ${slotId}`);
				}
			}

			maiPubLog(`Display ad rendered: ${slotId}`);

			// Size diagnostics: served size vs reserved container box vs the actually
			// rendered creative — one [SIZE-DIAG] line per fill so oversized "gray box"
			// renders can be diagnosed from the console alone, no ?dfpdeb needed.
			maiPubLogAdSizeDiag( slotId, event );

			// Viewability-gated refresh: reset the seen-time accumulator for this
			// new impression and start the clock if it's already in view. The slot
			// refreshes only after impressionViewable fires AND it banks refreshTime
			// of seen-time at/above its viewability threshold. (See maiPub*SeenCountdown.)
			maiPubArmViewableRefresh( slotId, slot, event );

			// An ad that draws nothing (a 1x1 or 0x0 frame, nothing else in the slot) is
			// never counted as seen, so it never refreshes: its slot is asked again once.
			maiPubWatchBlankRender( slotId, slot );
		}
	}); // <-- end googletag.pubads().addEventListener('slotRenderEnded')

		// Slot visibility change event -- GPT's built-in viewability tracking
		googletag.pubads().addEventListener('slotVisibilityChanged', (event) => {
			const slot = event.slot;
			const slotId = slot.getSlotElementId();
			const inView = event.inViewPercentage > 5;
			const viewabilityPercent = Math.round(event.inViewPercentage);

			if (!maiPubIsMaiSlot(slot) || !slotManager[slotId]) {
				return;
			}

			const wasPreviouslyVisible = slotManager[slotId].visible;
			slotManager[slotId].visible = inView;
			slotManager[slotId].currentInView = event.inViewPercentage; // live % for the viewability-gated refresh

			// Use GPT's viewability data for targeting and ORTB2 updates
			try {
				// Check if this is a sticky ad placement using kv_ad_position
				const adElement = document.getElementById(slotId);
				const adPosition = adElement ? adElement.getAttribute('data-ap') : null;
				// Only FIXED adhesion (ts/bs) may claim guaranteed viewability. Vertical
				// sticky was included here too, so a sidebar unit sitting at the bottom
				// of a mobile page — never on screen — was reported to bidders as 100%
				// viewable and in view. That is a false signal on inventory that isn't
				// seen; its real measured value is trustworthy, so use it.
				const isStickyAd = maiPubIsFixedAdhesion(adPosition);

				// For fixed adhesion, override viewability to 100% for ORTB2 data
				const ortb2ViewabilityPercent = isStickyAd ? 100 : viewabilityPercent;
				const ortb2InView = isStickyAd ? true : inView;

				// --- BEFORE (v820 Legacy Implementation) ---
				// Set viewability targeting using GPT's calculation (unchanged)

				// FIX (v857): Removed stray slot.setTargeting calls that were causing deprecation warnings.
				// slot.setTargeting('ad_visible', inView ? 'true' : 'false');
				// slot.setTargeting('viewability_percent', viewabilityPercent.toString());

				// --- AFTER (Optimized v821 Unified setConfig Layout) ---
				// Consolidate individual keys into a single, modern configuration block
				const viewabilityTargeting = {
					ad_visible: inView ? 'true' : 'false',
					viewability_percent: viewabilityPercent.toString()
				};

				if (isStickyAd) {
					viewabilityTargeting.ad_placement_type = 'sticky';
				}

				if (inView && !wasPreviouslyVisible) {
					viewabilityTargeting.viewability_event = 'entered_viewport';
				} else if (!inView && wasPreviouslyVisible) {
					viewabilityTargeting.viewability_event = 'left_viewport';
				}

				// Pass everything to the modern API endpoint in a single call
				slot.setConfig({ targeting: viewabilityTargeting });

			} catch (error) {
				maiPubLog(`Error tracking viewability for display ad ${slotId}:`, error);
			}

			// Viewability-gated refresh countdown: accumulate seen-time only while
			// the slot is at/above its MRC threshold (50%, or 30% for large formats);
			// pause (and bank) the moment it drops below. The refresh itself fires
			// from the timer in maiPubTryViewableRefresh once seen-time + impressionViewable
			// are satisfied — not from this event.
			const threshold = slotManager[slotId].vizThreshold || refreshViewabilityThreshold;
			if ( event.inViewPercentage >= threshold ) {
				maiPubResumeSeenCountdown( slotId );
			} else {
				maiPubPauseSeenCountdown( slotId );
			}
		}); // <-- end googletag.pubads().addEventListener('slotVisibilityChanged')

		// Impression viewable event -- GPT fires this when the current impression
		// meets the Active View standard (MRC 50%/1s, or 30%/1s for large formats).
		// This is the exact signal Active View reports on, so we use it to gate
		// refresh: a slot may only refresh after its current impression counted as
		// viewable. maiPubTryViewableRefresh checks this flag before firing.
		googletag.pubads().addEventListener('impressionViewable', (event) => {
			const slot = event.slot;
			const slotId = slot.getSlotElementId();
			if ( !maiPubIsMaiSlot(slot) || !slotManager[slotId] ) {
				return;
			}
			slotManager[slotId].hasBeenViewable = true;
			maiPubLog( () => `Impression viewable (Active View): ${slotId} — refresh-eligible once ${refreshTime / 1000}s seen-time is banked at >=${slotManager[slotId].vizThreshold || refreshViewabilityThreshold}%.` );
		}); // <-- end googletag.pubads().addEventListener('impressionViewable')
	}); // <-- end googletag.cmd.push
}

/**
 * Video player initialization. Extracted from maiPubRun in batch 1 (RevIQ P0
 * plan, §3.2) so it runs independently of display — enabling batch 2 to keep
 * video on our GAM while RevIQ owns display.
 *
 * @since TBD
 *
 * @param {NodeList} adVideoUnits  `.mai-ad-unit-video` divs from maiPubRun().
 * @return {void}
 */
function maiPubRunVideo(adVideoUnits) {
	try {
		// Initialize video ads after display ads are set up
		maiPubInitVideoAds( adVideoUnits );
	} catch (error) {
		maiPubLog('Error initializing video players:', error);
	}
}

/**
 * Audio player initialization. Extracted from maiPubRun in batch 1 (RevIQ P0
 * plan, §3.2) so it runs independently of display.
 *
 * Audio requestAd() is currently stubbed — David wires the real GAM/IMA call
 * in a follow-up. V895
 *
 * @since TBD
 *
 * @param {NodeList} adAudioUnits  `.mai-ad-unit-audio` divs from maiPubRun().
 * @return {void}
 */
function maiPubRunAudio(adAudioUnits) {
	try {
		const prebidAdAudioUnits = [];

		adAudioUnits.forEach( (element, index) => {
			// Normalize hash strings to neat sequential layout tokens
			const suffix = index === 0 ? '' : `-${index + 1}`;
			element.id = `mai-ad-unit-audio${suffix}`;

			// Initialize player instance
			const player = new MaiAdUnitAudio( element );

			// we want to check if magnite is enabled.  we don't check for just magnite-audio anymore
			// TODO: if catalyst is enabled, we need to ask them to bid on audio
			if (maiPubAdsVars.magnite === true && player.adUnitConfig) {
				prebidAdAudioUnits.push(player.adUnitConfig);
			}
		});

		// Bulk register audio bidders to Prebid cleanly
		if (prebidAdAudioUnits.length > 0 && typeof pbjs !== 'undefined') {
			pbjs.que.push(() => {
				pbjs.addAdUnits(prebidAdAudioUnits);
				maiPubLog(`MaiAudioPlayer: Registered ${prebidAdAudioUnits.length} audio ad unit(s) with Prebid.`);
			});
		}
	} catch (error) {
		maiPubLog('Error initializing audio players:', error);
	}
}

/**
 * Define a slot using the recommended setConfig method for targeting.
 *
 * @since 1.12.0
 *
 * @param {string} slug The ad slug.
 * @updated 1.19.5
 *
 * @return {object|null} The slot object, or null if definition fails.
 */
function maiPubIsSpotlightOopUnit( slug ) {
	if ( ! ads[slug] || ads[slug].id !== 'spotlight' ) {
		return false;
	}
	const sizes = ads[slug].sizes;
	if ( Array.isArray( sizes ) ) {
		return sizes.includes( 'out-of-page' );
	}
	return sizes === 'out-of-page';
}

/**
 * Extract the bare ad-unit slug from a `mai-ad-*` DOM element id, stripping
 * the `mai-ad-` prefix and any trailing `-N` repeat suffix (N = digits only).
 *
 * Examples:
 *   'mai-ad-rectangle-medium'        -> 'rectangle-medium'
 *   'mai-ad-rectangle-medium-2'      -> 'rectangle-medium'
 *   'mai-ad-sportsdata-leaderboard'  -> 'sportsdata-leaderboard'  (no -N)
 *   'mai-ad-leaderboard-2'           -> 'leaderboard'
 *
 * Only a trailing `-\d+` is treated as a repeat counter. Hyphens elsewhere
 * are part of the slug. Caveat: no slug in config.php's ad_units currently
 * ends in `-\d+`; if one is ever added, this helper will misparse it.
 *
 * Used by B2.3 (RevIQ branch) to look up `ads[slug].reviq_placement` from
 * the rendered `mai-ad-*` div id, but added in B2.2 so the helper exists
 * and is reviewable separately from the branch that consumes it.
 *
 * @since TBD
 *
 * @param {string} elementId  The DOM element id (e.g. 'mai-ad-leaderboard-2').
 * @return {string|null} The bare ad-unit slug, or null if the id doesn't match.
 */
function maiPubExtractAdSlug( elementId ) {
	if ( ! elementId || typeof elementId !== 'string' ) {
		return null;
	}
	const match = elementId.match( /^mai-ad-(.+?)(?:-(\d+))?$/ );
	return match ? match[1] : null;
}

function maiPubDefineSlot( slug ) {
	// Defensive check: prevent video ads from being defined in display ad slots
	const slotElement = document.getElementById('mai-ad-' + slug);
	if (slotElement && slotElement.classList.contains('mai-ad-video')) {
		maiPubLog(`maiPubDefineSlot(): Skipping video ad slot ${slug} - should be handled by video player`);
		return null;
	}

	// If there is no configuration for this slug, stop immediately.
	if ( ! ads[slug] ) {
		maiPubLog(`maiPubDefineSlot(): Halting definition for slug '${slug}' because no configuration was found in the 'ads' object.`);
		return null;
	}

	if ( maiPubIsSpotlightOopUnit( slug ) && spotlightOopSlug && spotlightOopSlug !== slug ) {
		maiPubLog( `maiPubDefineSlot(): Skipping duplicate spotlight slot ${slug}; only one per page.` );
		return null;
	}

	// Check if slot has already been defined to prevent duplicate slot errors
	const slotDivId = 'mai-ad-' + slug;
	const existingSlot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === slotDivId);
	if (existingSlot) {
		maiPubLog(`maiPubDefineSlot(): Slot ${slotDivId} already defined, returning existing slot`);
		return existingSlot;
	}

	// Get base from context.
	const base = ads?.[slug]?.['context'] && 'client' === ads[slug]['context'] ? gamChildNetworkCode : gamBase;
	maiPubLog( `maiPubDefineSlot() base: ${base}` );

	// Get the slot ID.
	maiPubLog( `maiPubDefineSlot() slotDivId: ${slotDivId}` );

	// Define slot ID (GAM Path).
	const gamId = base + ads[slug]['id'];
	maiPubLog( `maiPubDefineSlot() GAM Ad Unit Path: ${gamId}` );


	let slot;

	// Initialize targeting object
	const targetingConfig = {};

	// out-of page handling
	if ( ads[slug].sizes && (Array.isArray(ads[slug].sizes) ? ads[slug].sizes.includes('out-of-page') : ads[slug].sizes === 'out-of-page') ) {
		// Define out-of-page slot.
		slot = googletag.defineOutOfPageSlot( gamId, slotDivId );
		maiPubLog( `maiPubDefineSlot() defineOutOfPageSlot: ${gamId}` );
	} else {
		// Define normal slot.
		slot = googletag.defineSlot( gamId, ads[slug].sizes, slotDivId );
		maiPubLog( `maiPubDefineSlot() defineSlot: ${gamId}, sizes: ${ads[slug].sizes}, slotDivId: ${slotDivId}` );
	}

	//  Check if slot was successfully created
	if (!slot) {
		maiPubLog(`maiPubDefineSlot(): Failed to define slot for ${slotDivId} - googletag.defineSlot returned null`);
		return null;
	}

	if ( maiPubIsSpotlightOopUnit( slug ) ) {
		spotlightOopSlug = slug;
	}

	// Add pubads service.
	slot.addService( googletag.pubads() );
	maiPubLog( `maiPubDefineSlot() addService` );

	// Set refresh targeting (convert boolean to string) for GPT compatibility
	targetingConfig[refreshKey] = refreshValue.toString();
	maiPubLog( `maiPubDefineSlot() targetingConfig: Added ${refreshKey}: ${targetingConfig[refreshKey]}` );

	// Add slot-specific targets from ads config.
	if ( ads[slug].targets ) {
		Object.keys( ads[slug].targets ).forEach( key => {
			// Ensure values are strings or arrays of strings for GPT
			let value = ads[slug].targets[key];
			if (typeof value !== 'string' && !Array.isArray(value)) {
				value = value.toString();
			} else if (Array.isArray(value)) {
				value = value.map(item => item.toString());
			}
			targetingConfig[key] = value;
			maiPubLog( `maiPubDefineSlot() targetingConfig: Added slot target ${key}: ${value}` );
		});
	}

	// Set split testing.
	if ( ads[slug].splitTest && 'rand' === ads[slug].splitTest ) {
		const randomValue = Math.floor(Math.random() * 100).toString(); // Ensure it's a string
		targetingConfig['st'] = randomValue;
		maiPubLog( `maiPubDefineSlot() targetingConfig: Added splitTest 'st': ${randomValue}` );
	}

	// Check if this is a sticky ad.
	const adElement = document.getElementById(slotDivId); // We check adElement again here
	const adPosition = adElement ? adElement.getAttribute('data-ap') : null;
	const isStickyAd = adPosition && (adPosition === 'ts' || adPosition === 'bs' || adPosition === 'vs');

	if (isStickyAd) {
		// Set sticky ad targeting.
		targetingConfig['ad_placement_type'] = 'sticky';
		maiPubLog(`maiPubDefineSlot() targetingConfig: Added sticky 'ad_placement_type': 'sticky'`);

		// Note: ORTB2 viewability is handled elsewhere.
		maiPubLog(`maiPubDefineSlot() Sticky ad detected ${slotDivId} (position: ${adPosition}) - ORTB2 imp.viewability will be set later`, {
			isSticky: true,
			position: adPosition,
			ortb2Status: "Will be set at 100% for Prebid bidders during requestBids"
		});
	}

	// Apply all targeting with setConfig
	if (Object.keys(targetingConfig).length > 0) {
		slot.setConfig({ targeting: targetingConfig });
		maiPubLog(`maiPubDefineSlot() Applied setConfig with targeting:`, targetingConfig);
	} else {
		maiPubLog(`maiPubDefineSlot() No targeting keys found, skipping setConfig.`);
	}

	/**
	 * Define size mapping.
	 * Skip size mapping for out-of-page ads.
	 */
	maiPubLog( `maiPubDefineSlot() checking sizes for ${slug}:`, {
		sizes: ads[slug].sizes,
		sizesDesktop: ads[slug].sizesDesktop,
		sizesTablet: ads[slug].sizesTablet,
		sizesMobile: ads[slug].sizesMobile
	});

	// Check if any size arrays contain 'out-of-page'.
	const hasOutOfPage = (ads[slug].sizes && (Array.isArray(ads[slug].sizes) ? ads[slug].sizes.includes('out-of-page') : ads[slug].sizes === 'out-of-page')); // Simplified this check

	if (hasOutOfPage) {
		maiPubLog( `maiPubDefineSlot() skipping size mapping for out-of-page ad: ${slug}` );
	} else {
		// Ensure size arrays exist before mapping
		const sizesDesktop = ads[slug].sizesDesktop || [];
		const sizesTablet = ads[slug].sizesTablet || [];
		const sizesMobile = ads[slug].sizesMobile || [];

		slot.defineSizeMapping(
			googletag.sizeMapping()
			.addSize( [ 1024, 768 ], sizesDesktop )
			.addSize( [ 728, 480 ], sizesTablet )
			.addSize( [ 0, 0 ], sizesMobile )
			.build()
		);
		maiPubLog( `maiPubDefineSlot() defineSizeMapping` );
	}

	// Lazy payload. This was an eager object literal, so slot.getTargetingMap()
	// ran on every slot definition on every pageview even with logging off — the
	// only one of the three getTargetingMap() call sites that did. The other two
	// (slotRenderEnded empty / Slot filled) were already deferred.
	maiPubLog( `Defined slot: ${slotDivId} via ${gamId}`, () => ( {
		gamId: gamId,
		slot: slot,
		// Log the final targeting map *after* setConfig
		targets: slot.getTargetingMap(),
	} ) );

	// Add slot to tracking arrays.
	adGamIds.push( gamId );
	adSlots.push( slot );

	return slot;
}

/**
 * Batched request system to prevent rapid successive ad requests.
 * Collects slot requests and processes them in batches with a small delay.
 *
 * @since 1.18.5
 *
 * @param {array} slugs The slugs of the slots to potentially request.
 * @param {string} source Optional source identifier for debugging.
 *
 * @return {void}
 */
function maiPubBatchRequestSlots( slugs, source = 'unknown' ) {
	// Skip if no slugs provided
	if ( ! slugs || ! slugs.length ) {
		return;
	}

	maiPubLog( () => `maiPubBatchRequestSlots(): Adding ${slugs.length} slots from ${source}: ${slugs.join(', ')}` );

	// FIX: Block all scheduled background refreshes immediately
	if (document.hidden && source.includes('refresh-timeout')) {
		maiPubLog(`maiPubBatchRequestSlots(): Blocked scheduled refresh from ${source} because the page is hidden.`);
		// Crucially, we skip adding slugs and setting the timeout.
		return;
	}

	// Add slugs to pending set (Set automatically handles duplicates)
	slugs.forEach( slug => {
		requestBatcher.pendingSlugs.add( slug );
		requestBatcher.pendingSources.set( slug, source );
	});

	// Scroll look-ahead requests don't wait: the look-ahead reaches a set time ahead of the
	// reader, so time spent here comes out of the time the ad has to render. Slots that come
	// within reach on the same frame still arrive in one call. Ads on screen are already
	// being looked at empty, so they wait only fastBatchDelay.
	let delay = requestBatcher.batchDelay;

	if ( source.includes( 'scroll-lookahead' ) ) {
		delay = 0;
	} else if ( maiPubIsFastPathSource( source ) ) {
		delay = requestBatcher.fastBatchDelay;
	}

	// The wait counts from the batch's first request: a later one can bring it forward but
	// never pushes it back. Until 1.37.7-beta9 every request restarted it, so on
	// allhiphop.com the first ads waited 197ms when an on-screen ad joined 95ms in.
	const due = performance.now() + delay;

	if ( requestBatcher.batchTimeout && due >= requestBatcher.batchDue ) {
		return;
	}

	clearTimeout( requestBatcher.batchTimeout );
	requestBatcher.batchDue     = due;
	requestBatcher.batchTimeout = setTimeout( () => {
		maiPubProcessBatchedRequests();
	}, delay );
}

/**
 * Whether a batch source is an ad the reader can see now or will at once: the fast path
 * (sticky and above-the-fold ads) or a lazy ad already on screen when it was asked for.
 * These get the fast path's bidder timeout and the short batching wait.
 *
 * @since TBD
 *
 * @param {string} source The source passed to maiPubBatchRequestSlots().
 *
 * @return {boolean}
 */
function maiPubIsFastPathSource( source ) {
	return source.includes( 'fast-path' ) || source.includes( 'sticky' ) || source.includes( 'atf' ) || source.includes( 'onscreen' );
}

/**
 * Process all pending batched requests.
 *
 * @since 1.18.5
 *
 * @return {void}
 */
function maiPubProcessBatchedRequests() {
	// Skip if no pending requests
	if ( requestBatcher.pendingSlugs.size === 0 ) {
		return;
	}

	// A batch went out under 50ms ago: try again once that hold is over. Returning without a
	// new timer would leave these slots waiting for the next request to come along, which a
	// look-ahead request (no delay) right after another batch would hit.
	if ( requestBatcher.processing ) {
		requestBatcher.batchDue     = performance.now() + 50;
		requestBatcher.batchTimeout = setTimeout( maiPubProcessBatchedRequests, 50 );
		return;
	}

	// Convert Set to Array and clear pending
	const slugsToProcess = Array.from( requestBatcher.pendingSlugs );
	const sourcesToProcess = new Map( requestBatcher.pendingSources );

	requestBatcher.pendingSlugs.clear();
	requestBatcher.pendingSources.clear();
	requestBatcher.batchTimeout = null;
	requestBatcher.processing = true;

	maiPubLog( () => `maiPubProcessBatchedRequests(): Processing batch of ${slugsToProcess.length} slots: ${slugsToProcess.join(', ')}` );

	// Determine timeout based on sources - if any fast-path source exists, use fast timeout
	const hasFastPathSource = Array.from(sourcesToProcess.values()).some(maiPubIsFastPathSource);
	const timeoutToUse = hasFastPathSource ? fastPathTimeout : bidderTimeout;

	maiPubLog( `maiPubProcessBatchedRequests(): Using ${timeoutToUse}ms timeout (${hasFastPathSource ? 'fast-path' : 'regular'})` );

	// Process the batch using the original logic with timeout
	const batchSource = slugsToProcess.map( ( slug ) => sourcesToProcess.get( slug ) || '' ).join( '|' );
	maiPubMaybeRequestSlots( slugsToProcess, timeoutToUse, batchSource );

	// Reset processing flag after a short delay to prevent immediate re-processing
	setTimeout( () => {
		requestBatcher.processing = false;
	}, 50 );
}

/**
 * Maybe request a slot.
 *
 * @since 1.13.0
 *
 * @param {array} slots The slots to request.
 * @param {array} slugs The slugs of the slots to potentially request.
 * @param {number} timeout Optional timeout override. If not provided, uses bidderTimeout.
 *
 * @return {void}
 */
function maiPubMaybeRequestSlots( slugs, timeout = bidderTimeout, batchSource = '' ) {
	// Queue the logic to ensure slots are defined and GAM is ready.
	googletag.cmd.push(() => { // This outer push is correct
		// Set timestamp.
		const now = Date.now();

		// Process slugs to get actual slots and filter.
		const slotsToRequest = slugs.map( slug => {
			// Find the corresponding slot object from the global array.
			// Ensure slotManager has the slot before attempting to find.
			const slotId = 'mai-ad-' + slug;
			const slotInfo = slotManager[slotId];

			// MODIFIED: More robust check for slotInfo and slotObject, and ensure it's refreshable here.
			if (!slotInfo || !slotInfo.slotObject || !maiPubIsRefreshable(slotInfo.slotObject)) { // Added maiPubIsRefreshable check
				maiPubLog( `maiPubMaybeRequestSlots(): warning: Slot info or object missing/not refreshable for slug ${slug}.`, slotInfo );
				return null; // Return null to be filtered out
			}

			const slot = slotInfo.slotObject; // Use the stored slot object

			// 1.19.3 removed this: page isn't fully loaded yet on initial page load
			// // --- Page Visibility Check ---
			// if ( ! isPageVisible ) { // This is where the error occurred
			// 	maiPubLog( `maiPubMaybeRequestSlots(): skipping request for ${slotId} - page not visible.` );
			// 	return null;
			// }
			// --- End Page Visibility Check ---

			// If first render, return true, force a request.
			if ( slotInfo.firstRender ) {
				maiPubLog( `maiPubMaybeRequestSlots(): first request for ${slotId}` );
				return slot;
			}

			// Bail if the slot is already being processed.
			if ( slotInfo.processing ) {
				maiPubLog( `maiPubMaybeRequestSlots(): skipping request for ${slotId} - already being processed` );
				return null;
			}

			// Bail if the slot is not visible.
			if ( ! slotInfo.visible ) {
				maiPubLog( `maiPubMaybeRequestSlots(): skipping request for ${slotId} - not visible` );
				clearTimeout( timeoutManager[ slotId ] );
				delete timeoutManager[ slotId ];
				return null;
			}

			return slot; // Return the slot object if all checks pass
		}).filter( slot => slot !== null ); // Filter out nulls from the map operation

		// Bail if no slots to request after filtering.
		if ( ! slotsToRequest.length ) {
			return;
		}

		// Request the filtered slot objects.
		maiPubRequestSlots( slotsToRequest, timeout, batchSource );
	});
}

/**
 * Collects all currently "pending" ad load promises from all video players.
 *
 * @since 1.21.0
 *
 * @returns {Array<Promise>} An array of promises.
 */
function getVideoLoadPromises() {
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

	return videoPlayers
		.filter( ( player ) => {
			if ( player.adLoadPromise === null ) {
				return false;
			}
			// Only block display while an on-screen outstream pod is loading (not off-screen video).
			const el = player.container || document.getElementById( player.config.id );
			if ( ! el ) {
				return true;
			}
			const rect = el.getBoundingClientRect();
			return rect.bottom > 0 && rect.top < viewportHeight;
		} )
		.map( ( player ) => player.adLoadPromise );
}

/**
 * Does this slot take no space on the page? A sticky header or footer is fixed over the
 * screen and an out-of-page ad (spotlight) sits outside the page, so rendering either
 * can't shift the layout the video gate protects.
 *
 * @since TBD
 *
 * @param {string} slotId The slot's element id.
 *
 * @return {boolean}
 */
function maiPubTakesNoPageSpace( slotId ) {
	if ( maiPubIsOutOfPageSlug( slotId.replace( 'mai-ad-', '' ) ) ) {
		return true;
	}

	const el = document.getElementById( slotId );

	// .mai-ad-unit-sticky is what makes it position: fixed (not set in previews).
	return !! ( el && el.closest( '.mai-ad-unit-sticky' ) );
}

/**
 * Waits for outstream video ads to start, fail, come back empty or be replaced, for at most ms.
 *
 * @since TBD
 *
 * @param {Array<Promise>} promises From getVideoLoadPromises().
 * @param {number}         ms       The most to wait.
 *
 * @return {Promise<boolean>} True if they all finished in time, false if the wait ran out.
 */
function maiPubWaitForVideo( promises, ms ) {
	return new Promise( ( resolve ) => {
		const timer = setTimeout( () => resolve( false ), ms );

		Promise.all( promises ).then( () => {
			clearTimeout( timer );
			resolve( true );
		} );
	} );
}




/**
 * Tells GAM how many times each slot was asked for before on this pageview, in key rc:
 * 0 for its first ad, 1 for the next request (a refresh, or a retry of an empty or blank
 * ad), and so on, with requestCountMax standing for that many or more.
 *
 * GAM's reports can then split a slot's first ad from its refreshes (AdX eCPM by refresh,
 * say), and the sticky footer's rc=0 requests count pageviews. The `refresh` key can't:
 * it says whether the slot may refresh, the same on every request.
 *
 * @since TBD
 *
 * @param {array} batch The slots about to go to GAM.
 * @return {void}
 */
function maiPubSetRequestCount( batch ) {
	batch.forEach( ( slot ) => {
		const s = slotManager[ slot.getSlotElementId() ];

		if ( ! s || 'function' !== typeof slot.setConfig ) {
			return;
		}

		const count = s.gamRequests || 0;

		s.gamRequests  = count + 1;
		s.requestCount = String( Math.min( count, requestCountMax ) ); // For the log.
		slot.setConfig( { targeting: { [ requestCountKey ]: s.requestCount } } );
	} );
}

/**
 * A slot's div id with the rc it went to GAM with, for the log: "mai-ad-leaderboard (rc 0)".
 *
 * @since TBD
 *
 * @param {googletag.Slot} slot
 * @return {string}
 */
function maiPubSlotRequestLabel( slot ) {
	const slotId = slot.getSlotElementId();
	const s      = slotManager[ slotId ];

	return s && s.requestCount ? `${slotId} (${requestCountKey} ${s.requestCount})` : slotId;
}

/**
 * Request slots.
 * * NOTE: This is a complete rewrite of the requestSlots function.
 * Refactor to implement logic where the sendAdserverRequest() is triggered when either all active bidders have responded OR
 * the bidderTimeout has elapsed. The bidderTimeout should act as the absolute last resort.
 *
 * The requestManager logic take from Magnite docs.
 *
 * @link https://help.magnite.com/help/web-integration-guide#parallel-header-bidding-integrations
 *
 * @since 1.13.0
 *
 * @param {array} slots The slots to request.
 * @param {number} timeout Optional timeout override. If not provided, uses bidderTimeout.
 *
 * @return {void}
 */
function maiPubRequestSlots( slots, timeout = bidderTimeout, batchSource = '' ) {

	// v919, check demo mode, which may be forced by MOS preview worker 
	//   window.__MAI_PUB_DEMO__ === true;
	// --- Demo Mode: Simulate request for slots (no network calls) ---
	if (maiPubAdsVars.mode === 'demo') {
		maiPubLog(`Demo Mode: Simulating request for ${slots.length} slots (no network calls).`);

		// Optional: Simulate "Empty Slot" events here to test retry logic
		// without waiting for a real server timeout.
		return;
	}
	// ----------------------

	// Store the start time for performance tracking
	const requestStartTime = Date.now();

	// Slots asked again after their ad drew nothing (maiPubWatchBlankRender): no auction.
	const noAuction = [];

	// Loop through the slots.
	slots.forEach( slot => {
		// Get the slot ID.
		const slotId = slot.getSlotElementId();

		// Mark the slot as being processed.
		slotManager[ slotId ].processing = true;

		if ( slotManager[ slotId ].skipAuction ) {
			slotManager[ slotId ].skipAuction = false;
			noAuction.push( slotId );
		} else {
			slotManager[ slotId ].blankRetries = 0; // A new auction: its ad may be asked again once if it draws nothing.
		}

		// Clear any scheduled timeouts before requesting.
		clearTimeout( timeoutManager[ slotId ] );
		delete timeoutManager[ slotId];

	});

	// Log.
	maiPubLog( () => `Requesting slots: ${slots.map( slot => slot.getSlotElementId() ).join( ', ' )}` );

	// Object to manage each request state.
    const requestManager = {
        adserverRequestSent: false,
        magniteReady: !maiPubAdsVars.magnite,  // TRUE when disabled OR completed
        catalystReady: !maiPubAdsVars.catalyst, // TRUE when disabled OR completed
        magniteAuction: null, // { bids, prebidSlots } from rp.requestBids callback

        // Helper methods for clarity. RevIQ is not a bidder — when it wins
        // the split, maiPubRunDisplay no-ops and we never reach this code at
        // all, so no reviqReady gate is needed here (removed in B2.3).
        isPrebidEnabled() { return maiPubAdsVars.magnite; },
        isCatalystEnabled() { return maiPubAdsVars.catalyst; },
        canProceedWithAdserverRequest() { return this.magniteReady && this.catalystReady; }
    };


	// Store the start time of this ad request batch.
	// This will be used to determine if the bidderTimeout has elapsed.
	const requestBatchStartTime = Date.now();

	// Handle for the failsafe timeout (see registration after sendAdserverRequest).
	let failsafeTimeoutId = null;

	// --- AFTER (v813 Decoupled Optimization) ---
	// 1. Define distinct rules for the Video Gate and the GAM Correlator Cache Buster
	const bypassVideoGate =
		batchSource.includes( 'refresh' ) ||
		batchSource.includes( 'empty-slot' ) ||
		batchSource.includes( 'blank-retry' ) ||
		batchSource.includes( 'observer-visible' ) ||
		batchSource.includes( 'scroll-lookahead' ) ||
		batchSource.includes( 'viewability-change' );

	const forceNewCorrelator =
		batchSource.includes( 'refresh' ) ||
		batchSource.includes( 'empty-slot' ) ||
		batchSource.includes( 'blank-retry' );

	// Slots not yet sent to GAM. While an on-screen outstream video ad loads, the video
	// gate sends the ones that take no space on the page and holds the rest.
	let unsentSlots = slots;

	// When the held slots stop waiting on video (Date.now()), and whether a wait is running.
	let videoWaitUntil = 0;
	let videoWaiting   = false;

	/**
	 * Sends these slots to GAM, with the bids for them.
	 *
	 * @param {array} batch The slots.
	 */
	const commitAdserverRequest = function( batch ) {
		maiPubNoteLazyStep( batch, 'bids' );
		maiPubLog( () => `sendAdserverRequest(): requestManager() set to true to refresh slots: ${batch.map( slot => slot.getSlotElementId() ).join( ', ' )}` );

		googletag.cmd.push(() => {

			// make sure services are on even if no display() path ran yet
			if (!_gptEnabled) { googletag.enableServices(); _gptEnabled = true; }

			// first-render housekeeping
			batch.forEach(s => {
				const sid = s.getSlotElementId();
				if (slotManager[sid]) slotManager[sid].firstRender = false;
			});

			maiPubSetRequestCount( batch );

			// Crucially, set targeting here for ALL active bidders *before* refresh.
			// This ensures bids are applied even if they arrived late but before the refresh.
			if ( maiPubAdsVars.magnite && typeof pbjs !== 'undefined' ) {
				maiPubSetPrebidTargetingForGpt( batch, requestManager.magniteAuction );
				maiPubApplyPaapiForGpt( batch );
			}

			// TODO:  do we need to set targeting for Catalyst or RevIQ?

			// 3. Apply the targeted Correlator configuration rule
			const refreshOpts = {
				changeCorrelator: forceNewCorrelator, // FALSE for initial scroll loads, TRUE for true retries/refreshes
			};

			const runRefresh = () => {
				try {
					maiPubNoteGamRefresh( batch ); // Debug only: the "GAM timing" line.
					googletag.pubads().refresh( batch, refreshOpts );
					maiPubNoteLazyStep( batch, 'gam' );
					maiPubLog( () => `sendAdserverRequest(): googletag.pubads().refresh() called for slots ${batch.length} ${1 === batch.length ? 'slot' : 'slots'} (changeCorrelator: ${refreshOpts.changeCorrelator}): ${batch.map( maiPubSlotRequestLabel ).join( ', ' )}` );
				} catch (error) {
					maiPubLog( 'Error calling googletag.pubads().refresh():', error );
				}
			};

			// Refresh right after Prebid attaches the hb_* keys. maiPubSetPrebidTargetingForGpt
			// queues that on pbjs.que, which Prebid runs in order: at once in Magnite's wrapper
			// (Prebid 11.18.5, built with its own GreedyPromise), in a microtask in a build with
			// native promises. A refresh queued behind it goes out right after it either way.
			// Until 1.37.7-beta9 this waited 50ms instead; on allhiphop.com the keys were on the
			// slots 2-4ms in and the request sat out the rest. Prebid that isn't loaded (blocked,
			// or the failsafe gave up on it) would never run the queue, so then refresh now.
			if ( maiPubAdsVars.magnite && typeof pbjs !== 'undefined' ) {
				const refreshAfterTargeting = () => {
					maiPubLogSlotHeaderBiddingTargeting( batch, 'before refresh' );
					runRefresh();
				};

				if ( pbjs.libLoaded && pbjs.que ) {
					pbjs.que.push( refreshAfterTargeting );
				} else {
					refreshAfterTargeting();
				}
			} else {
				runRefresh();
			}
		}); // <-- end googletag.cmd.push
	};

	/**
	 * Send request to ad-server.
	 * This function is now the single point that triggers the GPT refresh.
	 * It ensures the request is sent only once.
	 *
	 * @link https://help.magnite.com/help/web-integration-guide#parallel-header-bidding-integrations
	 */
	const sendAdserverRequest = function() {
		// Bail if the request has already been sent.
		if ( requestManager.adserverRequestSent ) {
			maiPubLog( 'Adserver request already sent, skipping. State:', {
				magniteReady: requestManager.magniteReady,
				catalystReady: requestManager.catalystReady,
				slots: slots.map(slot => slot.getSlotElementId())
			});
			return;
		}

		// The bidders are in, or the failsafe gave up on them, so the failsafe's job is done.
		// Cleared here rather than when the request commits, so it can't call in again (and
		// start a second wait) while the batch waits on video below.
		clearTimeout( failsafeTimeoutId );

		// // Background refreshes (timed, empty-slot retry, etc.) must not wait on outstream video.
		// const isRefreshBatch = 
		// 	batchSource.includes( 'refresh' ) || 
		// 	batchSource.includes( 'empty-slot' ) || 
		// 	batchSource.includes( 'observer-visible' ) || 
		// 	batchSource.includes( 'viewability-change' );		

		// // Video gate: first layout cycle only (CLS / Core Web Vitals). Skipped when isRefreshBatch.
		// if ( ! isRefreshBatch ) {
		// 	const pendingVideoPromises = getVideoLoadPromises();

		// 	if ( pendingVideoPromises.length > 0 ) {
		// 		maiPubLog( `sendAdserverRequest(): Waiting for ${pendingVideoPromises.length} video ad(s) to finish loading...` );

		// 		Promise.all( pendingVideoPromises ).then( () => {
		// 			maiPubLog( 'sendAdserverRequest(): All video ads finished. Proceeding with display ad refresh.' );
		// 			sendAdserverRequest();
		// 		} );
		// 		return;
		// 	}
		// } else if ( getVideoLoadPromises().length > 0 ) {
		// 	maiPubLog( `sendAdserverRequest(): Skipping video gate for refresh batch (source: ${batchSource})` );
		// }

		// 2. Execute the Video Gate check independently
		if ( ! bypassVideoGate ) {
			const pendingVideoPromises = getVideoLoadPromises();

			if ( pendingVideoPromises.length > 0 ) {
				// A sticky header/footer or an out-of-page ad can't shift the page, so it doesn't wait.
				const noSpace = unsentSlots.filter( ( slot ) => maiPubTakesNoPageSpace( slot.getSlotElementId() ) );

				if ( noSpace.length ) {
					unsentSlots = unsentSlots.filter( ( slot ) => -1 === noSpace.indexOf( slot ) );
					maiPubLog( () => `sendAdserverRequest(): Not waiting for the video ad with ${noSpace.map( slot => slot.getSlotElementId() ).join( ', ' )}: it takes no space on the page.` );
					commitAdserverRequest( noSpace );
				}

				// A wait already running calls back in when it ends.
				if ( unsentSlots.length && videoWaiting ) {
					return;
				}

				if ( unsentSlots.length ) {
					videoWaitUntil = videoWaitUntil || Date.now() + videoGateMaxWaitMs;

					const waitMs = videoWaitUntil - Date.now();

					// Re-checked when the wait ends: the next pod of a looping video may be loading by then.
					if ( waitMs > 0 ) {
						videoWaiting = true;
						maiPubLog( `sendAdserverRequest(): Waiting for ${pendingVideoPromises.length} video ad(s) to finish loading...` );

						maiPubWaitForVideo( pendingVideoPromises, waitMs ).then( ( finished ) => {
							videoWaiting = false;
							maiPubLog( finished ? 'sendAdserverRequest(): All video ads finished. Proceeding with display ad refresh.' : `sendAdserverRequest(): The video ad is still loading after ${videoGateMaxWaitMs}ms. Proceeding with display ad refresh.` );
							sendAdserverRequest();
						} );
						return;
					}
				}
			}
		} else if ( getVideoLoadPromises().length > 0 ) {
			maiPubLog( `sendAdserverRequest(): Skipping video gate for refresh batch (source: ${batchSource})` );
		}

		// Set the request manager to true so it doesn't send again.
		requestManager.adserverRequestSent = true;

		if ( unsentSlots.length ) {
			commitAdserverRequest( unsentSlots );
			unsentSlots = [];
		}
	} // <-- end sendAdserverRequest()

	// Fire the ad-server request the moment every ACTIVE bidder has reported in,
	// rather than always waiting out the failsafe timeout. Gated on
	// canProceedWithAdserverRequest() (magniteReady && catalystReady);
	// sendAdserverRequest() is idempotent and clears the failsafe on commit, so
	// calling this after each bidder resolves is safe and only ever fires once.
	const maybeSendAdserverRequest = function() {
		if ( requestManager.canProceedWithAdserverRequest() ) {
			sendAdserverRequest();
		}
	};

	// ===== FAILSAFE TIMEOUT =====
	// The adserver request is otherwise only triggered from inside bidder callbacks
	// If an enabled bidder's callback (bidding adapter) never returns — hung SDK,
	// swallowed exception, stalled network the SDK doesn't time out internally — its *Ready
	// flag stays false and nothing re-checks the elapsed timeout, so the batch (and the
	// slot's `processing` flag) would stall for the rest of the session.
	//
	// This guarantees the request fires regardless. sendAdserverRequest() is idempotent
	// (guarded by adserverRequestSent) and clears this timer when it commits, so this is a
	// no-op whenever bidders resolve normally.
	//
	// Sizing: Prebid's waits before the auction (ID5 up to userSync.auctionDelay and, in
	// North America, SourcePoint) sit IN FRONT OF the bidder timeout, so a legitimate Prebid
	// callback can arrive as late as (those waits + timeout). The failsafe must clear that
	// plus a small margin, or it would fire mid-flight and send the adserver request without
	// the bids it was about to receive. prebidPreAuctionAllowance covers the waits. v887
	const failsafeTimeout = timeout + prebidPreAuctionAllowance + 300;
	failsafeTimeoutId = setTimeout( () => {
		if ( ! requestManager.adserverRequestSent ) {
			maiPubLog( () => `maiPubRequestSlots(): FAILSAFE timeout (${failsafeTimeout}ms) reached before all bidders returned (prebid:${requestManager.magniteReady} catalyst:${requestManager.catalystReady}). Forcing adserver request for: ${slots.map( s => s.getSlotElementId() ).join( ', ' )}` );
			sendAdserverRequest();
		}
	}, failsafeTimeout );

	// Without consent, strip user audience segments (ortb2.user.data — IAB
	// Audience Taxonomy, personal data) from the shared ORTB2 before any bidder
	// can read it. Defense-in-depth: the Magnite and Catalyst paths below are
	// already gated on hasConsent === true, but this guarantees user.data never
	// leaves in any current or future no-consent path.
	if ( hasConsent !== true && maiPubAdsVars.ortb2 && maiPubAdsVars.ortb2.user ) {
		delete maiPubAdsVars.ortb2.user.data;
	}

	// ===== HandleMagniteDM bids =====
	if ( maiPubAdsVars.magnite === true && hasConsent === true) {

		maiPubLog('maiPubRequestSlots(): Magnite DM adapter enabled, requesting bids');

		// Filter slots to only include those enabled for Prebid +++
		const prebidSlots = slots.filter(slot => {
			// Asked again after its ad drew nothing (maiPubWatchBlankRender): GAM gets the slot's
			// last auction again, less the bid that rendered blank (Prebid doesn't target a
			// rendered bid twice). A new auction would bring that bidder's next bid.
			if ( -1 !== noAuction.indexOf( slot.getSlotElementId() ) ) {
				maiPubLog( `Prebid slot check: ${slot.getSlotElementId()} is asked again after its ad drew nothing: no new auction, GAM gets the rest of its last one.` );
				return false;
			}

			const slug = slot.getSlotElementId().replace('mai-ad-', '');
			// Check if the 'enabled_bidders' array exists and includes 'magnite'.
			// PHP (class-output.php) pushes 'magnite' into enabled_bidders — the
			// token was renamed from 'prebid' → 'magnite' in ff79629 (2026-06-11)
			// but this check was left as 'prebid', which silently disabled all
			// Magnite header bidding. The ?. operator prevents errors if
			// enabled_bidders is missing.
			const hasPrebidEnabled = !!ads[slug]?.enabled_bidders?.includes('magnite');
			maiPubLog(`Prebid slot check:`, () => ({
				slug: slug,
				enabled_bidders: ads[slug]?.enabled_bidders,
				hasPrebidEnabled: hasPrebidEnabled,
				adsConfig: ads[slug]
			}));
			return hasPrebidEnabled;
		});

		maiPubLog(`Prebid slot filtering results:`, () => ({
			totalSlots: slots.length,
			prebidEligibleSlots: prebidSlots.length,
			prebidSlotIds: prebidSlots.map(slot => slot.getSlotElementId())
		}));

		if (prebidSlots.length > 0) {
			// We are re-introducing pbjs.que.push() here. This is the correct way to
			// ensure the bid request only happens AFTER the main Prebid configuration is complete,
			// solving the final race condition.
			pbjs.que.push(function() {
				const prebidStartTime = Date.now();

				// The manual setTimeout is still necessary because the wrapper's requestBids
				// function doesn't respect the auctionDelay. This delay gives the now-configured
				// User ID modules time to fetch their IDs.
				pbjs.rp.requestBids({
					gptSlotObjects: prebidSlots,
					ortb2: maiPubAdsVars.ortb2,
					timeout: timeout,
					callback: function(bids, timedOut, auctionId) {
						requestManager.magniteAuction = {
							bids: bids,
							prebidSlots: prebidSlots,
							timedOut: timedOut,
							auctionId: auctionId,
						};

						googletag.cmd.push(() => {
							requestManager.magniteReady = true;
							maybeSendAdserverRequest();

							const prebidResponseTime = Date.now() - prebidStartTime;
							maiPubLog(`sendAdserverRequest(): Prebid response time: ${prebidResponseTime}ms`, { bids, timedOut, auctionId });
							if ( debug || log ) {
								maiPubLogMagniteAuctionDebug( prebidSlots );
							}
						});
					}
				});
			});
		} else {
			// If no slots are eligible for Prebid, mark its bids as "received" immediately.
			maiPubLog('No slots eligible for Magnite DM bidding. Marking magniteReady as true.');
			requestManager.magniteReady = true;
		}
	} // <-- end if (maiPubAdsVars.magnite === true && hasConsent === true)
	else {
		if (maiPubAdsVars.magnite === true && hasConsent === false) {
			maiPubLog('Magnite DM adapter enabled but hasConsent is false (GDPR timeout). Skipping Prebid request, using non-personalized GAM ads.');
		} else {
			maiPubLog('Magnite DM adapter disabled globally, skipping Prebid request.');
		}
		// Ensure Prebid is marked as "done" if disabled or no consent
		requestManager.magniteReady = true;
	}

	// ===== CATALYST BID REQUEST =====
	if (maiPubAdsVars.catalyst === true && hasConsent === true) {
		// Filter slots to find those with Catalyst enabled
		const catalystSlots = slots.filter(slot => {
			const slug = slot.getSlotElementId().replace('mai-ad-', '');
			return !!maiPubAdsVars.ads[slug]?.enabled_bidders?.includes('catalyst');
		});

		if (catalystSlots.length > 0) {
			// Two-tier consent check:
			// 1. Client checks GVL ID 1494 (Catalyst) - do we have right to send data?
			// 2. Server checks bidder GVL IDs (Rubicon, PubMatic, etc.) - which SSPs to call?

			maiPubLog('Catalyst: Checking GVL ID 1494 consent before sending bid request');

			checkGVLConsent(1494).then((catalystHasConsent) => {
				if (!catalystHasConsent) {
					// No consent for Catalyst itself - block request entirely
					maiPubLog('Catalyst: No consent for GVL ID 1494 - blocking bid request to comply with GDPR');
					requestManager.catalystReady = false;

					return;
				}

				// We have consent for Catalyst - proceed with bid request
				maiPubLog(`Catalyst: GVL ID 1494 consent verified - requesting bids for ${catalystSlots.length} slots`);

				// Check if Catalyst SDK is available
				if (typeof window.catalyst?.requestBids === 'function') {
					const catalystStartTime = Date.now();

					// Build Catalyst request config with full identity and context data
					const catalystConfig = {
						slots: catalystSlots.map(slot => {
							const slug = slot.getSlotElementId().replace('mai-ad-', '');
							const adConfig = maiPubAdsVars.ads[slug];
							return {
								divId: slot.getSlotElementId(),
								sizes: adConfig?.sizes || [[300, 250]]
							};
						}),
						timeout: maiPubAdsVars.catalystTimeout || timeout,
						user: {
							gdprApplies: maiPubSessionData?.gdprApplies || false,
							consent: maiPubSessionData?.consent?.tcString || '', // IAB TCF consent string
							id: maiPubSessionData?.ppid || maiPubAdsVars?.ppid, // Publisher Provided ID
							ext: {}
						}
					};

					// Add ID5 and other extended user IDs in ORTB2 eids format
					// Get actual resolved user IDs from Prebid.js (not config, but runtime values)
					if (typeof pbjs !== 'undefined' && typeof pbjs.getUserIdsAsEids === 'function') {
						const eids = pbjs.getUserIdsAsEids();
						if (eids && eids.length > 0) {
							catalystConfig.user.ext.eids = eids;
							maiPubLog(`Catalyst: Added ${eids.length} extended user IDs (eids) from Prebid`, eids);
						} else {
							maiPubLog('Catalyst: No extended user IDs available from Prebid');
						}
					} else {
						maiPubLog('Catalyst: Prebid.js getUserIdsAsEids() not available');
					}

					// Add ORTB2 device data (UA, geo, device type, etc.)
					if (maiPubAdsVars?.ortb2?.device) {
						catalystConfig.device = maiPubAdsVars.ortb2.device;
						maiPubLog('Catalyst: Added ORTB2 device data');
					}

					// Add ORTB2 site data (domain, page, categories, and now
					// site.content — our content signals live at ortb2.site.content
					// per OpenRTB, so this clone carries them to Catalyst too).
					// site.id / publisher.id in the shared ORTB2 are our Magnite
					// (Demand Manager) publisher id — valid only on the Magnite path.
					// Strip them from Catalyst's copy so the Magnite id never rides on
					// a non-Magnite request. (RevIQ reads ortb2.site.content directly.)
					// Shallow-clone so we don't mutate the shared object; site.content
					// carries no Magnite ids, so it needs no stripping.
					if (maiPubAdsVars?.ortb2?.site) {
						const { id: _siteId, publisher, ...siteRest } = maiPubAdsVars.ortb2.site;
						const catalystSite = { ...siteRest };
						if (publisher) {
							const { id: _pubId, ...pubRest } = publisher;
							catalystSite.publisher = pubRest;
						}
						catalystConfig.site = catalystSite;
						maiPubLog('Catalyst: Added ORTB2 site data (incl. site.content; Magnite site.id/publisher.id stripped)');
					}

					// Add ORTB2 app data (for mobile apps)
					if (maiPubAdsVars?.ortb2?.app) {
						catalystConfig.app = maiPubAdsVars.ortb2.app;
						maiPubLog('Catalyst: Added ORTB2 app data');
					}

					window.catalyst.requestBids(catalystConfig, (bids) => {
						googletag.cmd.push(() => {
							const dur = Date.now() - catalystStartTime;
							maiPubLog(`Catalyst response time: ${dur}ms`, bids);

							// Apply Catalyst targeting
							window.catalyst.setTargeting(catalystSlots.map(s => s.getSlotElementId()));

							requestManager.catalystReady = true;
							maybeSendAdserverRequest();
						});
					});
				} else {
					maiPubLog('Catalyst SDK not available (window.catalyst missing). Marking done.');
					requestManager.catalystReady = true;
				}
			}).catch((error) => {
				maiPubLog('Catalyst: Error checking GVL consent:', error);
				// On error, mark ready and skip Catalyst
				requestManager.catalystReady = true;
			});
		} else {
			// No Catalyst-eligible slots in this batch
			maiPubLog('Catalyst: No eligible slots found');
			requestManager.catalystReady = true;
		}
	} else {
		if (maiPubAdsVars.catalyst === true && hasConsent === false) {
			maiPubLog('Catalyst enabled but hasConsent is false (GDPR timeout). Skipping Catalyst request.');
		} else {
			maiPubLog('No Catalyst enabled, skipping Catalyst request.');
		}
		requestManager.catalystReady = true;
	}

	// ===== fallback if neither bidder is active (or all disabled) =====
	if ( ! maiPubAdsVars.magnite && ! maiPubAdsVars.reviq && ! maiPubAdsVars.catalyst ) {
		maiPubLog( `No bidder adapters available. Sending adserver request with GAM: ${ slots.map( slot => slot.getSlotElementId() ).join( ', ' ) }`, slots );
		sendAdserverRequest();
	}

	// If every active bidder resolved synchronously (e.g. no eligible Prebid slots,
	// or a disabled bidder), fire now instead of waiting for the failsafe. No-op
	// while a bidder is still pending — its callback fires maybeSendAdserverRequest().
	maybeSendAdserverRequest();

	// Always set a failsafe timeout — if no bidder fires, fall back.
	// Not while the batch waits on video: the bids are in, and the wait calls back in itself.
	setTimeout(() => {
		if (!requestManager.adserverRequestSent && !videoWaiting) {
			maiPubLog('Failsafe timeout reached. Sending adserver request without all bids.');
			sendAdserverRequest();
		}
	}, bidderTimeout);

} // <-- end maiPubRequestSlots


/**
 * Creates a contextual closure matcher function used by Prebid to map incoming ad unit codes to a specific native GPT slot.
 *
 * This closure function is registered via Prebid's `customGptSlotMatching` hook. It resolves inventory mapping 
 * collisions when multiple structural layout containers share a duplicate Google Ad Manager path by validating 
 * targeted string permutations against the slot's true identity.
 *
 * @since 1.12.0 Custom hook wrapper matching sequence established.
 * @updated v880 Normalizes multi-network MCM Manage Inventory comma strings down to clean child target variables.
 *
 * @param {googletag.Slot} gptSlot - The native Google Publisher Tag slot instance to contextually anchor.
 * @return {function(string): boolean} A curried evaluation function that accepts a Prebid ad unit code string 
 * and returns true if it contextually matches any valid permutation of this slot.
 */
function maiPubPrebidAdUnitMatcher( gptSlot ) {
    const elementId = gptSlot.getSlotElementId();
    const slug = elementId.replace( 'mai-ad-', '' );
    const gamPath = gptSlot.getAdUnitPath();
    
    // Fix: Parse out the clean child path to support MCM Manage Inventory mappings
    const cleanChildPath = gamPath.includes(',') ? '/' + gamPath.split(',')[1] : gamPath;

    return function( adUnitCode ) {
        if ( ! adUnitCode ) {
            return false;
        }
        return adUnitCode === elementId ||
            adUnitCode === slug ||
            adUnitCode === gamPath ||
            adUnitCode === cleanChildPath ||
            adUnitCode === gamPath + '#' + elementId ||
            adUnitCode === gamPath + '#' + slug ||
            adUnitCode === cleanChildPath + '#' + elementId ||
            adUnitCode === cleanChildPath + '#' + slug;
    };
}

/**
 * Debug Prebid/Magnite auction state when ?maideb is on.
 *
 * @param {googletag.Slot[]} requestSlots
 */
function maiPubLogMagniteAuctionDebug( requestSlots ) {
	if ( typeof pbjs === 'undefined' ) {
		return;
	}

	const slotIds = requestSlots.map( ( s ) => s.getSlotElementId() );
	maiPubLog( 'Magnite auction for GPT slots:', slotIds );

	if ( pbjs.rp && ! maiPubLogMagniteAuctionDebug._rpLogged ) {
		maiPubLogMagniteAuctionDebug._rpLogged = true;
		const rpFns = Object.keys( pbjs.rp ).filter( ( key ) => typeof pbjs.rp[key] === 'function' );
		maiPubLog( 'pbjs.rp functions:', rpFns );
	}

	if ( pbjs.adUnits && pbjs.adUnits.length ) {
		maiPubLog(
			'Prebid adUnits:',
			pbjs.adUnits.map( ( au ) => ( {
				code: au.code,
				bids: maiPubGetBidsForAdUnitCode( au.code ).length,
			} ) )
		);
	}

	// No "winning bids" here: this runs before GAM has picked a winner, so it always
	// said none. A Prebid bid that wins gets its own "Prebid won" line once it renders
	// (maiPubWatchPrebidOutcomes).

	requestSlots.forEach( ( gptSlot ) => {
		const elementId = gptSlot.getSlotElementId();
		const codes = maiPubPrebidAdUnitCodesForGptSlot( gptSlot );
		const byCode = {};
		codes.forEach( ( code ) => {
			const targeting = typeof pbjs.getAdserverTargetingForAdUnitCode === 'function'
				? pbjs.getAdserverTargetingForAdUnitCode( code )
				: null;
			if ( targeting && Object.keys( targeting ).length ) {
				byCode[code] = targeting;
			}
		} );
		if ( Object.keys( byCode ).length ) {
			maiPubLog( `Prebid ad-server targeting candidates for ${elementId}:`, byCode );
		}
	} );
}

/**
 * Magnite rp.requestBids callback first arg is indexed GPT slots (not bid objects).
 *
 * @param {*} callbackPayload
 * @return {Array}
 */
function maiPubNormalizeMagniteCallbackEntries( callbackPayload ) {
	if ( Array.isArray( callbackPayload ) ) {
		return callbackPayload;
	}
	if ( callbackPayload && typeof callbackPayload === 'object' ) {
		return Object.keys( callbackPayload )
			.sort( ( a, b ) => Number( a ) - Number( b ) )
			.map( ( key ) => callbackPayload[key] );
	}
	return [];
}

/**
 * Compiles an array of candidate Prebid ad unit codes to try for a given GPT slot.
 *
 * Generates multiple string permutations—including raw element IDs, flat slot slugs, 
 * full GAM paths, and hash tokens—to ensure accurate header bidding allocation when 
 * multiple divs share identical ad server configurations.
 *
 * @since 1.12.0 Original display slot configuration pool matrix introduced.
 * @updated v880 Added an MCM Manage Inventory child path parser to solve double-network comma path blindspots.
 *
 * @param {googletag.Slot} gptSlot - The native Google Publisher Tag slot instance to evaluate.
 * @return {string[]} Deduplicated array of candidate string patterns eligible for bidder validation.
 */
function maiPubPrebidAdUnitCodesForGptSlot( gptSlot ) {
    const elementId = gptSlot.getSlotElementId();
    const slug = elementId.replace( 'mai-ad-', '' );
    const gamPath = gptSlot.getAdUnitPath();
    
    // Fix: Parse out the clean child path to support MCM Manage Inventory mappings
    const cleanChildPath = gamPath.includes(',') ? '/' + gamPath.split(',')[1] : gamPath;

    return [...new Set( [
        elementId,
        slug,
        gamPath,
        cleanChildPath,
        gamPath + '#' + elementId,
        gamPath + '#' + slug,
        cleanChildPath + '#' + elementId,
        cleanChildPath + '#' + slug,
    ] )];
}

/**
 * @param {string} code
 * @return {object[]}
 */
function maiPubGetBidsForAdUnitCode( code ) {
	if ( typeof pbjs === 'undefined' || ! code ) {
		return [];
	}

	if ( typeof pbjs.getHighestCpmBids === 'function' ) {
		const top = pbjs.getHighestCpmBids( code );
		if ( top && top.length ) {
			return top;
		}
	}

	if ( typeof pbjs.getBidResponses !== 'function' ) {
		return [];
	}

	const response = pbjs.getBidResponses( code );
	if ( ! response ) {
		return [];
	}
	if ( Array.isArray( response ) ) {
		return response;
	}
	return response.bids || [];
}

/**
 * Pick winning bid for a GPT slot from Prebid internals.
 *
 * @param {googletag.Slot} gptSlot
 * @return {object|null}
 */
function maiPubGetWinningBidForGptSlot( gptSlot ) {
	if ( typeof pbjs === 'undefined' || ! gptSlot ) {
		return null;
	}

	// A bid whose creative has rendered isn't targeted again: GAM would serve it a second time,
	// which its bidder has already counted, and one that rendered blank would come back blank
	// (maiPubWatchBlankRender). Prebid's winning bids are rendered ones, so they're no fallback:
	// with no bid left, the slot goes to GAM without hb_* keys.
	for ( const code of maiPubPrebidAdUnitCodesForGptSlot( gptSlot ) ) {
		const bids = maiPubGetBidsForAdUnitCode( code ).filter( ( bid ) => bid && 'rendered' !== bid.status );
		if ( bids.length ) {
			return bids.reduce( ( best, bid ) => ( ! best || bid.cpm > best.cpm ) ? bid : best, null );
		}
	}

	return null;
}

/**
 * Apply Prebid ad-server targeting map onto a GPT slot.
 *
 * @param {googletag.Slot} gptSlot
 * @param {Object} targeting
 * @return {boolean}
 */
function maiPubApplyAdserverTargetingToGptSlot( gptSlot, targeting ) {
	if ( ! gptSlot || ! targeting || typeof targeting !== 'object' ) {
		return false;
	}

	const slotTargeting = {};
	Object.keys( targeting ).forEach( ( key ) => {
		if ( key.indexOf( 'hb_' ) !== 0 ) {
			return;
		}
		const val = targeting[key];
		slotTargeting[key] = Array.isArray( val ) ? String( val[0] ) : String( val );
	} );

	if ( ! Object.keys( slotTargeting ).length ) {
		return false;
	}

	if ( typeof gptSlot.setConfig === 'function' ) {
		const existingCfg = gptSlot.getConfig( 'targeting' ) || {};
		const existing = existingCfg.targeting || existingCfg;
		gptSlot.setConfig( { targeting: { ...existing, ...slotTargeting } } );
	} else if ( typeof gptSlot.setTargeting === 'function' ) {
		Object.keys( slotTargeting ).forEach( ( key ) => {
			gptSlot.setTargeting( key, slotTargeting[key] );
		} );
	}

	return true;
}

/**
 * Apply hb_* targeting from a Prebid bid object onto a GPT slot.
 *
 * @param {googletag.Slot} gptSlot
 * @param {object} bid
 * @return {boolean}
 */
function maiPubApplyBidToGptSlot( gptSlot, bid ) {
	if ( ! gptSlot || ! bid ) {
		return false;
	}

	const hbTargeting = {};

	if ( typeof pbjs.getAdserverTargeting === 'function' ) {
		const adUnitCode = bid.adUnitCode || bid.code;
		if ( adUnitCode ) {
			Object.assign( hbTargeting, pbjs.getAdserverTargeting( bid ) || {} );
		}
	}

	if ( bid.cpm != null && hbTargeting.hb_pb == null ) {
		hbTargeting.hb_pb = Number( bid.cpm ).toFixed( 2 );
	}
	if ( ( bid.bidderCode || bid.bidder ) && ! hbTargeting.hb_bidder ) {
		hbTargeting.hb_bidder = bid.bidderCode || bid.bidder;
	}
	if ( bid.adId && ! hbTargeting.hb_adid ) {
		hbTargeting.hb_adid = bid.adId;
	}
	if ( bid.ad && ! hbTargeting.hb_adid ) {
		hbTargeting.hb_adid = bid.ad;
	}
	if ( bid.size && ! hbTargeting.hb_size ) {
		hbTargeting.hb_size = `${bid.size[0]}x${bid.size[1]}`;
	}

	const keys = Object.keys( hbTargeting ).filter( ( key ) => key.indexOf( 'hb_' ) === 0 );
	if ( ! keys.length ) {
		return false;
	}

	const slotTargeting = {};
	keys.forEach( ( key ) => {
		const val = hbTargeting[key];
		slotTargeting[key] = Array.isArray( val ) ? String( val[0] ) : String( val );
	} );

	// --- BEFORE (v820 Legacy Implementation) ---
	// if ( typeof gptSlot.setConfig === 'function' ) {
	// 	const existingCfg = gptSlot.getConfig( 'targeting' ) || {};
	// 	const existing = existingCfg.targeting || existingCfg;
	// 	gptSlot.setConfig( { targeting: { ...existing, ...slotTargeting } } );
	// } else if ( typeof gptSlot.setTargeting === 'function' ) {
	// 	keys.forEach( ( key ) => {
	// 		gptSlot.setTargeting( key, slotTargeting[key] );
	// 	} );
	// }

	// --- AFTER (Optimized v821 Modern Endpoint Gating) ---
	// Modern browsers fully support setConfig; we can safely remove the loop fallback branch
	if (typeof gptSlot.setConfig === 'function') {
		const existingCfg = gptSlot.getConfig('targeting') || {};
		const existing = existingCfg.targeting || existingCfg;
		gptSlot.setConfig({ targeting: { ...existing, ...slotTargeting } });
	} else {
		// Clean, streamlined fallback array loop logic
		const targetingMap = {};
		keys.forEach((key) => {
			targetingMap[key] = slotTargeting[key];
		});
		gptSlot.setConfig({ targeting: targetingMap });
	}

	return true;
}

/**
 * Processes and maps Prebid targeting parameters onto a batch of active GPT slots.
 *
 * Prioritizes custom div-scoped targeting keys before falling back to global ad unit paths.
 * If no explicit ad-server targeting maps match the evaluated code permutations, the engine
 * falls back to analyzing the highest-value raw bid response object available in the auction cache.
 *
 * @since 1.13.0 Original ad server refresh targeting module established.
 * @updated v881 Added MCM Manage Inventory clean child path mapping to codesOrdered array.
 * @updated v881 Optimized by caching loop condition variables to eliminate repetitive function validation ticks.
 *
 * @param {googletag.Slot[]} slots - An array of native Google Publisher Tag slots to target.
 * @return {void}
 */
function maiPubApplyPrebidAdserverTargetingToSlots( slots ) {
    if ( typeof pbjs === 'undefined' ) {
        return;
    }

    // Optimization: Cache the function check outside the loops. If Prebid hasn't exposed 
    // this targeting retrieval endpoint, exit early to protect thread bandwidth.
    const getTargetingFn = pbjs.getAdserverTargetingForAdUnitCode;
    const hasTargetingFn = typeof getTargetingFn === 'function';

    slots.forEach( ( gptSlot ) => {
        const elementId = gptSlot.getSlotElementId();
        const codes = maiPubPrebidAdUnitCodesForGptSlot( gptSlot );
        let applied = false;

        const gamPath = gptSlot.getAdUnitPath();
        const cleanChildPath = gamPath.includes(',') ? '/' + gamPath.split(',')[1] : gamPath;

        // Optimization: Streamlined array organization. Pulls high-priority targeted 
        // permutations first, then appends both the parent comma path and clean child path.
        // Wrapping in a Set handles any accidental deduplication cleanly.
        const codesOrdered = [...new Set([
            ...codes.filter( ( c ) => c.indexOf( '#' ) !== -1 || c === elementId || c === elementId.replace( 'mai-ad-', '' ) ),
            gamPath,
            cleanChildPath
        ])];

        // Optimization: Only execute the loop if Prebid's targeting function is active
        if ( hasTargetingFn ) {
            for ( const code of codesOrdered ) {
                const targeting = getTargetingFn( code );
                
                if ( targeting && Object.keys( targeting ).some( ( k ) => k.indexOf( 'hb_' ) === 0 ) ) {
                    if ( maiPubApplyAdserverTargetingToGptSlot( gptSlot, targeting ) ) {
                        applied = true;
                        maiPubLog( `Applied Prebid ad-server targeting for ${elementId} via code "${code}"`, targeting );
                        break;
                    }
                }
            }
        }

        // Fallback Layer: If no ad-server targeting maps matched, fall back to the highest raw bid object
        if ( ! applied ) {
            const winning = maiPubGetWinningBidForGptSlot( gptSlot );
            if ( winning && maiPubApplyBidToGptSlot( gptSlot, winning ) ) {
                maiPubLog( `Applied Prebid bid object for ${elementId}`, () => ({
                    cpm: winning.cpm,
                    bidder: winning.bidderCode || winning.bidder,
                    adUnitCode: winning.adUnitCode,
                }) );
            }
        }
    } );
}

/**
 * Apply Magnite/Prebid HB targeting to GPT slots after rp.requestBids.
 *
 * @param {googletag.Slot[]} slots
 * @param {{bids:*, prebidSlots:googletag.Slot[]}|null} magniteAuction
 */
function maiPubSetPrebidTargetingForGpt( slots, magniteAuction ) {
	if ( typeof pbjs === 'undefined' ) {
		return;
	}

	const slotIds = slots.map( ( s ) => s.getSlotElementId() );

	const runPrebidTargeting = () => {
		if ( typeof pbjs.setTargetingForGPTAsync === 'function' ) {
			try {
				pbjs.setTargetingForGPTAsync( slotIds );
				maiPubLog( () => `sendAdserverRequest(): Clean asynchronous targeting map compiled for: ${slotIds.join( ', ' )}` );
			} catch ( e ) {
				maiPubLog( 'sendAdserverRequest(): pbjs.setTargetingForGPTAsync failed', e );
			}
		} else if ( typeof pbjs.setTargetingForGPT === 'function' ) {
			try {
				pbjs.setTargetingForGPT( slotIds );
				maiPubLog( () => `sendAdserverRequest(): pbjs.setTargetingForGPT() for: ${slotIds.join( ', ' )}` );
			} catch ( e ) {
				maiPubLog( 'sendAdserverRequest(): pbjs.setTargetingForGPT failed', e );
			}
		} else {
			maiPubLog( 'sendAdserverRequest(): Prebid setTargetingForGPT(Async) unavailable; using manual HB targeting only' );
		}

		maiPubApplyPrebidAdserverTargetingToSlots( slots );
	};

	if ( typeof pbjs.que !== 'undefined' ) {
		pbjs.que.push( runPrebidTargeting );
	} else {
		runPrebidTargeting();
	}
}

/**
 * Extract hb_* targeting from a GPT slot (for empty-slot / HB debugging).
 *
 * @param {googletag.Slot} slot
 * @return {Object<string, string|string[]>}
 */
function maiPubGetSlotHbTargeting( slot ) {
	const hb = {};

	if ( ! slot ) {
		return hb;
	}

	if ( typeof slot.getTargetingMap === 'function' ) {
		const map = slot.getTargetingMap() || {};
		Object.keys( map ).forEach( ( key ) => {
			if ( key.indexOf( 'hb_' ) === 0 ) {
				hb[key] = map[key];
			}
		} );
		return hb;
	}

	if ( typeof slot.getConfig === 'function' ) {
		const cfg = slot.getConfig( 'targeting' ) || {};
		const targeting = cfg.targeting || cfg;
		Object.keys( targeting ).forEach( ( key ) => {
			if ( key.indexOf( 'hb_' ) === 0 ) {
				hb[key] = targeting[key];
			}
		} );
	}

	return hb;
}

/**
 * First targeting value from GPT (string or array).
 *
 * @param {string|string[]|undefined} value
 * @return {string|null}
 */
function maiPubTargetingFirstValue( value ) {
	if ( value == null || value === '' ) {
		return null;
	}
	if ( Array.isArray( value ) ) {
		if ( value.length === 0 ) {
			return null;
		}
		const first = value[0];
		if ( first == null || first === '' ) {
			return null;
		}
		return String( first );
	}
	const str = String( value );
	if ( str === 'undefined' || str === 'null' ) {
		return null;
	}
	return str;
}

/**
 * True when a value looks like a Prebid price bucket (hb_pb).
 *
 * @param {string|null|undefined} value
 * @return {boolean}
 */
function maiPubIsValidHbPriceBucket( value ) {
	if ( value == null || value === '' ) {
		return false;
	}
	return /^\d+(\.\d+)?$/.test( String( value ) );
}

/**
 * Summarize hb_* keys on a slot for ad-ops triage.
 *
 * @param {googletag.Slot} slot
 * @return {{hasHbPb: boolean, hb_pb: string|null, hb_bidder: string|null, hb_size: string|null}}
 */
function maiPubGetSlotHbBidSummary( slot ) {
	const hb = maiPubGetSlotHbTargeting( slot );
	const hbPbRaw = maiPubTargetingFirstValue( hb.hb_pb );
	const hbPb = maiPubIsValidHbPriceBucket( hbPbRaw ) ? hbPbRaw : null;

	return {
		hasHbPb: !! hbPb,
		hb_pb: hbPb,
		hb_bidder: maiPubTargetingFirstValue( hb.hb_bidder ),
		hb_size: maiPubTargetingFirstValue( hb.hb_size ),
	};
}

/**
 * When Prebid set hb_pb but GAM returned no creative — line-item / floor triage hint.
 *
 * @param {googletag.Slot} slot
 * @param {googletag.events.SlotRenderEndedEvent} event
 */
function maiPubLogHbPresentButGAMEmpty( slot, event ) {
	if ( ! debug && ! log ) {
		return;
	}

	const summary = maiPubGetSlotHbBidSummary( slot );
	if ( ! summary.hasHbPb ) {
		return;
	}

	const slotId = slot.getSlotElementId();
	const gamPath = slot.getAdUnitPath();
	const renderSize = event.size && event.size.length >= 2
		? `${event.size[0]}x${event.size[1]}`
		: 'unknown';

	maiPubLog(
		`HB on slot but GAM empty ${slotId}: hb_pb=${summary.hb_pb} hb_bidder=${summary.hb_bidder || 'n/a'} hb_size=${summary.hb_size || 'n/a'} — check GAM HB line items for ${gamPath} (render size ${renderSize})`,
		{
			hb_pb: summary.hb_pb,
			hb_bidder: summary.hb_bidder,
			hb_size: summary.hb_size,
			adUnitPath: gamPath,
			renderSize,
		}
	);
}

/**
 * Ad size diagnostics (debug/log only).
 *
 * On every fill, records the served creative size vs the reserved box vs what the ad
 * actually draws, so a "small ad floating in a big gray box" can be diagnosed straight
 * from the console — no ?dfpdeb / Publisher Console needed.
 *
 * Why measure the DOM instead of trusting event.size: header-bidding fills render
 * through a 1x1 GAM line item (the Prebid universal creative), so event.size is often
 * 1x1 while the real creative is e.g. 300x600. The visible whitespace is the box minus
 * what the ad draws, which only the laid-out DOM knows. Emits one greppable line per
 * fill, prefixed [SIZE-DIAG]; a gap over the threshold is flagged with "<-- MISMATCH".
 *
 * What counts:
 * - The ad is its frame plus anything it draws into the box outside the frame: Teads
 *   draws its player next to a 1x1 frame that stays 1x1, which read as a blank box. A
 *   creative still 1x1 a second after the render is measured again a second after its
 *   slot comes on screen, since such players are built there; and when nothing is drawn
 *   by then, 3 seconds later before it's flagged (Teads drew its player in allhiphop.com's
 *   sticky footer 2.4 seconds after the render).
 * - An ad the slot cuts off is named at its full size with what's shown, and flagged
 *   "<-- CUT OFF". The slot is overflow: hidden (v890), and only what the slot shows was
 *   measured: Teads' 320x201 player read "320x101 ... h=1px", an ad filling its box.
 *   Teads doesn't start its video when less than half the player is visible.
 * - The label (data-label, drawn by CSS above the ad) isn't whitespace.
 * - A fixed box (the sticky footer) is measured on screen: the fit moves the bar below the
 *   screen's edge with a transform, which leaves the box's size as it was, so a fitted
 *   bar read as having its empty strip.
 * - A width gap is flagged only when the ad is narrower than its slot: the box can be
 *   wider by design (the sticky bar spans the screen; an in-content box is the column).
 * - The reserved size is read from the slot (.mai-ad-unit), where the CSS resolves it
 *   from the box's per-breakpoint sizes. Read from the box, it was always "n/a".
 *
 * @since 1.34.5
 *
 * @param {string} slotId The ad unit div id (e.g. mai-ad-billboard).
 * @param {googletag.events.SlotRenderEndedEvent} event The render event for this fill.
 * @return {void}
 */
function maiPubLogAdSizeDiag( slotId, event ) {
	if ( ! debug && ! log ) {
		return;
	}

	// An out-of-page ad (spotlight) has no box on the page to measure: it read as a 0x0
	// box around a 0x1 creative, "whitespace h=-1px <-- MISMATCH", on every pageview.
	if ( maiPubIsOutOfPageSlug( slotId.replace( 'mai-ad-', '' ) ) ) {
		return;
	}

	// A 1x1 line item's creative (Prebid's universal creative) resizes itself once it has
	// rendered the bid, which can take longer than the first check: measured then, every
	// header-bidding fill read as a 1x1 creative with the whole box blank.
	const resizeWaitMs = 1000;

	// A player drawn beside the 1x1 frame can come late: on allhiphop.com Teads drew its own
	// in the sticky footer 2.4 seconds after the render, after the on-screen check.
	const lateWaitMs = 3000;

	// A later render of the slot drops a check still waiting on this one.
	const render = ( maiPubSizeDiagRenders[ slotId ] || 0 ) + 1;
	maiPubSizeDiagRenders[ slotId ] = render;

	const inert = ( el ) => /^(SCRIPT|STYLE|LINK|NOSCRIPT|TEMPLATE)$/.test( el.tagName );
	const clip  = ( r, to ) => ( { left: Math.max( r.left, to.left ), top: Math.max( r.top, to.top ), right: Math.min( r.right, to.right ), bottom: Math.min( r.bottom, to.bottom ) } );

	const measure = ( stage ) => {
		if ( render !== maiPubSizeDiagRenders[ slotId ] ) {
			return;
		}

		try {
			const inner = document.getElementById( slotId ); // .mai-ad-unit (the GPT target)
			if ( ! inner ) {
				maiPubLog( `[SIZE-DIAG] ${slotId}: element not found` );
				return;
			}
			const container = inner.closest( '.mai-ad-unit-container' ); // the visible box / gray band
			const box       = container || inner;

			// Served size per GAM (unreliable for HB: the 1x1 universal creative).
			const served = Array.isArray( event.size ) ? event.size.join( 'x' ) : String( event.size );

			// Reserved box (max width x max height of the ad unit's size list), resolved on the slot.
			const cs       = getComputedStyle( inner );
			const reserved = ( name ) => ( cs.getPropertyValue( name ) || '' ).trim().replace( /px$/, '' ) || 'n/a';

			// The label, drawn above the ad, isn't whitespace.
			let label = 0;
			if ( inner.hasAttribute( 'data-label' ) ) {
				const before = getComputedStyle( inner, '::before' );
				label = Math.round( ( parseFloat( before.height ) || 0 ) + ( parseFloat( before.marginBottom ) || 0 ) );
			}

			// The creative's frame: GPT's, else the first one in the slot.
			const iframe   = inner.querySelector( 'iframe[id^="google_ads_iframe"]' ) || inner.querySelector( 'iframe' );
			const ifW      = iframe ? iframe.offsetWidth  : null;
			const ifH      = iframe ? iframe.offsetHeight : null;
			const oneByOne = !! iframe && ifW <= 1 && ifH <= 1;

			if ( 'render' === stage && '1x1' === served && oneByOne ) {
				setTimeout( () => measure( 'resized' ), resizeWaitMs );
				return;
			}

			// Still 1x1: the creative draws outside its frame, or hasn't drawn yet. Teads
			// builds its player once the slot is near the screen, so look again then.
			if ( 'resized' === stage && oneByOne && 'function' === typeof IntersectionObserver ) {
				maiPubLog( `[SIZE-DIAG] ${slotId}: creative still 1x1 ${resizeWaitMs}ms after the render; measuring again ${resizeWaitMs}ms after the slot is on screen.` );

				const seen = new IntersectionObserver( ( entries ) => {
					if ( entries.some( ( e ) => e.isIntersecting ) ) {
						seen.disconnect();
						setTimeout( () => measure( 'on-screen' ), resizeWaitMs );
					}
				}, { threshold: 0.5 } );

				seen.observe( inner );
				return;
			}

			// The box the viewer sees. A fixed box (the sticky footer) only on screen: the fit
			// moves it partly below the screen's edge.
			const fixed  = 'fixed' === getComputedStyle( box ).position;
			const screen = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
			const boxR   = fixed ? clip( box.getBoundingClientRect(), screen ) : box.getBoundingClientRect();
			const boxW   = Math.max( 0, Math.round( boxR.right - boxR.left ) );
			const boxH   = Math.max( 0, Math.round( boxR.bottom - boxR.top ) );

			// What the ad draws within the box: its frame, and anything it draws outside the
			// frame (Teads' player beside a 1x1 frame, a native grid). The frame's wrappers
			// (the slot, GPT's container) and scripts don't count.
			let ad      = null;
			let outside = null; // the largest element drawn outside the frame, to name it
			for ( const el of box.querySelectorAll( '*' ) ) {
				if ( inert( el ) || ( iframe && el !== iframe && el.contains( iframe ) ) ) {
					continue;
				}

				const r = clip( el.getBoundingClientRect(), boxR );
				const w = Math.round( r.right - r.left );
				const h = Math.round( r.bottom - r.top );
				if ( w <= 1 || h <= 1 ) {
					continue;
				}

				ad = ad ? { left: Math.min( ad.left, r.left ), top: Math.min( ad.top, r.top ), right: Math.max( ad.right, r.right ), bottom: Math.max( ad.bottom, r.bottom ) } : r;

				if ( el !== iframe && ( ! outside || w * h > outside.w * outside.h ) ) {
					outside = { el, w, h };
				}
			}

			// Nothing drawn yet: the player may still come (see lateWaitMs).
			if ( 'on-screen' === stage && oneByOne && ! outside ) {
				setTimeout( () => measure( 'late' ), lateWaitMs );
				return;
			}

			const adW = ad ? Math.round( ad.right - ad.left ) : 0;
			const adH = ad ? Math.round( ad.bottom - ad.top ) : 0;

			// An element's full size, and what the page shows of it when that's less: clipped
			// by each ancestor that hides its overflow up to the box (the slot does), and for
			// the sticky footer by the screen. A fixed element is placed against the screen,
			// out of the slot's clip.
			const sizeOf = ( el ) => {
				const full = el.getBoundingClientRect();
				let r      = full;
				for ( let a = el; a !== box && a.parentElement; a = a.parentElement ) {
					if ( 'fixed' === getComputedStyle( a ).position ) {
						break;
					}
					const p = getComputedStyle( a.parentElement );
					if ( 'visible' !== p.overflowX || 'visible' !== p.overflowY ) {
						r = clip( r, a.parentElement.getBoundingClientRect() );
					}
				}
				r = fixed ? clip( r, screen ) : r;

				const w     = Math.round( full.width );
				const h     = Math.round( full.height );
				const shown = { w: Math.max( 0, Math.round( r.right - r.left ) ), h: Math.max( 0, Math.round( r.bottom - r.top ) ) };
				const cut   = w - shown.w > 1 || h - shown.h > 1;

				return { text: `${w}x${h}${ cut ? `, ${shown.w}x${shown.h} of it shown` : '' }`, cut };
			};

			const frameSize = iframe ? sizeOf( iframe ) : null;
			const drawnSize = outside ? sizeOf( outside.el ) : null;
			const cutOff    = ( frameSize && frameSize.cut ) || ( drawnSize && drawnSize.cut );

			let creative = `creative(iframe) ${ iframe ? ( frameSize.cut ? frameSize.text : `${ifW}x${ifH}` ) : 'none' }`;
			if ( outside ) {
				creative += `, drawn outside its frame: ${ maiPubDescribeElement( outside.el ) } ${drawnSize.text}`;
			} else if ( oneByOne ) {
				creative += ', nothing drawn outside it';
			}

			// Teads' box narrower than the slot: Teads sizes its ad from that box, and on
			// eurweb.com it came out 78px wide with the slot's stretch rule live. Say where the
			// box is and what limits it, to tell whose CSS does it.
			const teadsBox = inner.querySelector( '.teads-inread' );
			if ( teadsBox && teadsBox.offsetWidth < inner.clientWidth - 1 ) {
				const s     = getComputedStyle( teadsBox );
				const where = teadsBox.parentElement === inner ? 'the slot' : maiPubDescribeElement( teadsBox.parentElement );
				creative += `, Teads' box ${teadsBox.offsetWidth}px wide in ${where} (${inner.clientWidth}px): max-width ${s.maxWidth}${ teadsBox.style.maxWidth ? ' (set by Teads)' : '' }, width ${ teadsBox.style.width ? `${teadsBox.style.width} (set by Teads)` : 'not set' }, align-self ${s.alignSelf}, margins ${s.marginLeft} ${s.marginRight}`;
			}

			const onScreen = fixed && ( boxW !== box.offsetWidth || boxH !== box.offsetHeight ) ? ` (on screen ${boxW}x${boxH})` : '';

			// Visible whitespace = the box the viewer sees minus the label and the ad.
			const ap  = inner.getAttribute( 'data-ap' ) || '';
			const gw  = boxW - adW;
			const gh  = boxH - label - adH;
			const bad = ! ad || gh > 20 || adW < inner.offsetWidth - 20;

			maiPubLog(
				`[SIZE-DIAG] ${slotId} | served(gam) ${served} | reserved(css) ${ reserved( '--mai-ad-unit-width' ) }x${ reserved( '--mai-ad-unit-height' ) }${ label ? ` + ${label}px label` : '' }` +
				` | inner ${inner.offsetWidth}x${inner.offsetHeight}` +
				` | container ${ container ? `${container.offsetWidth}x${container.offsetHeight}` : 'n/a' }${onScreen}` +
				` | ${creative} | vp ${window.innerWidth} | ap=${ap}` +
				` | whitespace w=${gw}px h=${gh}px${ bad ? '  <-- MISMATCH' : '' }${ cutOff ? '  <-- CUT OFF' : '' }` +
				( 'resized' === stage ? ` (measured ${resizeWaitMs}ms later, for the 1x1 creative to resize)` : '' ) +
				( 'on-screen' === stage ? ` (measured ${resizeWaitMs}ms after the slot came on screen)` : '' ) +
				( 'late' === stage ? ` (measured ${ resizeWaitMs + lateWaitMs }ms after the slot came on screen)` : '' )
			);
		} catch ( e ) {
			maiPubLog( `[SIZE-DIAG] ${slotId}: diag error — ${ e && e.message }` );
		}
	};

	// Defer a tick so GPT's creative iframe is inserted and the box has laid out. The sticky
	// footer's fit has run by then: it checks as soon as the creative is in the slot.
	setTimeout( () => measure( 'render' ), 60 );
}

/**
 * Debug only: logs the layout shift an ad slot causes when its ad grows it past its
 * reserve, e.g. Teads' player opening an in-content slot (124px to about 612px for a
 * vertical video) or an AdX 345x115 in a 320x100 reserve, or shrinks it back. One line
 * per slot each time (a player animating open is many shifts), with the page's CLS so far:
 *
 *   "Layout shift 0.121: mai-ad-leaderboard-2 grew from 124px to 612px (Teads' player).
 *   Page CLS so far: 0.124."
 *
 * A slot that grows off screen moves nothing on screen and logs nothing. Shifts are put
 * down to a slot that changed height in the same frame (a ResizeObserver on each slot).
 * CLS is counted as Chrome does: the largest sum of shifts less than 1s apart within 5s,
 * leaving out shifts right after a tap or key press.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubLogAdLayoutShifts() {
	if ( ( ! debug && ! log ) || 'function' !== typeof ResizeObserver || 'function' !== typeof PerformanceObserver || ! ( PerformanceObserver.supportedEntryTypes || [] ).includes( 'layout-shift' ) ) {
		return;
	}

	// Each slot's height, where its latest growth started (a player opening over many
	// frames; the shifts stop once what's below it has moved off screen), and the changes.
	const heights = new WeakMap();
	const starts  = new WeakMap();
	let changes   = [];

	const sizes = new ResizeObserver( ( entries ) => {
		const at = performance.now();

		for ( const entry of entries ) {
			const el    = entry.target;
			const to    = el.offsetHeight;
			const from  = heights.get( el );
			const start = starts.get( el );

			heights.set( el, to );
			if ( undefined !== from && from !== to ) {
				starts.set( el, { from: start && at - start.at < 600 ? start.from : from, at } );
				changes.push( { el, at } );
			}
		}

		changes = changes.filter( ( c ) => at - c.at < 1000 );
	} );

	// The shifts of one slot growing, logged once it has stopped.
	const growing = new Map();
	let cls          = 0;
	let session      = 0;
	let sessionStart = 0;
	let last         = -Infinity;

	new PerformanceObserver( ( list ) => {
		for ( const shift of list.getEntries() ) {
			if ( shift.hadRecentInput ) {
				continue;
			}

			if ( shift.startTime - last > 1000 || shift.startTime - sessionStart > 5000 ) {
				session      = 0;
				sessionStart = shift.startTime;
			}
			session += shift.value;
			last     = shift.startTime;
			cls      = Math.max( cls, session );

			const change = changes.filter( ( c ) => Math.abs( c.at - shift.startTime ) < 100 ).pop();
			if ( ! change ) {
				continue;
			}

			const g = growing.get( change.el ) || { value: 0 };
			g.value += shift.value;
			clearTimeout( g.timer );
			g.timer = setTimeout( () => {
				growing.delete( change.el );
				const from  = starts.get( change.el ).from;
				const to    = change.el.offsetHeight;
				const teads = change.el.querySelector( '.teads-inread, .teads-player' ) ? " (Teads' player)" : '';
				maiPubLog( `Layout shift ${ g.value.toFixed( 3 ) }: ${change.el.id} ${ to > from ? 'grew' : 'shrank' } from ${from}px to ${to}px${teads}. Page CLS so far: ${ cls.toFixed( 3 ) }.` );
			}, 600 );
			growing.set( change.el, g );
		}
	} ).observe( { type: 'layout-shift', buffered: true } );

	// Every slot on the page, and each one rendered later (parsed late, or on a refresh).
	document.querySelectorAll( '.mai-ad-unit' ).forEach( ( el ) => sizes.observe( el ) );
	googletag.cmd.push( () => {
		googletag.pubads().addEventListener( 'slotRenderEnded', ( event ) => {
			const el = document.getElementById( event.slot.getSlotElementId() );
			if ( el ) {
				sizes.observe( el );
			}
		} );
	} );
}

/**
 * Log which demand source actually filled a slot — AdX/AdSense backfill (dynamic
 * allocation), an Open Bidding yield group, or a GAM line item (direct / house /
 * header-bidding) — alongside the hb_* bid that was on the slot. Lets ad-ops
 * reconcile "a Prebid bid was present" against what really rendered: e.g. the HB
 * bid lost to a house line, or AdX backfilled after an empty first pass. Complements
 * maiPubLogHbPresentButGAMEmpty() (the empty-render triage) on the fill side.
 *
 * Demand-source classification uses the GPT SlotRenderEnded event:
 *  - event.isBackfill === true        -> AdX / AdSense dynamic allocation (Open Auction)
 *  - event.yieldGroupIds is non-empty -> Open Bidding / Exchange Bidding yield group
 *  - event.lineItemId is present      -> a GAM line item (direct / house / HB)
 *
 * @since TBD
 *
 * @param {googletag.Slot} slot
 * @param {googletag.events.SlotRenderEndedEvent} event
 */
function maiPubLogSlotFillSource( slot, event ) {
	if ( ! debug && ! log ) {
		return;
	}

	const slotId     = slot.getSlotElementId();
	const renderSize = event.size && event.size.length >= 2
		? `${event.size[0]}x${event.size[1]}`
		: ( typeof event.size === 'string' ? event.size : 'unknown' );
	const lineItemId = event.lineItemId || event.sourceAgnosticLineItemId || null;

	let source;
	if ( event.isBackfill ) {
		source = 'adx-backfill';
	} else if ( event.yieldGroupIds && event.yieldGroupIds.length ) {
		source = 'open-bidding';
	} else if ( lineItemId ) {
		source = 'gam-line-item';
	} else {
		source = 'unknown';
	}

	// A Prebid bid that rendered here reports right after (bidWon), naming this line item.
	maiPubLastFill[ slotId ] = { lineItemId, advertiserId: event.advertiserId || null };

	const hb = maiPubGetSlotHbBidSummary( slot );

	maiPubLog(
		`Fill source ${slotId}: ${source} size=${renderSize} lineItem=${lineItemId || 'n/a'} advertiser=${event.advertiserId || 'n/a'}` +
		( hb.hasHbPb
			? ` | HB bid on slot: hb_pb=${hb.hb_pb} hb_bidder=${hb.hb_bidder || 'n/a'} hb_size=${hb.hb_size || 'n/a'}`
			: ' | no HB bid on slot' ),
		{
			source,
			renderSize,
			isBackfill:    !! event.isBackfill,
			lineItemId,
			advertiserId:  event.advertiserId || null,
			campaignId:    event.campaignId || null,
			creativeId:    event.creativeId || event.sourceAgnosticCreativeId || null,
			yieldGroupIds: event.yieldGroupIds || null,
			companyIds:    event.companyIds || null,
			hb,
		}
	);
}

/**
 * Log header-bidding keys on GPT slots when debugging empty renders.
 *
 * @param {googletag.Slot[]} slots
 * @param {string} label
 */
function maiPubLogSlotHeaderBiddingTargeting( slots, label ) {
	if ( ! debug && ! log ) {
		return;
	}

	slots.forEach( ( slot ) => {
		const hb = maiPubGetSlotHbTargeting( slot );
		const slotId = slot.getSlotElementId();
		const pbVal = Array.isArray( hb.hb_pb ) ? hb.hb_pb[0] : hb.hb_pb;
		const bidderVal = Array.isArray( hb.hb_bidder ) ? hb.hb_bidder[0] : hb.hb_bidder;
		const hasBidTargeting = !!( pbVal || bidderVal || hb.hb_adid ||
			( Array.isArray( hb.hb_adid ) && hb.hb_adid.length ) );

		// No bids for the slot at all (often the case for spotlight) is not a targeting problem.
		const responses = ( typeof pbjs !== 'undefined' && typeof pbjs.getBidResponsesForAdUnitCode === 'function' )
			? ( ( pbjs.getBidResponsesForAdUnitCode( slotId ) || {} ).bids || [] )
			: null;

		if ( ! hasBidTargeting && responses && ! responses.length ) {
			maiPubLog( `HB targeting (${label}) ${slotId}: no Prebid bids for this slot, so no hb_* keys.` );
		} else if ( ! hasBidTargeting ) {
			maiPubLog( `HB targeting (${label}) ${slotId}: no hb_pb/hb_bidder on slot — Prebid bid not applied (check ad unit code vs div id / duplicate GAM path)`, {
				adUnitPath: slot.getAdUnitPath(),
				keysPresent: Object.keys( hb ),
				hb,
			} );
		} else {
			maiPubLog( `HB targeting (${label}) ${slotId}:`, hb );
		}
	} );
}

function maiPubIsMaiSlot( slot ) {
	// Check if the slot object exists and if its GAM Ad Unit Path is in our tracked array.
	return slot && adGamIds.includes( slot.getAdUnitPath() );
}

/**
 * Viewability-gated refresh countdown (viewability hardening).
 *
 * Best-practice refresh gate: a slot may refresh only once (a) its current
 * impression actually counted as viewable — GPT `impressionViewable`, i.e. the
 * MRC 50%/1s Active View standard — (b) it has banked `refreshTime` of *seen*
 * time (time at/above its viewability threshold, NOT wall-clock), and (c) it is
 * still at/above threshold at the moment we fire and the tab is visible.
 * Seen-time accumulates via pause/resume: an ad scrolled out of view stops its
 * clock and resumes where it left off, so "30s" means 30s actually on screen.
 *
 * Threshold is 50% for standard display and 30% for large formats
 * (>= largeFormatPixels, e.g. 970x250) per the MRC large-ad standard.
 *
 * @since TBD
 */

// Is this slug an out-of-page slot (e.g. spotlight anchor)? OOP slots are not
// Active-View-measurable, so GPT's impressionViewable never fires for them —
// gating them on it would stop them refreshing. They get a simple time-based
// refresh instead (maiPubArmOopRefresh).
function maiPubIsOutOfPageSlug( slug ) {
	const sizes = ads[ slug ] && ads[ slug ].sizes;
	if ( ! sizes ) {
		return false;
	}
	return Array.isArray( sizes ) ? sizes.includes( 'out-of-page' ) : sizes === 'out-of-page';
}

// Simple wall-clock refresh for OOP slots (the viewability-gated countdown does
// not apply — they can't be viewability-measured). Gated only on page visibility.
function maiPubArmOopRefresh( slotId ) {
	const s = slotManager[ slotId ];
	if ( ! s || ! s.slotObject || ! isPageVisible || ! maiPubIsRefreshable( s.slotObject ) ) {
		return;
	}
	clearTimeout( timeoutManager[ slotId ] );
	timeoutManager[ slotId ] = setTimeout( () => {
		if ( isPageVisible ) {
			maiPubBatchRequestSlots( [ slotId.replace( 'mai-ad-', '' ) ], 'oop-refresh' );
		}
	}, refreshTime );
}

// Resolve the MRC viewability threshold from the slot's ACTUAL rendered box.
// We measure the element (not event.size) because header-bidding line items
// often render via a 1x1 universal creative whose event.size is [1,1] even
// though the painted ad is full size; the element's box reflects the real size.
function maiPubViewabilityThreshold( slotId, event ) {
	let area = 0;
	const el = document.getElementById( slotId );
	if ( el ) {
		const rect = el.getBoundingClientRect();
		area = rect.width * rect.height;
	}
	if ( ! area && event && Array.isArray( event.size ) && event.size.length >= 2 ) {
		area = event.size[0] * event.size[1];
	}
	return area >= largeFormatPixels ? largeFormatViewabilityThreshold : refreshViewabilityThreshold;
}

// Start/resume accumulating seen-time and arm the refresh timer for the
// remaining seen-time. No-op if already running or the tab is hidden.
function maiPubResumeSeenCountdown( slotId ) {
	const s = slotManager[ slotId ];
	if ( ! s || s.isOOP || s.seenSince || ! isPageVisible ) {
		return;
	}
	s.seenSince = Date.now();
	const remaining = Math.max( 0, refreshTime - ( s.seenMs || 0 ) );
	clearTimeout( timeoutManager[ slotId ] );
	timeoutManager[ slotId ] = setTimeout( () => maiPubTryViewableRefresh( slotId ), remaining );
}

// Pause the seen-time clock, banking elapsed viewable time and clearing the timer.
function maiPubPauseSeenCountdown( slotId ) {
	const s = slotManager[ slotId ];
	if ( ! s || s.isOOP || ! s.seenSince ) {
		return;
	}
	s.seenMs = ( s.seenMs || 0 ) + ( Date.now() - s.seenSince );
	s.seenSince = null;
	clearTimeout( timeoutManager[ slotId ] );
	delete timeoutManager[ slotId ];
}

// Reset the seen-time accumulator for a freshly rendered impression and, if it
// is already at/above threshold, start the clock. Called from slotRenderEnded.
function maiPubArmViewableRefresh( slotId, slot, event ) {
	const s = slotManager[ slotId ];
	if ( ! s ) {
		return;
	}
	clearTimeout( timeoutManager[ slotId ] );
	delete timeoutManager[ slotId ];
	s.slotObject = slot;
	s.seenMs = 0;
	s.seenSince = null;
	s.hasBeenViewable = false;
	s.deferChecks = 0;
	s.heldForTeads = false;

	// OOP slots (spotlight anchor) aren't Active-View-measurable — impressionViewable
	// never fires — so keep them on a simple time-based refresh rather than the
	// viewability gate (which would stop them refreshing entirely).
	s.isOOP = maiPubIsOutOfPageSlug( slotId.replace( 'mai-ad-', '' ) );
	if ( s.isOOP ) {
		// Kargo spotlight (OOP) is a Kargo-controlled high-impact format (overlay /
		// in-article / masthead), measured by Kargo/IAS — not GAM Active View — so it
		// never fires impressionViewable and stays out of the viewability gate. Whether
		// to refresh it at all is a separate Kargo revenue/UX call (refreshOutOfPage).
		if ( refreshOutOfPage ) {
			maiPubArmOopRefresh( slotId );
		}
		return;
	}

	s.vizThreshold = maiPubViewabilityThreshold( slotId, event );
	if ( maiPubIsRefreshable( slot ) && isPageVisible && ( s.currentInView || 0 ) >= s.vizThreshold ) {
		maiPubResumeSeenCountdown( slotId );
	}
}

/**
 * Teads' player, open in an in-content slot, or null. Teads draws it in the slot beside
 * its 1x1 frame, and the slot grows to hold it (CSS: no height cap for an in-content slot
 * holding .teads-inread, or .teads-player for its shoppable one). In a fixed sticky bar it
 * stays capped, so it doesn't count.
 *
 * @since TBD
 *
 * @param {string} slotId The ad unit div id.
 * @return {Element|null}
 */
function maiPubOpenTeadsPlayer( slotId ) {
	const inner  = document.getElementById( slotId );
	const player = inner ? inner.querySelector( '.teads-inread, .teads-player' ) : null;

	return player && player.offsetHeight > 1 && ! inner.closest( '.mai-ad-unit-sticky' ) ? player : null;
}

// Timer callback: the slot has banked `refreshTime` of seen-time. Fire the
// refresh only if it is viewable-qualified (impressionViewable fired), still
// at/above threshold, and the tab is visible; otherwise keep waiting.
function maiPubTryViewableRefresh( slotId ) {
	const s = slotManager[ slotId ];
	if ( ! s ) {
		return;
	}
	// Bank the final in-view run.
	if ( s.seenSince ) {
		s.seenMs = ( s.seenMs || 0 ) + ( Date.now() - s.seenSince );
		s.seenSince = null;
	}
	const threshold  = s.vizThreshold || refreshViewabilityThreshold;
	const atThreshold = ( s.currentInView || 0 ) >= threshold;
	const eligible = s.hasBeenViewable && atThreshold && isPageVisible &&
		( s.seenMs >= refreshTime ) && maiPubIsRefreshable( s.slotObject );

	// Teads' player opened the slot: a refresh would cut its video short or take its end
	// screen away, and shrink the slot back on screen, moving the page (0.2 of layout
	// shift for a vertical video mid-screen). Held while the player is there: the next
	// visibility change re-arms the clock (maiPubResumeSeenCountdown), and checks again.
	if ( eligible && maiPubOpenTeadsPlayer( slotId ) ) {
		if ( ! s.heldForTeads ) {
			s.heldForTeads = true;
			maiPubLog( () => `Viewable-refresh: ${slotId} held: Teads' player is open in the slot. A refresh would cut its video or end screen and shrink the slot on screen. It refreshes if Teads removes the player.` );
		}
	} else if ( eligible ) {
		maiPubLog( () => `Viewable-refresh: ${slotId} — banked ${Math.round( s.seenMs / 1000 )}s seen at >=${threshold}%, impressionViewable=true. Refreshing.` );
		maiPubBatchRequestSlots( [ slotId.replace( 'mai-ad-', '' ) ], 'viewable-refresh' );
	} else if ( atThreshold && isPageVisible && ( s.deferChecks || 0 ) < 5 ) {
		// Seen-time is satisfied but impressionViewable hasn't fired yet (rare —
		// it normally fires ~1s into viewing). Re-check up to 5x rather than spin.
		s.deferChecks = ( s.deferChecks || 0 ) + 1;
		maiPubLog( () => `Viewable-refresh: ${slotId} deferred (${s.deferChecks}/5) — seenMs=${Math.round( s.seenMs )} hasBeenViewable=${s.hasBeenViewable} inView=${Math.round( s.currentInView || 0 )}% (need >=${threshold}%).` );
		clearTimeout( timeoutManager[ slotId ] );
		timeoutManager[ slotId ] = setTimeout( () => maiPubTryViewableRefresh( slotId ), 1000 );
	}
	// else: out of view / tab hidden — paused. slotVisibilityChanged (or the tab
	// visibilitychange handler) will resume the clock when it returns.
}

/**
 * The frame of an ad that has drawn nothing, or null: GPT's frame in the slot is 1x1 or
 * smaller (0x0), and nothing else in the slot's box is drawn.
 *
 * On allhiphop.com, Teads won in-content slots with 1x1 bids and drew nothing there
 * ("creative(iframe) 1x1, nothing drawn outside it"), and Ogury's 300x50 in the sticky
 * footer shrank its frame to 0x0 a second after it rendered, drawing nothing anywhere else
 * on the page. An ad drawn beside its frame (Teads' player) has drawn, even cut off by its
 * slot. Scripts and styles don't count, nor anything without area (Teads' box before its
 * player opens is 0px tall).
 *
 * @since TBD
 *
 * @param {string} slotId The ad unit div id.
 * @return {HTMLIFrameElement|null}
 */
function maiPubBlankFrame( slotId ) {
	const inner  = document.getElementById( slotId );
	const iframe = inner ? inner.querySelector( 'iframe[id^="google_ads_iframe"]' ) || inner.querySelector( 'iframe' ) : null;

	if ( ! iframe || iframe.offsetWidth > 1 || iframe.offsetHeight > 1 ) {
		return null;
	}

	const box  = inner.closest( '.mai-ad-unit-container' ) || inner;
	const boxR = box.getBoundingClientRect();

	for ( const el of box.querySelectorAll( '*' ) ) {
		if ( /^(SCRIPT|STYLE|LINK|NOSCRIPT|TEMPLATE)$/.test( el.tagName ) || el.contains( iframe ) ) {
			continue;
		}

		const r = el.getBoundingClientRect();
		const w = Math.min( r.right, boxR.right ) - Math.max( r.left, boxR.left );
		const h = Math.min( r.bottom, boxR.bottom ) - Math.max( r.top, boxR.top );

		if ( w > 1 && h > 1 ) {
			return null;
		}
	}

	return iframe;
}

/**
 * The Prebid bid whose ad rendered in a slot, and whether one may still be on its way.
 *
 * The slot's hb_adid keys (the top bid's and each bidder's) name the bids GAM was sent, and
 * Prebid lists a bid among its winning bids once its creative has rendered it. That can come
 * seconds after GAM's render: on allhiphop.com, Teads' bids rendered 6 seconds after theirs,
 * so a check before then saw a blank frame that wasn't done yet.
 *
 * @since TBD
 *
 * @param {googletag.Slot} slot
 * @return {{bid: Object|null, pending: boolean}} pending: the slot carries bids and none has rendered.
 */
function maiPubRenderedBid( slot ) {
	const cfg       = slot && 'function' === typeof slot.getConfig ? slot.getConfig( 'targeting' ) || {} : {};
	const targeting = cfg.targeting || cfg;
	const adIds     = [];

	Object.keys( targeting ).forEach( ( key ) => {
		if ( 0 === key.indexOf( 'hb_adid' ) ) {
			[].concat( targeting[ key ] ).forEach( ( id ) => id && adIds.push( String( id ) ) );
		}
	} );

	const won = 'undefined' !== typeof pbjs && 'function' === typeof pbjs.getAllWinningBids
		? pbjs.getAllWinningBids().filter( ( bid ) => bid && -1 !== adIds.indexOf( String( bid.adId ) ) )
		: [];

	return { bid: won.length ? won[ won.length - 1 ] : null, pending: adIds.length > 0 && ! won.length };
}

/**
 * Asks GAM again for a slot whose ad has drawn nothing (maiPubBlankFrame).
 *
 * A header-bidding winner can render and draw nothing: on allhiphop.com, Teads' 1x1 wins in
 * content ($0.26, $2.01), and Ogury's 300x50 in the sticky footer, which shrank its frame to
 * 0x0 and left the bar an empty 100px box. The slot stayed blank for the rest of the page
 * view: GAM didn't count those blank frames as seen (no impressionViewable), and the viewable
 * refresh waits for that (maiPubTryViewableRefresh).
 *
 * The check comes blankRenderWaitMs after the slot first comes half on screen: Teads builds
 * its player there, and drew one in the sticky footer 2.4 seconds after the render. When the
 * slot's Prebid bid hasn't rendered yet, it's checked again later, up to 3 times, and once
 * more after the bid renders. A blank slot off screen, or in a hidden tab, is asked again
 * when it's back.
 *
 * That request runs no auction (maiPubRequestSlots): GAM gets the slot's last auction again,
 * less the bid that rendered blank, which Prebid doesn't target twice. A new auction would
 * bring the same bidder's next bid, likely as blank. With no bids left, GAM fills it alone.
 *
 * A slot is asked again maxBlankRetries times per auction: the count starts over when the
 * slot's next request runs one (maiPubRequestSlots), which the viewable refresh paces, so
 * blank and empty renders can't loop. Not on impressionViewable: GAM counts a 1x1 frame on
 * screen as seen, blank or not (Teads' 1x1 in the homepage sticky footer, seen 1.1 seconds
 * after its render), so blank ads one after another would each start it over. Out-of-page
 * slots (their ad is drawn over the page) and slots that don't refresh aren't asked again.
 *
 * @since TBD
 *
 * @param {string}         slotId  The ad unit div id.
 * @param {googletag.Slot} slot    The slot.
 * @param {boolean}        isEmpty GAM returned no ad: the last render's check just stops.
 * @return {void}
 */
function maiPubWatchBlankRender( slotId, slot, isEmpty = false ) {
	const s = slotManager[ slotId ];

	if ( ! s ) {
		return;
	}

	// A new render ends the last one's check, and a retry that never went out can't take the
	// auction from the slot's next request.
	if ( s.blankWatch ) {
		s.blankWatch();
	}

	s.skipAuction = false;

	const inner = document.getElementById( slotId );
	const slug  = slotId.replace( 'mai-ad-', '' );

	if ( isEmpty || ! inner || 'function' !== typeof IntersectionObserver || maiPubIsOutOfPageSlug( slug ) ) {
		return;
	}

	let timer    = null;
	let seenAt   = 0;     // When the slot first came half on screen.
	let onScreen = false;
	let due      = false; // Blank: asked again once on screen in a visible tab.
	let waits    = 0;     // Checks put off for a bid still to render.
	let waited   = false; // The last check was put off for one.
	let what     = '';    // The blank ad, for the log.

	const observer = new IntersectionObserver( ( entries ) => {
		const entry = entries[ entries.length - 1 ];

		onScreen = entry.isIntersecting && entry.intersectionRatio >= 0.49;

		if ( ! onScreen ) {
			return;
		}

		if ( due ) {
			retry();
		} else if ( ! seenAt ) {
			seenAt = Date.now();
			timer  = setTimeout( check, blankRenderWaitMs );
		}
	}, { threshold: 0.5 } );

	const resume = () => {
		if ( due && onScreen ) {
			retry();
		}
	};

	const stop = () => {
		observer.disconnect();
		clearTimeout( timer );
		document.removeEventListener( 'visibilitychange', resume );

		if ( stop === s.blankWatch ) {
			s.blankWatch = null;
		}
	};

	const retry = () => {
		if ( document.hidden ) {
			return;
		}

		stop();

		// Asked for since (a request on its way), not refreshable, or drawn by now.
		if ( s.processing || ! maiPubIsRefreshable( slot ) || ! maiPubBlankFrame( slotId ) ) {
			return;
		}

		s.blankRetries = ( s.blankRetries || 0 ) + 1;
		s.skipAuction  = true;
		s.visible      = true; // maiPubMaybeRequestSlots asks only for slots it holds visible; this one is half on screen.

		maiPubLog( () => `Blank ad: ${slotId} has drawn nothing ${ Math.round( ( Date.now() - seenAt ) / 1000 ) }s after it came on screen (${what}). Asking GAM again with the rest of its last auction.` );
		maiPubBatchRequestSlots( [ slug ], 'blank-retry' );
	};

	const check = () => {
		timer = null;

		const frame = maiPubBlankFrame( slotId );

		if ( ! frame ) {
			stop();
			return;
		}

		const { bid, pending } = maiPubRenderedBid( slot );

		// Its bid may still render, or just has: give it the same time to draw.
		if ( ( pending && waits < 3 ) || ( ! pending && waited ) ) {
			waits += pending ? 1 : 0;
			waited = pending;
			timer  = setTimeout( check, blankRenderWaitMs );
			return;
		}

		what = `its frame ${frame.offsetWidth}x${frame.offsetHeight}` + ( bid
			? `, ${ bid.bidderCode || bid.bidder || 'its bid' } $${ Number( bid.cpm ).toFixed( 2 ) } ${ bid.size || `${bid.width}x${bid.height}` } rendered nothing`
			: ( pending ? ', its header-bidding bid never rendered' : '' ) );

		if ( ( s.blankRetries || 0 ) >= maxBlankRetries ) {
			stop();
			maiPubLog( () => `Blank ad: ${slotId} has drawn nothing (${what}), and was asked again already. Left as it is until its next auction (${maxBlankRetries} ${ 1 === maxBlankRetries ? 'retry' : 'retries' } per auction).` );
			return;
		}

		due = true;

		if ( onScreen ) {
			retry();
		}
	};

	document.addEventListener( 'visibilitychange', resume );
	observer.observe( inner );
	s.blankWatch = stop;
}

/**
 * Check if a slot has targetting set to refresh.
 *
 * @since 1.12.0
 *
 * @param {object} slot The ad slot.
 *
 * @return {boolean} True if refreshable.
 */
function maiPubIsRefreshable( slot ) {
	if ( ! slot ) {
		return false;
	}

	let value;

	// Read targeting via getConfig('targeting') — returns { targeting: { refresh: 'true', ... } },
	// not a flat map. This replaces the deprecated Slot.getTargeting() (GPT message #201). getConfig
	// has been GA in GPT for years and GPT is always served latest from Google's CDN, so the old
	// getTargeting() fallback was dead code and is removed.
	//
	// A "Slot.getTargeting is deprecated" warning still shows up on live pages. It is not ours:
	// the GPT stack for it runs q_.dispatchEvent -> (anonymous) @ injected.js:9, i.e. a browser
	// extension's slotRenderEnded listener (Professor Prebid). Our own refresh() call is simply
	// the frame that dispatched the event, which is why the trace bottoms out in runRefresh().
	if ( typeof slot.getConfig === 'function' ) {
		const config    = slot.getConfig( 'targeting' ) || {};
		const targeting = config.targeting || config;
		value           = targeting[ refreshKey ];
	}

	return Boolean( Array.isArray( value ) ? value[0] : value );
}

/**
 * Establishes the visitor's PPID on a strict priority: Server > Newsletter > Cookie > Random.
 *
 * Synchronous by contract. Every caller uses the return value immediately —
 * GPT's setPublisherProvidedId, the Prebid pubProvidedId eid, the ORTB2 eids
 * injection, RevIQ's setUid, and the VAST identity path — so anything that
 * cannot be resolved on this tick cannot be part of the answer.
 *
 * Two rules govern the result, and both exist because a PPID that moves is
 * worth less than a stable pseudonymous one:
 *
 *   ADOPT, NEVER REGENERATE. Whatever id this device already carries stays its
 *   id. A visitor who arrived with a matomo-sourced value keeps it forever; we
 *   simply stop minting new ones that way.
 *
 *   ONLY EVER UPGRADE. The id may move device-level -> person-level (a random
 *   or cookie id replaced by the server email hash), never sideways. The old
 *   implementation replaced a random id with a Matomo-derived one, which is a
 *   sideways move between two equally pseudonymous device ids.
 *
 * Matomo was removed as a SOURCE here (it remains a destination — the resolved
 * id is pushed to it via setUserId, see class-tracking.php). Three reasons:
 *
 *   1. It was never reached. The upgrade sat behind `typeof Matomo !== 'undefined'`
 *      evaluated at OUR parse time, and Matomo's tracker is a separate async
 *      script that has almost never loaded that early. No retry was scheduled,
 *      so the branch silently no-opped — and `matomoTimeout` / `matomoReady`,
 *      the two constants that presumably existed to wait for it, were declared
 *      and never read anywhere in this file.
 *   2. When it DID fire it was too late to matter. The hash came from
 *      `crypto.subtle.digest(...).then(...)`, so it resolved after this
 *      function had already returned the random id to the caller. Its
 *      `return hashed` went nowhere, and `isGeneratingPpid = false` ran in a
 *      `finally` that executed before the promise settled, so the concurrency
 *      guard did not cover the async window it appeared to guard.
 *   3. The result was an id that CHANGED MID-SESSION. Step 2 excluded 'random'
 *      from the session short-circuit, so every pageview retried the upgrade;
 *      a visitor got a random id on pageview 1 and a Matomo-derived one on
 *      whichever later pageview Matomo happened to be ready. GAM saw two
 *      different people.
 *
 * And it bought nothing even when it worked: Matomo's visitor id is itself a
 * randomly generated value in a JS-set first-party cookie (`_pk_id.*`) — the
 * same storage class, the same ITP exposure, per-site, and absent entirely on
 * sites where Matomo is not installed.
 *
 * @since 1.18.5 Original identification engine initialized.
 * @updated v885 Swapped targeting arrays to utilize the client-side session data passport.
 * @updated v886 Fixed a Promise serialization bug by forcing strict synchronous string value returns.
 * @updated TBD  Matomo demoted from source to sink; adopt-never-regenerate; async path removed.
 *
 * @return {string} The resolved identity string, or '' if none could be established.
 */
function maiPubGeneratePpid() {
    // 1. SERVER ID (logged-in user). The only legitimate upgrade: this is the
    //    SHA256 of the user's email, a person-level id, so it outranks any
    //    device-level id already on the device.
    if (maiPubAdsVars.ppid) {
        const serverId = maiPubAdsVars.ppid;

        if (maiPubSessionData.ppid !== serverId || 'server' !== maiPubSessionData.ppidSource) {
            maiPubLog(`PPID: server id present (logged in) — upgrading from '${maiPubSessionData.ppidSource || 'none'}' to 'server'.`);

            maiPubSessionData.ppid       = serverId;
            maiPubSessionData.ppidSource = 'server';
            maiPubSaveSessionData({ ppid: serverId, ppidSource: 'server' });
            maiPubSetPpidCookie(serverId, 'server');
        }

        ppid = serverId;

        return ppid;
    }

    // 2. NEWSLETTER IDENTITY. maiPubCaptureUrlIdentity() puts a verified hashed
    //    email in the passport as `hem`; that is person-level too, so it also
    //    outranks a device id. Runs before this on the same tick.
    if (maiPubSessionData.hem && 'server' !== maiPubSessionData.ppidSource) {
        const hem = maiPubSessionData.hem;

        if (maiPubSessionData.ppid !== hem || 'fpid' !== maiPubSessionData.ppidSource) {
            maiPubLog(`PPID: hashed email in the passport — upgrading from '${maiPubSessionData.ppidSource || 'none'}' to 'fpid'.`);

            maiPubSessionData.ppid       = hem;
            maiPubSessionData.ppidSource = 'fpid';
            maiPubSaveSessionData({ ppid: hem, ppidSource: 'fpid' });
            maiPubSetPpidCookie(hem, 'fpid');
        }

        ppid = hem;

        return ppid;
    }

    // 3. SESSION. Already resolved this session — including 'random', which the
    //    old code deliberately excluded so it could retry the Matomo upgrade.
    //    That exclusion IS the mid-session instability: with nothing to upgrade
    //    to any more, re-resolving can only produce the same value or churn.
    if (maiPubSessionData.ppid && maiPubSessionData.ppidSource) {
        ppid = maiPubSessionData.ppid;

        return ppid;
    }

    // 4. COOKIE. A previous session on this device. Adopted verbatim, whatever
    //    minted it — including legacy 'matomo' values, which stay valid ids for
    //    the devices already carrying them.
    const cookieId = maiPubGetPpidCookie();

    if (cookieId) {
        const cookieSource = maiPubGetPpidSourceCookie() || 'cookie';

        maiPubSessionData.ppid       = cookieId;
        maiPubSessionData.ppidSource = cookieSource;
        maiPubSaveSessionData({ ppid: cookieId, ppidSource: cookieSource });

        maiPubLog(`PPID: adopted from cookie (source: ${cookieSource}).`);

        ppid = cookieId;

        return ppid;
    }

    // 5. RANDOM. New device, no identity. crypto.randomUUID() where available
    //    (122 bits), otherwise 16 CSPRNG bytes. Both clear GAM's 32-150
    //    alphanumeric gate once maiPubNormalizeGamPpid() strips the hyphens.
    try {
        if (!window.crypto) {
            throw new Error('Web Crypto API is not available.');
        }

        const identifier = 'function' === typeof crypto.randomUUID
            ? crypto.randomUUID()
            : Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => b.toString(16).padStart(2, '0')).join('');

        maiPubSessionData.ppid       = identifier;
        maiPubSessionData.ppidSource = 'random';
        maiPubSaveSessionData({ ppid: identifier, ppidSource: 'random' });
        maiPubSetPpidCookie(identifier, 'random');

        maiPubLog('PPID: no prior identity — generated a random id.');

        ppid = identifier;

        return ppid;
    } catch (error) {
        maiPubLog('PPID: generation failed.', error);

        return ppid || '';
    }
}

/**
 * Sets the PPID as a persistent cookie, but only if consent is granted.
 * @param {string} ppidValue - The hashed PPID to store.
 * @since 1.18.5
 */
function maiPubSetPpidCookie(ppidValue, source = 'unknown') {
    if (!maiPubConsentAllowsStorage()) {
        maiPubLog('Consent not settled (or withheld). Deferring persistent PPID cookie.');
        return;
    }
    if (!ppidValue) return;

    const expires = new Date(Date.now() + 31536000000).toUTCString(); // 1 year
    document.cookie = `maipub_ppid=${encodeURIComponent(ppidValue)};path=/;expires=${expires};SameSite=Lax;Secure`;
    document.cookie = `maipub_ppid_source=${encodeURIComponent(source)};path=/;expires=${expires};SameSite=Lax;Secure`; // Save source
    maiPubLog('PPID and Source saved to persistent cookies.', source);
}

/**
 * Gets the PPID source from the persistent cookie.
 *
 * @since 1.21.3
 */
/**
 * ============================================================================
 * IDENTITY MEASUREMENT
 * ============================================================================
 *
 * One computation, two destinations. These three resolvers are the ONLY place
 * arrival, identity tier and consent state are decided; Matomo custom
 * dimensions and GAM key-values both read from here.
 *
 * That matters more than it looks. The whole measurement design is
 * segment-level correspondence — Matomo answers "did they browse deeper",
 * GAM answers "were they worth more", and the two are compared by asserting
 * they describe the SAME population. If the two systems computed these values
 * independently they would drift, and every comparison drawn from them would
 * be quietly wrong.
 */

// MATOMO_DIM_ARRIVAL / MATOMO_DIM_IDENTITY_TIER are declared with the identity
// resolution block earlier in this file, ahead of the code that reads them.

// Referrer host fragments -> arrival channel. Deliberately small: this feeds a
// predefined GAM key-value list, and an unmatched host is 'other' rather than a
// new value GAM would silently drop.
const ARRIVAL_SEARCH_HOSTS = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'brave.'];
const ARRIVAL_SOCIAL_HOSTS = ['facebook.', 'instagram.', 'linkedin.', 'twitter.', 'x.com', 't.co', 'reddit.', 'bsky.', 'threads.'];

/**
 * How did this SESSION start?
 *
 * Resolved once on the first pageview and persisted, because arrival describes
 * the session, not the pageview. Reading it per-pageview would report
 * 'internal' for every page after the first and make the newsletter cohort
 * collapse to its landing pages — which is exactly the population whose
 * onward browsing we are trying to measure.
 *
 * @since TBD
 *
 * @return {string} newsletter|login|search|social|direct|other
 */
function maiPubResolveArrival() {
	if (maiPubSessionData.arrival) {
		return maiPubSessionData.arrival;
	}

	let arrival = 'other';

	// A newsletter click is the arrival CHANNEL, decided by the PRESENCE of the
	// parameter, not by its value. arrival answers "did this session start from an
	// email?"; whether we can also identify the PERSON is a separate question that
	// identity tier answers from the hashed-email VALUE. So this uses .has(), not a
	// truthy .get(): an empty `?sw_fpid=` (an ESP that cannot emit a hashed email)
	// or a merge tag that failed to populate still counts as a newsletter arrival —
	// an unidentified one — instead of silently falling through to 'direct'. It is
	// independent of login: a logged-in visitor who clicks an email link arrived via
	// the newsletter channel, and identity tier records the login separately.
	const fpidParam = (maiPubAdsVars.fpid && undefined !== maiPubAdsVars.fpid.param)
		? maiPubAdsVars.fpid.param
		: 'sw_fpid';

	let hasFpid = false;

	try {
		hasFpid = !!(fpidParam && new URLSearchParams(window.location.search).has(fpidParam));
	} catch (e) {
		hasFpid = false;
	}

	if (hasFpid) {
		arrival = 'newsletter';
	}
	else if (maiPubAdsVars.id5Identity && maiPubAdsVars.id5Identity.hem) {
		// Server-rendered identity only exists on an uncached response, which
		// means an authenticated request.
		arrival = 'login';
	}
	else {
		const ref = document.referrer || '';

		if (!ref) {
			arrival = 'direct';
		} else {
			try {
				const host = new URL(ref).hostname.toLowerCase();

				if (host === window.location.hostname) {
					// Same-host referrer on a session's first pageview means the
					// session boundary was a timeout, not a real entry. Not
					// 'direct' — we genuinely do not know.
					arrival = 'other';
				} else if (ARRIVAL_SEARCH_HOSTS.some(h => host.includes(h))) {
					arrival = 'search';
				} else if (ARRIVAL_SOCIAL_HOSTS.some(h => host.includes(h))) {
					arrival = 'social';
				}
			} catch (e) {
				arrival = 'other';
			}
		}
	}

	maiPubSessionData.arrival = arrival;
	maiPubSaveSessionData({ arrival: arrival });

	return arrival;
}

/**
 * How well do we know this visitor, and how did we come to know them?
 *
 * Ranked, not categorical. `person_returning` is the important one: it means
 * the identity was restored from a cookie written in an EARLIER visit, so it
 * doubles as the persistence measurement. If that bucket is healthy in Chrome
 * and absent in Safari, the hashed-email cookie is being lifetime-capped and
 * the newsletter identity is worth far less than it appears.
 *
 * Derived from hemSource/ppidSource rather than recomputed, so it cannot
 * disagree with what maiPubGeneratePpid() actually resolved.
 *
 * @since TBD
 *
 * @return {string} person_login|person_newsletter|person_returning|device|anonymous
 */
function maiPubResolveIdentityTier() {
	if (maiPubSessionData.hem) {
		switch (maiPubSessionData.hemSource) {
			case 'server': return 'person_login';
			case 'fpid':   return 'person_newsletter';
			case 'cookie': return 'person_returning';
		}

		// A hem with no recorded source shouldn't happen, but reporting it as a
		// person tier without saying which kind is more useful than dropping it.
		return 'person_returning';
	}

	// No email. A cookie- or matomo-sourced ppid still identifies the DEVICE
	// across sessions; 'random' means this session minted it and nothing
	// preceded it.
	if (maiPubSessionData.ppid && 'random' !== maiPubSessionData.ppidSource) {
		return 'device';
	}

	return 'anonymous';
}

/**
 * What did the visitor allow?
 *
 * Consent state is one of the largest and least visible drivers of CPM —
 * non-personalized ads earn dramatically less — and the mix shifts silently.
 * Without it, an identity-vs-CPM comparison can be measuring consent.
 *
 * @since TBD
 *
 * @return {string} full|npa|usnat_out|gdpr_ltd|unknown
 */
function maiPubResolveConsentState() {
	// An unresolved CMP is not consent; the optimistic default must never be
	// reported as a real answer.
	if (true !== maiPubSessionData.consentResolved) {
		return 'unknown';
	}

	if (false === maiPubSessionData.hasConsent) {
		if (maiPubSessionData.usnatApplies) {
			return 'usnat_out';
		}

		if (maiPubSessionData.gdprApplies) {
			return 'gdpr_ltd';
		}

		return 'npa';
	}

	return true === maiPubSessionData.hasConsent ? 'full' : 'unknown';
}

/**
 * (removed in 1.36.0.beta3) maiPubPushMatomoIdentity() — the ping/trackEvent
 * delivery of the arrival + identity-tier dimensions.
 *
 * Two defects retired it. First, a `ping` does NOT persist custom dimensions —
 * it only updates the visit's duration — so dimensions 1 and 3 were set on the
 * tracker and never recorded. Second, the setCustomDimension calls referenced
 * MATOMO_DIM_ARRIVAL before its top-level const was initialized (a TDZ
 * ReferenceError), swallowed by the try/catch. The trackEvent fallback would
 * have recorded, but at +1 action per visit — inflating Actions-per-Visit and
 * zeroing Bounce Rate, the very engagement metrics this instrumentation reports.
 *
 * Both dimensions are now delivered on the pageview itself: ads.js publishes the
 * resolved values as window.maiPubIdentity (see the identity block near the top
 * of this file) and mai-publisher-analytics.js applies them with
 * setCustomDimension() just before it replays trackPageView.
 */

/**
 * ITP probe: how old is the persistent hashed-email cookie?
 *
 * Returns a coarse bucket, never a raw age — these become GAM key-values, which
 * have cardinality limits and are predefined lists. The buckets map to the `ha`
 * key; any value not in GAM's predefined list is silently dropped, so these five
 * strings must match what is configured there exactly.
 *
 * The question being answered: Safari caps JS-set cookies well below the expiry
 * we request, and caps them harder still when the visitor arrived on a
 * link-decorated navigation from a classified domain — which is exactly what a
 * ?sw_fpid newsletter click is. If Safari never reaches h24to48 while Chrome
 * does, that cap is real and the newsletter identity cookie is worth far less
 * than it looks.
 *
 * @since TBD
 *
 * @return {string} One of: none, h0to24, h24to48, d2to7, d7plus.
 */
function maiPubGetHemCookieAgeBucket() {
	const match = document.cookie.match(/(?:^|;)\s*maipub_hem_set=([^;]*)(?:;|$)/);

	if (!match) {
		return 'none';
	}

	const written = parseInt(match[1], 10);

	// A malformed or future-dated stamp is not evidence of anything. Report it
	// as absent rather than inventing a bucket.
	if (!written || Number.isNaN(written) || written * 1000 > Date.now()) {
		return 'none';
	}

	const hours = (Date.now() - written * 1000) / 3600000;

	if (hours < 24)  return 'h0to24';
	if (hours < 48)  return 'h24to48';
	if (hours < 168) return 'd2to7';

	return 'd7plus';
}

/**
 * ITP probe: where the current PPID came from, as a GAM-safe value.
 *
 * Maps to the `ps` key. Constrained to the predefined list configured in GAM;
 * an unrecognized source reports 'none' rather than being dropped silently,
 * so an unexpected value shows up in the report instead of vanishing.
 *
 * 'matomo' is a legitimate value even though Matomo is no longer a PPID source:
 * devices that already carry a matomo-derived id keep it (adopt-never-
 * regenerate), so watching that bucket decay measures the transition.
 *
 * @since TBD
 *
 * @return {string} One of: server, fpid, cookie, matomo, random, none.
 */
function maiPubGetPpidSourceBucket() {
	const known = ['server', 'fpid', 'cookie', 'matomo', 'random'];
	const source = maiPubSessionData.ppidSource || '';

	return ( maiPubSessionData.ppid && known.includes(source) ) ? source : 'none';
}

function maiPubGetPpidSourceCookie() {
    const cookieMatch = document.cookie.match(/(?:^|;)\s*maipub_ppid_source=([^;]*)(?:;|$)/);
    return cookieMatch ? decodeURIComponent(cookieMatch[1]) : '';
}

/**
 * Gets the PPID from the persistent cookie.
 * This function is already in your file but is included here for completeness.
 * @returns {string} The PPID from the cookie, or an empty string if not found.
 * @since 1.18.5
 */
function maiPubGetPpidCookie() {
    const cookieMatch = document.cookie.match(/(?:^|;)\s*maipub_ppid=([^;]*)(?:;|$)/);
    return cookieMatch ? decodeURIComponent(cookieMatch[1]) : '';
}

// ---- NEW CORE SESSION FUNCTIONS ----

/**
 * Loads the session data from sessionStorage, increments the page count,
 * and updates the global maiPubSessionData object.
 * This should be called once at the very beginning of the script.
 * @since 1.18.5
 */
function maiPubLoadSessionData() {
    try {
        const storedData = sessionStorage.getItem(SESSION_KEY);
        if (storedData) {
            // --- THIS IS A SUBSEQUENT PAGE LOAD ---
            maiPubSessionData = JSON.parse(storedData);

            // ** REPAIR CORRUPTED PPID IF DETECTED **
            if (maiPubSessionData.ppid && typeof maiPubSessionData.ppid === 'object' && maiPubSessionData.ppid !== null) {
                maiPubLog('Session: Detected corrupted PPID during load, repairing to string:', maiPubSessionData.ppid);
                maiPubSessionData.ppid = Object.values(maiPubSessionData.ppid).join('');

				maiPubSaveSessionData({ ppid: maiPubSessionData.ppid });
				maiPubLog('PPID repaired and saved to session passport:', maiPubSessionData.ppid);
            }

            // ** PRUNE LEGACY NESTED CONSENT COPIES **
            maiPubPruneNestedConsent();

            // ** INCREMENT THE PAGE COUNT **
            maiPubSessionData.pageCount = (maiPubSessionData.pageCount || 1) + 1;

            // ** IMMEDIATELY SAVE THE UPDATED COUNT **
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(maiPubSessionData));

            maiPubLog('Session data successfully loaded and updated from cache:', maiPubSessionData);

        } else {
            // --- THIS IS THE FIRST PAGE VIEW OF THE SESSION ---
            maiPubSessionData = {
                version: "1.1",
                createdAt: Date.now(),
                pageCount: 1, // Initialize at 1
				hasConsent: true,
				gdprConsentUUID: '',
				usnatConsentUUID: '',
				gdprApplies: false,
				usnatApplies: false,
            };
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(maiPubSessionData));
            maiPubLog('First page view: Initialized session data.');
        }
    } catch (e) {
        maiPubLog('Could not parse session data, starting fresh.');
        sessionStorage.removeItem(SESSION_KEY);
        maiPubSessionData = { version: "1.0", createdAt: Date.now(), pageCount: 1 };
    }
}

/**
 * Saves or updates data in the session "passport" and writes it back to sessionStorage.
 * @param {object} newData - An object containing the data to merge and save.
 * Example: { geo: { country: 'US' }, ppid: '123' }
 * @since 1.18.5
 */
function maiPubSaveSessionData(newData) {
    try {
        // Deep merge the new data into the existing session data
        // This allows updating nested properties like consent.tcString without wiping other consent data
        for (const key in newData) {
            if (newData.hasOwnProperty(key)) {
                // Special handling for ppid - ensure it's always stored as a string
                if (key === 'ppid') {
                    // If ppid is somehow an object (corrupted), convert it to string
                    // if (typeof newData[key] === 'object' && newData[key] !== null) {
                        // maiPubLog('PPID corruption detected and will be repaired during save, bad data: ', newData[key]);
                    //     maiPubSessionData[key] = Object.values(newData[key]).join('');
                    // } else {
						let newPpid = String(newData[key]);
                        maiPubSessionData[key] = newPpid;
						maiPubLog('maiPubSaveSessionData new ppid as ', newPpid);
                    // }
                }
                // For other objects (like consent, geo), do deep merge
                else if (typeof newData[key] === 'object' && newData[key] !== null && !Array.isArray(newData[key])) {
                    maiPubSessionData[key] = { ...maiPubSessionData[key], ...newData[key] };
                } else {
                    maiPubSessionData[key] = newData[key];
                }
            }
        }

        sessionStorage.setItem(SESSION_KEY, JSON.stringify(maiPubSessionData));
        maiPubLog('Session data updated and saved:', maiPubSessionData);
    } catch (e) {
        console.error('[Mai Publisher] Failed to save session data:', e);
    }
}

// --- NEW CORE CONSENT FUNCTIONS ---

/**
 * Whether we may write identity to persistent (cross-session) storage yet.
 *
 * Two conditions, and the second one is the fix: consent must be granted AND the
 * consent state must actually be settled. At init `hasConsent` defaults to true
 * before the CMP has said anything — which is exactly when a newsletter click
 * lands, since that is a first pageview — so checking `hasConsent` alone wrote a
 * year-long cookie containing a hashed email before the visitor had been asked.
 *
 * Gated on cmpConsentProcessed rather than consentResolved on purpose.
 * cmpConsentProcessed is set by EVERY path that settles consent: a real CMP
 * answer, the timeout fallback, and the reuse of an earlier decision in this
 * session. consentResolved is deliberately narrower (genuine CMP answers only, so
 * the fast path never reuses a guess), and gating storage on it would mean sites
 * with no Sourcepoint configured — where the timeout fallback is the only decision
 * point — never persisted an identity cookie at all.
 *
 * sessionStorage is unaffected: the passport is session-scoped and cleared with the
 * tab, so capture still works immediately and only the durable write waits.
 *
 * @since TBD
 *
 * @return {boolean}
 */
function maiPubConsentAllowsStorage() {
    return true === hasConsent && true === cmpConsentProcessed;
}

/**
 * Writes any identity held in the passport out to persistent cookies.
 *
 * Capture happens at init, before consent is settled, so the cookie writes are
 * refused at that point (see maiPubConsentAllowsStorage). This re-attempts them
 * once consent settles. Idempotent — rewriting the same cookie is harmless.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubPersistIdentityCookies() {
    if (!maiPubConsentAllowsStorage()) {
        return;
    }

    if (maiPubSessionData.hem) {
        maiPubSetHemCookie(maiPubSessionData.hem);
    }

    if (maiPubSessionData.ppid && maiPubSessionData.ppidSource) {
        maiPubSetPpidCookie(maiPubSessionData.ppid, maiPubSessionData.ppidSource);
    }
}

/**
 * SourcePoint's saved consent record for this site.
 *
 * SourcePoint keeps the visitor's choice in localStorage as
 * `_sp_user_consent_<propertyId>`, e.g. {"gdpr":{"applies":false,...},"usnat":
 * {"applies":true,"consentStatus":{"rejectedAny":false,...},"expirationDate":
 * "2027-10-24T15:12:28.551Z",...}}. Read so a returning visitor's choice is known
 * before SourcePoint has loaded.
 *
 * @since TBD
 *
 * @return {object|null} { gdprApplies, usnatApplies, rejectedAny, expires }, or null with no record.
 */
function maiPubSourcepointSavedConsent() {
	try {
		let key = null;

		for (let i = 0; i < localStorage.length; i++) {
			if (0 === String(localStorage.key(i)).indexOf('_sp_user_consent_')) {
				key = localStorage.key(i);
				break;
			}
		}

		const record = key ? JSON.parse(localStorage.getItem(key) || 'null') : null;

		if (!record || 'object' !== typeof record) {
			return null;
		}

		const gdpr  = record.gdpr || {};
		const usnat = record.usnat || {};
		const dates = [gdpr.expirationDate, usnat.expirationDate].map((date) => Date.parse(date)).filter((time) => time > Date.now());

		return {
			gdprApplies:  true === gdpr.applies,
			usnatApplies: true === usnat.applies,
			rejectedAny:  !!(usnat.consentStatus && true === usnat.consentStatus.rejectedAny),
			expires:      dates.length ? Math.min(...dates) : null,
		};
	} catch (e) {
		return null;
	}
}

/**
 * The choice made in RevIQ's consent dialog (RevIQ split only).
 *
 * Same storage as maiPubReviqOptedOut(), but tells "no choice made" apart from
 * "opted back in", which a saved decision from the other CMP needs.
 *
 * @since TBD
 *
 * @return {string|null} 'out', 'in', or null with no choice on record.
 */
function maiPubReviqDialogChoice() {
	if ('reviq' !== displayMode) {
		return null;
	}

	try {
		const stored = JSON.parse(localStorage.getItem('c15t') || 'null');

		if (!stored || !stored.consents || 'boolean' !== typeof stored.consents.marketing) {
			return null;
		}

		return stored.consents.marketing ? 'in' : 'out';
	} catch (e) {
		return null;
	}
}

/**
 * Whether the browser's time zone is plausibly North American.
 *
 * The consent cookie remembers the visitor's country from their last visit. A
 * visitor who has since traveled to Europe should wait for the CMP rather than be
 * treated as American, and the time zone is the one location signal available
 * before geo.js answers. No time zone at all is not evidence either way.
 *
 * @since TBD
 *
 * @return {boolean}
 */
function maiPubTimezoneLooksNorthAmerican() {
	try {
		const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
		return !zone || /^(America|US|Canada|Pacific)\//.test(zone);
	} catch (e) {
		return true;
	}
}

/**
 * Reads the consent cookie.
 *
 * @since TBD
 *
 * @return {object|null} The saved decision, or null with none (or an unreadable one).
 */
function maiPubReadConsentCookie() {
	try {
		const match = document.cookie.match(new RegExp(`(?:^|;)\\s*${CONSENT_COOKIE}=([^;]*)`));

		if (!match) {
			return null;
		}

		const saved = JSON.parse(decodeURIComponent(match[1]));

		if (!saved || 1 !== saved.v || (0 !== saved.con && 1 !== saved.con)) {
			return null;
		}

		return saved;
	} catch (e) {
		return null;
	}
}

/**
 * Saves the current consent decision to the consent cookie.
 *
 * Runs with every real CMP answer (see maiPubMarkConsentResolved()), from
 * SourcePoint or from RevIQ's CMP, including a choice changed later in either
 * dialog. It holds the decision, where it applied and which CMP made it, not an
 * identifier, so it is written whether or not consent was granted: an opt-out has
 * to be remembered too.
 *
 * Expires with SourcePoint's own record when that is sooner than a year.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubWriteConsentCookie() {
	if ('boolean' !== typeof maiPubSessionData.hasConsent) {
		return;
	}

	const geo   = window.maiPubGeo && window.maiPubGeo.country ? window.maiPubGeo : (maiPubSessionData.geo || {});
	const saved = {
		v:     1,
		cmp:   'reviq' === displayMode ? 'reviq' : 'sourcepoint',
		con:   maiPubSessionData.hasConsent ? 1 : 0,
		gdpr:  true === maiPubSessionData.gdprApplies ? 1 : 0,
		usnat: true === maiPubSessionData.usnatApplies ? 1 : 0,
		c:     String(geo.country || '').toUpperCase(),
		r:     String(geo.region || '').toUpperCase(),
		at:    Date.now(),
	};

	const sourcepoint = 'sourcepoint' === saved.cmp ? maiPubSourcepointSavedConsent() : null;
	const expires     = Math.min(Date.now() + CONSENT_COOKIE_MAX_MS, sourcepoint && sourcepoint.expires ? sourcepoint.expires : Infinity);

	try {
		document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(saved))};path=/;expires=${new Date(expires).toUTCString()};SameSite=Lax;Secure`;
		maiPubLog('cmp: consent decision saved to the consent cookie.', saved);
	} catch (e) {
		maiPubLog('cmp: could not save the consent cookie.', e);
	}
}

/**
 * Applies a consent decision saved earlier, so this pageview does not wait on the CMP.
 *
 * In order: the session passport (an earlier pageview in this tab), then the consent
 * cookie (an earlier visit). The CMP still loads and still reports; a different
 * answer updates the passport and the cookie.
 *
 * The cookie only counts in North America, where US privacy is opt-out. Under GDPR
 * the bidders need the CMP's consent string, so an EU visitor still waits for the
 * CMP; so does a visitor whose time zone has left the Americas.
 *
 * In the RevIQ split the dialog choice is read again from RevIQ's own storage, so a
 * choice made on the previous pageview applies now, and an opt-out saved by
 * SourcePoint carries over until the visitor chooses in RevIQ's dialog. Under GDPR
 * the RevIQ split still waits for consent.ready(): its answer can change within a
 * pageview, after the passport was written.
 *
 * @since TBD
 *
 * @return {boolean} Whether a saved decision was applied.
 */
function maiPubApplySavedConsent() {
	let saved = null;

	if (true === maiPubSessionData.consentResolved && maiPubSessionData.pageCount > 1) {
		saved = {
			from:         'session',
			hasConsent:   maiPubSessionData.hasConsent,
			gdprApplies:  true === maiPubSessionData.gdprApplies,
			usnatApplies: true === maiPubSessionData.usnatApplies,
		};
	} else {
		const cookie = maiPubReadConsentCookie();

		if (cookie && !cookie.gdpr && ('US' === cookie.c || 'CA' === cookie.c) && maiPubTimezoneLooksNorthAmerican()) {
			saved = {
				from:         'cookie',
				hasConsent:   1 === cookie.con,
				gdprApplies:  false,
				usnatApplies: 1 === cookie.usnat,
			};
		} else if (cookie) {
			maiPubLog('CMP: consent cookie found, but not for North America (or the time zone is elsewhere) — waiting for the CMP.', cookie);
		}
	}

	if (!saved || 'boolean' !== typeof saved.hasConsent) {
		return false;
	}

	if ('reviq' === displayMode && saved.gdprApplies) {
		maiPubLog('CMP: RevIQ bucket under GDPR — waiting for reviq.consent.ready().');
		return false;
	}

	let consent = saved.hasConsent;
	let usnat   = saved.usnatApplies;

	if ('reviq' === displayMode) {
		const choice = maiPubReviqDialogChoice();

		if (choice) {
			consent = 'in' === choice;
			usnat   = 'out' === choice;
		}
	}

	hasConsent = consent;

	const update = { hasConsent: consent, gdprApplies: saved.gdprApplies, usnatApplies: usnat };

	// A real answer from an earlier visit, so it stands as this session's decision
	// until the CMP reports again.
	if ('cookie' === saved.from) {
		update.source          = 'cookie';
		update.consentResolved = true;
	}

	maiPubSaveSessionData(update);
	maiPubLog(`CMP: Reusing the consent decision saved ${'session' === saved.from ? 'earlier this session' : 'on an earlier visit (consent cookie)'} — skipping the wait.`, { hasConsent: consent });
	markCMPAsReady();

	return true;
}

/**
 * Settles consent from the visitor's region when nothing was saved, so a North
 * American visitor does not wait on the CMP.
 *
 * US privacy is opt-out, so with no opt-out on record there is nothing to wait
 * for. On record means the CMP's own saved choice: in the RevIQ split the dialog
 * choice, applied directly; in the SourcePoint split a past rejection, or a record
 * GDPR applies to, waits for SourcePoint to read it. Not a resolved decision: the
 * CMP still answers this pageview, and its answer is what gets saved.
 *
 * Outside North America, or with no geo yet, this does nothing and the CMP is
 * awaited as before.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubSettleConsentFromRegion() {
	if (true === cmpConsentProcessed || 'na' !== maiPubGetGeoRegion()) {
		return;
	}

	let consent = true;

	if ('reviq' === displayMode) {
		consent = 'out' !== maiPubReviqDialogChoice();
	} else {
		const sourcepoint = maiPubSourcepointSavedConsent();

		if (sourcepoint && (sourcepoint.gdprApplies || (sourcepoint.usnatApplies && sourcepoint.rejectedAny))) {
			maiPubLog('CMP: North America, but SourcePoint has a saved choice to read — waiting for SourcePoint.', sourcepoint);
			return;
		}
	}

	hasConsent = consent;

	// An opt-out made in RevIQ's dialog is a US opt-out; the cn key reports it as usnat_out.
	maiPubSaveSessionData('reviq' === displayMode ? { hasConsent: consent, usnatApplies: !consent } : { hasConsent: consent });

	maiPubLog(`CMP: North America with no saved decision — not waiting for the CMP (hasConsent=${consent}).`);
	markCMPAsReady();
}

/**
 * Preloads RevIQ's loader in the RevIQ split.
 *
 * The loader goes in at DOMContentLoaded, once the slot divs exist; this starts
 * its download from <head> instead. It is RevIQ's CMP as well as its ad stack, so
 * it loads before consent in every region anyway. crossorigin matches the tag
 * injectScript() adds, or the browser would download it twice.
 *
 * Magnite's wrapper needs no preload: once consent is settled it is injected
 * straight away, and outside North America it waits for consent.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubPreloadScripts() {
	if ('reviq' !== displayMode || !maiPubAdsVars.domain) {
		return;
	}

	const href = `//js.rev.iq/${maiPubAdsVars.domain}`;

	if (document.querySelector(`link[rel="preload"][href="${href}"]`)) {
		return;
	}

	const link       = document.createElement('link');
	link.rel         = 'preload';
	link.as          = 'script';
	link.href        = href;
	link.crossOrigin = 'anonymous';
	document.head.appendChild(link);

	maiPubLog(`Preload: RevIQ loader ${href}`);
}

/**
 * Debug only: logs when SourcePoint was ready on this pageview and where its time went.
 *
 * Runs once, from the first SourcePoint consent event. One "CMP timing (SourcePoint)"
 * line: its script, site settings (meta-data) and message check (messages) on the
 * network, and the rest waiting on the page. Times are ms after navigation, to line up
 * with DevTools. SourcePoint sends no Timing-Allow-Origin, so only start and end times
 * can be read; a request that took under 20ms came from the browser cache.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubLogSourcepointTiming() {
	if ( ( ! debug && ! log ) || null !== maiPubTiming.spReady ) {
		return;
	}

	maiPubTiming.spReady = performance.now();

	try {
		const ms       = (t) => Math.round(t);
		const ready    = maiPubTiming.spReady;
		const ourStart = timestamp - performance.timeOrigin;
		const entries  = performance.getEntriesByType('resource');
		const find     = (re) => entries.find((r) => re.test(r.name));
		const script   = find(/privacy-mgmt\.com\/unified\/wrapperMessaging/);
		const meta     = find(/privacy-mgmt\.com\/wrapper\/v2\/meta-data/);
		const messages = find(/privacy-mgmt\.com\/wrapper\/v2\/messages/);

		if (!script) {
			maiPubLog(`CMP timing (SourcePoint): ready ${ms(ready)}ms after navigation (our script started at ${ms(ourStart)}ms); its script is not in this page's resource timings.`);
			return;
		}

		const net     = (r) => (r ? r.responseEnd - r.startTime : 0);
		const part    = (label, r) => (r ? `${label} ${ms(net(r))}ms${net(r) < 20 ? ' (browser cache)' : ''}` : `${label} not requested`);
		const network = net(script) + net(meta) + net(messages);
		const total   = ready - script.startTime;

		maiPubLog(`CMP timing (SourcePoint): ready ${ms(ready)}ms after navigation, ${ms(total)}ms after its script was requested (our script started at ${ms(ourStart)}ms). Network ${ms(network)}ms: ${part('script', script)}, ${part('site settings', meta)}, ${part('message check', messages)}. Waiting on the page: ${ms(total - network)}ms.`);
	} catch (e) {
		maiPubLog('CMP timing (SourcePoint): could not read resource timings.', e);
	}
}

/**
 * Debug only: logs how long Prebid held the first auction after we asked for bids.
 *
 * The hold is split into the wait for SourcePoint (Prebid waits up to its consent
 * timeout for the CMP) and the rest, which is ID5 (up to userSync.auctionDelay). A
 * "CMP timing (ID5)" line follows once ID5 has settled.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubWatchPrebidTiming() {
	if ( ( ! debug && ! log ) || typeof pbjs === 'undefined' || typeof pbjs.onEvent !== 'function' ) {
		return;
	}

	let logged = false;

	pbjs.onEvent('auctionInit', () => {
		if (logged) {
			return;
		}

		logged = true;

		try {
			const ms     = (t) => Math.round(t);
			const events = typeof pbjs.getEvents === 'function' ? pbjs.getEvents() : [];
			const asked  = events.find((e) => 'requestBids' === e.eventType);
			const init   = events.find((e) => 'auctionInit' === e.eventType);

			if (asked && init) {
				const held   = init.elapsedTime - asked.elapsedTime;
				const sp     = maiPubTiming.spReady;
				const spWait = null === sp ? held : Math.max(0, Math.min(sp, init.elapsedTime) - asked.elapsedTime);

				maiPubLog(`CMP timing (Prebid): bids asked for at ${ms(asked.elapsedTime)}ms after navigation, first auction started at ${ms(init.elapsedTime)}ms. Held ${ms(held)}ms: ${ms(spWait)}ms waiting for SourcePoint${null === sp ? ' (not ready yet)' : ''}, then ${ms(held - spWait)}ms for ID5 and the rest (auctionDelay ${prebidAuctionDelay}ms).`);
			}
		} catch (e) {
			maiPubLog('CMP timing (Prebid): could not read Prebid events.', e);
		}

		// Give an ID5 fetch time to finish and save, so the "after" state is final.
		setTimeout(maiPubLogId5Timing, 1500);
	});
}

/**
 * Debug only: logs the bidders an auction went ahead without, and each Prebid bid that
 * won in GAM.
 *
 * "Prebid timeout" comes from Prebid's bidTimeout event: the bid requests still
 * unanswered when an auction reached its timeout, named per bidder with their ad units.
 * The bids-back callback can't say which: Magnite's wrapper passes it no timedOut flag.
 *
 * "Prebid won" comes from bidWon, which fires when a bid's creative renders, i.e. the
 * bid beat everything else in GAM. GAM reports the render as the Prebid line item it
 * went through, so the "Fill source" line before it says gam-line-item; this line names
 * the bidder and price. It replaces the "Prebid winning bids" line, which was printed
 * before GAM answered and so always said none.
 *
 * Hooks whichever Prebid is on the page: Magnite's when we configure it, RevIQ's once
 * its loader has put it there. Events from before the hook are read back from Prebid's
 * own log.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubWatchPrebidOutcomes() {
	if ((!debug && !log) || _prebidOutcomesWatched) {
		return;
	}

	_prebidOutcomesWatched = true;

	const timeouts = {}; // auctionId -> the auction's timeout (ms), from auctionInit.
	let tries      = 0;

	const handle = (type, args) => {
		if ('auctionInit' === type) {
			if (args && args.auctionId) {
				timeouts[args.auctionId] = args.timeout;
			}
		} else if ('bidTimeout' === type) {
			maiPubLogBidTimeouts(args, timeouts);
		} else if ('bidWon' === type) {
			maiPubLogPrebidWin(args);
		}
	};

	const hook = () => {
		const pb = window.pbjs;

		if (!pb || typeof pb.onEvent !== 'function') {
			if (++tries < 40) {
				setTimeout(hook, 250);
			}
			return;
		}

		(typeof pb.getEvents === 'function' ? pb.getEvents() || [] : []).forEach((e) => handle(e.eventType, e.args));
		['auctionInit', 'bidTimeout', 'bidWon'].forEach((type) => pb.onEvent(type, (args) => handle(type, args)));
	};

	hook();
}

/**
 * Debug only: one "Prebid timeout" line for an auction's unanswered bid requests.
 *
 * @since TBD
 *
 * @param {Object[]} bids     The bidTimeout event's bid requests (bidder, adUnitCode, auctionId).
 * @param {Object}   timeouts auctionId -> the auction's timeout in ms.
 * @return {void}
 */
function maiPubLogBidTimeouts(bids, timeouts) {
	const list = (Array.isArray(bids) ? bids : [bids]).filter((b) => b && (b.bidder || b.bidderCode));

	if (!list.length) {
		return;
	}

	const byBidder = {};
	list.forEach((b) => {
		const units = byBidder[b.bidder || b.bidderCode] = byBidder[b.bidder || b.bidderCode] || [];
		if (b.adUnitCode && -1 === units.indexOf(b.adUnitCode)) {
			units.push(b.adUnitCode);
		}
	});

	const auctionId = list[0].auctionId;
	const ms        = 'number' === typeof timeouts[auctionId] ? timeouts[auctionId] : list[0].timeout;
	const bidders   = Object.keys(byBidder);
	const named     = bidders.map((b) => byBidder[b].length ? `${b} (${byBidder[b].join(', ')})` : b).join(', ');

	maiPubLog(`Prebid timeout: ${bidders.length} ${1 === bidders.length ? 'bidder' : 'bidders'} didn't answer ${'number' === typeof ms ? `within the auction's ${ms}ms` : 'before the auction timed out'}, so it went ahead without them: ${named}.`);
}

/**
 * Debug only: one "Prebid won" line when a Prebid bid's creative renders.
 *
 * @since TBD
 *
 * @param {Object} bid The bidWon event's bid.
 * @return {void}
 */
function maiPubLogPrebidWin(bid) {
	if (!bid) {
		return;
	}

	const code   = bid.adUnitCode || '';
	const slotId = maiPubSlotIdForPrebidCode(code) || code;
	const fill   = maiPubLastFill[slotId];
	const cpm    = 'number' === typeof bid.cpm ? bid.cpm.toFixed(2) : null;
	const price  = null === cpm ? 'price n/a' : (bid.currency && 'USD' !== bid.currency ? `${cpm} ${bid.currency}` : `$${cpm}`);
	const size   = bid.size || (bid.width && bid.height ? `${bid.width}x${bid.height}` : '');
	const via    = fill && fill.lineItemId ? `, through GAM line item ${fill.lineItemId} (advertiser ${fill.advertiserId || 'n/a'})` : '';

	maiPubLog(`Prebid won ${slotId}: ${bid.bidderCode || bid.bidder || 'unknown bidder'} ${price}${size ? ` ${size}` : ''}${bid.mediaType ? ` ${bid.mediaType}` : ''}${via}.`);
}

/**
 * The slot div id a Prebid ad unit code belongs to, or ''. Magnite's codes are the div
 * ids; RevIQ's may be the GAM path (see maiPubPrebidAdUnitCodesForGptSlot()), which
 * several slots can share (leaderboard, leaderboard-2...): then '' rather than a guess.
 *
 * @since TBD
 *
 * @param {string} code
 * @return {string}
 */
function maiPubSlotIdForPrebidCode(code) {
	if (!code) {
		return '';
	}

	if (document.getElementById(code)) {
		return code;
	}

	if (!window.googletag || typeof googletag.pubads !== 'function') {
		return '';
	}

	const slots = googletag.pubads().getSlots().filter((s) => -1 !== maiPubPrebidAdUnitCodesForGptSlot(s).indexOf(code));

	return 1 === slots.length ? slots[0].getSlotElementId() : '';
}

/**
 * The ID5 entry from our userSync config, or null.
 *
 * @since TBD
 *
 * @return {Object|null}
 */
function maiPubId5Config() {
	const ids = maiPubAdsVars.id5UserSync && Array.isArray(maiPubAdsVars.id5UserSync.userIds) ? maiPubAdsVars.id5UserSync.userIds : [];

	return ids.find((u) => u && 'id5Id' === u.name) || null;
}

/**
 * Debug only: ID5's ID as Prebid saved it in localStorage.
 *
 * Prebid reuses the saved ID ("using cached ID") unless none is saved, it is older than
 * storage.refreshInSeconds, it holds no response for our partner, or Prebid's consent
 * fingerprint (<name>_cst) differs from the one saved with it. Otherwise it asks ID5
 * again (ID5 may answer from its own cache), and the first auction waits for the answer,
 * up to auctionDelay. A saved ID is in that auction either way.
 *
 * @since TBD
 *
 * @return {Object|null} { cached, partners, last, cst, exp, sharedOurs }, or null if ID5 is
 *                       not configured for localStorage or storage can't be read. exp: the
 *                       expiry Prebid saved with the ID, as stored. sharedOurs: ID5's shared
 *                       "id5id" key still holds an ID for our partner, saved before we moved
 *                       to a key of our own.
 */
function maiPubReadId5Saved() {
	const cfg = maiPubId5Config();

	if (!cfg || !cfg.storage || 'html5' !== cfg.storage.type) {
		return null;
	}

	const name     = cfg.storage.name || 'id5id';
	const partners = (raw) => {
		const saved = raw ? JSON.parse(decodeURIComponent(raw)) : null;

		return saved && saved.pbjs && 'object' === typeof saved.pbjs ? Object.keys(saved.pbjs) : [];
	};

	try {
		const raw     = localStorage.getItem(name);
		const last    = localStorage.getItem(`${name}_last`);
		const partner = cfg.params && cfg.params.partner ? String(cfg.params.partner) : '';
		let sharedOurs = false;

		if (partner) {
			try {
				sharedOurs = -1 !== partners(localStorage.getItem('id5id')).indexOf(partner);
			} catch (e) {}
		}

		return { cached: !!raw, partners: partners(raw), last: last ? Date.parse(last) : null, cst: localStorage.getItem(`${name}_cst`), exp: localStorage.getItem(`${name}_exp`), sharedOurs };
	} catch (e) {
		return null;
	}
}

/**
 * Debug only: logs whether Prebid could reuse ID5's saved ID on this pageview, and if
 * not, why it asked ID5 again.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubLogId5Timing() {
	const before = maiPubTiming.id5Saved;
	const after  = maiPubReadId5Saved();

	if (!before || !after) {
		return;
	}

	const cfg     = maiPubId5Config() || {};
	const partner = cfg.params && cfg.params.partner ? String(cfg.params.partner) : '';
	const refresh = cfg.storage && 'number' === typeof cfg.storage.refreshInSeconds ? cfg.storage.refreshInSeconds : null;
	const age     = before.last ? Math.round((Date.now() - before.last) / 1000) : null;
	let reason;

	if (!before.cached && before.sharedOurs) {
		// The shared key's ID may be ours from before we moved, or another ID5 integration's
		// that uses our partner too (Kargo's writes partners 562 and 1846 on some sites).
		reason = `no ID is saved under our own key (${cfg.storage.name}) yet, so Prebid asked ID5 for one. ID5's shared "id5id" key has one for our partner, saved before we moved to our own key or by another ID5 integration on the page`;
	} else if (!before.cached) {
		reason = 'no ID was saved (first visit, or storage cleared), so Prebid asked ID5 for one';
	} else if (partner && -1 === before.partners.indexOf(partner)) {
		reason = `the saved ID has no response for partner ${partner}, so Prebid asked ID5 for one`;
	} else if (null !== age && null !== refresh && age > refresh) {
		reason = `the saved ID was ${age}s old (refresh after ${refresh}s), so Prebid asked ID5 again`;
	} else if (before.cst !== after.cst) {
		reason = `Prebid's consent fingerprint changed (${before.cst} -> ${after.cst}), so Prebid asked ID5 again`;
	} else {
		reason = 'the saved ID was reused';
	}

	maiPubLog(`CMP timing (ID5): ${reason}. Saved before this pageview: ${before.cached ? `yes, ${null === age ? '?' : age}s old, partners [${before.partners.join(', ')}]` : 'none'}; consent fingerprint before ${before.cst}, after ${after.cst}.`);

	// Whose ID5 settings Prebid used. Magnite's (its DM parameter set) win when Prebid
	// starts ID5 before our config arrives. They save the ID for a different number of
	// days, or, with Magnite's default parameter set, under ID5's shared key.
	const expires = cfg.storage && 'number' === typeof cfg.storage.expires ? cfg.storage.expires : null;
	const days    = after.exp && after.exp !== before.exp ? Math.round((Date.parse(after.exp) - Date.now()) / 864e5) : null;
	let theirs    = '';

	if (maiPubTiming.sharedByWrapper) {
		theirs = 'it saved the ID under ID5\'s shared "id5id" key';
	} else if (null !== days && null !== expires && Math.abs(days - expires) > 1) {
		theirs = `it saved the ID for ${days} days, ours say ${expires}`;
	}

	if (theirs) {
		const sp   = maiPubTiming.spReady;
		const wl   = maiPubTiming.wrapperLoaded;
		const when = null !== sp && null !== wl && sp < wl
			? ` SourcePoint was ready at ${Math.round(sp)}ms, before Magnite's wrapper loaded at ${Math.round(wl)}ms, so Prebid started ID5 inside Magnite's own setup.`
			: ' That happens when SourcePoint answers before Magnite\'s wrapper loads: Prebid starts ID5 inside Magnite\'s own setup.';

		maiPubLog(`CMP timing (ID5): Prebid used Magnite's ID5 settings this pageview, not ours (${theirs}).${when} pd came from window.mai_publisher_pd (${window.mai_publisher_pd ? 'set' : 'not set'}).`);
	}
}

/**
 * Debug only: logs the ID5 settings RevIQ's Prebid uses, next to ours.
 *
 * RevIQ configures ID5 in its own loader (partner, storage name) and builds pd from
 * what setUid gave it. This shows whether it has picked up ours: partner, storage name,
 * and pd from window.mai_publisher_pd. Read at RevIQ's first auction, once its config
 * is in place.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubLogReviqId5() {
	if (!debug && !log) {
		return;
	}

	let tries = 0;

	const hook = () => {
		if (!window.pbjs || typeof window.pbjs.onEvent !== 'function') {
			if (++tries < 40) {
				setTimeout(hook, 250);
			}
			return;
		}

		let logged = false;

		window.pbjs.onEvent('auctionInit', () => {
			if (logged) {
				return;
			}

			logged = true;

			try {
				const userIds = (window.pbjs.getConfig('userSync') || {}).userIds || [];
				const theirs  = userIds.find((u) => u && 'id5Id' === u.name);
				const ours    = maiPubId5Config();

				if (!theirs) {
					maiPubLog('ID5 (RevIQ): RevIQ\'s Prebid has no ID5 configured.');
					return;
				}

				const tp      = theirs.params && theirs.params.partner ? String(theirs.params.partner) : '?';
				const tn      = theirs.storage && theirs.storage.name ? theirs.storage.name : '?';
				const op      = ours && ours.params && ours.params.partner ? String(ours.params.partner) : '?';
				const on      = ours && ours.storage && ours.storage.name ? ours.storage.name : '?';
				const tpd     = theirs.params ? theirs.params.pd : undefined;
				const ourPd   = window.mai_publisher_pd;
				const pdState = 'string' === typeof tpd && tpd
					? (ourPd && tpd === ourPd ? 'ours (window.mai_publisher_pd)' : 'RevIQ\'s own')
					: 'none';
				const same    = tp === op && tn === on && 0 === pdState.indexOf('ours');

				maiPubLog(`ID5 (RevIQ): RevIQ's Prebid uses partner ${tp}, saves under "${tn}", pd ${pdState}. Ours: partner ${op}, "${on}", pd from window.mai_publisher_pd (${ourPd ? 'set' : 'not set'}). ${same ? 'RevIQ is using our ID5 settings.' : 'RevIQ is not using our ID5 settings: they are set in RevIQ\'s config.'}`);
			} catch (e) {
				maiPubLog('ID5 (RevIQ): could not read RevIQ\'s Prebid config.', e);
			}
		});
	};

	hook();
}

/**
 * Debug only: in the RevIQ split, logs when each ad was asked for and rendered relative
 * to when its slot came on screen, like the "Lazy ad timing" line in our own split.
 *
 * RevIQ requests these ads itself, so the steps come from RevIQ's Prebid (auction
 * started, bids back) and from GPT's own events (GAM asked, rendered). "On screen" is
 * the slot's first pixel in view, as in our line. RevIQ's loader starts a slot's auction
 * only once half of the slot is visible (seen on lamag.com), so the line shows how long
 * a slot sits empty on screen. Out-of-page slots are left out by the caller: they take
 * no space on the page.
 *
 * @since TBD
 *
 * @param {Array} units [{ el, slug }]: the slot divs RevIQ fills, with our slug (RevIQ
 *                      gives each div an id of its own).
 *
 * @return {void}
 */
function maiPubWatchReviqAdTiming(units) {
	if ((!debug && !log) || !units.length || !window.IntersectionObserver) {
		return;
	}

	// Slot div -> what happened to its first ad, in ms after navigation.
	const timing = new Map();

	units.forEach(({ el, slug }) => timing.set(el, { slug, seen: null, speed: 0, auction: null, bids: null, gam: null, rendered: null, empty: false, logged: false }));

	// The slot for an id from RevIQ's Prebid or GPT: the div itself, or the one around
	// (or inside) the element with that id.
	const slotFor = (id) => {
		const node = id ? document.getElementById(id) : null;

		if (!node) {
			return undefined;
		}

		return timing.get(node) || timing.get(node.closest('.mai-ad-unit')) || timing.get(node.querySelector('.mai-ad-unit'));
	};

	// First time only: later auctions and refreshes don't count.
	const note = (t, step, at = performance.now()) => {
		if (!t || null !== t[step]) {
			return;
		}

		t[step] = at;
		maiPubLogReviqAdTiming(t);
	};

	// Scroll speed when a slot comes on screen. Our lazy loader keeps these samples in
	// our own split; it doesn't run in this one.
	window.addEventListener('scroll', maiPubNoteScroll, { passive: true });

	const seen = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			const t = timing.get(entry.target);

			if (!entry.isIntersecting || !t) {
				return;
			}

			seen.unobserve(entry.target);
			t.speed = maiPubScrollSpeed();
			note(t, 'seen');
		});
	}, { threshold: 0 });

	timing.forEach((t, el) => seen.observe(el));

	window.googletag.cmd.push(() => {
		googletag.pubads().addEventListener('slotRequested', (event) => note(slotFor(event.slot.getSlotElementId()), 'gam'));
		googletag.pubads().addEventListener('slotRenderEnded', (event) => {
			const t = slotFor(event.slot.getSlotElementId());

			if (t && null === t.rendered) {
				t.empty = !!event.isEmpty;
			}

			note(t, 'rendered');
		});
	});

	// RevIQ's Prebid loads with its loader. Auctions that ran before we hooked in are
	// read back from its event log, with the time Prebid logged them.
	const auction = (step, args, at) => ((args && args.adUnitCodes) || []).forEach((code) => note(slotFor(code), step, at));
	let tries     = 0;

	const hook = () => {
		const pb = window.pbjs;

		if (!pb || typeof pb.onEvent !== 'function') {
			if (++tries < 40) {
				setTimeout(hook, 250);
			}
			return;
		}

		(typeof pb.getEvents === 'function' ? pb.getEvents() || [] : []).forEach((e) => {
			if ('auctionInit' === e.eventType) {
				auction('auction', e.args, e.elapsedTime);
			} else if ('auctionEnd' === e.eventType) {
				auction('bids', e.args, e.elapsedTime);
			}
		});

		pb.onEvent('auctionInit', (args) => auction('auction', args));
		pb.onEvent('auctionEnd', (args) => auction('bids', args));
	};

	hook();
}

/**
 * Debug only: logs a RevIQ slot's first ad once it has both rendered and come on screen.
 * Each step is timed from the moment the slot came on screen; minus means before it.
 *
 * @since TBD
 *
 * @param {Object} t The slot's timing, from maiPubWatchReviqAdTiming().
 *
 * @return {void}
 */
function maiPubLogReviqAdTiming(t) {
	if (t.logged || null === t.seen || null === t.rendered) {
		return;
	}

	t.logged = true;

	const ms    = (x) => Math.round(x);
	const rel   = (x) => (null === x ? '?' : (x >= t.seen ? `+${ms(x - t.seen)}ms` : `-${ms(t.seen - x)}ms`));
	const speed = Math.abs(t.speed) < 1 ? 'not scrolling' : `scrolling ${ms(Math.abs(t.speed))}px/s ${t.speed > 0 ? 'down' : 'up'}`;
	let wait;

	if (t.empty) {
		wait = 'GAM returned no ad';
	} else if (t.seen <= t.rendered) {
		wait = `it was on screen with no ad for ${ms(t.rendered - t.seen)}ms`;
	} else {
		wait = `the ad was ready ${ms(t.seen - t.rendered)}ms before it came into view`;
	}

	maiPubLog(`RevIQ ad timing (${t.slug}): came on screen ${ms(t.seen)}ms after navigation (${speed}). From then (minus = before): RevIQ's auction started ${rel(t.auction)}, bids back ${rel(t.bids)}, GAM asked ${rel(t.gam)}, ${t.empty ? 'came back empty' : 'rendered'} ${rel(t.rendered)}: ${wait}.`);
}

/**
 * Debug only: logs who writes ID5's shared localStorage keys, "id5id" and "id5id_cst",
 * and our own key's consent fingerprint ("<our key>_cst").
 *
 * Prebid's ID5 module suggests the storage name "id5id", so other ID5 integrations on
 * the page (other Prebid setups, often inside ads) save their IDs there. Each write stamps
 * that integration's consent fingerprint into id5id_cst. While we shared the key, that
 * made Prebid ask ID5 again on our next pageview and hold the first auction. We now save
 * under our own name, so these writes no longer touch our ID; this only names the writer.
 *
 * Our own fingerprint is what Prebid compares on the next pageview. Prebid writes it
 * once as it starts; a later write, or one from another tab, sets what the next pageview
 * is compared with, so it is named too.
 *
 * A write from this window is reported with the scripts that made it. A write from
 * another frame or tab on this site arrives as a storage event, with its page address.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubWatchId5Keys() {
	if (!debug && !log) {
		return;
	}

	const keys    = ['id5id', 'id5id_cst'];
	const cfg     = maiPubId5Config();
	const ours    = cfg && cfg.storage && cfg.storage.name ? cfg.storage.name : '';
	const ourCst  = ours && 'id5id' !== ours ? `${ours}_cst` : ''; // Already watched when we use the shared key.
	const watched = ourCst ? keys.concat(ourCst) : keys;
	const note    = 'id5id' === ours ? 'Our ID5 also saves under this key.' : `We save ID5 under ${ours || 'our own key'}, so this doesn't affect our ID.`;
	const at      = () => `${Math.round(performance.now())}ms after navigation`;
	let ourWrites = 0;

	const shown = (key, value) => {
		if ('id5id' !== key) {
			return String(value);
		}

		try {
			const saved = JSON.parse(decodeURIComponent(value));

			return saved && saved.pbjs && 'object' === typeof saved.pbjs ? `an ID for partners [${Object.keys(saved.pbjs).join(', ')}]` : 'an ID in another format (no per-partner responses)';
		} catch (e) {
			return 'a value that is not an ID5 ID';
		}
	};

	// One line per write: a shared key, or our fingerprint with how many times it was written.
	const line = (key, value, by, when) => {
		if (key !== ourCst) {
			return `ID5 shared key: ${key} set to ${shown(key, value)} by ${by} (${when}). ${note}`;
		}

		ourWrites++;

		const which = 1 === ourWrites ? 'The next pageview is compared with this' : `Written ${ourWrites} times this pageview; the next pageview is compared with the last`;

		return `ID5 our key: ${key} set to ${value} by ${by} (${when}). ${which}: if Prebid's consent fingerprint differs then, it asks ID5 again.`;
	};

	let store;

	try {
		store = window.localStorage;
	} catch (e) {
		return;
	}

	// Writes from this window: name the scripts on the stack, other than this one.
	const native = Storage.prototype.setItem;

	Storage.prototype.setItem = function(key, value) {
		const result = native.apply(this, arguments);

		try {
			if (this === store && -1 !== watched.indexOf(key)) {
				const urls    = ((new Error()).stack || '').match(/https?:\/\/[^\s)]+?(?=:\d+:\d+)/g) || [];
				const scripts = urls.map((u) => u.split('?')[0]).filter((u, i, all) => !/\/mai-publisher-ads\.js$/.test(u) && i === all.indexOf(u));
				const by      = scripts.length ? scripts.slice(0, 3).join(' < ') : 'a script with no file (inline or eval)';

				// Magnite's wrapper writing the shared key means Prebid used Magnite's ID5
				// settings (default parameter set), not ours. See maiPubLogId5Timing().
				if (-1 !== keys.indexOf(key) && 'id5id' !== ours && scripts.some((u) => /rubiconproject\.com\//.test(u))) {
					maiPubTiming.sharedByWrapper = true;
				}

				maiPubLog(line(key, value, by, at()));
			}
		} catch (e) {}

		return result;
	};

	// Writes from other frames and tabs on this site.
	window.addEventListener('storage', (e) => {
		if (e.storageArea === store && -1 !== watched.indexOf(e.key) && null !== e.newValue) {
			maiPubLog(line(e.key, e.newValue, 'another frame or tab', `${e.url || 'unknown page'}, ${at()}`));
		}
	});
}

/**
 * Persists the current consent state and marks it as genuinely resolved.
 *
 * "Resolved" means a real CMP decision was captured — not the optimistic default
 * and not the timeout fallback. Only a resolved decision is safe to reuse on the
 * next pageview (see the fast path in the init block), because a guess cached as
 * fact would let bidders initialize on an assumption for the rest of the session.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubMarkConsentResolved() {
    // Never mark a non-decision as resolved — the whole point of this flag is that
    // the next pageview can trust it.
    if (typeof maiPubSessionData.hasConsent !== 'boolean') {
        maiPubLog('cmp: no boolean consent decision to resolve — not marking.');
        return;
    }

    const state = { consentResolved: true };

    CONSENT_PASSPORT_KEYS.forEach((key) => {
        if (undefined !== maiPubSessionData[key]) {
            state[key] = maiPubSessionData[key];
        }
    });

    maiPubSessionData.consentResolved = true;

    maiPubSaveSessionData(state);

    // Covers a mid-pageview change (privacy manager), where markCMPAsReady() has
    // already run and short-circuits.
    maiPubPersistIdentityCookies();

    // Kept across visits, so the next new tab or visit doesn't wait on the CMP.
    maiPubWriteConsentCookie();

    // An opt-out outside GDPR that arrives after we went ahead (on a saved decision,
    // the region default or the timeout fallback): the GAM requests still to come
    // this pageview go out with restricted data processing.
    if (true === cmpConsentProcessed && false === maiPubSessionData.hasConsent && true !== maiPubSessionData.gdprApplies && window.googletag && googletag.cmd) {
        googletag.cmd.push(() => googletag.pubads().setPrivacySettings({ restrictDataProcessing: true }));
        maiPubLog('cmp: opt-out arrived after consent was settled — restricted data processing on for the rest of this pageview.');
    }

    maiPubLog('cmp: consent decision resolved and persisted:', { hasConsent: maiPubSessionData.hasConsent });
}

/**
 * Strips legacy nested copies of the passport out of passport.consent.
 *
 * maiPubSetConsent used to persist with maiPubSaveSessionData({ consent:
 * maiPubSessionData }), passing the whole passport as its own `consent` key.
 * Because the save deep-merges objects, each CMP event folded another copy of
 * the passport — including the previous copy — into .consent, so the structure
 * nested one level deeper on every event and sessionStorage grew all session.
 *
 * The save is fixed, but a session already in flight carries the accumulated
 * structure and would keep re-serializing it, so prune on load. Only the raw
 * consent strings are kept; every field that belongs at the top level is already
 * there, written directly by maiPubSetConsent.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubPruneNestedConsent() {
    const consent = maiPubSessionData.consent;

    if (!consent || typeof consent !== 'object' || Array.isArray(consent)) {
        return;
    }

    const extras = Object.keys(consent).filter(key => !CONSENT_STRING_KEYS.includes(key));

    if (!extras.length) {
        return;
    }

    const pruned = {};

    CONSENT_STRING_KEYS.forEach((key) => {
        if (undefined !== consent[key]) {
            pruned[key] = consent[key];
        }
    });

    maiPubSessionData.consent = pruned;

    maiPubLog(`Session: pruned ${extras.length} legacy key(s) from passport.consent.`);
}

/**
 * Resolves NA vs non-NA from geo for CMP gating.
 *
 * @return {'na'|'non-na'|'unknown'}
 */
function maiPubGetGeoRegion() {
	const geo = window.maiPubGeo || maiPubSessionData.geo || {};

	if (geo.isNorthAmerica === true || geo.country === 'US' || geo.country === 'CA') {
		return 'na';
	}

	if (geo.isNorthAmerica === false || (geo.country && geo.country !== 'US' && geo.country !== 'CA')) {
		return 'non-na';
	}

	return 'unknown';
}

/**
 * North America for CMP timeout/fallback (unknown geo treated as NA).
 *
 * @return {boolean}
 */
function maiPubIsCmpNorthAmerica() {
	const region = maiPubGetGeoRegion();
	return region === 'na' || region === 'unknown';
}

/**
 * Initializes additional consent-gated vendors after sell-or-share consent is allowed.
 *
 * @since 1.19.7
 */
function initSellOrShareVendors(){
    //call vendors code below

    maiPubLog("cmp: consent allowed, initialize any additional vendors.")
	// todo: I think this is where we would init matomo and GA and the like.

}

/**
 * USNat Consent Check Callback
 *
 * This is called asynchronously by Sourcepoint's USNat API after getUserConsents().
 * Processes the consent response and marks CMP as ready since this completes
 * the USNat consent flow.
 *
 * @param {object} consents - The consents object from Sourcepoint
 * @param {boolean} status - Whether the request was successful
 *
 * @since 1.21.0 - Added CMP readiness tracking
 */
function checkConsent(consents, status) {
	// Check if the request was successful and the consents object is valid
	if (!status || !consents || !consents.categories) {
	  maiPubLog('cmp: Sourcepoint consent check failed: Invalid status or missing consents object.');
	  // Even if the check failed, CMP has fired - mark as ready so we don't block indefinitely
	  markCMPAsReady();
	  maiPubLog('cmp: USNat consent check failed - CMP marked as ready (error case).');
	  return;
	}

	// Find the specific category object for Sale/Sharing (System ID 3)
	// Object.values() turns the categories object into an array we can search
	const saleShareCategory = Object.values(consents.categories).find(category => category.systemId == 3);

	// Check if we found the category and if the user consented to it
	if (saleShareCategory && saleShareCategory.consented === true) {
		maiPubLog('cmp: Consent for Sale or Sharing / Targeted Advertising found (System ID 3)');
		initSellOrShareVendors();
		hasConsent = true;
		maiPubSessionData.hasConsent = true;
	} else {
		maiPubLog('cmp: Consent for Sale or Sharing / Targeted Advertising NOT granted (System ID 3)');
		hasConsent = false;
		maiPubSessionData.hasConsent = false;
	}

	// Persist it. This callback is async — maiPubSetConsent() already returned and
	// saved the optimistic hasConsent:true well before Sourcepoint answered, so
	// without this the real USNat decision only ever lived in memory. An opt-out
	// was lost at the end of the pageview and the next page loaded hasConsent:true
	// from the passport until the CMP re-fired and corrected it.
	maiPubMarkConsentResolved();

	// USNat callback means the US privacy flow completed. Mark CMP ready unless we
	// already know the user is outside NA (then GDPR is the gate).
	const region = maiPubGetGeoRegion();

	if (region === 'non-na') {
		maiPubLog('cmp: USNat consent check completed, but user is NOT in North America - waiting for GDPR before marking CMP ready.');
		return;
	}

	markCMPAsReady();
	maiPubLog('cmp: USNat consent check completed - CMP marked as ready.');
  }

/**
 * Applies RevIQ's consent answer in the RevIQ bucket, where RevIQ's CMP
 * replaces SourcePoint.
 *
 * reviq.consent.ready() resolves with the IAB TCF tcData once no consent law
 * applies (gdprApplies false), or once the visitor accepts or rejects. Under
 * GDPR, consent means purposes 1, 3 and 4: storing on the device, and building
 * and using a profile for personalized ads. That is what hasConsent gates on our
 * side: the PPID cookie, setUid, and personalized requests to our GAM.
 *
 * The answer carries no US state decision, and RevIQ's CMP exposes no IAB
 * __gpp/__uspapi API, so GAM and other tags see none either. Outside GDPR our
 * policy is opt-out: consent unless the visitor opted out in RevIQ's dialog,
 * which we read from RevIQ's own storage (see maiPubReviqOptedOut()). That
 * includes California, where RevIQ's own policy is opt-in.
 *
 * In the EU it resolves at page load, before the visitor answers the banner
 * (eventStatus "tcloaded", empty purposes), so a first pageview reads as no
 * consent. It resolves once per pageview: a choice made later reaches us with
 * the next pageview's answer.
 *
 * @since TBD
 *
 * @param {object} tcData The tcData from reviq.consent.ready().
 *
 * @return {boolean|null} Whether consent was granted, or null with no usable answer.
 */
function maiPubSetReviqConsent(tcData) {
	if (!tcData || 'object' !== typeof tcData) {
		maiPubLog('cmp: RevIQ consent.ready() gave no tcData — the CMP timeout fallback decides consent.', tcData);
		return null;
	}

	const gdprApplies = true === tcData.gdprApplies;
	const consents    = (tcData.purpose && tcData.purpose.consents) || {};
	const optedOut    = !gdprApplies && maiPubReviqOptedOut();
	const granted     = gdprApplies ? [1, 3, 4].every((id) => true === consents[id]) : !optedOut;
	const late        = true === cmpConsentProcessed;

	hasConsent = granted;
	maiPubSessionData.source      = 'cmp';
	maiPubSessionData.hasConsent  = granted;
	maiPubSessionData.gdprApplies = gdprApplies;

	// Lets the cn key report an opt-out as usnat_out rather than npa.
	if (!gdprApplies) {
		maiPubSessionData.usnatApplies = optedOut;
	}

	maiPubLog(`cmp: RevIQ consent ready${late ? ' (consent was already settled)' : ''}: gdprApplies=${gdprApplies}, hasConsent=${granted}${optedOut ? ' (opted out in RevIQ\'s dialog)' : ''}.`);

	// A real answer from RevIQ's CMP, so it is final for this pageview.
	maiPubMarkConsentResolved();
	markCMPAsReady();
	maiPubWireReviqPmLink(gdprApplies);

	return granted;
}

/**
 * Whether the visitor opted out in RevIQ's consent dialog (RevIQ split only).
 *
 * Outside GDPR our policy is opt-out: nobody waits for consent, and an opt-out is
 * honored. RevIQ's CMP stores the dialog choice (Reject All, or the Marketing
 * toggle) but reports it nowhere: consent.ready() only covers GDPR, and there is
 * no __gpp or __uspapi. So it is read from the CMP's own storage, the `c15t`
 * localStorage entry, e.g. {"consents":{"marketing":false,...},...}. Marketing
 * off is an opt-out of targeted advertising. No entry (no choice made), or one
 * that can't be read, is not an opt-out. RevIQ (Lyric) confirmed the entry is
 * stable enough to rely on for now.
 *
 * Global Privacy Control is not treated as an opt-out here.
 *
 * @since TBD
 *
 * @return {boolean}
 */
function maiPubReviqOptedOut() {
	if ('reviq' !== displayMode) {
		return false;
	}

	try {
		const stored = JSON.parse(localStorage.getItem('c15t') || 'null');
		return !!(stored && stored.consents) && false === stored.consents.marketing;
	} catch (e) {
		return false;
	}
}

/**
 * Wires the footer privacy link to RevIQ's consent dialog.
 *
 * Same wording as SourcePoint's link: GDPR visitors get "Privacy Manager GDPR",
 * everyone else "Do Not Sell/Share My Personal Information".
 *
 * The link opens the dialog through data-reviq-consent="manage": RevIQ's loader
 * listens for clicks on that attribute and opens its Privacy Settings dialog in
 * every location (tested: no-law US states, California, Germany). The documented
 * consent.show() only goes through __uspapi/__tcfapi, so for US visitors it
 * opened nothing.
 *
 * @since TBD
 *
 * @param {boolean} gdprApplies Whether GDPR applies to this visitor.
 *
 * @return {void}
 */
function maiPubWireReviqPmLink(gdprApplies) {
	const wire = (pmLink) => {
		pmLink.style.display        = 'inline';
		pmLink.textContent          = gdprApplies ? 'Privacy Manager GDPR' : 'Do Not Sell/Share My Personal Information';
		pmLink.dataset.reviqConsent = 'manage';
		// RevIQ's listener opens the dialog; this only keeps href="#" from jumping to the top.
		pmLink.onclick = (e) => e.preventDefault();
	};

	// Defined inline in <head> with the SourcePoint config, which prints it for every
	// bucket. It waits for the footer to be parsed.
	if (typeof window.maiPubWhenPmLink === 'function') {
		window.maiPubWhenPmLink(wire);
		return;
	}

	// No SourcePoint config. RevIQ's loader only goes in after DOMContentLoaded, so
	// the footer is parsed by the time its consent answer arrives.
	const pmLink = document.getElementById('pmLink');
	if (pmLink) {
		wire(pmLink);
	}
}

/**
 * Sets the consent state, updates the session passport, and stores a persistent cookie.
 * This is the single source of truth for changing consent.
 *
 * @param {string} consentData.message_type - indicates if onConsentReady is for GDPR or USNat
 * @param {string} consentData.consentSource - Where the data came from ('cmp', 'geo', 'cookie', 'session').
 * @param {string} consentData.consentUUID - The UUID of the consent
 * @param {boolean} consentData.typeApplies - Whether the consent type applies to this user
 *
 * @since 1.18.5
 *
 * updated @1.19.7
 * updated @1.21.0 - Added CMP readiness tracking
 */
function maiPubSetConsent(consentData) {

    // Ensure this function is available to external inline scripts/wrappers
    if (typeof window.maiPubSetConsent !== 'function') {
        window.maiPubSetConsent = maiPubSetConsent;
    }

	if (!consentData || !consentData.consentSource) {
        maiPubLog('maiPubSetConsent: Invalid consentData - missing or no source');
        return;
    }

	// Don't let a geo-based assumption override an explicit user choice from the
	// CMP or a cookie. This has to run BEFORE we touch the passport: it used to
	// sit at the bottom of the function, by which point hasConsent and the
	// gdpr/usnat fields had already been overwritten in memory, so the early
	// return only skipped the save — the override it exists to prevent had
	// already happened. It also compared against maiPubSessionData.consent.source,
	// which only existed as a side effect of the passport-nesting bug fixed below,
	// and tested consentData.source, a key no caller sets (the check above
	// requires consentSource, so a payload with only `source` never gets here).
	if ('geo' === consentData.consentSource
		&& ('cmp' === maiPubSessionData.source || 'cookie' === maiPubSessionData.source)) {
		maiPubLog('cmp: Skipping geo-based consent; an explicit user choice already exists.');
		return;
	}

	maiPubSessionData.source = consentData.consentSource;

	// Track if this is an actual CMP event (not geo/cookie/session)
	const isCmpEvent = consentData.consentSource === 'cmp';

	// assume we have all consent until we know otherwise
	hasConsent = true;

	if (consentData.message_type === 'gdpr') {
		maiPubSessionData.gdprConsentUUID = consentData.consentUUID;
		maiPubSessionData.gdprApplies = consentData.applies;
		maiPubSessionData.gdprConsentedToAll = consentData.consentedToAll;

		if (consentData.applies === true) {
			if ((consentData.consentedToAll === true)) {
				hasConsent = true;
				maiPubSessionData.hasConsent = true;
				initSellOrShareVendors();

				maiPubLog('cmp: GDPR applies and all consent granted.');
			}
			else {
				hasConsent = false;
				maiPubSessionData.hasConsent = false;
				maiPubLog('cmp: GDPR applies but consent denied.');
			}

			// Only a GDPR event that APPLIES has actually decided anything. GDPR
			// processing is synchronous, so the decision is final here.
			//
			// Deliberately NOT marked when applies === false: that event rules out
			// GDPR and says nothing about US privacy, and neither hasConsent nor the
			// passport is updated above. Marking it resolved would cache the
			// optimistic default as a real answer, and a later pageview would reuse
			// it and skip waiting for the CMP — running bidders on a guess for a
			// visitor whose USNat opt-out simply hadn't arrived yet. When GDPR does
			// not apply the decision belongs to the USNat flow, which marks it
			// resolved in both of its own paths.
			if (isCmpEvent) {
				maiPubMarkConsentResolved();
			}
		}

		// GDPR processing is synchronous - mark CMP as ready when appropriate for this region.
		if (isCmpEvent) {
			const region = maiPubGetGeoRegion();

			if (region === 'na') {
				maiPubLog('cmp: GDPR processed, but user is in North America - waiting for USNat before marking CMP ready.');
			} else if (region === 'non-na') {
				const wasAlreadyReady = cmpConsentProcessed === true;
				markCMPAsReady();
				if (wasAlreadyReady) {
					maiPubLog(`cmp: GDPR consent arrived late (after timeout) - consent state updated: hasConsent=${hasConsent}, applies=${consentData.applies}, consentedToAll=${consentData.consentedToAll}`);
				} else {
					maiPubLog('cmp: GDPR consent processed - CMP marked as ready.');
				}
			} else {
				maiPubLog('cmp: GDPR processed - geo unknown, waiting for USNat (or geo) before marking CMP ready.');
			}
		}
	}

	if (consentData.message_type === 'usnat') {
		maiPubSessionData.usnatConsentUUID = consentData.consentUUID;
		maiPubSessionData.usnatApplies = consentData.typeApplies;

		if ((consentData.typeApplies === true)) {
			maiPubLog('cmp: USNat applies, checking consent.');
			// USNat requires async callback - CMP readiness will be marked in checkConsent()
			// See Step 3 for implementation
			window._sp_.usnat.getUserConsents((consents, status) => checkConsent(consents, status));

		}
		else if (maiPubSessionData.gdprApplies === true) {
			// USNat not applying says nothing about GDPR. Sourcepoint fires GDPR first,
			// so for an EU visitor this event used to overwrite their GDPR answer with
			// hasConsent:true and persist it, and later pageviews bid on it. Keep the
			// GDPR decision (the reset at the top of this function cleared it in memory).
			hasConsent = maiPubSessionData.hasConsent === true;
			maiPubSessionData.hasConsent = hasConsent;

			maiPubLog(`cmp: USNat does not apply - keeping the GDPR decision (hasConsent=${hasConsent}).`);
		}
		else {
			hasConsent = true;
			maiPubSessionData.hasConsent = true;

			initSellOrShareVendors();
			maiPubLog('cmp: USNat does not apply.');
		}

		if (consentData.typeApplies !== true && isCmpEvent) {
			// USNat not applying is itself a final answer — no async callback follows.
			maiPubMarkConsentResolved();

			const region = maiPubGetGeoRegion();

			if (region === 'na') {
				markCMPAsReady();
				maiPubLog('cmp: USNat does not apply - CMP marked as ready.');
			} else if (cmpConsentProcessed) {
				maiPubLog('cmp: USNat does not apply (CMP already marked ready).');
			} else if (region === 'non-na' && maiPubSessionData.gdprConsentUUID) {		// fixed: v817
				markCMPAsReady();
				maiPubLog('cmp: USNat does not apply - CMP marked as ready (GDPR is primary).');
			} else {
				maiPubLog('cmp: USNat does not apply - waiting for GDPR before marking CMP ready.');
			}
		}

		maiPubLog('cmp: USNat consent updated in session passport:', maiPubSessionData.usnatConsentUUID, maiPubSessionData.usnatApplies);
	}

	// Store TCF consent string for server-side validation
	if (consentData.euconsent) {
		if (!maiPubSessionData.consent) {
			maiPubSessionData.consent = {};
		}
		maiPubSessionData.consent.tcString = consentData.euconsent;
		maiPubLog('TCF consent string stored for server-side validation');
	}

	// Persist the consent state.
	//
	// This used to be maiPubSaveSessionData({ consent: maiPubSessionData }) —
	// passing the entire passport as its own `consent` key. maiPubSaveSessionData
	// deep-merges objects, so every CMP event folded another copy of the whole
	// passport (including the copy left by the previous event) into .consent. The
	// structure nested one level deeper each time and sessionStorage grew on every
	// page for the life of the session.
	//
	// The fields below are already mutated on maiPubSessionData above; all this
	// needs to do is persist them. `.consent` now holds only the raw consent
	// strings other systems read (tcString), which is what line ~2897 expects.
	const consentState = {};

	CONSENT_PASSPORT_KEYS.forEach((key) => {
		if (undefined !== maiPubSessionData[key]) {
			consentState[key] = maiPubSessionData[key];
		}
	});

	if (maiPubSessionData.consent) {
		consentState.consent = maiPubSessionData.consent;
	}

	maiPubSaveSessionData(consentState);

    maiPubLog('Consent for personalized ads:', maiPubSessionData.hasConsent);

}

/**
 * Check if a specific GVL ID has user consent via TCF API
 *
 * @since 1.22.1
 *
 * @param {number} gvlId - Global Vendor List ID to check (e.g., 1494 for Catalyst)
 * @returns {Promise<boolean>} - true if vendor has consent or legitimate interest
 */
function checkGVLConsent(gvlId) {
    // 1. If GDPR does NOT apply (e.g., US user), we don't need TCF checks.
    // We assume true (Opt-Out model) or rely on the GPP string handling elsewhere.
    if (maiPubSessionData.gdprApplies === false) {
        maiPubLog(`TCF: GDPR does not apply (US/Global). Skipping GVL check for ${gvlId}.`);
        return Promise.resolve(true);
    }

    // 2. Only run TCF check if we are actually in a TCF jurisdiction
    if (typeof window.__tcfapi === 'function') {
        return new Promise((resolve) => {
            // Add a timeout so we don't hang if CMP is unresponsive
            let responded = false;
            const t = setTimeout(() => {
                if(!responded) {
                    responded = true;
                    maiPubLog(`TCF: Timeout checking GVL ID ${gvlId}. Defaulting to false.`);
                    resolve(false);
                }
            }, 2000); // Increased timeout to allow CMP to fully load

            // TCF v2 uses 'getTCData', NOT 'getVendorConsents' (v1.1 legacy)
            window.__tcfapi('getTCData', 2, (tcData, success) => {
                if(responded) return;
                responded = true;
                clearTimeout(t);

                if (!success || !tcData) {
                    maiPubLog(`TCF: getTCData failed for GVL ID ${gvlId}. success=${success}`);
                    resolve(false);
                    return;
                }

                maiPubLog(`TCF: gdprApplies=${tcData.gdprApplies}, eventStatus=${tcData.eventStatus}, cmpStatus=${tcData.cmpStatus}`);

                // If GDPR doesn't apply per TCF response, allow
                if (tcData.gdprApplies === false) {
                    maiPubLog(`TCF: GDPR does not apply per TCF response. Allowing GVL ID ${gvlId}.`);
                    resolve(true);
                    return;
                }

                // TCF v2 vendor consent path: tcData.vendor.consents[vendorId]
                const vendorId = gvlId;
                const hasVendorConsent = tcData?.vendor?.consents?.[vendorId] === true;
                const hasLegitInterest = tcData?.vendor?.legitimateInterests?.[vendorId] === true;

                maiPubLog(`TCF: GVL ID ${gvlId} - consent=${hasVendorConsent}, legInt=${hasLegitInterest}`);
                resolve(hasVendorConsent || hasLegitInterest);
            });
        });
    }

    // Fallback: No __tcfapi available and GDPR applies - block to be safe
    maiPubLog(`TCF: No __tcfapi found but GDPR applies. Blocking GVL ID ${gvlId}.`);
    return Promise.resolve(false);
}

/**
 * Gets the browser's effective connection type.
 *
 * @since 1.18.5
 *
 * @returns {string} '4g', '3g', '2g', 'slow-2g', or empty string.
 */
function getBrowserConnectionType() {
	try {
	  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
	  if (connection && connection.effectiveType) {
		return connection.effectiveType;
	  }
	} catch (error) {
	  maiPubLog('Could not retrieve connection type from browser:', error);
	}
	return '';
  }

/**
 * Updates the global ORTB2 object with data from the geo worker/cache.
 * @param {object} pd The processed geo data object.
 * @since 1.18.5
 */
function enrichOrtb2FromGeo(pd) {
    if (!pd) {
        maiPubLog('enrichOrtb2FromGeo: No geo data provided.');
        return;
    }

    if (window.maiPubAdsVars && window.maiPubAdsVars.ortb2) {
        const ortb2 = window.maiPubAdsVars.ortb2;

        // Ensure nested objects exist
        ortb2.device = ortb2.device || {};
        ortb2.device.geo = ortb2.device.geo || {};
        ortb2.user = ortb2.user || {};
        ortb2.user.ext = ortb2.user.ext || {};
        ortb2.user.ext.data = ortb2.user.ext.data || {};

        // 1. Update Device Geo
        if (pd.country) ortb2.device.geo.country = pd.country;
        if (pd.region) ortb2.device.geo.region = pd.region;
        if (pd.city) ortb2.device.geo.city = pd.city;
        if (pd.postalCode) ortb2.device.geo.zip = pd.postalCode;
        if (pd.metroCode) ortb2.device.geo.metro = pd.metroCode.toString();
        if (pd.lat) ortb2.device.geo.lat = pd.lat;
        if (pd.lon) ortb2.device.geo.lon = pd.lon;
        ortb2.device.geo.ipservice = 3; // Cloudflare

        // 2. Update Device Type
        const deviceTypeMap = { 'desktop': 2, 'mobile': 4, 'tablet': 5 };
        if (pd.deviceType && deviceTypeMap[pd.deviceType]) {
            ortb2.device.devicetype = deviceTypeMap[pd.deviceType];
        }

        // 3. Update Connection Type (with browser fallback)
        const connTypeMap = { 'ethernet': 1, 'wifi': 2, 'cellular': 3, '2g': 4, 'slow-2g': 4, '3g': 5, '4g': 6, '5g': 7 };
        let finalConnType = pd.connectionType || getBrowserConnectionType();
        if (finalConnType && connTypeMap[finalConnType.toLowerCase()]) {
            ortb2.device.connectiontype = connTypeMap[finalConnType.toLowerCase()];
        }

        // 4. Update User ISP
        if (pd.isp) {
            ortb2.user.ext.data.isp = pd.isp;
        }

        maiPubLog('ORTB2 object updated with geo data:', ortb2);

    } else {
        maiPubLog('maiPubAdsVars.ortb2 object not found. Cannot update with geo data.');
    }
}


/**
 * Compares a geo.js worker version against a minimum, e.g. '1.0.6' >= '1.0.5'.
 *
 * @param {string} version The version reported by the worker.
 * @param {string} minimum The minimum version we accept.
 *
 * @return {boolean} True when version is present and >= minimum.
 *
 * @since TBD
 */
function maiPubGeoVersionAtLeast(version, minimum) {
    if (!version || typeof version !== 'string') {
        return false;
    }

    const actual   = version.split('.').map(n => parseInt(n, 10) || 0);
    const required = minimum.split('.').map(n => parseInt(n, 10) || 0);
    const length   = Math.max(actual.length, required.length);

    for (let i = 0; i < length; i++) {
        const a = actual[i] || 0;
        const r = required[i] || 0;

        if (a > r) return true;
        if (a < r) return false;
    }

    return true; // Exactly equal.
}

/**
 * Copies the server-rendered ID5 identity signals into the session passport.
 *
 * maiPubAdsVars.id5Identity is only populated on a logged-in — and therefore
 * uncached — response. Persisting it to the passport is what lets ID5 pd key 1
 * survive the Varnish-cached pageviews that follow, which is the entire reason
 * anonymous pageviews used to ship an empty pd.
 *
 * Stored unconditionally (same as ppid) but only ever transmitted under consent;
 * see maiPubBuildId5Pd(). This is the visitor's own hashed email, in first-party
 * sessionStorage, on a site they logged into.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubStoreId5Identity() {
    const identity = maiPubAdsVars.id5Identity || {};

    if (!identity.hem) {
        // Anonymous or cached response. Fall back to the cookie so an identity
        // learned in an earlier session (a newsletter click last week, a login
        // before that) still recognizes this visitor.
        if (!maiPubSessionData.hem) {
            const cookieHem = maiPubGetHemCookie();

            if (cookieHem) {
                maiPubSessionData.hem       = cookieHem;
                maiPubSessionData.hemSource = 'cookie';

                // The cookie carries only the hash, so anything describing a previous
                // email (phone, burner flag) cannot belong to it.
                maiPubSessionData.phone         = '';
                maiPubSessionData.isBurnerEmail = '';

                maiPubSaveSessionData({ hem: cookieHem, hemSource: 'cookie', phone: '', isBurnerEmail: '' });
                maiPubLog('ID5: hashed email restored from persistent cookie.');
            }
        }

        return;
    }

    if (maiPubSessionData.hem === identity.hem && 'server' === maiPubSessionData.hemSource) {
        return;
    }

    maiPubSessionData.hem           = identity.hem;
    maiPubSessionData.hemSource     = 'server';
    maiPubSessionData.isBurnerEmail = identity.isBurnerEmail || '';
    maiPubSessionData.phone         = identity.phone || '';

    maiPubSaveSessionData({
        hem:           maiPubSessionData.hem,
        hemSource:     'server',
        isBurnerEmail: maiPubSessionData.isBurnerEmail,
        phone:         maiPubSessionData.phone
    });

    // Persist beyond the session so a login recognizes this visitor next time too.
    maiPubSetHemCookie(identity.hem);

    maiPubLog('ID5: identity signals stored in session passport.');
}

/**
 * Normalizes a first-party ID from a URL into a SHA256 hex string.
 *
 * ID5 pd key 1 must be the lowercase hex SHA256 of a normalized email. Newsletter
 * platforms hand out that hash in more than one encoding — Listrak documents its
 * UniversalEmailKey only as "an encoded version of the email address" — so detect
 * the shape rather than assume it:
 *
 *   - 64 hex chars                -> already what we need, just lowercase it
 *   - base64/base64url of 32 bytes -> the same digest, re-encoded; convert to hex
 *   - anything else               -> not a SHA256 digest; caller must not use it
 *                                    as a hashed email
 *
 * @since TBD
 *
 * @param {string} value The raw parameter value.
 *
 * @return {string} Lowercase hex SHA256, or '' if the value isn't one.
 */
function maiPubNormalizeFpid(value) {
    if (!value || typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();

    // Already hex.
    if (/^[a-f0-9]{64}$/i.test(trimmed)) {
        return trimmed.toLowerCase();
    }

    // Base64 or base64url of a 32-byte digest (43 chars unpadded, 44 padded).
    if (/^[A-Za-z0-9+/_-]{43}=?$/.test(trimmed)) {
        try {
            const binary = atob(trimmed.replace(/-/g, '+').replace(/_/g, '/'));

            if (binary.length !== 32) {
                return '';
            }

            // Reject decoded TEXT. Base64 of a 32-byte digest and base64 of a
            // 32-CHARACTER PLAINTEXT EMAIL are indistinguishable by length —
            // both are 43 chars unpadded and decode to 32 bytes. Several email
            // platforms offer a base64-encoded-email merge tag right next to
            // their hashed ones (beehiiv lists "Email (base64 encoded)" first
            // in the dropdown), so picking the wrong one is a realistic
            // misconfiguration rather than a hypothetical.
            //
            // Without this check, a subscriber whose address happens to be
            // exactly 32 characters — 'first.lastname@somecompany.co.uk' is a
            // real-world example — would have their REVERSIBLE plaintext email
            // accepted as a hash: written to the year-long maipub_hem cookie and
            // sent to ID5 as pd key 1. That is the same class of exposure removed
            // from class-tracking.php in 1.35.0, reintroduced through our own door.
            //
            // A SHA-256 digest is 32 uniformly random bytes. The chance that all
            // 32 land in printable ASCII is (95/256)^32, about 1 in 10^13, so
            // rejecting all-printable payloads costs nothing real and closes the
            // hole for every platform with a base64-email option, not just one.
            let printable = 0;

            for (let i = 0; i < binary.length; i++) {
                const code = binary.charCodeAt(i);

                if (code >= 0x20 && code <= 0x7E) {
                    printable++;
                }
            }

            if (printable === binary.length) {
                maiPubLog('Identity: fpid decoded to printable text, not a digest — rejected. Check the email platform is sending a SHA-256 hash, not a base64-encoded address.');

                return '';
            }

            let hex = '';

            for (let i = 0; i < binary.length; i++) {
                hex += binary.charCodeAt(i).toString(16).padStart(2, '0');
            }

            return hex;
        } catch (e) {
            return '';
        }
    }

    return '';
}

/**
 * Captures the hashed email carried on inbound newsletter links.
 *
 * A newsletter click is the one moment an otherwise-anonymous visitor tells us who
 * they are, so this is the highest-value identity signal available on traffic that
 * never logs in. The captured hash lands in the session passport as `hem`, exactly
 * where a server-rendered login hash would, and flows on to ID5 pd key 1 and
 * RevIQ's setUid from there. A cookie carries it beyond the session, so a click
 * today still identifies the visitor weeks later.
 *
 * Precedence: a server-rendered hash wins. That one belongs to an authenticated
 * user on this site; the URL value only proves someone opened an email and clicked.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubCaptureUrlIdentity() {
    const config = maiPubAdsVars.fpid || {};

    // An explicitly empty param name disables capture for this site. Distinguish
    // "not configured" (undefined -> use the default) from "configured to nothing"
    // (the off switch); `config.param || 'sw_fpid'` collapsed the two and made
    // opting out impossible.
    const param = undefined === config.param ? 'sw_fpid' : config.param;

    if (!param) {
        return;
    }

    let raw;

    try {
        raw = new URLSearchParams(window.location.search).get(param);
    } catch (e) {
        return;
    }

    // .get() returns null when the parameter is absent, and '' when it is present
    // but empty -- `?sw_fpid=` from an ESP that cannot emit a hashed email, or a
    // merge tag that never populated. Absent means there is nothing to identify or
    // strip. A present-but-empty value is a deliberate channel signal on its own:
    // arrival is captured (presence-based) by maiPubResolveArrival() ahead of this
    // call, so here we skip identity derivation but STILL fall through to the strip
    // below, so a bare `?sw_fpid=` does not linger in the URL to be shared and
    // re-counted as a newsletter arrival.
    if (null === raw) {
        return;
    }

    // Only a non-empty value can be a hashed email; decide identity only then.
    // Whatever we decide, the parameter must not survive in the URL -- the strip
    // runs after this regardless. (An earlier version returned straight out on the
    // empty/malformed paths and skipped the strip, leaving a hashed email in the
    // query string for exactly the visitors it was meant to be removed for, where
    // Prebid sends it to every bidder as refererInfo and GAM receives it too.)
    if (raw) {
        maiPubDecideUrlIdentity(param, raw);
    }

    // Drop it from the URL. Prebid hands the full landing URL to every bidder as
    // refererInfo and GAM receives it too, so a hashed email left in the query
    // string is broadcast to the entire auction. ON by default since 1.34.0:
    // sw_fpid is our own parameter, so nothing else reads it and there is no
    // attribution to starve. The Newsletter ID in URL setting, and the
    // maipub_fpid_strip_param filter, opt out per site.
    //
    // Anything that needs to know a newsletter click happened must read it from
    // the passport, not the URL, and must run before this point --
    // maiPubResolveArrival() is called ahead of this function for that reason.
    if (config.strip) {
        try {
            const url = new URL(window.location.href);

            url.searchParams.delete(param);
            window.history.replaceState({}, '', url.toString());
            maiPubLog(`Identity: ${param} stripped from the URL.`);
        } catch (e) {
            maiPubLog(`Identity: could not strip ${param} from the URL.`, e);
        }
    }
}

/**
 * Decides what, if anything, the value on an inbound newsletter link tells us.
 *
 * Split out of maiPubCaptureUrlIdentity() so that every way of declining the
 * value is a plain `return` here, and the caller still strips the parameter from
 * the URL afterwards. Folding these branches back into the caller reintroduces
 * the defect where declining the value also meant leaving it in the query string.
 *
 * @since TBD
 *
 * @param {string} param The query parameter the value arrived on.
 * @param {string} raw   The raw parameter value.
 *
 * @return {void}
 */
function maiPubDecideUrlIdentity(param, raw) {
    // A server-rendered hash outranks the URL: that visitor is authenticated here.
    if (maiPubAdsVars.id5Identity && maiPubAdsVars.id5Identity.hem) {
        maiPubLog(`Identity: ${param} present but the user is logged in — keeping the server hash.`);
        return;
    }

    const hem = maiPubNormalizeFpid(raw);

    if (!hem) {
        // Not a SHA256 digest in any encoding we recognize. Never guess it into
        // key 1: a value that isn't really a hashed email links to nothing and
        // pollutes ID5's deterministic graph. Kept for debugging only.
        maiPubSessionData.fpidRaw = raw;
        maiPubSaveSessionData({ fpidRaw: raw });
        maiPubLog(`Identity: ${param} is not a recognizable SHA256 digest — stored for debugging, NOT used as a hashed email.`);
        return;
    }

    if (maiPubSessionData.hem !== hem) {
        maiPubSessionData.hem       = hem;
        maiPubSessionData.hemSource = 'fpid';

        // Clear the fields that DESCRIBE the previous email. They were derived from a
        // different address (a prior login this session), and the URL gives us no way
        // to recompute them for this one — the phone belongs to the old identity and
        // the burner flag was decided by inspecting the old domain. Leaving them would
        // ship pd key 1 for one person alongside keys 2 and 13 for another.
        maiPubSessionData.phone         = '';
        maiPubSessionData.isBurnerEmail = '';

        maiPubSaveSessionData({ hem: hem, hemSource: 'fpid', phone: '', isBurnerEmail: '' });
        maiPubSetHemCookie(hem);

        maiPubLog(`Identity: hashed email captured from ${param} (newsletter click).`);
    }
}

/**
 * Persists the hashed email to a first-party cookie.
 *
 * sessionStorage dies with the tab; the point of a newsletter click is that it
 * should still identify the visitor on their next visit. Mirrors the PPID cookie's
 * consent gate and lifetime.
 *
 * @since TBD
 *
 * @param {string} hem Lowercase hex SHA256 of the normalized email.
 *
 * @return {void}
 */
function maiPubSetHemCookie(hem) {
    if (!maiPubConsentAllowsStorage()) {
        maiPubLog('Identity: consent not settled (or withheld) — deferring the hashed-email cookie.');
        return;
    }

    if (!hem) {
        return;
    }

    const expires = new Date(Date.now() + 31536000000).toUTCString(); // 1 year

    document.cookie = `maipub_hem=${encodeURIComponent(hem)};path=/;expires=${expires};SameSite=Lax;Secure`;

    // Companion timestamp for the ITP probe. A browser never tells JavaScript
    // when a cookie was set — document.cookie returns name=value only — and ITP
    // silently caps the lifetime we asked for rather than reporting it. So the
    // only way to measure how long this cookie actually survives is to record
    // when we wrote it and read the age back later. Written with the SAME flags
    // and expiry so it is capped identically; if they diverge the measurement is
    // meaningless. Temporary — remove with the probe.
    document.cookie = `maipub_hem_set=${Math.floor(Date.now() / 1000)};path=/;expires=${expires};SameSite=Lax;Secure`;

    maiPubLog('Hashed email saved to persistent cookie.');
}

/**
 * Reads the hashed email from the persistent cookie.
 *
 * @since TBD
 *
 * @return {string} The stored hash, or ''.
 */
function maiPubGetHemCookie() {
    const match = document.cookie.match(/(?:^|;)\s*maipub_hem=([^;]*)(?:;|$)/);

    return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Builds ID5's partner data (pd) string.
 *
 * pd is a base64-encoded QUERY STRING with numeric keys — not JSON. ID5 uses it
 * for cross-domain user recognition: hard signals (hashed email) drive
 * deterministic linking, and the softer ones (IP, user agent) feed their
 * probabilistic algorithm. Per ID5, publisher-supplied IP/UA are not redundant
 * with what they read from the HTTP request; they compare the two and use ours
 * when it looks more accurate.
 *
 * Two rules from ID5 that shape this function:
 *   - Omit keys you don't have. Never send empty strings or placeholder defaults.
 *   - Only identity/device signals belong here. Contextual data (content id,
 *     keywords, IAB categories) is ignored.
 *
 * Deliberately NOT sent: key 8 (full URL). ID5's own client derives its local
 * cache key from a hash of the whole pd string (see getCacheId() in their
 * multiplexing package), so a value that changes on every pageview would give
 * every pageview a distinct cache id, orphan the stored response and force a
 * fresh ID5 fetch each time — behind a 250ms auctionDelay. Everything we do send
 * is stable for the session. ID5 lists key 8 as "recommended"; raise it with them
 * before adding it.
 *
 * @since TBD
 * @see https://wiki.id5.io/docs/passing-partner-data-to-id5
 *
 * @return {string} The base64-encoded pd string, or '' when we have no signals.
 */
function maiPubBuildId5Pd() {
    // Belt and braces. Prebid is only injected when hasConsent === true, so this
    // never runs for a visitor who declined sale/sharing under USNat or withheld
    // GDPR consent — but pd carries identity, so gate it here too rather than
    // relying on a caller staying correct.
    if (true !== hasConsent) {
        maiPubLog('ID5: no consent — pd not built.');
        return '';
    }

    const signals = {};
    const geo     = maiPubSessionData.geo || window.maiPubGeo || {};

    // --- Identity ---
    // key 1 (hem) is a real, ID5-normalized email hash and nothing else. The
    // passport's ppid is only email-derived when ppidSource === 'server'; for
    // matomo/random it's a pseudonymous id, which belongs in key 5 (puid). A
    // hashed username or session id in key 1 would link to nothing and pollute
    // ID5's deterministic graph.
    const hem = maiPubSessionData.hem || '';

    if (hem) {
        signals.hem = hem;

        // key 13 — describes the address in key 1, so only meaningful with it.
        if (maiPubSessionData.isBurnerEmail) {
            signals.isBurnerEmail = maiPubSessionData.isBurnerEmail;
        }

        // key 2 — SHA256 E.164 phone, when the server could build a real one.
        if (maiPubSessionData.phone) {
            signals.phone = maiPubSessionData.phone;
        }
    }

    // key 5 (puid) — our partner-specific id. This is the signal that gives pd
    // coverage on anonymous traffic, which is nearly all of it: every visitor has
    // a stable, cookie-backed passport id even when we have no email.
    //
    // Skipped whenever the ppid IS the hashed email, because that value is
    // already in key 1 and ID5 would receive the same string under two different
    // semantic keys. That was true for 'server' alone until 'fpid' was added;
    // testing the source directly rather than the CATEGORY is what would have
    // made a newsletter visitor send their email hash twice.
    if (maiPubSessionData.ppid && ! maiPubIsPersonLevelPpid()) {
        signals.puid = maiPubSessionData.ppid;
    }

    // --- Device / network ---
    // key 9 — stable per site, so it costs nothing in cache churn.
    if (maiPubAdsVars.domain) {
        signals.domain = maiPubAdsVars.domain;
    }

    // keys 10/11 — from the geo.js worker via the passport. Cloudflare only
    // exposes the family the client actually connected with, so at most one of
    // these is ever set. Absent on workers older than v1.0.6; the key is simply
    // omitted until they're updated.
    if (geo.ipv4) {
        signals.ipv4 = geo.ipv4;
    }

    if (geo.ipv6) {
        signals.ipv6 = geo.ipv6;
    }

    // key 12 — the UA of the device actually making the ID5 call. Must come from
    // the browser: the server-side equivalent would be baked into Varnish-cached
    // HTML and describe whoever happened to miss the cache.
    if (navigator.userAgent) {
        signals.ua = navigator.userAgent;
    }

    // --- Encode: querystring of numeric keys, then base64 ---
    const parts = [];

    Object.keys(signals).forEach((name) => {
        const numericKey = ID5_PD_KEYS[name];
        const value      = signals[name];

        // Omit anything we don't actually have, per ID5.
        if (undefined === numericKey || !value) {
            return;
        }

        parts.push(numericKey + '=' + encodeURIComponent(String(value)));
    });

    if (!parts.length) {
        maiPubLog('ID5: no signals available — pd omitted.');
        return '';
    }

    // encodeURIComponent above guarantees an ASCII-safe string, so btoa can't
    // throw on a multi-byte user agent. ID5's own client uses plain btoa here
    // (their wiki says "URL-safe base64 ... using btoa()", which is
    // self-contradictory; we match their implementation, not their prose).
    const pd = btoa(parts.join('&'));

    maiPubLog(`ID5: pd built with ${parts.length} key(s):`, Object.keys(signals).join(', '));

    return pd;
}

/**
 * Publishes our ID5 partner data (pd) as window.mai_publisher_pd.
 *
 * Magnite's own ID5 settings (its DM parameter set) read pd from this variable, and
 * Prebid uses those settings, not ours, on a pageview where SourcePoint answered
 * before Magnite's wrapper loaded: Prebid then starts ID5 inside Magnite's own setup,
 * before our config arrives. Queuing our config ahead of the wrapper doesn't help
 * (tested). Without the variable, that copy carried no pd at all. RevIQ's ID5 settings
 * can read it too.
 *
 * Only with consent (maiPubBuildId5Pd checks) and only once geo is known (callers
 * check): pd waits for the IP. It holds what our first-party storage already holds
 * (the session passport and the maipub_hem cookie), plus the IP and user agent.
 *
 * @since TBD
 *
 * @return {void}
 */
function maiPubPublishId5Pd() {
    if (!maiPubId5Config()) {
        return;
    }

    const pd = maiPubBuildId5Pd();

    if (pd) {
        window.mai_publisher_pd = pd;
        maiPubLog('ID5: pd published as window.mai_publisher_pd, for Magnite\'s and RevIQ\'s ID5 settings.');
    } else {
        delete window.mai_publisher_pd;
    }
}

/**
 * Gets geo data from session cache or Cloudflare Worker, then enriches ORTB2.
 * @param {function} onComplete Optional callback to run after data is processed.
 * @since 1.18.5
 */
function maiPubGetGeo(onComplete) {

    // If we already have geo data from the session cache, we're done.
    if (maiPubSessionData.geo && maiPubSessionData.geo.country) {
        maiPubLog('[Mai Publisher] Geo data was loaded from session. Skipping worker call.');
        if (typeof onComplete === 'function') {
            onComplete(true);
        }
        return;
    }

    // If no cache, fetch from the worker.
    const handleLoad = function () {
        try {
            if (!window.maiPubGeo) {
                maiPubLog('[Mai Publisher] geo.js loaded, but window.maiPubGeo is missing.');
                if (typeof onComplete === 'function') onComplete(false);
                return;
            }

            let geo = JSON.parse(atob(window.maiPubGeo));

            // Minimum-version check, not an exact match. The workers live in
            // several Cloudflare accounts and are deployed independently, so the
            // fleet is always mixed. An exact '1.0.5' comparison turned every
            // worker release into an outage: newer workers were rejected outright
            // and the site lost all geo enrichment until the plugin caught up.
            if (!maiPubGeoVersionAtLeast(geo.version, GEO_MIN_VERSION)) {
                maiPubLog(`[Mai Publisher] geo.js version too old, got "${geo.version}", need >= ${GEO_MIN_VERSION}`);
                if (typeof onComplete === 'function') onComplete(false);
                return;
            }

            // Process the worker data into the 'pd' object
            const pd = {
                version: geo.version || '',
                cfRay: geo.cfRay || '',
                isNorthAmerica: geo.isNorthAmerica || false,
                city: geo.city || '',
                region: geo.regionCode || '',
                country: geo.country || '',
                postalCode: geo.postalCode || '',
                metroCode: geo.metroCode || '',
                continent: geo.continent || '',
                timezone: geo.timezone || '',
                lat: geo.latFloat || null,
                lon: geo.lonFloat || null,
                asn: geo.asn || '',
                isp: geo.asnDesc || '',
                deviceType: geo.deviceType || '',
                connectionType: geo.connectionType || '',
                // ID5 pd keys 10/11. Cloudflare only ever exposes the address family
                // the client actually connected with (CF-Connecting-IP), so exactly
                // one of these is populated per visitor, never both. Written
                // defensively: workers that predate v1.0.6 don't send them, and pd
                // simply omits the key, per ID5's "don't send keys you don't have".
                ipv4: geo.ipv4 || '',
                ipv6: geo.ipv6 || '',
                ppid: geo.ppid || window.maiPubAdsVars?.ppid || '',
                screenWidth: window.screen?.width || '',
                screenHeight: window.screen?.height || ''
            };

            // Update the global object
            window.maiPubGeo = pd;
            maiPubLog('[Mai Publisher] geo.js enrichment complete:', pd);

			// Save fresh geo data to session passport
			maiPubSaveSessionData({ geo: pd });

			// --- (v820 Dynamic Timeout Extension) ---
			// If the user is verified to be in an international GDPR region, 
			// dynamically extend the fallback window to 10 seconds.
			if (pd.isNorthAmerica === false) {
				if (typeof window.maiPubExtendCmpTimeout === 'function') {
					window.maiPubExtendCmpTimeout(10000);
				}
			}			

			// Update ORTB2 enrichment logic - only enrich if consent HAS been confirmed
			if (hasConsent) { // Check the current global state AFTER potential maiPubSetConsent call
				enrichOrtb2FromGeo(pd);
			} else {
				maiPubLog('No consent. Skipping ORTB2 enrichment.');
			}

			if (typeof onComplete === 'function') {
				onComplete(true);
			}
        } catch (err) {
            console.error('[Mai Publisher] geo.js processing error:', err);
            if (typeof onComplete === 'function') onComplete(false);
        }
    };

    const handleError = function () {
        // The most common cause by far is that the Cloudflare Worker route does not
        // cover this hostname, so /geo.js falls through to the origin and returns an
        // HTML 404 the browser refuses to execute. Subdomains are the usual culprit:
        // the worker's own ALLOWED_HOSTS accepts them, but the ROUTE has to match the
        // hostname before the worker ever runs.
        maiPubLog(`geo.js failed to load from ${window.location.origin}/geo.js — check that a Cloudflare Worker route covers this exact hostname. Falling back to server-side data only.`);
        if (typeof onComplete === 'function') onComplete(false);
    };

    // On a first pageview the Sourcepoint location key (class-output.php) starts
    // /geo.js ahead of the Sourcepoint wrapper. Adopt that request instead of
    // sending a second one, which would also reset window.maiPubGeo to the raw
    // payload after handleLoad() has decoded it.
    const early = window.maiPubGeoEarly;
    if (early && typeof early.then === 'function') {
        window.maiPubGeoEarly = null;
        maiPubLog('[Mai Publisher] Using the geo.js request started by the Sourcepoint location key.');
        early.then(function (loaded) {
            if (loaded) {
                handleLoad();
            } else {
                handleError();
            }
        });
        return;
    }

    const script = document.createElement('script');

	script.src = '/geo.js';
    script.async = true;
    script.onload = handleLoad;
    script.onerror = handleError;

    document.head.appendChild(script);
}

/**
 * ========================================================================
 * MAI AUDIO PLAYER v1.0
 * ========================================================================
 */


/**
 * Creates a Prebid ad unit configuration for an audio player.
 * Solves duplicate naming conflicts by building clean audio mediaType layers.
 */
function createPrebidAudioAdUnit(adUnitCode, adSlug) {
	const playerElement = document.getElementById(adUnitCode);
	if (!playerElement) return null;

	const adConfig = (typeof ads === 'object' && ads) ? ads[adSlug] : null;
	const bids = adConfig ? (adConfig.bids || []) : [];

	return {
		code: adUnitCode,
		mediaTypes: {
			audio: {
				context: 'instream',
				playerSize: [1, 1],
				mimes: ['audio/mp3', 'audio/aac', 'audio/ogg'],
				protocols: [2, 3, 5, 6],
				delivery: [1]
			}
		},
		bids: bids
	};
}


/**
 * ========================================================================
 * MAI VIDEO PLAYER v2.0
 * ========================================================================
 */

/**
 * ========================================================================
 * MAGNITE DEMAND MANAGER - VIDEO HEADER BIDDING
 * ========================================================================
 *
 * Video bids are requested through Demand Manager (pbjs.rp.requestVideoBids),
 * NOT through vanilla pbjs.requestBids. DM matches the adSlotName against the
 * wrapper's Ad Unit Pattern (AUP) "Slot Pattern" - the GAM ad unit path - and
 * builds the Prebid ad unit itself from that AUP's Media Type Object and
 * Bidders. That means bidder identity and bid params (seatId/tagId, accountId/
 * siteId/zoneId, ...) live in the Magnite DM UI and MUST NOT be duplicated here.
 * This mirrors how display already works via pbjs.rp.requestBids({gptSlotObjects}).
 *
 * Ref: Magnite "Web Integration Guide - Developer Reference":
 *   pbjs.rp.requestVideoBids({ adSlotName, playerSize, adServer?, callback })
 *   callback receives a videoBidResultObject:
 *     - adTagUrl : a constructed GAM request URL inclusive of ad-server
 *                  targeting key/values (undefined when there are no bids)
 *     - vastUrl  : the winning VAST URL, for integrations that do not want to
 *                  call the ad server first (undefined when there are no bids)
 *
 * The previous implementation hardcoded `bids: [{bidder:'magnite', params:{
 * placementId:'YOUR_PLACEMENT_ID'}}]` in config.php and called vanilla
 * pbjs.requestBids. That never bid: placementId is not a magnite adapter param
 * (it takes accountId/siteId/zoneId) and the values were placeholders.
 */

/**
 * How a winning Demand Manager video bid is handed to IMA.
 *
 * This is the single swappable decision pending Magnite's answer on whether
 * header-bidding line items can fill slots INSIDE a GAM ad-rules VMAP pod:
 *
 *   'merge'  (default) Keep OUR GAM VMAP pod URL and merge DM's hb_* targeting
 *            into its cust_params. Preserves output=vmap&ad_rule=1, the ad-rules
 *            pod, and every first-party signal we attach (asn, geo, ppid,
 *            durations), while still letting GAM arbitrate HB vs AdX/direct.
 *   'adtag'  Hand IMA DM's constructed adTagUrl as-is. GAM arbitrates, but any
 *            parameter DM does not carry over (notably the pod params) is lost.
 *   'vast'   Hand IMA the winning VAST directly. Bypasses GAM entirely, so the
 *            ad-rules pod does NOT apply - this is what the old code did.
 *
 * Override per-pageview for A/B testing without a deploy:  ?maivideobid=adtag
 *
 * @since TBD
 */
let maiPubVideoBidMode = 'merge';

try {
	const maiPubVideoBidModeParam = new URLSearchParams(window.location.search).get('maivideobid');
	if (maiPubVideoBidModeParam && ['merge', 'adtag', 'vast'].includes(maiPubVideoBidModeParam)) {
		maiPubVideoBidMode = maiPubVideoBidModeParam;
	}
} catch (e) {
	// Non-fatal: keep the default mode.
}

/**
 * Milliseconds to wait for Demand Manager before falling back to the plain GAM
 * pod URL. The DM wrapper has its own bidder timeout (2000ms on the jamiegeller
 * wrappers); this is a belt-and-braces failsafe so a callback that never fires
 * cannot stall the player forever.
 *
 * @since TBD
 */
const maiPubVideoBidTimeout = 3000;

/**
 * Per-pageview override for the video header-bidding gate. Lets us keep the
 * `magnite_video_enabled` option OFF in the admin — so production video is not
 * running a Prebid auction we are still validating — while still forcing the
 * auction on for a single pageview to test.
 *
 *   ?maivideohb=1  (or on/true/yes)  force the video Prebid auction ON
 *   ?maivideohb=0  (or off/false/no) force it OFF
 *   (absent)                         use the magnite_video_enabled setting
 *
 * null = no override. Read once at load; see maiPubVideoHbEnabled().
 *
 * @since TBD
 */
let maiPubVideoHbOverride = null;

try {
	const maiPubVideoParams  = new URLSearchParams(window.location.search);
	const maiPubVideoHbParam = maiPubVideoParams.get('maivideohb');

	if (maiPubVideoHbParam !== null) {
		maiPubVideoHbOverride = ['1', 'on', 'true', 'yes'].includes(maiPubVideoHbParam.toLowerCase());
	}
} catch (e) {
	// Non-fatal: keep the defaults.
}

/**
 * Whether the video Prebid auction should run: the ?maivideohb override when
 * present, otherwise the magnite_video_enabled option emitted from PHP.
 *
 * @since TBD
 *
 * @return {boolean}
 */
function maiPubVideoHbEnabled() {
	return maiPubVideoHbOverride !== null ? maiPubVideoHbOverride : (maiPubAdsVars.magniteVideo === true);
}

/**
 * Roll up a slice of the Prebid event log into per-auction facts.
 *
 * An empty videoBidResultObject ({adTagUrl: undefined, vastUrl: undefined}) is
 * documented as "no bids", but it does not distinguish between three very
 * different causes. The auction record does:
 *
 *   - No auction carrying a video ad unit at all
 *       -> adSlotName matched no Ad Unit Pattern. Slot-name/pattern problem.
 *   - A video auction ran but requested zero bidders
 *       -> the AUP matched but has no bidders or no video Media Type Object
 *          attached. Configuration problem on the Magnite side.
 *   - Bidders were requested and all returned noBid/timeout
 *       -> genuine no demand. Nothing to fix in code.
 *
 * Callers pass a SLICE (events recorded since a snapshot index) so a probe can
 * attribute auctions to the specific requestVideoBids call that created them.
 *
 * @since TBD
 *
 * @param {Array} events Prebid event objects from pbjs.getEvents().
 * @return {object} { auctions, videoAuctionIds, verdict, video }
 */
function maiPubSummarizeVideoAuctions(events) {
	const auctions = {};
	const bucket = (id) => {
		auctions[id] = auctions[id] || {
			adUnitCodes: [],
			hasVideo: false,
			biddersRequested: [],
			bidResponses: [],
			noBids: 0,
			timeouts: 0,
			// The video Media Type Object DM built from the AUP, plus the bidder
			// params it attached. This is what the exchanges were actually asked.
			videoMediaType: null,
			ortb2Imp: null,
			adUnitParams: {},
			requestedParams: {},
		};
		return auctions[id];
	};

	(events || []).forEach((ev) => {
		const args = ev.args || {};
		const id   = args.auctionId || (Array.isArray(args) && args[0] && args[0].auctionId);

		switch (ev.eventType) {
			case 'auctionInit': {
				const row = bucket(id);
				(args.adUnits || []).forEach((au) => {
					if (row.adUnitCodes.indexOf(au.code) === -1) {
						row.adUnitCodes.push(au.code);
					}
					if (au.mediaTypes && au.mediaTypes.video) {
						row.hasVideo = true;

						if (!row.videoMediaType) {
							row.videoMediaType = au.mediaTypes.video;
						}
						// plcmt/placement often ride on ortb2Imp rather than mediaTypes.
						if (au.ortb2Imp && !row.ortb2Imp) {
							row.ortb2Imp = au.ortb2Imp;
						}
						(au.bids || []).forEach((b) => {
							if (b && b.bidder) {
								row.adUnitParams[b.bidder] = b.params || null;
							}
						});
					}
				});
				break;
			}
			case 'bidRequested': {
				const row = bucket(id);
				const bidder = args.bidderCode || args.bidderRequestId;
				if (bidder && row.biddersRequested.indexOf(bidder) === -1) {
					row.biddersRequested.push(bidder);
				}
				// Params as actually sent, after any DM rewriting of the AUP config.
				(args.bids || []).forEach((b) => {
					if (b && b.bidder) {
						row.requestedParams[b.bidder] = b.params || null;
					}
				});
				break;
			}
			case 'bidResponse': {
				bucket(id).bidResponses.push({
					bidder: args.bidderCode || args.bidder,
					cpm: args.cpm,
					mediaType: args.mediaType,
				});
				break;
			}
			case 'noBid':
				bucket(id).noBids++;
				break;
			case 'bidTimeout':
				// bidTimeout args is an array of timed-out bids.
				(Array.isArray(args) ? args : [args]).forEach((b) => {
					bucket(b && b.auctionId).timeouts++;
				});
				break;
		}
	});

	const videoAuctionIds = Object.keys(auctions).filter((id) => auctions[id].hasVideo);

	// Collapse the video auctions in this slice into one row. A single
	// requestVideoBids call produces one, so this is a merge of one in practice.
	const video = {
		adUnitCodes: [],
		biddersRequested: [],
		bidResponses: [],
		noBids: 0,
		timeouts: 0,
		videoMediaType: null,
		ortb2Imp: null,
		adUnitParams: {},
		requestedParams: {},
	};

	videoAuctionIds.forEach((id) => {
		const row = auctions[id];
		video.adUnitCodes      = video.adUnitCodes.concat(row.adUnitCodes);
		video.biddersRequested = video.biddersRequested.concat(row.biddersRequested);
		video.bidResponses     = video.bidResponses.concat(row.bidResponses);
		video.noBids          += row.noBids;
		video.timeouts        += row.timeouts;
		video.videoMediaType   = video.videoMediaType || row.videoMediaType;
		video.ortb2Imp         = video.ortb2Imp || row.ortb2Imp;
		Object.assign(video.adUnitParams, row.adUnitParams);
		Object.assign(video.requestedParams, row.requestedParams);
	});

	let verdict;

	if (!videoAuctionIds.length) {
		verdict = 'NO video auction — adSlotName matched no AUP';
	} else if (!video.biddersRequested.length) {
		verdict = 'AUP matched, ZERO bidders requested — no bidders / no video MTO on the pattern';
	} else if (!video.bidResponses.length) {
		verdict = `bidders requested, none responded (${video.noBids} noBid, ${video.timeouts} timeout) — no demand`;
	} else {
		verdict = `${video.bidResponses.length} bid response(s) — empty result is downstream of the auction`;
	}

	return { auctions, videoAuctionIds, verdict, video };
}

/**
 * Print the video Media Type Object DM built, and the bidder params it attached.
 *
 * Every bidder fired a bidRequested and returned noBid, which means the requests
 * were valid enough to send and the exchanges answered "no bid" - so the problem
 * is not adapter-level param validation. What is left is WHAT was asked: the MTO
 * DM assembled from the AUP, and the seat/zone identifiers behind each bidder.
 *
 * `context` is called out on its own line because it is the single field most
 * likely to be wrong here: Magnite specified a web_video_instream MTO, and an
 * outstream context would explain broad DSP rejection on its own.
 *
 * @since TBD
 *
 * @param {object} summary Result of maiPubSummarizeVideoAuctions().
 * @param {string} label   Prefix for the log lines.
 * @return {void}
 */
function maiPubLogVideoMediaTypeObject(summary, label) {
	const video = summary && summary.video;

	if (!video || !video.videoMediaType) {
		console.warn(`[maiPub] ${label}: no video mediaType captured (auctionInit carried no video ad unit).`);
		return;
	}

	const mt      = video.videoMediaType;
	const context = mt.context || '(unset)';

	console.log(
		`[maiPub] ${label}: video MTO — context="${context}", playerSize=${JSON.stringify(mt.playerSize)}, ` +
		`plcmt=${mt.plcmt !== undefined ? mt.plcmt : '(unset)'}, placement=${mt.placement !== undefined ? mt.placement : '(unset)'}`
	);

	if ('instream' !== context) {
		console.warn(
			`[maiPub] ${label}: MTO context is "${context}", not "instream". Magnite specified a web_video_instream ` +
			'MTO for this inventory — an outstream context alone can explain broad DSP rejection.'
		);
	}

	console.log(`[maiPub] ${label}: full video mediaType:`, mt);

	if (video.ortb2Imp) {
		console.log(`[maiPub] ${label}: ortb2Imp (plcmt/placement often live here):`, video.ortb2Imp);
	}

	// Seat/zone identifiers per bidder: rubicon accountId/siteId/zoneId,
	// imds seatId/tagId, etc. A display zone on a video request no-bids silently.
	const params = Object.keys(video.requestedParams).length ? video.requestedParams : video.adUnitParams;

	if (Object.keys(params).length) {
		console.log(`[maiPub] ${label}: bidder params as sent:`, params);
	} else {
		console.warn(`[maiPub] ${label}: no bidder params captured.`);
	}
}

/**
 * Log why the live (non-probe) video auction came back empty.
 *
 * Only runs under ?maideb (maiPubLog is a no-op otherwise) and never throws.
 *
 * @since TBD
 *
 * @param {string} label      Context for the log line (the unit id).
 * @param {number} eventIndex Index into pbjs.getEvents() taken before the call.
 * @return {void}
 */
function maiPubLogVideoAuctionDiagnostics(label, eventIndex) {
	if (!debug && !log) {
		return;
	}

	if (typeof pbjs === 'undefined') {
		maiPubLog(`MaiAdUnitVideo DM: ${label} diagnostics - pbjs undefined.`);
		return;
	}

	try {
		// Ad units DM built for itself. A video mediaType here means the AUP matched.
		const adUnits = (pbjs.adUnits || []).map((au) => ({
			code: au.code,
			mediaTypes: Object.keys(au.mediaTypes || {}),
			bidders: (au.bids || []).map((b) => b.bidder),
		}));

		maiPubLog(`MaiAdUnitVideo DM: ${label} pbjs.adUnits at callback time:`, adUnits.length ? adUnits : 'none');

		if (typeof pbjs.getEvents !== 'function') {
			maiPubLog(`MaiAdUnitVideo DM: ${label} diagnostics - pbjs.getEvents unavailable.`);
			return;
		}

		const all     = pbjs.getEvents() || [];
		const slice   = 'number' === typeof eventIndex ? all.slice(eventIndex) : all;
		const summary = maiPubSummarizeVideoAuctions(slice);

		maiPubLog(`MaiAdUnitVideo DM: ${label} verdict — ${summary.verdict}`, () => summary);

		// When bidders were asked and all declined, what they were asked is the
		// only thing left to look at.
		if (summary.videoAuctionIds.length && !summary.video.bidResponses.length) {
			maiPubLogVideoMediaTypeObject(summary, label);
		}
	} catch (e) {
		maiPubLog(`MaiAdUnitVideo DM: ${label} diagnostics threw: ${e.message}`);
	}
}

/**
 * Merge Demand Manager's hb_* targeting into our own GAM VMAP pod URL.
 *
 * DM returns a fully constructed adTagUrl, but it is built from the AUP and does
 * not necessarily carry the parameters we depend on (output=vmap, ad_rule=1,
 * cust_params, ppid, min/max_ad_duration). Rather than trade the pod for the
 * bid, we lift just the hb_* key/values out of DM's URL and graft them onto
 * ours, so GAM sees the header-bidding targeting on a request that still asks
 * for the ad-rules pod.
 *
 * @since TBD
 *
 * @param {string} ourUrl    Our GAM VMAP pod URL (from PHP).
 * @param {string} adTagUrl  DM's constructed GAM URL.
 * @return {string|null} Merged URL, or null when there is nothing to merge.
 */
function maiPubMergeVideoHbTargeting(ourUrl, adTagUrl) {
	if (!ourUrl || !adTagUrl) {
		return null;
	}

	try {
		const src = new URL(adTagUrl);
		const dst = new URL(ourUrl);
		const srcCust = new URLSearchParams(src.searchParams.get('cust_params') || '');
		const dstCust = new URLSearchParams(dst.searchParams.get('cust_params') || '');
		const applied = [];

		// HB targeting rides in cust_params on a GAM video tag.
		srcCust.forEach((value, key) => {
			if (key.indexOf('hb_') === 0) {
				dstCust.set(key, value);
				applied.push(key);
			}
		});

		// Defensive: some builds surface hb_* at the top level instead.
		src.searchParams.forEach((value, key) => {
			if (key.indexOf('hb_') === 0) {
				dstCust.set(key, value);
				applied.push(key);
			}
		});

		if (!applied.length) {
			return null;
		}

		// Match how requestAd() writes cust_params: a raw a=1&b=2 string that the
		// URL serializer encodes once as a single value.
		dst.searchParams.set('cust_params', dstCust.toString());

		maiPubLog(`MaiAdUnitVideo DM: merged ${applied.length} hb_* key(s) into the GAM pod URL.`, () => applied);

		return dst.toString();
	} catch (e) {
		maiPubLog(`MaiAdUnitVideo DM: could not merge hb targeting: ${e.message}`);
		return null;
	}
}

/**
 * Resolve the URL to hand IMA from a Demand Manager video bid result.
 *
 * Falls back to our plain GAM pod URL whenever DM has no bid, or when the
 * selected mode cannot produce a usable URL - so a failed/empty auction always
 * degrades to exactly today's behavior rather than an empty player.
 *
 * @since TBD
 *
 * @param {object} videoBidResult DM videoBidResultObject ({adTagUrl, vastUrl}).
 * @param {string} fallbackUrl    Our GAM VMAP pod URL.
 * @return {{url: string, source: string}}
 */
function maiPubResolveVideoBidUrl(videoBidResult, fallbackUrl) {
	const adTagUrl = videoBidResult && videoBidResult.adTagUrl;
	const vastUrl  = videoBidResult && videoBidResult.vastUrl;

	// Both undefined = no bids (documented DM behavior).
	if (!adTagUrl && !vastUrl) {
		return { url: fallbackUrl, source: 'gam-pod (no bid)' };
	}

	if (maiPubVideoBidMode === 'vast' && vastUrl) {
		return { url: vastUrl, source: 'dm-vast (bypasses GAM, no pod)' };
	}

	if (maiPubVideoBidMode === 'adtag' && adTagUrl) {
		return { url: adTagUrl, source: 'dm-adtag' };
	}

	if (maiPubVideoBidMode === 'merge' && adTagUrl) {
		const merged = maiPubMergeVideoHbTargeting(fallbackUrl, adTagUrl);
		if (merged) {
			return { url: merged, source: 'gam-pod + dm hb targeting' };
		}
		// Nothing to merge: prefer DM's own GAM URL over dropping the bid.
		return { url: adTagUrl, source: 'dm-adtag (merge found no hb_*)' };
	}

	// Mode could not be satisfied - use whatever DM gave us, else fall back.
	return {
		url: adTagUrl || vastUrl || fallbackUrl,
		source: adTagUrl ? 'dm-adtag (fallback path)' : (vastUrl ? 'dm-vast (fallback path)' : 'gam-pod'),
	};
}

/**
 * MaiAdUnitVideo Class v2.2
 * Manages video playback, Prebid auctions, and Google IMA SDK integration.
 *
 * @uses Mai_Publisher_Ad_Unit_Video (PHP class) to render the video player.
 */
class MaiAdUnitVideo {
	/**
	 * @param {HTMLElement} element The main container element for the player.
	 */
	constructor(element) {
		// --- Container Elements ---
		this.container = element;

		// --- Player Elements ---
		this.innerEl      = element.querySelector('.mai-ad-unit-video__inner');
		this.videoEl      = element.querySelector('.mai-ad-unit-video__video');
		this.adContainer  = element.querySelector('.mai-ad-unit-video__ad');
		this.controls     = {
			container: element.querySelector('.mai-ad-unit-video__controls'),
			playPauseBtn: element.querySelector('.mai-ad-unit-video__play'),
			progressBar: element.querySelector('.mai-ad-unit-video__progress'),
			progressFilled: element.querySelector('.mai-ad-unit-video__progress-filled'),
			currentTimeEl: element.querySelector('.mai-ad-unit-video__time'),
			durationEl: element.querySelector('.mai-ad-unit-video__duration'),
			muteBtn: element.querySelector('.mai-ad-unit-video__mute'),
			fullscreenBtn: element.querySelector('.mai-ad-unit-video__fullscreen'),
		};

		const unitId = this.container.dataset.unitId;

		// --- Player Configuration ---
		this.config = {
			id: this.container.id,
			isPlaceholder: this.container.classList.contains('is-placeholder'),
			playerType: this.container.dataset.playerType || 'outstream',
			unitId: unitId, // <-- Use the variable here
			contentSrc: this.videoEl ? this.videoEl.getAttribute('src') : '',

			// pulls the vast url from the bidder: Use the unitId variable
			vastUrl: maiPubAdsVars.videoAds[unitId]?.url,
			// Magnite DM slot identifier (the GAM ad unit path). DM matches this against
			// the wrapper's Ad Unit Pattern and supplies the bidders itself - see
			// runPrebidAuction(). Emitted by class-output.php from $ad_tag_params['iu'].
			adSlotName: maiPubAdsVars.videoAds[unitId]?.adSlotName || '',
			width: maiPubAdsVars.videoAds[unitId]?.width || 640,
			height: maiPubAdsVars.videoAds[unitId]?.height || 360,

			// hasFloat: this.container.dataset.floatPositionMobile === 'bottom-right' || this.container.dataset.floatPositionTablet === 'bottom-right' || this.container.dataset.floatPositionDesktop === 'bottom-right',
			// Instream players are click-to-play only. Editor "auto play muted" choice is ignored for instream.
			clickToStart: (this.container.dataset.playerType === 'instream') ? true : (this.container.dataset.clickToStart === 'true'),
			loopAds: this.container.dataset.loopAds === 'true',
			// Floating player configuration
			// floatPositionMobile: this.container.dataset.floatPositionMobile,
			// floatPositionTablet: this.container.dataset.floatPositionTablet,
			// floatPositionDesktop: this.container.dataset.floatPositionDesktop,
			// Media type for targeting
			mediaType: this.container.dataset.mediaType,
		};


		// --- State Management ---
		this.isAdPlaying           = false;
		this.adsLoader             = null;
		this.adsManager            = null;
		this.adProcessStarted      = false;  // I only added because I combined float logic into intersection observer. This can be changed with whatever other logic already exists. --jivedig.
		this.retryTimer            = null;
		this.adState               = 'init';
		this.wasPausedByVisibility = false;
		this.wasPausedByViewability = false;
		this._handlingAdError      = false;
		this._destroyingAdsManager = false;
		this._adRequestSeq         = 0;
		this._adErrorRetryCount    = 0;
		this.maxRetriesReached     = false;
		this.viewabilityObserver   = null;
		this._resizeObserver       = null;
		this._resizeRafId          = 0;
		this.isMuted               = this.container.dataset.muted === 'true';
		this.isFloating            = false;

		// New Fix: Track state properties for requestAnimationFrame throttling & cleanup, v883
		this._onScroll             = null;  // Named reference to cleanly unbind the scroll listener
		this._syncPending          = false; // Tracks if a frame redraw is scheduled
		this._syncRafId            = null;  // Stores the active rAF ID for explicit cancellation

		// Add properties to manage the ad load promise
		this.adLoadPromise = null;         // This will hold the "gate" Promise
		this.resolveAdLoadPromise = null;  // This will store the function to open the gate

		// NOTE: video players no longer build a Prebid ad unit on page. Demand
		// Manager constructs it from the Ad Unit Pattern matched by adSlotName
		// during pbjs.rp.requestVideoBids() - see runPrebidAuction().
	}

	// This is the sequence — Auction → Get VAST URL → Load IMA SDK → Request Ad

	/**
	 * Initializes the player, sets up observers and event listeners.
	 */

	// (v823 Event Listener Ordering)
	// FIX (v866 Dynamic Content Metadata Mapping): Appended a native metadata listener to automatically
	// re-configure horizontal max boundaries into tall vertical proportions if a vertical MP4 file is detected.
	initialize() {
		if (! (this.videoEl && this.adContainer) && !this.config.isPlaceholder) {
			maiPubLog(`MaiAdUnitVideo Error: Missing critical elements for player: ${this.config.id}`);
			return;
		}

		// FIX: Bind the close handler to strip player footprints out of the DOM tree on execution, v836
		// Revised:  842, 843 replace with new code

		// --- REPLACE WITH THIS CORRECTED FLOATING-SAFE ROUTING ---
		this.closeBtn = this.container.querySelector('.mai-ad-unit-video__close');
		if (!this.closeBtn && this.innerEl) {
			this.closeBtn = document.createElement('button');
			this.closeBtn.className = 'mai-ad-unit-video__close';
			this.closeBtn.setAttribute('aria-label', 'Dismiss Advertisement');
			// FIX: Append to innerEl so the button travels with the fixed floating viewport layer
			this.innerEl.appendChild(this.closeBtn); 
			maiPubLog(`MaiAdUnitVideo Cleanup: Programmatically injected close button into ${this.config.id}`);
		}

		// FIX: Route the interactive media tray back to innerEl so it floats smoothly
		if (this.controls.container && this.controls.container.parentNode !== this.innerEl) {
			this.innerEl.appendChild(this.controls.container);
			maiPubLog(`MaiAdUnitVideo Cleanup: Safely migrated controls tray into the floating canvas layer.`);
		}

		// FIX (v866 Native Content Sizing Engine): Monitor file load properties to scale vertical content natively.
		if (this.videoEl) {
			this.videoEl.addEventListener('loadedmetadata', () => {
				const naturalWidth = this.videoEl.videoWidth;
				const naturalHeight = this.videoEl.videoHeight;
				maiPubLog(`MaiAdUnitVideo Sizing Engine: Native media file metadata loaded (${naturalWidth}x${naturalHeight}) for ${this.config.id}`);
				
				if (naturalWidth && naturalHeight) {
					this.container.style.setProperty('--aspect-ratio', `${naturalWidth}/${naturalHeight}`);
					
					// If the source video file is taller than it is wide, adjust the horizontal layout limits
					if (naturalWidth < naturalHeight) {
						maiPubLog(`MaiAdUnitVideo Sizing Engine: Vertical media asset confirmed. Adjusting container constraints.`);
						this.container.style.setProperty('--max-width', '360px');
						this.container.style.setProperty('--max-height', '640px');
					}
				}
			});
		}

		this.bindEvents();

		// Set up a persistent intersection observer for outstream videos
		const observerOptions = {
			root: null,
			rootMargin: '200px 0px', 
			threshold: [0.5]         
		};

		this.viewabilityObserver = new IntersectionObserver(this.handleViewabilityChange.bind(this), observerOptions);
		this.viewabilityObserver.observe(this.container);

		// === OPTIMIZATION: SYNCHRONIZED MENU PROTECTION GUARD (v831 Sync Scroll) ===, v883
		// Monitors real-time page physics to prevent layout containers from overlapping elements.
		// New Fix: Route named handler through an rAF throttle valve to eliminate layout thrashing
		this._onScroll = () => {
			if (!this._syncPending) {
				this._syncPending = true;
				this._syncRafId = requestAnimationFrame(() => {
					this.syncPlaybackState();
					this._syncPending = false;
				});
			}
		};
		window.addEventListener('scroll', this._onScroll, { passive: true });

		// FIX: Force initial sync to establish the correct layout context, var841
		this.syncPlaybackState();

		// A player can boot after maiPubRunPrelude already measured the footer, so
		// re-sync the --footer-offset onto every player now (cheap + idempotent). Keeps
		// the float clear of the sticky footer regardless of init order.
		maiPubSyncFooterOffset();

		// For outstream videos, set up autoplay muted with ad pod looping
		if ('outstream' === this.config.playerType) {
			maiPubLog(`MaiAdUnitVideo: Outstream video ${this.config.id} initialized. Loop ads: ${this.config.loopAds}, Muted: ${this.isMuted}`);
			this.adLoaded = false; 

			if (this.isMuted === undefined || this.isMuted === null) {
				this.isMuted = true;
				maiPubLog(`MaiAdUnitVideo: Auto-setting muted=true for autoplay compliance: ${this.config.id}`);
			}
			return;
		}
	}

	/**
	 * Starts the ad process for instream videos.
	 */
	startAdProcess() {
		if (this.adProcessStarted) {
			return;
		}

		this.adProcessStarted = true;

		// Check if Magnite/Prebid is enabled globally.
		if (maiPubVideoHbEnabled()) {
			maiPubLog(`MaiAdUnitVideo: Magnite video enabled. Starting Prebid auction for ${this.config.id}.`);
			this.runPrebidAuction();
		} else {
			// If Magnite/Prebid is disabled, skip the auction and go straight to GAM.
			maiPubLog(`MaiAdUnitVideo: Magnite disabled. Skipping Prebid, calling loadIMA directly from GAM for ${this.config.id}.`);
			this.loadIMA(this.config.vastUrl); // Use the fallback GAM URL.
		}
	}

	/**
	 * Opens the gate this pod holds on the page's first display ads (see getVideoLoadPromises):
	 * its ad started, failed, came back with no ad, was dismissed or is being replaced.
	 *
	 * @since TBD
	 */
	_openAdGate() {
		if (this.resolveAdLoadPromise) {
			this.resolveAdLoadPromise();
		}

		this.adLoadPromise = null;
		this.resolveAdLoadPromise = null;
	}

	/**
	 * Loads an ad pod for outstream videos with autoplay muted
	 */
	loadAdPod() {
		if (this.isAdPlaying || this.adState === 'loading') {
			maiPubLog(`MaiAdUnitVideo: Skipping loadAdPod for ${this.config.id} — ad is already ${this.adState}.`);
			return;
		}
		if (this.maxRetriesReached) {
			maiPubLog(`MaiAdUnitVideo: Skipping loadAdPod for ${this.config.id} — max retries reached.`);
			return;
		}

		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}

		this.adState = 'loading';
		if (this.adLoaded && !this.config.loopAds) {
			maiPubLog(`MaiAdUnitVideo: Ad pod already loaded for ${this.config.id} and looping is disabled`);
			return;
		}

		// A pod still holding the gate is being replaced: let its waiters check again, against this one.
		this._openAdGate();

		// Create and store the ad load promise "gate"
		// This promise will resolve when the ad starts (onAdEvent), errors (onAdError) or
		// ends with no ad (ALL_ADS_COMPLETED without STARTED).
		this.adLoadPromise = new Promise((resolve) => {
			// Store the 'resolve' function so other methods can call it
			// to "open the gate" when the ad load is complete.
			this.resolveAdLoadPromise = resolve;
		});

		// For looping ads, we allow retries even if adLoaded is true
		if (this.adLoaded && this.config.loopAds) {
			maiPubLog(`MaiAdUnitVideo: Retrying ad pod for ${this.config.id} (looping enabled)`);
		} else {
			this.adLoaded = true;
			maiPubLog(`MaiAdUnitVideo: Loading ad pod for outstream video ${this.config.id}`);
		}

		// A new pod: no ad in it has started or failed to play, and it hasn't been counted as a failed try.
		this._podStarted   = false;
		this._podPlayError = false;
		this._podCounted   = false;

		// Before the first auction, check the browser lets a video start without a tap (once per page,
		// so later pods don't wait on it).
		maiPubCheckAutoplay(this.adContainer || this.container, !this.isMuted).then((autoplay) => {
			if (this.maxRetriesReached) {
				return;
			}

			// No video may start without a tap (iPhone Low Power Mode, a "never auto-play" setting).
			// Every ad would win its auction, then fail to play and send GAM a VAST 400.
			if ('blocked' === autoplay) {
				this.adState = 'completed';
				this._haltRequests('the browser does not let video start without a tap');
				return;
			}

			// Sound needs a tap but muted video may start: play muted, and tell IMA so.
			if ('muted' === autoplay && !this.isMuted) {
				this.isMuted = true;
				maiPubLog(`MaiAdUnitVideo: Sound needs a tap here. Playing ${this.config.id} muted.`);
			}

			// Check if Magnite/Prebid is enabled globally
			if (maiPubVideoHbEnabled()) {
				maiPubLog(`MaiAdUnitVideo: Magnite video enabled. Starting Prebid auction for ${this.config.id}.`);
				this.runPrebidAuction();
			} else {
				// If Magnite/Prebid is disabled, skip the auction and go straight to GAM
				maiPubLog(`MaiAdUnitVideo: Magnite disabled. Skipping Prebid, calling loadIMA directly from GAM for ${this.config.id}.`);
				this.loadIMA(this.config.vastUrl);
			}
		});
	}

	/**
	 * Binds all necessary event listeners for the player controls and video element.
	 */

	bindEvents() {

		// FIX: Bind the close handler to strip player footprints out of the DOM tree on execution, v836
		if (this.closeBtn) {
			this.closeBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				e.preventDefault();
				maiPubLog(`MaiAdUnitVideo Close Button Fired for ${this.config.id}`);
				this.dismissPlayerPermanently();
			});
		}

		// Add click-to-play functionality for all video elements
		// FIX (v864 Propagation Guard): Pass the event object (e) and invoke stopPropagation 
		// to prevent click interactions from bubbling up to innerEl and triggering double-toggles.
		this.videoEl.addEventListener('click', (e) => {
			maiPubLog(`MaiAdUnitVideo: Video element clicked for ${this.config.id}`);

			// For outstream videos, handle click based on ad state
			if (this.config.playerType === 'outstream') {
				// If an ad is loaded and ready to play, start it
				if (this.adsManager && !this.isAdPlaying) {
					maiPubLog(`MaiAdUnitVideo: Starting outstream ad for ${this.config.id}`);
					this.adsManager.start();
					return;
				}

				// Otherwise, toggle mute/unmute
				this.toggleMute();
				return;
			}

			// FIX (v862 Video Surface Initiation): If this is an instream player and click-to-start is active,
			// clicking the main video surface layer must trigger the ad auction before attempting track toggles.
			if (this.config.playerType === 'instream' && this.config.clickToStart && !this.adProcessStarted) {
				e.preventDefault();
				e.stopPropagation();
				maiPubLog(`MaiAdUnitVideo: Video surface clicked before ad process initialization. Booting ad pipeline.`);
				this.startAdProcess();
				return;
			}

			// FIX (v865 Native Controls Canvas Guard): If native browser controls are actively rendered on screen,
			// do NOT invoke a custom togglePlay() command on a direct video frame click. The browser natively 
			// handles canvas clicks to toggle play/pause; running custom JS alongside it creates a double-toggle conflict.
			if (this.config.playerType === 'instream' && this.videoEl.controls) {
				maiPubLog(`MaiAdUnitVideo: Canvas frame click delegated entirely to native browser control logic.`);
				return;
			}

			e.preventDefault();
			e.stopPropagation();
			this.togglePlay();
		});

		// FIX (v862 Container Surface Fallback): Secure an alternate initiation listener on the inner layout frame
		// to catch user inputs even if a blank poster frame or an empty media container covers the underlying video node.
		if (this.config.playerType === 'instream' && this.innerEl) {
			this.innerEl.addEventListener('click', (e) => {
				// Ignore canvas clicks that explicitly targeted the control tray buttons or the upper dismiss link
				if (e.target.closest('.mai-ad-unit-video__close') || e.target.closest('.mai-ad-unit-video__controls')) {
					return;
				}
				
				// FIX (v865 Container Bubble Guard): If the click explicitly landed directly on the video track element,
				// exit immediately. The video element's native handler will process the interaction without double-toggling here.
				if (e.target === this.videoEl) {
					return;
				}

				// FIX (v863 Dynamic Lifecycle Toggle): Transform the background canvas wrapper into a dual-purpose listener.
				// If the ad process hasn't fired yet, boot the pipeline. If the ad is already over, toggle the content playback track.
				if (this.config.clickToStart) {
					// FIX (v866 User Gesture Protection): Isolated preventDefault so it ONLY executes on the initial 
					// ad initialization pass. Calling preventDefault during content playback breaks browser security guidelines.
					e.stopPropagation();
					
					if (!this.adProcessStarted) {
						e.preventDefault();
						maiPubLog(`MaiAdUnitVideo: Inner layout wrapper clicked. Booting ad pipeline.`);
						this.startAdProcess();
					} else {
						maiPubLog(`MaiAdUnitVideo: Center overlay area clicked post-ad. Toggling content playback.`);
						this.togglePlay();
					}
				}
			});
		}

		// FIX: Register the custom mute button listener universally for all player layouts, v830
		if (this.controls.muteBtn) {
			this.controls.muteBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.toggleMute();
			});
		}		

		if (this.config.playerType === 'instream') {
			this.controls.playPauseBtn.addEventListener('click', () => {
				if (this.config.clickToStart && !this.adProcessStarted) {
					this.startAdProcess();
				} else if (!this.isAdPlaying) {
					this.togglePlay();
				}
			});
			this.videoEl.addEventListener('timeupdate', () => this.updateProgressBar());
			this.videoEl.addEventListener('durationchange', () => this.updateTimeDisplay());
			this.videoEl.addEventListener('play', () => {
				this.container.classList.remove('is-paused');
				this.container.classList.add('is-playing');
			});
			this.videoEl.addEventListener('pause', () => {
				this.container.classList.remove('is-playing');
				this.container.classList.add('is-paused');
			});
			this.videoEl.addEventListener('ended', () => {
				maiPubLog(`MaiAdUnitVideo: Content ended for ${this.config.id}, notifying IMA for post-roll`);
				if (this.adsLoader) {
					this.adsLoader.contentComplete();
				}
			});
			this.controls.muteBtn.addEventListener('click', () => this.toggleMute());

			// this.controls.fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
			// --- AFTER (Optimized v832 Contextual Playback Gate) ---
			if (this.controls.fullscreenBtn) {
				if (this.config.playerType === 'instream') {
					// Instream View: Enable interactive fullscreen capabilities
					this.controls.fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
					maiPubLog(`MaiAdUnitVideo Controls: Instream view verified. Fullscreen button active.`);
				} else {
					// Outstream Ads: Completely hide the button element from layout rendering
					this.controls.fullscreenBtn.style.setProperty('display', 'none', 'important');
					maiPubLog(`MaiAdUnitVideo Controls: Outstream ad verified. Fullscreen button hidden programmatically.`);
				}
			}

			this.controls.progressBar.addEventListener('click', (e) => this.seek(e));
		} else {
			// For outstream videos, add basic play/pause state tracking
			this.videoEl.addEventListener('play', () => {
				this.container.classList.remove('is-paused');
				this.container.classList.add('is-playing');
				maiPubLog(`MaiAdUnitVideo: Video started playing for ${this.config.id}`);
			});
			this.videoEl.addEventListener('pause', () => {
				this.container.classList.remove('is-playing');
				this.container.classList.add('is-paused');
				maiPubLog(`MaiAdUnitVideo: Video paused for ${this.config.id}`);
			});
		}
	}

	togglePlay() { this.videoEl.paused ? this.videoEl.play() : this.videoEl.pause(); }
	toggleMute() {	// v830
		this.isMuted = !this.isMuted;
		this.videoEl.muted = this.isMuted;
		this.container.classList.toggle('is-muted', this.isMuted);

		// Synchronize the volume layer with Google IMA if an ad is actively playing
		if (this.adsManager) {
			const targetVolume = this.isMuted ? 0 : 1;
			this.adsManager.setVolume(targetVolume);
			maiPubLog(`MaiAdUnitVideo: Toggled adsManager volume level to ${targetVolume}`);
		}
	}
	toggleFullscreen() {
		if (!document.fullscreenElement) {
			this.container.requestFullscreen().catch(err => {
				maiPubLog(`Fullscreen Error: ${err.message}`);
			});
		} else {
			document.exitFullscreen();
		}
	}

	updateProgressBar() {
		const percentage = (this.videoEl.currentTime / this.videoEl.duration) * 100;
		this.controls.progressFilled.style.width = `${percentage}%`;
		this.updateTimeDisplay();
	}

	updateTimeDisplay() {
		this.controls.currentTimeEl.textContent = this.formatTime(this.videoEl.currentTime);
		if (!isNaN(this.videoEl.duration)) {
			this.controls.durationEl.textContent = this.formatTime(this.videoEl.duration);
		}
	}

	formatTime(time) {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60).toString().padStart(2, '0');
		return isNaN(minutes) || isNaN(seconds) ? '0:00' : `${minutes}:${seconds}`;
	}

	/**
	 * Outstream-only: show the countdown pill and click-for-sound overlay while
	 * an ad is playing. Driven by adsManager.getRemainingTime() at 4 Hz.
	 */
	startAdCountdown() {
		if (this.adCountdownTimer) {
			clearInterval(this.adCountdownTimer);
		}

		this.container.classList.add('is-ad-playing');
		// Reflect mute state so the sound overlay is gated correctly.
		this.container.classList.toggle('is-muted', this.isMuted);

		const badgeTime = this.container.querySelector('.mai-ad-unit-video__ad-badge-time');
		const soundPrompt = this.container.querySelector('.mai-ad-unit-video__sound-prompt');

		const tick = () => {
			if (!this.adsManager) return;
			const remaining = this.adsManager.getRemainingTime();
			if (badgeTime) {
				badgeTime.textContent = this.formatTime(remaining > 0 ? remaining : 0);
			}
		};
		tick();
		this.adCountdownTimer = setInterval(tick, 250);

		if (soundPrompt && !this.soundPromptBound) {
			soundPrompt.addEventListener('click', (e) => {
				e.stopPropagation();
				if (this.adsManager) {
					this.adsManager.setVolume(1);
				}
				this.videoEl.muted = false;
				this.isMuted = false;
				this.container.classList.remove('is-muted');
				this.container.classList.add('is-sound-prompt-dismissed');
			});
			this.soundPromptBound = true;
		}
	}

	stopAdCountdown() {
		if (this.adCountdownTimer) {
			clearInterval(this.adCountdownTimer);
			this.adCountdownTimer = null;
		}
		this.container.classList.remove('is-ad-playing');
	}

	seek(event) {
		const rect = this.controls.progressBar.getBoundingClientRect();
		const percent = Math.min(Math.max(0, event.clientX - rect.left), rect.width) / rect.width;
		this.videoEl.currentTime = percent * this.videoEl.duration;
	}

	// /**
	//  * Gets the current float position setting based on viewport width.
	//  * Uses breakpoints from mai-publisher-styles.css
	//  */
	// getCurrentFloatPosition() {
	// 	const width = window.innerWidth || document.documentElement.clientWidth;

	// 	if (width <= 727) { // Mobile
	// 		return this.config.floatPositionMobile;
	// 	}
	// 	if (width >= 728 && width <= 1023) { // Tablet
	// 		return this.config.floatPositionTablet;
	// 	}
	// 	if (width >= 1024) { // Desktop
	// 		return this.config.floatPositionDesktop;
	// 	}

	// 	return 'inline'; // Default fallback
	// }

	/**
	 * Handles viewability changes for outstream players.
	 * Fired by the persistent viewabilityObserver.
	 */
	// v826 comment this out and replace below 
	// handleViewabilityChange(entries) {
	// 	// Get the first entry and its properties.


	// 	// --- BEFORE (v821 Legacy Style Check) ---
	// 	// const container = entries[0];
	// 	// // const container = entry.target;
	// 	// const innerEl   = container.target.querySelector('.mai-ad-unit-video__inner');
	// 	// const isInView  = container.isIntersecting && container.intersectionRatio > 0;
	// 	// const position  = getComputedStyle(innerEl).position;
	// 	// const isFixed   = 'fixed' === position; // This would only be fixed if the responsive CSS allows it.
	// 	// // It's simpler than checking window dimensions in JS. --jivedig.

	// 	// --- AFTER (Optimized v822 Viewport Coordinate Guard) ---
	// 	const container = entries[0]; // Fixed: Using the first entry directly
	// 	const innerEl   = container.target.querySelector('.mai-ad-unit-video__inner');
	// 	const isInView  = container.isIntersecting && container.intersectionRatio > 0;
		
	// 	// Physical Coordinate Check: Measure the actual location of the inner player.
	// 	// If the inner layout frame has visible height on the user's screen space, 
	// 	// it means it is actively floating and visible.
	// 	const innerRect = innerEl ? innerEl.getBoundingClientRect() : null;
	// 	const isInnerVisible = innerRect && innerRect.width > 0 && innerRect.height > 0 && 
	// 						innerRect.bottom > 0 && innerRect.top < (window.innerHeight || document.documentElement.clientHeight);
		
	// 	const position  = getComputedStyle(innerEl).position;
	// 	const isFixed   = 'fixed' === position || isInnerVisible || container.target.classList.contains('is-floating');

	// 	// Set flag for first time the ad is seen.
	// 	let isSeen = false;

	// 	// If the container is intersecting.
	// 	// --- AD IS MORE THAN 50% VISIBLE ---
	// 	// (This handles scrolling back into view, or tabbing back to the page)
	// 	if ( container.isIntersecting ) {
	// 		// If not a placeholder.
	// 		if ( ! this.config.isPlaceholder ) {

	// 			// We must only call ONE loading function, not two.
	// 			if (this.config.playerType === 'outstream') {
	// 				// Initial load only — loops and error retries are driven by
	// 				// ALL_ADS_COMPLETED / onAdError, not repeated observer callbacks.
	// 				if (!this.adLoaded) {
	// 					maiPubLog(`MaiPlayer: Outstream ad ${this.config.id} is > 50% visible. Calling loadAdPod().`);
	// 					this.loadAdPod();
	// 				}
	// 			} else {
	// 				// For instream, only auto-start if clickToStart is disabled.
	// 				// If clickToStart is true, the play button triggers startAdProcess() on first click.
	// 				if (!this.config.clickToStart) {
	// 					maiPubLog(`MaiPlayer: Instream ad ${this.config.id} is > 50% visible. Calling startAdProcess().`);
	// 					this.startAdProcess();
	// 				} else {
	// 					maiPubLog(`MaiPlayer: Instream ad ${this.config.id} is > 50% visible. Waiting for user click (click-to-start enabled).`);
	// 				}
	// 			}

	// 			// Resume if we paused due to scrolling out of view.
	// 			if (this.adsManager && this.isAdPlaying && this.wasPausedByViewability) {
	// 				maiPubLog(`MaiPlayer: Ad for ${this.config.id} is more than 50% visible, RESUMING.`);
	// 				this.adsManager.resume();
	// 				this.wasPausedByViewability = false;
	// 			}
	// 		}

	// 		// If not already seen, add the is-seen class.
	// 		if ( ! isSeen ) {
	// 			isSeen = true;
	// 			this.container.classList.add('is-seen');
	// 		}

	// 		// Add 'in-view' class to the player container.
	// 		// We always add this, regardless of float position.
	// 		if ( isInView ) {
	// 			if ( ! this.container.classList.contains('in-view') ) {
	// 				this.container.classList.add('in-view');
	// 			}
	// 		}
	// 	}
	// 	// Not intersecting.
	// 	// entry.isIntersecting will be FALSE when ratio drops < 0.5
	// 	// --- AD IS LESS THAN 50% VISIBLE ---
	// 	else {
	// 		// If the container is not fixed, remove the in-view class.
	// 		// If it was fixed (floating), it's still in view, so we don't remove the class.
	// 		if ( ! isFixed ) {
	// 			maiPubLog(`MaiPlayer: Ad for ${this.config.id} is 'inline' only and less than 50% visible, PAUSING.`);

	// 			// Remove the in-view class from the container.
	// 			if ( this.container.classList.contains('in-view') ) {
	// 				this.container.classList.remove('in-view');
	// 			}
	// 		}

	// 		// If not a placeholder.
	// 		if ( ! this.config.isPlaceholder ) {
	// 			if (this.adsManager && this.isAdPlaying && !isFixed) {
	// 				this.adsManager.pause();
	// 				this.wasPausedByViewability = true;
	// 			}
	// 		}
	// 	}
	// }

	/**
		 * Handles viewability changes for outstream players.
		 * Fired by the persistent viewabilityObserver.
		 * v877
		 */
	handleViewabilityChange(entries) {
		const container = entries[0];

		// Cache the native browser intersection state
		this.isPlaceholderIntersecting = container.isIntersecting;

		if (container.isIntersecting) {
			if (!this.config.isPlaceholder) {
				if (this.config.playerType === 'outstream' && !this.adLoaded) {
					maiPubLog(`MaiAdUnitVideo: Outstream ad ${this.config.id} is visible. Calling loadAdPod().`);
					this.loadAdPod();
				} else if (this.config.playerType === 'instream' && !this.config.clickToStart) {
					maiPubLog(`MaiAdUnitVideo: Instream ad ${this.config.id} is visible. Calling startAdProcess().`);
					this.startAdProcess();
				}
			}

			if (!this.container.classList.contains('is-seen')) {
				this.container.classList.add('is-seen');
			}
		}
		
		// Delegate view state evaluations exclusively to the geometric sync loop
		this.syncPlaybackState();
	}

	/**
	 * Determines if floating is enabled for the visitor's current viewport breakpoint. v833
	 */
	canFloatOnCurrentViewport() {
		const width = window.innerWidth || document.documentElement.clientWidth;
		let floatSetting = 'none';

		if (width <= 727) {
			floatSetting = this.container.getAttribute('data-float-position-mobile');
		} else if (width >= 728 && width <= 1023) {
			floatSetting = this.container.getAttribute('data-float-position-tablet');
		} else {
			floatSetting = this.container.getAttribute('data-float-position-desktop');
		}

		return floatSetting && floatSetting !== 'none' && floatSetting !== 'inline';
	}


	syncPlaybackState() {
		if (!this.container) return;

		const scrollTop = window.scrollY || document.documentElement.scrollTop;

		// === PERF (v885): syncPlaybackState runs once per animation frame per player. ===
		// Three changes here cut avoidable per-frame layout cost:
		//   (1) Cache the .site-header element (was a document.querySelector every frame).
		//   (2) Read the header rect once (was getBoundingClientRect twice).
		//   (3) Read the container rect BEFORE writing --computed-header-offset, so measuring
		//       geometry no longer forces a synchronous reflow right after a style write.
		// getComputedStyle stays per-frame on purpose, in case a theme swaps the header
		// position between static and fixed/sticky on scroll.
		if (this._headerEl === undefined) {
			this._headerEl = document.querySelector('.site-header');
		}
		const headerElement = this._headerEl;

		// (1)+(2) Evaluate menu proximity and header offset from a single rect read.
		let isNearMenu = scrollTop < 120;
		let computedHeaderOffset = 0;
		if (headerElement) {
			const headerStyle = window.getComputedStyle(headerElement);
			const isStickyOrFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';
			const headerRect = headerElement.getBoundingClientRect();

			if (isStickyOrFixed) {
				isNearMenu = scrollTop < 150;
			} else {
				isNearMenu = headerRect.bottom > 0;
			}

			computedHeaderOffset = Math.max(0, headerRect.bottom);
		}

		// 3. Determine exact layout state contexts
		// Fix: Rely on the native browser intersection state tracker, falling back to rect geometry only on initial boot, v875
		const rect = this.container.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

		// (3) Write the header-offset CSS var only after the geometry reads above, to avoid
		// a read-after-write reflow within the frame.
		this.container.style.setProperty('--computed-header-offset', `${computedHeaderOffset}px`);

		// Guard: If the container collapses to 0 width or height during a browser reflow tick,
		// exit early to protect the active inline structural layout maps.
		if (rect.width === 0 || rect.height === 0) {
			return;
		}

		// Calculate if the native layout slot placeholder is physically inside the viewport bounds flow.
		// A slot is truly in view if its bottom edge is below the header and its top edge is above the fold.
		const isPlaceholderInView = rect.bottom > computedHeaderOffset && rect.top < viewportHeight;

		// Fix #855: Restrict below-the-fold class removal strictly to un-scrolled initial paints
		const isBelowViewport = rect.top >= viewportHeight;
		if (isBelowViewport && scrollTop === 0) {
			this.container.classList.remove('is-seen');
		}

		// Fix #854 + float-recovery: `is-seen` latches once the placeholder is genuinely in
		// view, at ANY scroll position — not only at scrollTop === 0. Previously the re-add was
		// gated on scrollTop === 0, which left a below-the-fold player unable to recover the
		// class on a normal downward scroll: the boot guard above strips `is-seen` at
		// scrollTop === 0, and the IntersectionObserver had already spent its entry transition
		// at boot (rootMargin 200px), so no fresh isIntersecting event fired to re-add it. The
		// player refused to float until the reader scrolled back up (forcing a new observer
		// entry) and down again. isPlaceholderInView and isBelowViewport are mutually exclusive,
		// so this add never fights the strip guard within a single frame.
		if (isPlaceholderInView) {
			this.container.classList.add('is-seen');
		}

		const canFloat = this.canFloatOnCurrentViewport();

		// Add a strict scrollTop requirement to block floating layouts on initial boot
		const shouldFloatNow = canFloat && this.container.classList.contains('is-seen') && !isPlaceholderInView && !isBelowViewport && scrollTop > 0;
		
		// Only activate the menu guard if the ad is attempting to float over header navigation space
		const isViolatingMenuSpace = isNearMenu && shouldFloatNow;

		if (isPlaceholderInView || (shouldFloatNow && !isViolatingMenuSpace)) {
			// Restore visibility state
			if (this.container.classList.contains('is-menu-collapsed')) {
				this.container.classList.remove('is-menu-collapsed');
				if (this.innerEl) {
					this.innerEl.style.removeProperty('display');
				}
				maiPubLog('MaiAdUnitVideo Sync: Clearing menu collapse fields.');
			}

			// Play control handling
			if (this.adsManager && this.adState === 'playing' && (!this.isAdPlaying || this.wasPausedByViewability)) {
				maiPubLog('MaiAdUnitVideo Sync: Resuming active video ad playback.');
				this.adsManager.resume();
				this.isAdPlaying = true;
				this.wasPausedByViewability = false;
			}
		} else {
			// Trigger collapse only if actively violating header or floating near menu space
			if (isViolatingMenuSpace && !this.container.classList.contains('is-menu-collapsed')) {
				this.container.classList.add('is-menu-collapsed');
				if (this.innerEl) {
					this.innerEl.style.setProperty('display', 'none', 'important');
				}
				maiPubLog('MaiAdUnitVideo Sync: Near top header layout. Collapsing floating container.');
			}

			// Safe pause operation
			if (this.adsManager && this.isAdPlaying) {
				maiPubLog('MaiAdUnitVideo Sync: Pausing active video ad playback.');
				this.adsManager.pause();
				this.isAdPlaying = false;
				this.wasPausedByViewability = true;
			}
		}

		// FIX (v861 Master Class Governor): Control layout assignment explicitly.
		// If an ad is authorized to float right now, drop the inline tag. Otherwise, enforce it.
		if (shouldFloatNow && !isViolatingMenuSpace) {
			this.container.classList.remove('in-view');
		} else {
			this.container.classList.add('in-view');
		}
	}

	/**
	 * Read-only test for whether the player is actually visible to the user right
	 * now — either in-flow within the viewport, or pinned as a floating clone.
	 *
	 * This mirrors the visibility predicate in syncPlaybackState() exactly:
	 *   isPlaceholderInView || (shouldFloatNow && !isViolatingMenuSpace)
	 * but performs NO side effects (no CSS var writes, no class mutations), so it
	 * is safe to call outside the scroll/rAF sync path. Used to gate loop pod
	 * requests so we never fire a GAM request into an off-screen slot.
	 *
	 * NOTE: keep this in sync with syncPlaybackState() if that predicate changes.
	 *
	 * @return {boolean} True when the player is on screen (in-flow or floating).
	 */
	isPlayerUserVisible() {
		if (!this.container) {
			return false;
		}

		const scrollTop = window.scrollY || document.documentElement.scrollTop;

		if (this._headerEl === undefined) {
			this._headerEl = document.querySelector('.site-header');
		}
		const headerElement = this._headerEl;

		let isNearMenu = scrollTop < 120;
		let computedHeaderOffset = 0;
		if (headerElement) {
			const headerStyle = window.getComputedStyle(headerElement);
			const isStickyOrFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';
			const headerRect = headerElement.getBoundingClientRect();
			if (isStickyOrFixed) {
				isNearMenu = scrollTop < 150;
			} else {
				isNearMenu = headerRect.bottom > 0;
			}
			computedHeaderOffset = Math.max(0, headerRect.bottom);
		}

		const rect = this.container.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

		// A collapsed/zero-size container is not measurable — treat as not visible.
		if (rect.width === 0 || rect.height === 0) {
			return false;
		}

		const isPlaceholderInView = rect.bottom > computedHeaderOffset && rect.top < viewportHeight;
		const isBelowViewport = rect.top >= viewportHeight;
		const canFloat = this.canFloatOnCurrentViewport();
		const shouldFloatNow = canFloat && this.container.classList.contains('is-seen') && !isPlaceholderInView && !isBelowViewport && scrollTop > 0;
		const isViolatingMenuSpace = isNearMenu && shouldFloatNow;

		return isPlaceholderInView || (shouldFloatNow && !isViolatingMenuSpace);
	}

	/**
	 * Unconditional DOM Cleansing Utility
	 * Completely wipes tracking tokens and layouts to avoid ghost containers.
	 */
/**
	 * Unconditional DOM Cleansing Utility
	 * Completely wipes tracking tokens and layouts to avoid ghost containers.
	 */
	// v833
	cleanPlayerDOMState(isResetOnly = false) {
		if (this.adContainer) {
			this.adContainer.innerHTML = '';
		}
		if (this.container) {
			this.container.classList.remove('is-playing', 'in-view', 'is-sound-prompt-dismissed', 'is-menu-collapsed');
			
			// Protect the seen state flag across normal looping pods
			if (!isResetOnly) {
				this.container.classList.remove('is-seen');
			}
		}
		if (this.innerEl) {
			this.innerEl.style.removeProperty('display');
			this.innerEl.style.removeProperty('position');
		}
	}


	// // this version loaded the script dynamically.  We've moved script loading to class-output.php
	// loadIMA(vastUrl) { // It now accepts the VAST URL.
	// 	// Check if GPT is loaded and has the necessary methods
	// 	if (window.googletag && window.googletag.pubads && typeof window.googletag.pubads === 'function') {
	// 		maiPubLog(`MaiAdUnitVideo: GPT is loaded for ${this.config.id}. Proceeding with IMA SDK loading.`);
	// 		this.loadIMASDK(vastUrl);
	// 	} else {
	// 		// Wait for GPT to be loaded using the command queue
	// 		maiPubLog(`MaiAdUnitVideo: GPT not loaded yet for ${this.config.id}. Waiting for GPT to load.`);
	// 		window.googletag.cmd.push(() => {
	// 			maiPubLog(`MaiAdUnitVideo: GPT is now loaded for ${this.config.id}. Proceeding with IMA SDK loading.`);
	// 			this.loadIMASDK(vastUrl);
	// 		});
	// 	}
	// }

	// loadIMASDK(vastUrl) {
    //     if (window.google && window.google.ima) {
	// 		// IMA is already loaded, just request the ad.
	// 		maiPubLog(`MaiAdUnitVideo: IMA SDK already loaded for ${this.config.id}.`);
	// 		this.requestAd(vastUrl);
    //         return;
    //     }

    //     if (window.isImaSdkLoading) {
	// 		// Another player is loading it, wait for the 'imaReady' event.
	// 		maiPubLog(`MaiAdUnitVideo: IMA SDK is loading via another player for ${this.config.id}. Waiting.`); // Add log
	// 		document.addEventListener('imaReady', () => {
	// 				maiPubLog(`MaiAdUnitVideo: imaReady event received for ${this.config.id}.`); // Add log
	// 				this.requestAd(vastUrl);
	// 		}, { once: true });
    //         return;
    //     }

	// 	maiPubLog(`MaiAdUnitVideo: Attempting to load IMA SDK for ${this.config.id}.`);
    //     window.isImaSdkLoading = true;
    //     const script = document.createElement('script');
    //     script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
    //     script.onload = () => {
	// 		maiPubLog(`MaiAdUnitVideo: IMA SDK successfully loaded for ${this.config.id}.`);
    //         window.isImaSdkLoading = false;
    //         document.dispatchEvent(new CustomEvent('imaReady'));
	// 		// CHANGE: The final step now happens here, when the SDK is guaranteed to be ready.
	// 		this.requestAd(vastUrl);
    //     };
    //     script.onerror = () => {
	// 		maiPubLog(`MaiAdUnitVideo Error: Failed to load Google IMA SDK script for ${this.config.id}.`);
    //          window.isImaSdkLoading = false;
	// 		// Optionally, handle the error state here (e.g., play content if instream)
	// 		if (this.config.playerType === 'instream' && this.config.contentSrc) {
	// 			maiPubLog(`MaiAdUnitVideo: Playing content for ${this.config.id} after IMA SDK load failure.`);
	// 			this.videoEl.play();
	// 		}
    //     }
    //     document.head.appendChild(script);
    // }

    /**
     * Request video bids through Magnite Demand Manager, then hand IMA a URL.
     *
     * DM resolves bidders from the wrapper's Ad Unit Pattern by matching
     * adSlotName (our GAM ad unit path) - we send no bidder params. Every exit
     * path calls loadIMA() exactly once, and any failure degrades to the plain
     * GAM VMAP pod URL, i.e. exactly the pre-header-bidding behavior.
     *
     * @since TBD
     */
    runPrebidAuction() {
        const fallbackUrl = this.config.vastUrl;

        // One-shot handoff: whichever of (callback | failsafe | guard) lands first wins.
        let settled = false;
        const settle = (url, source) => {
            if (settled) {
                return;
            }
            settled = true;
            maiPubLog(`MaiAdUnitVideo DM: ${this.config.id} using ${source}.`);
            this.loadIMA(url);
        };

        // No adSlotName means PHP could not build an ad unit path - nothing for DM
        // to pattern-match against, so skip the auction entirely.
        if (!this.config.adSlotName) {
            settle(fallbackUrl, 'gam-pod (no adSlotName)');
            return;
        }

        if (typeof pbjs === 'undefined' || !pbjs.que) {
            settle(fallbackUrl, 'gam-pod (pbjs unavailable)');
            return;
        }

        pbjs.que.push(() => {
            // requestVideoBids only exists in a Demand Manager wrapper build. If the
            // page is running stock Prebid, fall back rather than throw.
            if (!pbjs.rp || typeof pbjs.rp.requestVideoBids !== 'function') {
                settle(fallbackUrl, 'gam-pod (pbjs.rp.requestVideoBids unavailable)');
                return;
            }

            const failsafe = setTimeout(() => {
                settle(fallbackUrl, `gam-pod (DM timeout ${maiPubVideoBidTimeout}ms)`);
            }, maiPubVideoBidTimeout);

            // The GAM ad unit path PHP emitted; DM matches it against the AUP
            // Slot Pattern. Same path display and the GAM pod already use.
            const adSlotName = this.config.adSlotName;
            const playerSize = [this.config.width, this.config.height];

            // Snapshot the event log so the auction this call creates can be told
            // apart from the display auctions already in it.
            const eventIndex = (typeof pbjs.getEvents === 'function' && (pbjs.getEvents() || []).length) || 0;

            maiPubLog(`MaiAdUnitVideo DM: requesting video bids for ${this.config.id}`, () => ({
                adSlotName: adSlotName,
                emitted: this.config.adSlotName,
                playerSize: playerSize,
                mode: maiPubVideoBidMode,
            }));

            try {
                pbjs.rp.requestVideoBids({
                    adSlotName: adSlotName,
                    playerSize: playerSize,
                    callback: (videoBidResult) => {
                        clearTimeout(failsafe);

                        maiPubLog(`MaiAdUnitVideo DM: video bid result for ${this.config.id}`, () => ({
                            hasAdTagUrl: !!(videoBidResult && videoBidResult.adTagUrl),
                            hasVastUrl: !!(videoBidResult && videoBidResult.vastUrl),
                            mode: maiPubVideoBidMode,
                            // Full object: DM may carry keys beyond the two documented
                            // ones (bids, errors, a reason) that explain an empty result.
                            keys: videoBidResult ? Object.keys(videoBidResult) : null,
                            raw: videoBidResult,
                        }));

                        // An empty result does not say WHY. Inspect the auction record.
                        if (!videoBidResult || (!videoBidResult.adTagUrl && !videoBidResult.vastUrl)) {
                            maiPubLogVideoAuctionDiagnostics(this.config.id, eventIndex);
                        }

                        const resolved = maiPubResolveVideoBidUrl(videoBidResult, fallbackUrl);
                        settle(resolved.url, resolved.source);
                    },
                });
            } catch (e) {
                clearTimeout(failsafe);
                settle(fallbackUrl, `gam-pod (requestVideoBids threw: ${e.message})`);
            }
        });
    }

	loadIMA(videoVastUrl) { // Explicit parameter definition
		if (window.googletag && window.googletag.pubads && typeof window.googletag.pubads === 'function') {
			maiPubLog(`MaiAdUnitVideo: GPT is loaded for ${this.config.id}. Proceeding with IMA SDK loading.`);
			this.loadIMASDK(videoVastUrl);
		} else {
			window.googletag.cmd.push(() => {
				maiPubLog(`MaiAdUnitVideo: GPT is now loaded for ${this.config.id}. Proceeding with IMA SDK loading.`);
				this.loadIMASDK(videoVastUrl);
			});
		}
	}

	loadIMASDK(videoVastUrl) { // Explicit parameter definition
		if (window.google && window.google.ima) {
			maiPubLog(`MaiAdUnitVideo: IMA SDK already loaded for ${this.config.id}.`);
			this.requestAd(videoVastUrl);
			return;
		}

		if (window.isImaSdkLoading) {
			maiPubLog(`MaiAdUnitVideo: IMA SDK is loading via another player for ${this.config.id}. Waiting.`);
			document.addEventListener('imaReady', () => {
				maiPubLog(`MaiAdUnitVideo: imaReady event received for ${this.config.id}.`);
				this.requestAd(videoVastUrl);
			}, { once: true });
			return;
		}

		maiPubLog(`MaiAdUnitVideo: Attempting to load IMA SDK for ${this.config.id}.`);
		window.isImaSdkLoading = true;
		const script = document.createElement('script');
		script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
		script.onload = () => {
			maiPubLog(`MaiAdUnitVideo: IMA SDK successfully loaded for ${this.config.id}.`);
			window.isImaSdkLoading = false;
			document.dispatchEvent(new CustomEvent('imaReady'));
			this.requestAd(videoVastUrl);
		};
		script.onerror = () => {
			maiPubLog(`MaiAdUnitVideo Error: Failed to load Google IMA SDK script for ${this.config.id}.`);
			window.isImaSdkLoading = false;
			if (this.config.playerType === 'instream' && this.config.contentSrc) {
				this.videoEl.play();
			}
		};
		document.head.appendChild(script);
	}

	requestAd(videoVastUrl) {
		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}

		if (this.adRequestTimeout) {
			clearTimeout(this.adRequestTimeout);
			this.adRequestTimeout = null;
		}

		const requestSeq = ++this._adRequestSeq;

		if (videoVastUrl && videoVastUrl.includes('pubads.g.doubleclick.net')) {
			try {
				const urlObj = new URL(videoVastUrl);
				let custParams = urlObj.searchParams.get('cust_params') || '';
				
				const geo = window.maiPubGeo || {};
				const paramMap = {
					asn: this.config.id,
					geo_ct: geo.country || '',
					geo_rg: geo.region || '',
					geo_ci: geo.city || '',
					geo_zi: geo.postalCode || '',
					geo_lat: geo.lat || '',
					geo_lon: geo.lon || '',
					conn: geo.connectionType || getBrowserConnectionType() || ''
				};

				const paramStrings = Object.entries(paramMap)
					.filter(([_, val]) => val !== '')
					.map(([key, val]) => `${key}=${encodeURIComponent(val)}`);

				if (paramStrings.length > 0) {
					const compiledParams = paramStrings.join('&');
					if (custParams) {
						paramStrings.forEach(pString => {
							const keyCheck = pString.split('=')[0] + '=';
							if (!custParams.includes(keyCheck)) {
								custParams += `&${pString}`;
							}
						});
					} else {
						custParams = compiledParams;
					}
				}

				urlObj.searchParams.set('cust_params', custParams);

				// Attach publisher identity (ppid) + consent (npa) client-side,
				// matching display. See maiPubApplyVastIdentity.
				maiPubApplyVastIdentity( urlObj );

				videoVastUrl = urlObj.toString();
			} catch (e) {
				maiPubLog('MaiAdUnitVideo: Error appending ORTB2 targeting to VAST URL:', e);
			}
		}
		maiPubLog('Requesting VAST URL:', videoVastUrl);
		maiPubLog(`MaiAdUnitVideo: requestAd called for ${this.config.id}`, {
			vastUrl: videoVastUrl,
			playerType: this.config.playerType,
			hasVideoElement: !!this.videoEl,
			hasAdContainer: !!this.adContainer
		});

		if (!videoVastUrl) {
			maiPubLog(`MaiAdUnitVideo Error: No VAST URL available for ${this.config.id}.`);
			if (this.config.playerType === 'instream' && this.config.contentSrc) {
				this.videoEl.play();
			}
			return;
		}


        if (!this.adsLoader) {
			let adDisplayContainer;
			try {
				if (this.config.playerType === 'instream') {
					adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainer, this.videoEl);
				} else {
					adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainer);
				}

				adDisplayContainer.initialize();

				if (this.config.playerType === 'outstream' && this.isMuted) {
					this.container.classList.add('is-muted');
				}
			} catch (error) {
				maiPubLog(`MaiAdUnitVideo: Error creating AdDisplayContainer for ${this.config.id}:`, error);
				return;
			}

			this.adsLoader = new google.ima.AdsLoader(adDisplayContainer);
			// VPAID INSECURE: run VPAID creatives in the page context instead of
			// IMA's secure sandboxed iframe. Many legacy VPAID creatives fail in
			// the sandbox ("Blocked script execution in about:blank … sandboxed")
			// and report VAST error 901. INSECURE trades sandbox isolation for
			// VPAID compatibility. See REVIQ/video-error notes.
			try {
				this.adsLoader.getSettings().setVpaidMode( google.ima.ImaSdkSettings.VpaidMode.INSECURE );
			} catch ( e ) {
				maiPubLog( `MaiAdUnitVideo: Unable to set VPAID mode for ${this.config.id}:`, e );
			}
			this.adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (e) => this.onAdsManagerLoaded(e), false);
			this.adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (e) => this.onAdError(e.error), false);
		}

        const adsRequest = new google.ima.AdsRequest();
        adsRequest.adTagUrl = videoVastUrl;

		adsRequest.linearAdSlotWidth = this.config.width;
    	adsRequest.linearAdSlotHeight = this.config.height;

        maiPubLog(`MaiAdUnitVideo: Requesting ad with dimensions ${this.config.width}x${this.config.height} for ${this.config.id}`);

        adsRequest.setAdWillAutoPlay(!this.config.clickToStart);
        adsRequest.setAdWillPlayMuted(this.isMuted);

        // Set up a timeout to detect if ads are taking too long
        this.adRequestTimeout = setTimeout(() => {
            if (requestSeq !== this._adRequestSeq) {
                return;
            }
            maiPubLog(`MaiAdUnitVideo: Ad request timeout for ${this.config.id} - no response after 10 seconds`);
            this.onAdError({
                message: 'Ad request timeout',
                getErrorCode: () => 1010,
                getErrorType: () => 'AD_LOAD'
            });
        }, 10000);

        this.adsLoader.requestAds(adsRequest);
    }

	onAdsManagerLoaded(event) {
        // Clear the timeout since we got a response
        if (this.adRequestTimeout) {
            clearTimeout(this.adRequestTimeout);
            this.adRequestTimeout = null;
        }

        if (this.adsManager) {
            this._destroyAdsManager(true);
        }

		// v827
		this.isAdPlaying = false;
		this.wasPausedByVisibility = false;
		this.wasPausedByViewability = false;


		// For instream ads, we pass the <video> element (this.videoEl).
		// For outstream ads, we must pass the ad container <div> (this.adContainer).
		//
		const adsManagerElement = (this.config.playerType === 'instream') ? this.videoEl : this.adContainer;


		// FIX (v858): Always pass the adContainer element so the IMA SDK has a UI surface
		// to render the "Skip Ad" buttons and countdown clocks for instream ads.
		// AdsRenderingSettings: give slow media files more time before a linear
		// timeout (VAST 400/402) and preload the media to reduce start latency.
		let adsRenderingSettings = null;
		try {
			adsRenderingSettings = new google.ima.AdsRenderingSettings();
			adsRenderingSettings.loadVideoTimeout = 15000; // default 8000ms
			adsRenderingSettings.enablePreloading = true;
		} catch ( e ) {
			maiPubLog( `MaiAdUnitVideo: Unable to build AdsRenderingSettings for ${this.config.id}:`, e );
		}
		this.adsManager = adsRenderingSettings
			? event.getAdsManager( this.adContainer, adsRenderingSettings )
			: event.getAdsManager( this.adContainer );
		// this.adsManager = event.getAdsManager(adsManagerElement);

		this.adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, () => this.onAdEvent('STARTED'));
        this.adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, () => this.onAdEvent('COMPLETE'));
        this.adsManager.addEventListener(google.ima.AdEvent.Type.SKIPPED, () => this.onAdEvent('SKIPPED'));
        this.adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => this.onAdEvent('ALL_ADS_COMPLETED'));
        this.adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (e) => this.onAdError(e.error));
        // In a pod, an ad that comes back empty or fails to play is only logged here: IMA moves on to the next ad.
        this.adsManager.addEventListener(google.ima.AdEvent.Type.LOG, (e) => this.onAdLog(e));
        this.adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
            if (this.config.playerType === 'instream') {
                maiPubLog(`MaiAdUnitVideo: CONTENT_PAUSE_REQUESTED for ${this.config.id}`);
                this.wasPlayingBeforeAd = !this.videoEl.paused;
                this.videoEl.pause();
            }
        }, false);
        this.adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
            if (this.config.playerType === 'instream' && this.config.contentSrc && this.wasPlayingBeforeAd) {
                maiPubLog(`MaiAdUnitVideo: CONTENT_RESUME_REQUESTED for ${this.config.id}`);
                this.videoEl.play().catch(err => maiPubLog(`MaiAdUnitVideo: play() rejected for ${this.config.id}: ${err.message}`));
            }
        }, false);

        try {
            this._attachResizeObserver();
            const sizingEl = this.innerEl || this.container;
            this.adsManager.init(sizingEl.clientWidth, sizingEl.clientHeight, google.ima.ViewMode.NORMAL);

            // // For outstream videos, configure autoplay muted
            // if (this.config.playerType === 'outstream') {
            //     this.adsManager.setVolume(0); // Start muted
            //     maiPubLog(`MaiAdUnitVideo: Outstream ad configured for autoplay muted: ${this.config.id}`);
            // }
			// FIX: Configure the initial volume path using the user's explicit preference context, v830
			const initialVolume = this.isMuted ? 0 : 1;
			this.adsManager.setVolume(initialVolume);
			maiPubLog(`MaiAdUnitVideo: Outstream ad initialized with volume tracking context: ${initialVolume}`);

            this.adsManager.start();
        } catch (adError) {
            this.onAdError(adError);
        }
    }

	/**
	 * Explicit Dismissal Handler (v836 Compliance Check)
	 * Permanently collapses the ad layout container and isolates event nodes.
	 */
	dismissPlayerPermanently() {
		this.maxRetriesReached = true; // Hard-locks the loop sequence from firing background auctions
		this.adState = 'completed';
		this._openAdGate();
		
		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}
		if (this.adRequestTimeout) {
			clearTimeout(this.adRequestTimeout);
			this.adRequestTimeout = null;
		}

		// Perform total manager erasure
		this._destroyAdsManager(true);
		
		// Force purge all remaining structural visibility style properties
		this.cleanPlayerDOMState(false); 

		if (this.container) {
			this.container.style.setProperty('display', 'none', 'important');
		}

		// New Fix: Unbind named scroll handler and cancel any loose animation ticks, v883
		if (this._onScroll) {
			window.removeEventListener('scroll', this._onScroll);
			this._onScroll = null;
		}
		if (this._syncRafId) {
			cancelAnimationFrame(this._syncRafId);
			this._syncRafId = null;
		}		

		if (this.viewabilityObserver) {
			this.viewabilityObserver.disconnect();
			this.viewabilityObserver = null;
			this._detachResizeObserver();
		}
	}	

    /**
     * Watches the inner box for size changes (float ↔ inline transitions,
     * breakpoint changes, viewport resize) and forwards the new dimensions
     * to the IMA adsManager so the ad creative tracks the container.
     */
    _attachResizeObserver() {
        if (typeof ResizeObserver === 'undefined' || this._resizeObserver || !this.innerEl) {
            return;
        }

        this._resizeObserver = new ResizeObserver(() => {
            if (this._resizeRafId) {
                return;
            }
            this._resizeRafId = requestAnimationFrame(() => {
                this._resizeRafId = 0;
                if (!this.adsManager || !this.innerEl) {
                    return;
                }
                const w = this.innerEl.clientWidth;
                const h = this.innerEl.clientHeight;
                if (!w || !h) {
                    return;
                }
                try {
                    this.adsManager.resize(w, h, google.ima.ViewMode.NORMAL);
                } catch (err) {
                    // IMA throws if called in the wrong state; safe to ignore.
                    maiPubLog(`MaiAdUnitVideo: adsManager.resize() skipped for ${this.config.id}: ${err.message}`);
                }
            });
        });

        this._resizeObserver.observe(this.innerEl);
    }

    _detachResizeObserver() {
        if (this._resizeRafId) {
            cancelAnimationFrame(this._resizeRafId);
            this._resizeRafId = 0;
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    /**
     * Tear down the current adsManager once. Guards against re-entrant AD_ERROR
     * events that IMA fires during destroy().
     */
	_destroyAdsManager(isPermanent = false) {
		this.stopAdCountdown();
		this.isAdPlaying = false;

		if (this.config.playerType === 'outstream') {
			this.cleanPlayerDOMState(true);
		} else if (this.adContainer) {
			this.adContainer.innerHTML = isPermanent ? '' : this.adContainer.innerHTML;
			this.adContainer.style.pointerEvents = 'none';
		}

		if (this.adsManager) {
			const manager = this.adsManager;
			this.adsManager = null;
			try {
				manager.destroy();
			} catch (err) {
				maiPubLog(`MaiAdUnitVideo: adsManager.destroy() skipped:`, err.message);
			}
		}

		// Fix: Always reset the loader for outstream units since their DOM footprint is scrubbed.
		// Keep the optimization active strictly for instream nodes to safeguard rotations.
		if (this.adsLoader && (isPermanent || this.config.playerType === 'outstream')) {
			this.adsLoader = null;
			maiPubLog(`MaiAdUnitVideo Cleanup: Reset adsLoader state for ${this.config.id}.`);
		}
		
		this.syncPlaybackState();
		maiPubLog(`MaiAdUnitVideo Cleanup: Force coordinate sync after adsManager.destroy() for ${this.config.id}.`);
	}

    /**
     * Schedule the next outstream pod after a loop error or completion.
     *
     * @param {number} delayMs Milliseconds before loadAdPod runs.
     */
    _scheduleLoopRetry(delayMs) {
        if (!this.config.loopAds || this.config.playerType !== 'outstream') {
            return;
        }
        if (!isPageVisible || this.maxRetriesReached) {
            return;
        }
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            if (!isPageVisible || this.isAdPlaying || this.adState === 'loading') {
                return;
            }
            // Viewport gate: only request the next pod while the player is actually
            // on screen (in-flow or floating). Firing a GAM request into an
            // off-screen slot inflates request volume and can never be viewable,
            // so instead of burning the request we re-arm and re-check shortly —
            // the reel resumes automatically once the user scrolls it back in.
            if (!this.isPlayerUserVisible()) {
                maiPubLog(`MaiAdUnitVideo: Loop retry deferred for ${this.config.id} — player off screen. Re-checking.`);
                this._scheduleLoopRetry(1000);
                return;
            }
            maiPubLog(`MaiAdUnitVideo: Loop retry for ${this.config.id}.`);
            this.loadAdPod();
        }, delayMs);
    }

    onAdEvent(eventType) {
        maiPubLog(`MaiAdUnitVideo: Ad event '${eventType}' for ${this.config.id}`, {
            playerType: this.config.playerType,
            isAdPlaying: this.isAdPlaying,
            loopAds: this.config.loopAds
        });

        switch (eventType) {
            case 'STARTED':
				this.adState = 'playing';
                this._adErrorRetryCount = 0;
                this._podStarted = true;
                this.isAdPlaying = true;
                this.container.classList.add('is-playing');
                maiPubLog(`MaiAdUnitVideo: Ad started playing for ${this.config.id}`);

				// Dynamic Aspect-Ratio Adaption: Read true creative bounds from the IMA engine, v834
				if (this.adsManager) {
					try {
						const currentAd = this.adsManager.getCurrentAd();
						if (currentAd) {
							const creativeWidth = currentAd.getWidth();
							const creativeHeight = currentAd.getHeight();
							
							if (creativeWidth && creativeHeight && creativeWidth > 0 && creativeHeight > 0) {
								// Update the local CSS custom property variable on the fly
								this.container.style.setProperty('--aspect-ratio', `${creativeWidth}/${creativeHeight}`);
								maiPubLog(`MaiAdUnitVideo Layout Sync: Adjusted container to match creative context (${creativeWidth}x${creativeHeight}).`);
							}
						}
					} catch (layoutErr) {
						maiPubLog(`MaiAdUnitVideo Layout Sync: Dimension check bypassed: ${layoutErr.message}`);
					}
				}

				// Outstream ad UI: countdown pill + click-for-sound overlay.
				if (this.config.playerType === 'outstream') {
					this.startAdCountdown();
				}

				// Resolve the promise to "open the gate"
				this._openAdGate();

                break;
            case 'COMPLETE':
                maiPubLog(`MaiAdUnitVideo: Ad completed for ${this.config.id}`);
                break;
            case 'SKIPPED':
                maiPubLog(`MaiAdUnitVideo: Ad skipped for ${this.config.id}`);
                break;
				case 'ALL_ADS_COMPLETED': {   // v838
                this.adState = 'completed';
                this.isAdPlaying = false;
                this.container.classList.remove('is-playing');
                this.stopAdCountdown();
                maiPubLog(`MaiAdUnitVideo: All ads completed for ${this.config.id}`);

                // Ending without STARTED means the pod came back with no ad: nothing will open the gate it holds.
                this._openAdGate();
            
                this._destroyAdsManager(false);
            
                if (this.viewabilityObserver && !this.config.loopAds) {
                    this.viewabilityObserver.disconnect();
                    this.viewabilityObserver = null;
                    maiPubLog(`MaiAdUnitVideo: Disconnected viewability observer for ${this.config.id} (looping disabled).`);
                    this._detachResizeObserver();
                    
                    // FIX: Hard-collapse the container layout tree out of the DOM if the ad finishes and won't loop
                    // if (this.container) {
                    //  this.container.style.setProperty('display', 'none', 'important');
                    //  maiPubLog(`MaiAdUnitVideo Cleanup: Collapsed container footprint out of the document tree.`);
                    // }

                    // FIX (v858): Only hard-collapse outstream units! 
                    // Instream units must stay visible to play the main video content.
                    if (this.container && this.config.playerType === 'outstream') {
                        this.container.style.setProperty('display', 'none', 'important');
                        maiPubLog(`MaiAdUnitVideo Cleanup: Collapsed container footprint out of the document tree.`);
                    }
                }
            
                if (this.config.playerType === 'outstream' && this.config.loopAds) {
                    if (this._podStarted) {
                        maiPubLog(`MaiAdUnitVideo: Ad pod for ${this.config.id} completed. Looping enabled, scheduling next pod.`);
                        this._scheduleLoopRetry(300);
                    } else {
                        // No ad in the pod started: each came back empty or failed to play. IMA reports those
                        // as LOG events and ends the pod here, never with AD_ERROR, so count it as a failed try.
                        // Asking again 300ms later looped for as long as the player stayed on screen.
                        maiPubLog(`MaiAdUnitVideo: Ad pod for ${this.config.id} ended with no ad played. Counting it as a failed try.`);
                        this.adState = 'error';
                        this._countFailedAttempt(!this._podPlayError);
                    }
                }
                // FIX (v859/v860): Re-enable native browser controls, clear ad-specific aspect ratios,
                // and force an immediate coordinate sync check to keep the player inline on initial page view.
                else if (this.config.playerType === 'instream' && this.config.contentSrc) {
                    this.videoEl.controls = true;
                    this.container.style.removeProperty('--aspect-ratio');
                    this.syncPlaybackState();
                    this.videoEl.play();
                }
                break;
				}
		}
    }

	onAdError(error) {
        if (this._handlingAdError || this._destroyingAdsManager) {
            return;
        }

        this._handlingAdError = true;

        try {
            this.adState = 'error';
            this.isAdPlaying = false;
            this.stopAdCountdown();

            // Resolve the promise to "open the gate"
            this._openAdGate();

            // FIX (v867 Instream Persistence Guard): Only disconnect the viewport observer for outstream placements.
            // Instream players must keep their tracking active to manage floating states for content video tracks.
            if (this.config.playerType === 'outstream' && this.viewabilityObserver && !this.config.loopAds) {
                this.viewabilityObserver.disconnect();
                this.viewabilityObserver = null;
                maiPubLog(`MaiAdUnitVideo: Disconnected viewability observer after error for ${this.config.id} (looping disabled).`);
            }

            if (this.adRequestTimeout) {
                clearTimeout(this.adRequestTimeout);
                this.adRequestTimeout = null;
            }

            const errorCode = error.getErrorCode ? error.getErrorCode() : null;
            const errorType = error.getErrorType ? error.getErrorType() : null;
            // getVastErrorCode() is the actual VAST code (400/303/900/901…) that
            // maps to the GAM VAST-error report — distinct from the IMA
            // getErrorCode() above. Surface it so future errors are diagnosable
            // from our own console.
            const vastCode = error.getVastErrorCode ? error.getVastErrorCode() : null;
            const isNoFill = errorCode === 303 || errorCode === 1009 || errorCode === 1010;

            maiPubLog(`MaiAdUnitVideo Ad Error for ${this.config.id}:`, {
                message: error.message || 'No message',
                code: errorCode ?? 'No code',
                vastCode: vastCode ?? 'No VAST code',
                type: errorType ?? 'No type',
                vastUrl: this.config.vastUrl,
                playerType: this.config.playerType
            });

            // First-party render-error telemetry (no-ops unless ?maideb). Feeds
            // the session tally inspectable via maiPubVastErrorReport().
            maiPubRecordVastError({
                id:         this.config.id,
                code:       errorCode,
                vastCode:   vastCode,
                type:       errorType,
                message:    error.message || null,
                playerType: this.config.playerType
            });

            if (!isNoFill && error.message !== 'Ad request timeout') {
                console.error('Full IMA Error Object:', error);
            }

            this._destroyAdsManager(true);
            this.container.classList.remove('is-playing');

			// The browser refused to start the ad without a tap: no retry would play either.
			if (this.config.playerType === 'outstream' && maiPubIsAutoplayRefused(error)) {
				this._haltRequests('the browser refused to start the ad without a tap');
				return;
			}

			// --- AFTER (Optimized v812 Circuit Breaker) ---
			if (this.config.playerType === 'outstream' && this.config.loopAds) {
				this._countFailedAttempt(isNoFill);
			}
			// FIX (v868 Outstream Non-Looping Error Collapse): If an outstream player fails to load an ad 
			// and looping is disabled, collapse its footprint immediately to prevent empty frames from floating.
			else if (this.config.playerType === 'outstream' && !this.config.loopAds) {
				maiPubLog(`MaiAdUnitVideo Cleanup: Outstream unit failed to fill and looping is disabled. Hard-collapsing layout frame.`);
				this.cleanPlayerDOMState(false); // Pass false to aggressively strip the residual is-seen token
				if (this.container) {
					this.container.style.setProperty('display', 'none', 'important');
				}
				if (this.viewabilityObserver) {
					this.viewabilityObserver.disconnect();
					this.viewabilityObserver = null;
					this._detachResizeObserver();
				}
			}
			// FIX (v864 Instream Error Recovery): Explicitly restore user controls, clear ad aspect ratios,
			// and evaluate scroll coordinates if an ad fails to fill so content playback remains fully functional.
			else if (this.config.playerType === 'instream' && this.config.contentSrc) {
				this.videoEl.controls = true;
				// FIX (v867 Anti-Flash Alignment): Retain properties to keep layout scales uniform.
				// this.container.style.removeProperty('--aspect-ratio');
				this.syncPlaybackState();
                this.videoEl.play();
            }
        } finally {
            this._handlingAdError = false;
        }
    }

	/**
	 * A non-fatal ad error from IMA. In a pod, an ad that comes back empty or fails to play is only
	 * reported here: IMA moves on to the next ad, then ends the pod with ALL_ADS_COMPLETED.
	 *
	 * @since TBD
	 *
	 * @param {google.ima.AdEvent} event The LOG event.
	 */
	onAdLog(event) {
		const data  = event && event.getAdData ? event.getAdData() : null;
		const error = data && data.adError;

		if (!error) {
			return;
		}

		const errorCode = error.getErrorCode ? error.getErrorCode() : null;
		const vastCode  = error.getVastErrorCode ? error.getVastErrorCode() : null;
		const errorType = error.getType ? error.getType() : null;
		const message   = error.getMessage ? error.getMessage() : null;
		const isNoFill  = errorCode === 303 || errorCode === 1009 || errorCode === 1010;

		maiPubLog(`MaiAdUnitVideo: Ad in pod failed for ${this.config.id}:`, {
			message: message || 'No message',
			code: errorCode ?? 'No code',
			vastCode: vastCode ?? 'No VAST code',
			type: errorType ?? 'No type'
		});

		// First-party render-error telemetry (no-ops unless ?maideb).
		maiPubRecordVastError({
			id:         this.config.id,
			code:       errorCode,
			vastCode:   vastCode,
			type:       errorType,
			message:    message,
			playerType: this.config.playerType
		});

		if (!isNoFill) {
			this._podPlayError = true;
		}

		// The browser refused to start the ad without a tap. The pod's next ads, and every pod after it,
		// would be refused the same way, each sending GAM another VAST 400.
		if (this.config.playerType === 'outstream' && maiPubIsAutoplayRefused(error)) {
			this.adState = 'completed';
			this._destroyAdsManager(true);
			this._haltRequests('the browser refused to start the ad without a tap');
		}
	}

	/**
	 * Counts a failed try at a looping outstream pod (an AD_ERROR, or a pod in which no ad started),
	 * then backs off before the next one or trips the circuit breaker. A started ad resets the count.
	 *
	 * @since TBD
	 *
	 * @param {boolean} isNoFill Whether no ad came back (no-fill), rather than an ad that failed.
	 */
	_countFailedAttempt(isNoFill) {
		// Once per pod: IMA can end a pod with ALL_ADS_COMPLETED after its AD_ERROR.
		if (this.maxRetriesReached || this._podCounted) {
			return;
		}

		this._podCounted = true;
		this._adErrorRetryCount++;

		// CIRCUIT BREAKER 1: If demand networks return a true 303 No-Fill, do not loop aggressively.
		// Cut the max retry ceiling from 8 down to outstreamNoFillLimit (2) to protect thread bandwidth.
		const maxAllowedRetries = isNoFill ? outstreamNoFillLimit : 3;

		if (this._adErrorRetryCount >= maxAllowedRetries) {
			this._haltRequests(`tripped after ${this._adErrorRetryCount} consecutive failed pods`);
			return;
		}

		if (isPageVisible) {
			// CIRCUIT BREAKER 2: Implement a steeper exponential backoff multiplier
			// to reduce request frequency if the tracker remains degraded.
			const backoffMs = Math.min(10000 * this._adErrorRetryCount, 60000);
			maiPubLog(`MaiAdUnitVideo: Ad error managed. Backing off for ${backoffMs}ms before attempt ${this._adErrorRetryCount + 1}.`);

			// FIX (v869 Looping Backoff Sync): Force a coordinate validation pass to verify actual window
			// scrolling telemetry before entering the backoff wait window. This keeps the layout inline on scrollTop === 0.
			this.syncPlaybackState();

			this._scheduleLoopRetry(backoffMs);
		} else {
			maiPubLog(`MaiAdUnitVideo: Ad error managed. Page hidden — retry deferred until visible.`);
		}
	}

	/**
	 * Stops this outstream player asking for ads for the rest of the page and collapses it: the circuit
	 * breaker tripped, or the browser won't start a video without a tap.
	 *
	 * @since TBD
	 *
	 * @param {string} reason Why, for the log.
	 */
	_haltRequests(reason) {
		this.maxRetriesReached = true;
		maiPubLog(`MaiAdUnitVideo Circuit Breaker: Halting auction requests for ${this.config.id}: ${reason}.`);

		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}

		// Nothing will start now: let the page's first display ads go.
		this._openAdGate();

		// Force a absolute clear and drop the container out of the rendering hierarchy, v833
		this.cleanPlayerDOMState(false); // false strips everything including is-seen
		if (this.container) {
			this.container.style.display = 'none';
		}

		// New Fix: Clean up scroll listener thread when the circuit breaker trips, v883
		if (this._onScroll) {
			window.removeEventListener('scroll', this._onScroll);
			this._onScroll = null;
		}
		if (this._syncRafId) {
			cancelAnimationFrame(this._syncRafId);
			this._syncRafId = null;
		}

		if (this.viewabilityObserver) {
			this.viewabilityObserver.disconnect();
			this.viewabilityObserver = null;
			this._detachResizeObserver();
		}
	}

}

/**
 * MaiAdUnitAudio Class (v905)
 * Handles native HTML5 audio tracking loops, programmatic Prebid digital audio auctions,
 * and standalone Google IMA SDK execution layers.
 */
class MaiAdUnitAudio {
	constructor(element) {
		this.container   = element;
		this.audioEl     = element.querySelector('.mai-ad-unit-audio__audio');
		this.adContainer = element.querySelector('.mai-ad-unit-audio__ad');

		this.config = {
			id:              element.id,
			slotSlug:        element.id.replace('mai-ad-unit-', ''), // 'audio', 'audio-2' — matches $new_slot_name in the gam registry
			unitId:          element.dataset.unitId || 'audio-default',
			audioSrc:        element.dataset.audioSrc || '',
			vastUrl:         maiPubAdsVars.audioAds?.[element.dataset.unitId || 'audio-default']?.url || '', 
			preroll:         element.dataset.preroll === 'true',
			postroll:        element.dataset.postroll === 'true',
			midrollInterval: parseInt(element.dataset.midrollInterval || '0', 10),
			targets:         element.dataset.targets || '',
			mediaType:       element.dataset.mediaType || 'ao',
		};

		this.state = {
			adPlaying:      false,
			nextMidrollAt:  this.config.midrollInterval || 0,
			prerollPlayed:  false,
			postrollPlayed: false,
		};

		if (maiPubAdsVars.magnite === true) {
			this.adUnitConfig = createPrebidAudioAdUnit(this.config.id, this.config.slotSlug);
		} else {
			this.adUnitConfig = null;
		}

		this.init();
	}

	init() {
		if (!this.audioEl) {
			maiPubLog(`MaiAdUnitAudio: no audio element found for ${this.config.id}`);
			return;
		}

		maiPubLog(`MaiAdUnitAudio: initialized ${this.config.id}`, this.config);

		this.audioEl.addEventListener('play',       () => this.onPlay());
		this.audioEl.addEventListener('timeupdate', () => this.onTimeUpdate());
		this.audioEl.addEventListener('ended',      () => this.onEnded());
	}

	onPlay() {
		if (this.config.preroll && !this.state.prerollPlayed && !this.state.adPlaying) {
			this.state.prerollPlayed = true;
			this.loadAudioPod('preroll'); // Now hits the entry gateway first
		}
	}

	onTimeUpdate() {
		if (!this.config.midrollInterval || this.state.adPlaying) {
			return;
		}

		if (this.audioEl.currentTime >= this.state.nextMidrollAt) {
			maiPubLog(`MaiAdUnitAudio: midroll trigger at ${this.audioEl.currentTime.toFixed(1)}s for ${this.config.id}`);
			this.state.nextMidrollAt += this.config.midrollInterval;
			this.loadAudioPod('midroll'); // Now hits the entry gateway first
		}
	}

	onEnded() {
		if (this.config.postroll && !this.state.postrollPlayed && !this.state.adPlaying) {
			this.state.postrollPlayed = true;
			this.loadAudioPod('postroll'); // Now hits the entry gateway first
		}
	}

	/**
	 * Shared Independent IMA Script Loader
	 */
	loadIMA(callback) {
		if (window.google && window.google.ima) {
			callback();
			return;
		}

		if (window.isImaSdkLoading) {
			maiPubLog(`MaiAdUnitAudio: IMA SDK is loading via another component. Deferring execution request.`);
			document.addEventListener('imaReady', () => callback(), { once: true });
			return;
		}

		maiPubLog(`MaiAdUnitAudio: IMA SDK missing from page layout tree. Dynamically injecting loader...`);
		window.isImaSdkLoading = true;
		
		const script = document.createElement('script');
		script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
		script.async = true;
		
		script.onload = () => {
			maiPubLog(`MaiAdUnitAudio: IMA SDK successfully loaded for audio stream tracking.`);
			window.isImaSdkLoading = false;
			document.dispatchEvent(new CustomEvent('imaReady'));
			callback();
		};
		
		script.onerror = () => {
			maiPubLog(`MaiAdUnitAudio Error: Failed to fetch Google IMA SDK framework hooks.`);
			window.isImaSdkLoading = false;
			this.resumeAudio();
			this.state.adPlaying = false;
		};
		
		document.head.appendChild(script);
	}

	/**
	 * Audio Entry Gateway (Matches MaiAdUnitVideo.loadAdPod)
	 * Resolves programmatic demand state loops before firing the request engine.
	 */
	loadAudioPod(adBreakType) {
		if (this.state.adPlaying) return;

		maiPubLog(`MaiAdUnitAudio: Preparing pipeline loop for break profile: [${adBreakType}]`);
		
		let audioVastUrl = maiPubAdsVars.audioAds?.[this.config.unitId]?.url || this.config.vastUrl;

		this.pauseAudio();
		this.state.adPlaying = true;

		const proceedToIMA = (finalAudioVastUrl) => {
			// Chimer passes a clean, fully resolved URL string downstream
			this.requestAd(finalAudioVastUrl, adBreakType);
		};

		if (maiPubAdsVars.magnite === true && this.adUnitConfig) {
			maiPubLog(`MaiAdUnitAudio: Starting Prebid auction for ${this.config.id} [${adBreakType}]`);
			pbjs.que.push(() => {
				pbjs.requestBids({
					adUnitCodes: [this.adUnitConfig.code],
					bidsBackHandler: (bids) => {
						const winningBid = bids[this.adUnitConfig.code]?.bids?.[0];
						if (winningBid && winningBid.vastUrl) {
							maiPubLog(`MaiAdUnitAudio: Prebid won auction for ${this.config.id}. CPM: ${winningBid.cpm}`);
							proceedToIMA(winningBid.vastUrl);
						} else {
							maiPubLog(`MaiAdUnitAudio: Prebid auction had no fills. Falling back to default tag.`);
							proceedToIMA(audioVastUrl);
						}
					}
				});
			});
		} else {
			proceedToIMA(audioVastUrl);
		}
	}

	/**
	 * Unified Request Engine (Matches MaiAdUnitVideo.requestAd)
	 * Executes a clean URL query string string across the IMA runtime interface.
	 */
	requestAd(audioVastUrl, adBreakType) {
		maiPubLog(`MaiAdUnitAudio.requestAd[${adBreakType}]: Executing VAST request for ${this.config.id}`);

		this.loadIMA(() => {
			if (!this.adContainer) {
				this.adContainer = document.createElement('div');
				this.adContainer.className = 'mai-ad-unit-audio__ad';
				this.container.appendChild(this.adContainer);
			}

			const adDisplayContainer = new google.ima.AdDisplayContainer(this.adContainer);
			adDisplayContainer.initialize();

			const adsLoader = new google.ima.AdsLoader(adDisplayContainer);

			// VPAID INSECURE — run VPAID in page context (compatibility over
			// sandbox isolation). Matches the video path; reduces VAST 901.
			try {
				adsLoader.getSettings().setVpaidMode( google.ima.ImaSdkSettings.VpaidMode.INSECURE );
			} catch ( e ) {
				maiPubLog( `MaiAdUnitAudio: Unable to set VPAID mode for ${this.config.id}:`, e );
			}

			adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (event) => {
				const err      = event.getError();
				// getVastErrorCode() maps to the GAM VAST-error report; distinct
				// from IMA's getErrorCode().
				const vastCode = err.getVastErrorCode ? err.getVastErrorCode() : null;
				const imaCode  = err.getErrorCode ? err.getErrorCode() : null;
				maiPubLog(`MaiAdUnitAudio Error: IMA Ad compilation failed for ${this.config.id}:`, {
					message:  err.getMessage ? err.getMessage() : 'No message',
					code:     imaCode ?? 'No code',
					vastCode: vastCode ?? 'No VAST code',
				});
				this.state.adPlaying = false;
				adsLoader.destroy();
				this.resumeAudio();
			}, false);

			adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (adsManagerLoadedEvent) => {
				// AdsRenderingSettings — longer media timeout + preloading, matching video.
				let audioRenderingSettings = null;
				try {
					audioRenderingSettings = new google.ima.AdsRenderingSettings();
					audioRenderingSettings.loadVideoTimeout = 15000; // default 8000ms
					audioRenderingSettings.enablePreloading = true;
				} catch ( e ) {
					maiPubLog( `MaiAdUnitAudio: Unable to build AdsRenderingSettings for ${this.config.id}:`, e );
				}
				const adsManager = audioRenderingSettings
					? adsManagerLoadedEvent.getAdsManager( this.adContainer, audioRenderingSettings )
					: adsManagerLoadedEvent.getAdsManager( this.adContainer );
				
				adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, () => {
					maiPubLog(`MaiAdUnitAudio: Ad track playing on ${this.config.id}`);
				});

				adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
					maiPubLog(`MaiAdUnitAudio: Ad track complete on ${this.config.id}. Restoring content track.`);
					this.state.adPlaying = false;
					adsManager.destroy();
					adsLoader.destroy();
					this.resumeAudio();
				});

				try {
					adsManager.init(this.container.clientWidth || 300, 40, google.ima.ViewMode.NORMAL);
					adsManager.start();
				} catch (adError) {
					this.state.adPlaying = false;
					this.resumeAudio();
				}
			}, false);

			// ========================================================================
			// MASTER QUERY STRING ALIGNMENT ENGINE
			// ========================================================================
			try {
				const urlObj = new URL(audioVastUrl);
				let rawCustParams = urlObj.searchParams.get('cust_params') || '';
				let combinedParams = {};
				
				if (rawCustParams) {
					rawCustParams.split('&').forEach(pair => {
						const [k, v] = pair.split('=');
						if (k) combinedParams[decodeURIComponent(k)] = decodeURIComponent(v || '');
					});
				}

				// 1. Add vformat and vpos to the whitelist so they aren't stripped
				const standardGamKeys = ['iu', 'sz', 'description_url', 'gdfp_req', 'env', 'output', 'unviewed_position_start', 'correlator', 'ad_type', 'vpmute', 'tfcd', 'npa', 'ciu_szs', 'impl'];
				const currentTopLevelKeys = Array.from(urlObj.searchParams.keys());
				
				currentTopLevelKeys.forEach(key => {
					if (!standardGamKeys.includes(key) && key !== 'cust_params') {
						combinedParams[key] = urlObj.searchParams.get(key);
						urlObj.searchParams.delete(key);
					}
				});

				combinedParams['asn'] = this.config.id;
				combinedParams['break_position'] = adBreakType; // Keep as custom targeting

				const compiledCustParams = Object.entries(combinedParams)
					.map(([k, v]) => `${k}=${v}`)
					.join('&');

				urlObj.searchParams.set('cust_params', compiledCustParams);

				// // 2. Explicitly tell GAM the native video position
				// urlObj.searchParams.set('vpos', adBreakType);

				// Attach publisher identity (ppid) + consent (npa) client-side,
				// matching display and video. See maiPubApplyVastIdentity.
				maiPubApplyVastIdentity( urlObj );

				audioVastUrl = urlObj.toString();

				// 3. Log the final payload so we can inspect it
				maiPubLog(`MaiAdUnitAudio Final VAST URL [${adBreakType}]:`, audioVastUrl);

				const adsRequest = new google.ima.AdsRequest();
				adsRequest.adTagUrl = audioVastUrl;
				adsRequest.linearAdSlotWidth = this.container.clientWidth || 300;
				adsRequest.linearAdSlotHeight = 40;
				adsRequest.setAdWillAutoPlay(true);
				adsRequest.setAdWillPlayMuted(false); 

				adsLoader.requestAds(adsRequest);
			} catch (adError) {
				maiPubLog(`MaiAdUnitAudio Sizing Engine Error:`, adError);
				this.state.adPlaying = false;
				this.resumeAudio();
			}
		});
	}

	pauseAudio()  { this.audioEl.pause(); }
	resumeAudio() { this.audioEl.play(); }
}

/**
 * Normalize a session PPID into a GAM-valid Publisher Provided ID.
 *
 * GAM requires the PPID to be 32-150 alphanumeric characters. The
 * random-source PPID is a UUID with hyphens (e.g.
 * "bd678060-3248-475c-b744-1f5846cf2459"), which GAM rejects. Stripping
 * hyphens yields a 32-char hex string; server/matomo PPIDs are already
 * 64-char SHA-256 hex. Returns '' if the result isn't in the valid range,
 * so callers can skip setting it rather than passing something GAM ignores.
 *
 * Used by both the display path (setPublisherProvidedId) and the video/audio
 * VAST path (maiPubApplyVastIdentity) so all three treat PPID identically.
 *
 * @since TBD
 *
 * @param {string} raw The raw session PPID (maiPubSessionData.ppid).
 * @return {string} A GAM-valid PPID, or '' if none.
 */
/**
 * Is the resolved PPID a PERSON-level id rather than a device-level one?
 *
 * True for 'server' (SHA256 of the logged-in user's email) and 'fpid' (the same
 * hash arriving on a newsletter link). False for 'cookie', 'matomo' and
 * 'random', which are pseudonymous ids belonging to one browser.
 *
 * This exists because several consumers used to test `'server' === ppidSource`
 * as shorthand for "this is a hashed email". That was true while 'server' was
 * the only person-level source; adding 'fpid' silently broke every one of them.
 * Encoding the question once means the next source added has one place to
 * declare itself.
 *
 * @since TBD
 *
 * @param {string} [source] Defaults to the current session's ppidSource.
 * @return {boolean}
 */
function maiPubIsPersonLevelPpid( source ) {
	const src = undefined === source ? maiPubSessionData.ppidSource : source;

	return 'server' === src || 'fpid' === src;
}

function maiPubNormalizeGamPpid( raw ) {
	if ( ! raw ) {
		return '';
	}
	const ppid = String( raw ).replace( /-/g, '' );
	return ( ppid.length >= 32 && ppid.length <= 150 ) ? ppid : '';
}

/**
 * Apply publisher identity (ppid) and consent (npa) to a GAM VAST request URL,
 * mirroring what GPT already does for display. Video and audio bypass GPT —
 * they hit the pubads VAST endpoint directly via the IMA SDK — so without this
 * their requests carry no PPID and (previously) hardcoded npa=0 regardless of
 * consent. Done client-side so we can use the resolved session PPID
 * (server > matomo > random), which is the same value display sends via
 * setPublisherProvidedId and is populated for all visitors, not just logged-in.
 *
 * GAM requires ppid to be 32-150 alphanumeric characters (per Google's GPT reference). The random-source
 * PPID is a UUID with hyphens, so we strip hyphens (→ 32 hex chars) to keep it
 * valid; server/matomo PPIDs are already 64-char SHA-256 hex.
 *
 * Only GAM pubads URLs are touched — Prebid cache VAST URLs (video/audio bid
 * wins) are left untouched since ppid/npa are meaningless there.
 *
 * @since TBD
 *
 * @param {URL} urlObj A parsed VAST request URL, mutated in place.
 * @return {void}
 */
function maiPubApplyVastIdentity( urlObj ) {
	try {
		if ( ! urlObj || ! /(^|\.)pubads\.g\.doubleclick\.net$/.test( urlObj.hostname ) ) {
			return;
		}

		if ( hasConsent === true ) {
			// Personalized: keep npa=0 and attach our identity if valid.
			urlObj.searchParams.set( 'npa', '0' );
			const ppid = maiPubNormalizeGamPpid( maiPubSessionData.ppid );
			if ( ppid ) {
				urlObj.searchParams.set( 'ppid', ppid );
				maiPubLog( `VAST identity: attached ppid (source: ${maiPubSessionData.ppidSource || 'unknown'}).` );
			} else {
				maiPubLog( 'VAST identity: no valid PPID to attach (length out of 32-150 range).' );
			}
		} else {
			// No consent: non-personalized, strip any identity.
			urlObj.searchParams.set( 'npa', '1' );
			urlObj.searchParams.delete( 'ppid' );
			maiPubLog( 'VAST identity: no consent — set npa=1, removed ppid.' );
		}
	} catch ( e ) {
		maiPubLog( 'maiPubApplyVastIdentity: error applying identity to VAST URL', e );
	}
}

/**
 * Initializes all video ad units on the page using the MaiAdUnitVideo class.
 *
 * @since 1.16.3
 */
function maiPubInitVideoAds( adVideoUnits ) {
	maiPubLog('MaiAdUnitVideo: Initializing MaiAdUnitVideo video players.');
	maiPubLog(`MaiAdUnitVideo: Using provided ${adVideoUnits.length} video ad units.`);

	// Loop through each video ad unit.
	adVideoUnits.forEach(adVideoUnit => {
		// Create a new MaiAdUnitVideo instance.
		const player = new MaiAdUnitVideo(adVideoUnit);

		// Add player to the global array.
		videoPlayers.push(player);
	});

	// NOTE: no pbjs.addAdUnits() for video. Demand Manager builds the Prebid ad
	// unit itself from the Ad Unit Pattern matched during requestVideoBids(),
	// so registering on-page ad units here would duplicate (and fight) the AUP.

	// Now initialize the players
	videoPlayers.forEach(player => { // Use the global array
		player.initialize();
	});
}

/**
 * Logs PAAPI module availability after pbjs.setConfig (Prebid 10.x + paapiForGpt).
 *
 * @since 1.26.3
 */
function maiPubConfigurePrebidPaapi() {
	if ( typeof pbjs === 'undefined' ) {
		return;
	}

	const hasGptPaapi = typeof pbjs.setPAAPIConfigForGPT === 'function';

	maiPubLog(
		`pbjs Init: PAAPI config owned by Magnite (wrapper build + parameter set). ` +
		`paapiForGpt: ${hasGptPaapi ? 'available — bridging via setPAAPIConfigForGPT' : 'not in wrapper — enable PAAPI in Magnite DM so the module is in the build'}`
	);
}

/**
 * Apply PAAPI component-auction configs to GPT slots after targeting (refreshes included).
 *
 * @param {googletag.Slot[]} slots GPT slots being refreshed.
 * @since 1.26.3
 */
function maiPubApplyPaapiForGpt( slots ) {
	if ( ! maiPubAdsVars.magnite || typeof pbjs === 'undefined' || typeof pbjs.setPAAPIConfigForGPT !== 'function' ) {
		return;
	}

	try {
		if ( slots && slots.length === 1 ) {
			const slug = slots[0].getSlotElementId().replace( 'mai-ad-', '' );
			pbjs.setPAAPIConfigForGPT( { adUnitCode: slug } );
			maiPubLog( `sendAdserverRequest(): pbjs.setPAAPIConfigForGPT({ adUnitCode: '${slug}' })` );
		} else {
			pbjs.setPAAPIConfigForGPT();
			maiPubLog( 'sendAdserverRequest(): pbjs.setPAAPIConfigForGPT()' );
		}
	} catch ( e ) {
		maiPubLog( 'sendAdserverRequest(): setPAAPIConfigForGPT failed', e );
	}
}

/**
 * Whether an IMA AdError is the browser refusing to start the ad without a tap (IMA 1205,
 * AUTOPLAY_DISALLOWED). IMA reports it to GAM as VAST 400, with the 1205 as its inner error.
 *
 * @since TBD
 *
 * @param {google.ima.AdError|Object} error The error from AD_ERROR or a LOG event.
 *
 * @return {boolean}
 */
function maiPubIsAutoplayRefused( error ) {
	const code  = ( e ) => ( e && 'function' === typeof e.getErrorCode ? e.getErrorCode() : null );
	const inner = error && 'function' === typeof error.getInnerError ? error.getInnerError() : null;

	return 1205 === code( error ) || 1205 === code( inner );
}

/**
 * The video maiPubCheckAutoplay() plays: 0.2s of 16x16 black H.264 with a silent AAC track. The track
 * matters: Safari lets video with no audio start with sound, which would pass the check with sound.
 * Made with: ffmpeg -f lavfi -i color=c=black:s=16x16:r=10:d=0.2 -f lavfi -i anullsrc=r=8000:cl=mono -t 0.2
 * -c:v libx264 -profile:v baseline -crf 51 -g 2 -bsf:v filter_units=remove_types=6 -c:a aac -b:a 8k -ac 1 -movflags +faststart
 *
 * @since TBD
 */
const maiPubAutoplayProbeSrc = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAVMbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAAMgAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAlp0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAABAAAAAAAAAMgAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAABAAAAAQAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAADIAAAAAAABAAAAAAHSbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAoAAAACABVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABfW1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAT1zdGJsAAAAuXN0c2QAAAAAAAAAAQAAAKlhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAABAAEABIAAAASAAAAAAAAAABDExhdmMgbGlieDI2NAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAAL2F2Y0MBQsAK/+EAFmdCwArZHsBEAAADAAQAAAMAUDxImSABAAZoy4BlLIAAAAAQcGFzcAAAAAEAAAABAAAAFGJ0cnQAAAAAAAADmAAAA5gAAAAYc3R0cwAAAAAAAAABAAAAAgAABAAAAAAUc3RzcwAAAAAAAAABAAAAAQAAABxzdHNjAAAAAAAAAAEAAAABAAAAAQAAAAEAAAAcc3RzegAAAAAAAAAAAAAAAgAAAA4AAAAJAAAAGHN0Y28AAAAAAAAAAgAABYAAAAWSAAACQXRyYWsAAABcdGtoZAAAAAMAAAAAAAAAAAAAAAIAAAAAAAAAyAAAAAAAAAAAAAAAAQEAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAACRlZHRzAAAAHGVsc3QAAAAAAAAAAQAAAMgAAAQAAAEAAAAAAbltZGlhAAAAIG1kaGQAAAAAAAAAAAAAAAAAAB9AAAAKQFXEAAAAAAAtaGRscgAAAAAAAAAAc291bgAAAAAAAAAAAAAAAFNvdW5kSGFuZGxlcgAAAAFkbWluZgAAABBzbWhkAAAAAAAAAAAAAAAkZGluZgAAABxkcmVmAAAAAAAAAAEAAAAMdXJsIAAAAAEAAAEoc3RibAAAAH5zdHNkAAAAAAAAAAEAAABubXA0YQAAAAAAAAABAAAAAAAAAAAAAQAQAAAAAB9AAAAAAAA2ZXNkcwAAAAADgICAJQACAASAgIAXQBUAAAAAAB9AAAABJAWAgIAFFYhW5QAGgICAAQIAAAAUYnRydAAAAAAAAB9AAAABJAAAACBzdHRzAAAAAAAAAAIAAAACAAAEAAAAAAEAAAJAAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAABAAAAAQAAABRzdHN6AAAAAAAAAAQAAAADAAAAHHN0Y28AAAAAAAAAAwAABXwAAAWOAAAFmwAAABpzZ3BkAQAAAHJvbGwAAAACAAAAAf//AAAAHHNiZ3AAAAAAcm9sbAAAAAEAAAADAAAAAQAAAD11ZHRhAAAANW1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAACGlsc3QAAAAIZnJlZQAAACttZGF0ARggBwAAAApliIQM8mKAAID+ARggBwAAAAVBmjgX6gEYIAc=';

/**
 * One autoplay check per page for each kind (with or without sound), shared by every player.
 *
 * @since TBD
 */
const maiPubAutoplayChecks = {};

/**
 * Whether this browser lets a video start without a tap. Google's IMA guide: "The only way to check
 * if a video can be autoplayed is to try to play it." So a tiny video is played where the ad would
 * play: with sound first if the player wants sound, then muted.
 *
 * Only a NotAllowedError counts as refused. Any other failure, or no answer within 1s, resolves
 * 'unknown' and the player asks for ads as before, so a check that can't run doesn't cost an ad.
 *
 * @since TBD
 *
 * @param {HTMLElement} host      Where the ad will play.
 * @param {boolean}     withSound Whether to try with sound first.
 *
 * @return {Promise<string>} 'unmuted', 'muted', 'blocked' or 'unknown'.
 */
function maiPubCheckAutoplay( host, withSound ) {
	const key = withSound ? 'sound' : 'muted';

	if ( maiPubAutoplayChecks[ key ] ) {
		return maiPubAutoplayChecks[ key ];
	}

	let video = null;
	let check;

	// Resolves 'played', 'refused' (NotAllowedError) or 'unknown'.
	const tryPlay = ( muted ) => new Promise( ( resolve ) => {
		const timer  = setTimeout( () => resolve( 'unknown' ), 1000 );
		const answer = ( result ) => { clearTimeout( timer ); resolve( result ); };
		const failed = ( e ) => answer( e && 'NotAllowedError' === e.name ? 'refused' : 'unknown' );

		video.muted  = muted;
		video.volume = muted ? 0 : 1;

		try {
			const played = video.play();

			if ( ! played || 'function' !== typeof played.then ) {
				answer( 'unknown' );
				return;
			}

			played.then( () => { video.pause(); answer( 'played' ); }, failed );
		} catch ( e ) {
			failed( e );
		}
	} );

	try {
		video = document.createElement( 'video' );
		video.setAttribute( 'playsinline', '' ); // An iPhone only starts inline video without a tap.
		video.setAttribute( 'webkit-playsinline', '' );
		video.setAttribute( 'aria-hidden', 'true' );
		video.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
		video.src = maiPubAutoplayProbeSrc;
		host.appendChild( video );

		const muted = () => tryPlay( true ).then( ( result ) => ( 'played' === result ? 'muted' : ( 'refused' === result ? 'blocked' : 'unknown' ) ) );

		check = ! withSound ? muted() : tryPlay( false ).then( ( result ) => ( 'played' === result ? 'unmuted' : ( 'refused' === result ? muted() : 'unknown' ) ) );
	} catch ( e ) {
		check = Promise.resolve( 'unknown' );
	}

	maiPubAutoplayChecks[ key ] = check.then( ( result ) => {
		try {
			video.pause();
			video.removeAttribute( 'src' );
			video.load();
			video.remove();
		} catch ( e ) {}

		maiPubLog( `Autoplay check (${ withSound ? 'with sound first' : 'muted' }): ${ result }.` );

		return result;
	} );

	return maiPubAutoplayChecks[ key ];
}

/**
 * Log if debugging.
 *
 * @since 1.12.0
 *
 * @param {mixed} mixed The data to log.
 *
 * @return {void}
 */
/**
 * First-party VAST error telemetry — behind ?maideb only.
 *
 * The GAM "VAST error report" (report #4) is the source of truth, but it lags
 * and can't slice by our own creative/context. When debug is on we keep a
 * running, in-page tally of every AdError keyed by VAST code + device so the
 * render-error mix (audit #13/#14: VPAID 900, mobile 400, no-fill 303) can be
 * cross-checked live. No-ops entirely in production (gated on `debug`), so it
 * adds no memory or work on normal pageviews.
 *
 * Inspect at any point from the console:  maiPubVastErrorReport()
 *
 * @param {Object} entry Normalized error fields: { id, code, vastCode, type, message, playerType }.
 */
function maiPubRecordVastError( entry ) {
	if ( ! debug ) {
		return;
	}

	// Coarse device bucket, aligned with the float breakpoints used elsewhere,
	// so it roughly maps to GAM's Smartphone / Tablet / Desktop dimension.
	const width  = window.innerWidth || document.documentElement.clientWidth || 0;
	const device = width <= 727 ? 'mobile' : ( width <= 1023 ? 'tablet' : 'desktop' );

	const store = window.maiPubVastErrors = window.maiPubVastErrors || {
		total: 0,
		byVastCode: {},
		byImaCode: {},
		byDevice: {},
		entries: []
	};

	const vastKey = entry.vastCode ?? 'none';
	const imaKey  = entry.code ?? 'none';

	store.total++;
	store.byVastCode[ vastKey ] = ( store.byVastCode[ vastKey ] || 0 ) + 1;
	store.byImaCode[ imaKey ]   = ( store.byImaCode[ imaKey ]  || 0 ) + 1;
	store.byDevice[ device ]    = ( store.byDevice[ device ]   || 0 ) + 1;

	// Bounded rolling window of the most recent errors for row-level inspection.
	store.entries.push( {
		t:          new Date().toLocaleTimeString( 'en-US', { hour12: true } ),
		id:         entry.id,
		vastCode:   vastKey,
		imaCode:    imaKey,
		type:       entry.type ?? 'none',
		device,
		playerType: entry.playerType,
		message:    entry.message || '',
		connection: ( typeof getBrowserConnectionType === 'function' ? getBrowserConnectionType() : '' ),
		ua:         navigator.userAgent
	} );
	if ( store.entries.length > 100 ) {
		store.entries.shift();
	}

	maiPubLog( `MaiAdUnitVideo VAST error tally [${device}] vast:${vastKey} ima:${imaKey} — total ${store.total}`, () => ( {
		byVastCode: store.byVastCode,
		byDevice:   store.byDevice
	} ) );
}

// Console accessor: type `maiPubVastErrorReport()` to dump the current tally.
if ( typeof window !== 'undefined' ) {
	window.maiPubVastErrorReport = function () {
		const store = window.maiPubVastErrors;
		if ( ! store ) {
			console.log( 'maipub: no VAST errors recorded (is ?maideb on?)' );
			return null;
		}
		console.log( `maipub VAST errors — total ${store.total}` );
		console.table( store.byVastCode );
		console.log( 'By device:', store.byDevice, '| By IMA code:', store.byImaCode );
		return store;
	};
}

function maiPubLog( ...mixed ) {
	// If not debugging or logging, bail BEFORE touching any arguments.
	if ( ! debug && ! log ) {
		return;
	}

	// Lazy args: any argument passed as a function is invoked only now,
	// once we know we're actually logging. This lets hot call sites defer
	// expensive payload construction (.map/.join, getTargetingMap(), object
	// literals, etc.) so that work is skipped entirely in production.
	// NOTE: a function passed to maiPubLog is treated as a deferred payload,
	// not logged as-is.
	mixed = mixed.map( arg => ( typeof arg === 'function' ? arg() : arg ) );

	// Set log variables.
	let timer = 'maipub ';

	// Set times.
	const current = Date.now();
	const now     = new Date().toLocaleTimeString( 'en-US', { hour12: true } );

	// If first, start.
	if ( timestamp === current ) {
		timer += 'start';
	}
	// Not first, add time since.
	else {
		timer += current - timestamp + 'ms';
	}

	// Log to console with both timer and caller name.
	console.log( `${timer} ${now}`, ...mixed );
}
